/* ====================================================================
   hoh-shop.js — the Shop tab: card packs, collection, card inspector.
   R234.

   What lives here:
     - The Shop view renderer (window.hohHallRenderShop), mounted by the
       Your Hall hub as the 4th pill.
     - Pack banking and saved-pack opening against the server wallet RPCs
       (the server charges, grants, rolls and consumes; this file animates
       only the committed result).
     - The reveal modal, reusing the Hall's .hoh-pack-* CSS with mini
       card faces, followed by the dust animation (non-kept cards
       dissolve into heart motes that fly to the balance chip).
     - The collection boards (FramedFantasy portraits + your own
       heroes) and the full-card inspector (the approved v8 card:
       Gilded Tome standard, Ink Fade mythic, universal logo back).
     - Owner editing of card text via save_card_text.
     - Jangela debug: free opens + one-tap generation of missing FF
       card texts through the gift-cards worker.

   Economy (R712): packs cost 100 hearts; raffle tickets level heroes and are
   no longer pack currency. Earned and bought packs wait in the Wallet.
   Keeps: FF portraits + your own heroes. Everything else dusts:
   1/3/5/8/10/20 by rarity (display mirror only — values are computed
   server-side; never trust these numbers for anything but UI copy).
   ==================================================================== */
(function(){
  'use strict';

  var FF_LOGO = 'https://cdn.shopify.com/s/files/1/0884/2389/1261/files/Logo_Clean.webp?v=1775168383';
  /* ⛔ ============ R569 · CARD TEXT IS A QUEST-AI JOB ============
     Jan: "its supposed to all work in the quest ai cloudflare ... the hall of
     heroes or battlegrounds has nothing to do with gift cards at all, gift
     cards was an old project that i set up and forgot about."
     He is right and this line was the whole problem. A previous wave put the
     Hall's card-text generator on the `gift-cards` Worker and pointed the Hall
     here. That Worker is a separate dormant project holding three secrets,
     none of them ANTHROPIC_API_KEY or SUPABASE_URL, so the route could not
     have worked there at all. quest-ai already holds all three keys and is
     already the Worker every other Hall and Battlegrounds feature talks to.
     The gift-cards address is GONE from this file entirely. It survives only
     in `assets/gift-quiz.js`, which is a real gift-card surface and genuinely
     belongs to that Worker. Nothing in the Hall or Battlegrounds touches it. */
  var CARD_TEXT_BASE = 'https://quest-ai.fondlyframed.workers.dev';
  var CARD_TEXT_ENDPOINT = CARD_TEXT_BASE + '/card-text';
  /* Ember, the kintsugi cleric, fronts the pack on the shelf; falls
     back to the most-hearted FF portrait if she ever leaves the pool. */
  var EMBER_ID = 'c844a24c-8972-430c-b55d-445e9721f35d';
  var reduced = false;
  try{ reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); }catch(_){}
  /* ⛔ R571: `reduced` above is read ONCE, when this file parses, and never
     again. The stylesheet is not so frozen: it re-evaluates
     prefers-reduced-motion continuously. So a device that reports the setting
     late, or turns it on after load, ends up running the ANIMATED branch in
     here against CSS that has already removed the animation. Ask at the moment
     you are about to animate, never at parse time. */
  function motionReduced(){
    try{ return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); }
    catch(_){ return reduced; }
  }
  /* ⛔ R571: answers "is an animation really going to run on this element",
     so nothing ever waits on an `animationstart` that cannot arrive.
     Two shipped rules make that a live possibility on a phone:
     hoh-shop.css sets `.ffshop-card.ffshop-turn{animation:none}` under
     prefers-reduced-motion, and hall-of-heroes.css sets that same card's
     `animation-duration:.01ms!important`. The 0.02s floor below is what
     catches the second one. */
  /* ⛔ R576: a touch device, asked at flip time rather than trusted from parse,
     the same lesson R571 paid for with prefers-reduced-motion. A phone that
     gains a mouse, or a tablet used both ways, answers correctly either way. */
  function coarsePointer(){
    try{ return !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches); }
    catch(_){ return false; }
  }
  /* ⛔ R576 · THE REPAINT KICK. WebKit can leave a face swapped in the DOM and
     unpainted on screen, which is the whole of Jan's "it flips to its same
     side". Reading offsetHeight only forces LAYOUT, and layout is not what is
     stale here, so R572's flush was never enough. Removing the element from
     the render tree and putting it straight back is the one instruction the
     compositor cannot defer: it must lay out and paint from scratch.
     ⛔ ONLY CALL THIS WHERE NO ANIMATION IS RUNNING. A display toggle restarts
     CSS animations inside the subtree, so on the animated desktop path it would
     make the flip stutter. The coarse-pointer branch runs no animation at all,
     which is what makes it safe there. */
  function forceCardRepaint(el){
    if(!el) return;
    try{
      var prev = el.style.display;
      el.style.display = 'none';
      /* the read is what makes the hide take effect before it is undone */
      void el.offsetHeight;
      el.style.display = prev || '';
      void el.offsetHeight;
    }catch(_){ }
  }
  function ffshopWillAnimate(el){
    try{
      var cs = getComputedStyle(el);
      if(!cs.animationName || cs.animationName === 'none') return false;
      return (parseFloat(cs.animationDuration) || 0) > 0.02;
    }catch(_){ return true; }
  }
  /* R380 (2026-08-06, Jan): packs cost 100 hearts now. One raffle ticket
     still opens three. */
  var PACK_COST_HEARTS = 100, PACKS_PER_TICKET = 3;
  /* A card must still open when the cold Shop-state read stalls on a weak
     connection. This is deliberately longer than the normal request but
     finite; the fallback card can render from the Hall pool while the late
     state response continues to warm the next open. */
  var INSPECTOR_COLD_WAIT_MS = 5000;
  var TIER_COLOR = { common:'rgba(160,154,174,.55)', uncommon:'rgba(126,212,154,.8)', rare:'rgba(126,212,255,.85)', epic:'rgba(183,148,244,.85)', legendary:'rgba(255,179,71,.92)', mythic:'rgba(239,148,255,.95)' };
  /* R247: rebalanced up at the top end (epic 8->10, legendary 10->20, mythic
     20->30). This is the DISPLAY mirror of the server ff_card_value function
     (see sql/R247-card-value-rebalance.sql) — the server is the source of
     truth for actual dust + cash payouts, so both must stay in lockstep. */
  var TIER_VALUE = { common:1, uncommon:3, rare:5, epic:10, legendary:20, mythic:30 };
  var PACK_RANK = { common:0, uncommon:1, rare:2, epic:3, legendary:4, mythic:5 };
  /* shared by the flip burst AND the idle pre-flip sparks */
  var PALETTES = { common:['#cfc7da','#a9a2bd'], uncommon:['#7ee6a0','#bdf5cf'], rare:['#8fc0ff','#cfe2ff'], epic:['#d6a4ff','#b06aff'], legendary:['#ffd27a','#ffb84a','#fff0c8'], mythic:['#ef94ff','#b3f5ee','#ffffff'] };
  /* R240: the shelf pack art rotates through the Hall's three shop
     faces. Matched by NAME at runtime (ids proved unreliable to
     transcribe); Ember also has a pinned id below as first choice. */
  var SHELF_NAMES = ['ember','dylora','jaezlan'];
  var shelfTimer = 0;

  var shopState = null;      /* last get_shop_state payload */
  var shopStateEmail = '';   /* the account that payload belongs to */
  var shopStatePin = '';     /* credential generation boundary; never persisted */
  var shopStateGeneration = 0;
  var hostEl = null;
  var busy = false;
  /* Invalidates callbacks and delayed reveal work whenever the visible account
     changes or a newer pack operation takes ownership of the table. */
  var packOperationGeneration = 0;
  var activeRevealScope = null;
  /* R410: OFF by default and only ever flipped by the god panel's own switch.
     The old note here claimed "the server exempts god anyway". It does not.
     No migration on disk carries a god email or an exemption of any kind, so
     the database charges Jan exactly what it charges a player. Flipping this
     on skips the CLIENT side spend only, which means the two disagree. Leave
     it off unless you are deliberately testing the open animation. */
  var debugFree = false;
  var packQueue = [];        /* packs pending reveal after a 3-pack open */
  var lastKeptIds = [];      /* kept pulls this open, for the tile pop after re-render */
  /* R430: ids that landed in this collection within the last 24h, recorded
     CLIENT-side the moment they arrive. It exists because the two moments a
     card becomes new are both invisible to a stale shop payload: a pack
     keeping a pull, and a hero being submitted (whose card R422 grants by
     trigger, with no pull timestamp for the grid to read).
     Stored rather than held in memory so the badge survives the page reload
     that follows a submission, and self-pruning at 24h so it can never grow
     without bound or outlive the badge it drives. */
  var NEW_IDS_KEY = 'hoh-cards-new-v1';
  function newIdsRead(){
    try {
      var m = JSON.parse(localStorage.getItem(NEW_IDS_KEY) || '{}') || {};
      var now = Date.now(), out = {}, dirty = false;
      for(var k in m){
        if(!Object.prototype.hasOwnProperty.call(m, k)) continue;
        if(now - (m[k] | 0) < 86400000) out[k] = m[k]; else dirty = true;
      }
      if(dirty){ try { localStorage.setItem(NEW_IDS_KEY, JSON.stringify(out)); } catch(_){} }
      return out;
    } catch(_){ return {}; }
  }
  /* PERF, and it would have bitten hard: justArrived is called once per TILE,
     and a full Hall grid is a thousand of them. Parsing the stored JSON on
     every call meant a thousand JSON.parse plus a thousand prune loops per
     repaint — on a phone that is a visible stall on a grid that already
     renders a thousand images. The map is read once and held; the only thing
     that can change it is a write, which clears the cache itself. */
  var _newIdsCache = null;
  function newIdsMap(){
    if(_newIdsCache) return _newIdsCache;
    _newIdsCache = newIdsRead();
    return _newIdsCache;
  }
  function justArrived(id){
    if(id == null) return false;
    try { return !!newIdsMap()[String(id)]; } catch(_){ return false; }
  }
  /* public so the submit flow can mark a freshly submitted hero's card */
  window.hohShopMarkNewCard = function(id){
    if(id == null) return;
    try {
      var m = newIdsRead();
      m[String(id)] = Date.now();
      localStorage.setItem(NEW_IDS_KEY, JSON.stringify(m));
      _newIdsCache = m;      /* keep the read cache honest after a write */
    } catch(_){}
  };
  var modalEl = null;
  /* R268: free-first-pack availability, cached from claim_free_pack once per
     page load so the Open button can label itself. Server stays the authority —
     this is only a hint for the label. null = not yet checked. */
  var freePackAvail = null;
  /* R271: whether we have reconciled the free-pack label with the server this
     session (a read-only peek that never consumes the free pack). Guards the
     peek so it runs once, not on every shop repaint. */
  var freePackPeeked = false;
  var PACK_BANK_PENDING_PREFIX = 'hoh-pack-bank-pending-v1:';
  var PACK_OPEN_PENDING_PREFIX = 'hoh-pack-open-pending-v1:';
  function packBankPending(account){
    try {
      var row = JSON.parse(localStorage.getItem(PACK_BANK_PENDING_PREFIX + String(account || '').toLowerCase()) || 'null');
      return row && row.source === 'hearts' && typeof row.id === 'string' && row.id ? row : null;
    } catch(_) { return null; }
  }
  function savePackBankPending(account, row){
    try { localStorage.setItem(PACK_BANK_PENDING_PREFIX + String(account || '').toLowerCase(), JSON.stringify(row)); return true; }
    catch(_) { return false; }
  }
  function clearPackBankPending(account, id){
    try {
      var row = packBankPending(account);
      if(!row || !id || row.id === id) localStorage.removeItem(PACK_BANK_PENDING_PREFIX + String(account || '').toLowerCase());
    } catch(_) {}
  }
  function packOpenPending(account){
    try {
      var row = JSON.parse(localStorage.getItem(PACK_OPEN_PENDING_PREFIX + String(account || '').toLowerCase()) || 'null');
      return row && typeof row.id === 'string' && row.id ? row : null;
    } catch(_) { return null; }
  }
  function savePackOpenPending(account, row){
    try { localStorage.setItem(PACK_OPEN_PENDING_PREFIX + String(account || '').toLowerCase(), JSON.stringify(row)); return true; }
    catch(_) { return false; }
  }
  function clearPackOpenPending(account, id){
    try {
      var row = packOpenPending(account);
      if(!row || !id || row.id === id) localStorage.removeItem(PACK_OPEN_PENDING_PREFIX + String(account || '').toLowerCase());
    } catch(_) {}
  }
  /* R391/R712: the daily pack still has one cross-device UTC-day gate, but it
     now becomes an unopened Wallet entitlement instead of dealing instantly.
     This local flag remains only a paint mirror. */
  function dailyPackKey(account){ return 'hoh-daily-pack-' + new Date().toISOString().slice(0,10) + '-' + String(account||shopAccount()).toLowerCase(); }
  function dailyPackClaimedLocal(account){ try { return localStorage.getItem(dailyPackKey(account)) === '1'; } catch(_){ return false; } }
  function markDailyPackClaimed(account){ try { localStorage.setItem(dailyPackKey(account), '1'); } catch(_){} }
  function dailyResetText(){
    var now = new Date();
    var next = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0, 0);
    var totalMin = Math.max(0, Math.floor((next - now.getTime()) / 60000));
    var h = Math.floor(totalMin / 60), m = totalMin % 60;
    return h > 0 ? 'resets in ' + h + 'h ' + m + 'm' : 'resets in ' + m + 'm';
  }
  function claimDailyPack(btn){
    if(busy) return;
    var scope = captureShopAccount(), em = scope.email, pn = scope.pin;
    if(!em || !pn){ toast('Sign in to save packs.'); return; }
    if(dailyPackClaimedLocal(scope.account)){ if(hostEl) renderShop(hostEl); return; }
    bankSavedPack('daily', {
      accountScope: scope,
      button: btn,
      requestId: 'pack-bank-daily-' + new Date().toISOString().slice(0,10),
      onSuccess: function(){
      markDailyPackClaimed(scope.account);
      }
    });
  }

  /* ============ R489/R712 · THE EXPLORE GIFT BANKS ITS OWN PACK ==========
     The final Explore node pays a pack ON TOP of its raffle ticket and
     Battlegrounds entry. This is deliberately separate from the Shop's daily
     pack: completing Explore is the entitlement, not whether the Shop door was
     used first.

     `hoh_pack_bank(..., 'explore', ...)` atomically owns the cross-device
     milestone gate and entitlement. Its stable account/day request id makes a
     dropped response safe to repeat without opening or minting another pack. */
  function exploreGiftPackShownKey(account){
    return 'hoh-explore-gift-pack-shown-' + new Date().toISOString().slice(0,10) + '-' + String(account || shopAccount()).toLowerCase();
  }
  function exploreGiftPackWasShown(account){
    try { return localStorage.getItem(exploreGiftPackShownKey(account)) === '1'; } catch(_){ return false; }
  }
  function markExploreGiftPackShown(account){
    try { localStorage.setItem(exploreGiftPackShownKey(account), '1'); } catch(_){}
  }
  var exploreGiftPackBankBusy = null;
  /* Compatibility alias for older cached Explore code. Even that caller now
     follows the no-modal completion contract. */
  window.hohShopOpenExploreGiftPack = function(){
    if(typeof window.hohShopBankExploreGiftPack === 'function'){
      return window.hohShopBankExploreGiftPack();
    }
  };

  /* Explore ends on its own cooldown surface. Grant the pack with the exact
     same server receipt and stable request id as the reveal path, but never
     mount summoning or card-reveal UI. A failed request leaves the local latch
     open so the cooldown renderer can retry on the next paint/page visit. */
  window.hohShopBankExploreGiftPack = function(){
    var scope = captureShopAccount(), em = scope.email, pn = scope.pin;
    if(!em || !pn) return Promise.resolve({ok:false,status:'signed_out'});
    if(exploreGiftPackWasShown(scope.account)) return Promise.resolve({ok:true,idempotent:true});
    if(exploreGiftPackBankBusy){
      return exploreGiftPackBankBusy.account === scope.account
        ? exploreGiftPackBankBusy.promise : Promise.resolve({ok:false,status:'busy'});
    }
    var pending = {account:scope.account,promise:null};
    exploreGiftPackBankBusy = pending;
    pending.promise = bankSavedPack('explore', {
      accountScope: scope,
      background: true,
      requestId: 'explore-gift-' + new Date().toISOString().slice(0,10),
      onSuccess: function(d){
        if(d && d.ok === true) markExploreGiftPackShown(scope.account);
      }
    }).then(function(result){
      if(exploreGiftPackBankBusy === pending) exploreGiftPackBankBusy = null;
      return result;
    }, function(){
      if(exploreGiftPackBankBusy === pending) exploreGiftPackBankBusy = null;
      return {ok:false,status:'offline'};
    });
    return pending.promise;
  };

  /* R391 (Jan): HEARTS REPAIR FOR EPHEMERAL CARDS. A dusted own non FF card
     has two roads back now — pull a copy from a pack, or pay hearts at the
     hero's rarity price and the card is whole again. The server RPC
     (hoh_repair_card, R391 SQL) recomputes the price from the hero's hall
     hearts on the engine's own ladder and refuses a stale price, so the
     charge below can always be refunded exactly. FF portraits never dust. */
  var REPAIR_COSTS = [10, 30, 50, 100, 120, 150];
  /* ⛔ R769F: this ladder mirrors the SERVER's hoh_repair_card (R391), which
     prices the repair itself and refuses any price the browser computed
     differently ('price_moved'). R769 moved the bands everywhere else while
     this pair stayed on the old ones, because that function's SQL is not in
     this repo; sql/R769E returned it, sql/R769F re-cuts it, and the two move
     TOGETHER: this line ships in the theme push that sits beside that file.
     Between the two a re-tiered hero's repair is refused and the hearts come
     back, and every band change makes a repair cheaper, so a stale price can
     never overcharge. Display rarity everywhere else uses tierFor. */
  function repairTier(hearts){ hearts = hearts | 0; return hearts >= 1200 ? 5 : hearts >= 1000 ? 4 : hearts >= 500 ? 3 : hearts >= 200 ? 2 : hearts >= 50 ? 1 : 0; }
  /* v39 (Jan): "if you own a hero you can always turn them into something to
     fight with". You could not, and this function is why.
     When the card is not in the cached collection, ownership fell back to
     comparing the signed in email against the CHARACTER AUTHOR's email. A card
     you PULLED FROM A PACK was written by whoever created that hero, so the
     comparison fails and this returned null, and null means the card screen
     prints "Pull it again from a pack" with no repair offered at all. That is
     Chad in the screenshot exactly: made by Evan, pulled by Jan, no way back.
     Callers that already know it is the player's card can now say so. The
     hint only ever loosens a guess we had no business making, and it cannot be
     abused, because hoh_repair_card is the real gate and a refusal refunds the
     hearts in full one screen down. */
  window.hohRepairInfo = function(heroId, hintOwn){
    /* R422 (Jan): REPAIR IS RETIRED — nothing spends copies anymore, so
       "needs repair" cannot be true of any card the server would refuse to
       field. The old formula here still subtracted spent_copies (frozen
       history now), which would have hung Repair buttons with real heart
       prices on cards that fight perfectly well — and the server's
       hoh_repair_card would answer 'already_playable' AFTER the wallet was
       debited. A fully DUSTED card was a choice, and its road back is a
       pack, priced by the pack. Every repair chip, button and lock-panel
       offer keys off this null, so they all retire together; the functions
       stay for API compat. */
    return null;
  };
  window.hohRepairCard = function(heroId, opts){
    opts = opts || {};
    return new Promise(function(resolve){
      var info = window.hohRepairInfo(heroId);
      if(!info){ resolve({ ok:false, why:'not_applicable' }); return; }
      if(!info.need){ resolve({ ok:true, already:true }); return; }
      var em = email(), pn = pin();
      if(!em || !pn){ toast('Sign in first.'); resolve({ ok:false, why:'signed_out' }); return; }
      if(heartBank() < info.cost){
        toast('You need ' + info.cost + ' ♥ to repair ' + info.name + '. Explore the Hall pays hearts daily.');
        resolve({ ok:false, why:'poor' }); return;
      }
      bankAdd(-info.cost, 'spend_card_repair');
      rpc('hoh_repair_card', { p_email: em, p_pin: pn, p_character_id: String(heroId), p_cost: info.cost }).then(function(res){
        var d = res && res.data;
        if(d && d.ok){
          /* mirror the server: restore a play on the cached row, or create one */
          try {
            var st = shopState, hit = null;
            if(st && st.collection){ for(var i=0;i<st.collection.length;i++){ if(String(st.collection[i].character_id) === String(heroId)){ hit = st.collection[i]; break; } } }
            if(hit){
              var spentN = (hit.spent_copies != null ? hit.spent_copies : hit.spent) | 0;
              if(spentN > 0){ if(hit.spent_copies != null) hit.spent_copies = spentN - 1; else hit.spent = spentN - 1; }
              else hit.copies = (hit.copies | 0) + 1;
              if(typeof hit.plays_left === 'number') hit.plays_left = Math.max(1, (d.plays_left | 0) || 1);
            } else if(st && st.collection){
              var hero2 = heroById(String(heroId)) || {};
              st.collection.push({ character_id: String(heroId), name: hero2.name || 'Hero', ff:false, own:true,
                copies:1, cashed:0, kept:1, hearts: hero2.hearts | 0,
                img: hero2.image_thumb_url || hero2.image_url || '', plays_left:1, spent:0 });
            }
          } catch(_){}
          try { document.dispatchEvent(new CustomEvent('hoh:card-repaired', { detail:{ heroId: String(heroId) } })); } catch(_){}
          try { if(typeof window.__hohBuilderRepaint === 'function') window.__hohBuilderRepaint(); } catch(_){}
          try { if(hostEl && document.body.contains(hostEl) && hostEl.querySelector('#ffshop-grid-all')) renderShop(hostEl); } catch(_){}
          if(!opts.quiet) toast(info.name + '’s card is whole again. Ready to fight.');
          resolve({ ok:true, cost: info.cost });
        } else {
          bankAdd(info.cost, 'skip');
          var why = (d && d.error) || 'unknown';
          if(why === 'already_playable'){ resolve({ ok:true, already:true }); return; }
          if(why === 'price_moved') toast('The price moved with this hero’s hearts. Nothing was spent. Try again.');
          else toast('The repair could not be recorded. Nothing was spent.');
          resolve({ ok:false, why: why });
        }
      });
    });
  };

  function esc(s){ if(s==null) return ''; var d=document.createElement('div'); d.textContent=s; return d.innerHTML; }
  /* R268 (grid rework): heroes the user has DUSTED from their own collection.
     They stay in the Hall but leave the Satchel/Shop grid forever. Persisted as
     an array of hero ids so the removal survives reloads and re-renders. */
  var DUSTED_OWN_KEY = 'hoh-shop-dusted-own';
  function dustedOwnIds(){ try{ var a=JSON.parse(localStorage.getItem(DUSTED_OWN_KEY)||'[]'); return Array.isArray(a)?a:[]; }catch(_){ return []; } }
  function isDustedOwn(id){ id=String(id); return dustedOwnIds().some(function(x){ return String(x)===id; }); }
  function addDustedOwn(id){
    try{
      var a=dustedOwnIds(); id=String(id);
      if(!a.some(function(x){ return String(x)===id; })){ a.push(id); localStorage.setItem(DUSTED_OWN_KEY, JSON.stringify(a)); }
    }catch(_){}
  }
  function email(){ return (typeof window.hohYourHallEmail==='function') ? (window.hohYourHallEmail()||'') : ''; }
  function pin(){ try{ var t=JSON.parse(localStorage.getItem('hoh-your-hall-token')||'null'); return (t&&t.pin)||''; }catch(_){ return ''; } }
  function shopAccount(){ return String(email() || '').trim().toLowerCase(); }
  function useShopAccount(){
    var now = shopAccount();
    var nowPin = String(pin() || '');
    if(shopStateEmail !== now || shopStatePin !== nowPin){
      /* A destructive review is consent for one exact account snapshot. An
         in-page sign-in switch must revoke that surface before the new
         account can press its old confirmation button. */
      try{ closeDustAccountUi(now); }catch(_){}
      /* R494: this module stays alive across the Hall's in-page account
         switch. Never let a successful payload from the previous account
         remain readable while the new account is loading or unauthenticated.
         R495: include PIN + generation. Email-only comparison accepted an old
         A response after A -> B -> A and kept old state after a PIN rotation. */
      shopState = null;
      shopStateEmail = now;
      shopStatePin = nowPin;
      shopStateGeneration++;
      authFailed = false;
      freePackAvail = null;
      freePackPeeked = false;
      /* A reveal queue and its busy latch belong to the account that opened
         it. Invalidate both before the new account can touch the Shop. */
      packOperationGeneration++;
      busy = false;
      dustBusyOwner = null;
      packQueue = [];
      lastKeptIds = [];
      activeRevealScope = null;
      try{ closeSummoning(); }catch(_){}
      try{ closeShopInspectors(); }catch(_){}
      /* The old account may still have an inspector state read in flight.
         Cancel its timer and mark that exact wait complete; otherwise its
         late callback can clear or reopen the new account's inspector. */
      try{ cancelInspectorColdWait(window.__hohInspWait); }catch(_){}
    }
    return now;
  }
  function captureShopAccount(){
    return { account:useShopAccount(), generation:shopStateGeneration, email:email(), pin:pin() };
  }
  function shopAccountIsCurrent(scope){
    if(!scope) return false;
    useShopAccount();
    return shopStateEmail === scope.account &&
      shopStateGeneration === scope.generation &&
      shopStatePin === String(scope.pin || '');
  }
  function sb(){ return window.hohSupabaseClient || null; }
  function isGod(){ try{ return (typeof window.hohIsGodMode==='function') && window.hohIsGodMode(); }catch(_){ return false; } }
  /* R235: shop hearts ARE the heart bank (hoh-heart-bank-v1) — the same
     account currency Explore, Fresh Faces and the satchel pouch spend.
     Read through heartsRemainingToday when the host page exposes it,
     write through the same localStorage + hoh:hearts-bumped channel the
     dice trader uses so every counter on the page stays in sync. */
  function heartBank(){
    try{ if(typeof window.heartsRemainingToday==='function') return window.heartsRemainingToday()||0; }catch(_){}
    try{ return parseInt(localStorage.getItem('hoh-heart-bank-v1')||'0',10)||0; }catch(_){ return 0; }
  }
  /* R286: bankAdd takes an optional reason. Every positive add used to fall
     into the 'earn_shop_reward' default, which collapsed FIVE different
     flows (pack auto-dust, cashing spare copies, Dust all extras, dusting
     your own hero, failed-roll refunds) into one giant "Shop rewards + dust"
     bar on the Data Hub. Dusting is the Hall's biggest heart faucet, so it
     needs to be visible on its own. Callers pass a reason; anything that
     does not still lands on the old default, so nothing can go unlogged. */
  function bankAdd(n, reason){
    /* R275: attribute shop bank moves unless a caller already hinted */
    try{ if(!window.__hohHeartHint && window.hohHeartHint) window.hohHeartHint(reason || (n < 0 ? 'spend_shop_pack' : 'earn_shop_reward')); }catch(e){}
    if(!n) return;
    /* ⛔ R594: THE SHOP NO LONGER WRITES THE BANK DIRECTLY. Hearts are the
       server's now, so every move goes through the one funnel, which writes
       the local mirror immediately (so this stays effectively synchronous for
       the caller), tells the server behind it, and puts the mirror back if the
       server refuses. The funnel also fires hoh:hearts-bumped and repaints the
       header, which is why those two lines have gone from here rather than
       being duplicated.
       ⛔ The fallback is not decoration: if hoh-wallet.js ever fails to load,
       the shop must keep working exactly as it did rather than silently
       stop paying people. */
    if(window.HOHWallet && typeof window.HOHWallet.apply === 'function'){
      window.HOHWallet.apply('hearts', n, reason || (n < 0 ? 'spend_shop_pack' : 'earn_shop_reward'));
    } else {
      try{
        var raw=parseInt(localStorage.getItem('hoh-heart-bank-v1')||'0',10)||0;
        localStorage.setItem('hoh-heart-bank-v1', String(Math.max(0, raw+n)));
        try{ document.dispatchEvent(new CustomEvent('hoh:hearts-bumped',{ detail:(n<0?{spent:-n}:{gained:n}) })); }catch(_){}
        try{ if(typeof window.hohRenderHeaderCounter==='function') window.hohRenderHeaderCounter(); }catch(_){}
      }catch(_){}
    }
    updateHeartChip();
  }
  /* Pack payments are taken before the server roll. If Account A leaves while
     that request is in flight, a failed roll must not refund Account B. Keep
     the refund under A's durable email and release it only when A is visible;
     no PIN is persisted. */
  var SHOP_REFUND_KEY = 'hoh-shop-account-refunds-v1';
  function shopRefundsRead(){
    try{ var m=JSON.parse(localStorage.getItem(SHOP_REFUND_KEY)||'{}'); return (m&&typeof m==='object')?m:{}; }
    catch(_){ return {}; }
  }
  function shopRefundsWrite(m){
    try{
      if(Object.keys(m||{}).length) localStorage.setItem(SHOP_REFUND_KEY,JSON.stringify(m));
      else localStorage.removeItem(SHOP_REFUND_KEY);
    }catch(_){}
  }
  function refundShopTicketNow(){
    try{
      if(typeof window.hohRefundUnspentTicket==='function'){ window.hohRefundUnspentTicket(); return true; }
      if(typeof window.hohAddUnspentTicket==='function'){ window.hohAddUnspentTicket(1,'refund'); return true; }
    }catch(_){}
    return false;
  }
  function shopRefundRow(m,account){
    var due=m[account]||{};
    if(!Array.isArray(due.heart_actions)) due.heart_actions=[];
    if((due.hearts|0)>0){ due.heart_actions.push({delta:due.hearts|0,reason:'earn_refund'}); delete due.hearts; }
    due.tickets=Math.max(0,due.tickets|0);
    return due;
  }
  function accountShopHeart(scope,delta,reason){
    var account=String(scope&&scope.account||'').trim().toLowerCase();
    delta=delta|0;
    if(!account||!delta) return;
    if(shopAccount()===account){ bankAdd(delta,reason); return; }
    var m=shopRefundsRead(), due=shopRefundRow(m,account);
    due.heart_actions.push({delta:delta,reason:reason||(delta>0?'earn_shop_reward':'spend_shop_pack')});
    m[account]=due; shopRefundsWrite(m);
  }
  function queueShopRefund(scope, hearts, tickets){
    var account=String(scope&&scope.account||'').trim().toLowerCase();
    hearts=Math.max(0,hearts|0); tickets=Math.max(0,tickets|0);
    if(!account||(!hearts&&!tickets)) return;
    if(shopAccount()===account){
      if(hearts){ accountShopHeart(scope,hearts,'earn_refund'); hearts=0; }
      while(tickets>0&&refundShopTicketNow()) tickets--;
      if(!tickets) return;
    }
    var m=shopRefundsRead(), due=shopRefundRow(m,account);
    if(hearts) due.heart_actions.push({delta:hearts,reason:'earn_refund'});
    due.tickets=Math.max(0,(due.tickets|0)+tickets);
    m[account]=due; shopRefundsWrite(m);
  }
  function flushShopRefunds(){
    var account=shopAccount();
    if(!account) return;
    var m=shopRefundsRead(), due=m[account];
    if(!due) return;
    due=shopRefundRow(m,account);
    delete m[account]; shopRefundsWrite(m);
    var actions=due.heart_actions.slice(), tickets=Math.max(0,due.tickets|0);
    for(var i=0;i<actions.length;i++) bankAdd(actions[i].delta|0,actions[i].reason);
    while(tickets>0&&refundShopTicketNow()) tickets--;
    if(tickets) queueShopRefund({account:account},0,tickets);
  }
  /* R236: the Shop no longer renders its own tickets/hearts chips —
     the page's corner counter is the single display. bankAdd already
     repaints it via hohRenderHeaderCounter; animations target it too. */
  function cornerPill(){
    return document.getElementById('hoh-tk-header') || document.getElementById('hoh-heart-remaining') || null;
  }
  function updateHeartChip(){ /* corner counter repaints via hohRenderHeaderCounter */ }
  /* ⛔ R599 (Jan): "when they claim a free crystal make sure the crystal flies
     from where they claimed it into their inventory same when they buy one it
     should have some particle effects and look clean and magical as it flies
     cleanly into their wallet area."
     ⛔ R648 (Jan): "when you claim your free daily crystal or buy them there
     needs to be an animation and particle effect of it flying to your
     inventory right now its very lame."
     HE IS RIGHT, AND TWO OF THE THREE REASONS WERE DEFECTS RATHER THAN TASTE.

     1. THE BURST WAS NEVER SEEN ON THE FREE CLAIM, THE ONE HE NAMED FIRST.
        crystalBurst appended its particle layer INSIDE the button, and the
        daily handler calls repaintShelf() in the same tick, which rewrites
        host.innerHTML and destroys that layer before one frame of it is
        painted. Fourteen particles, zero of them ever on screen.
     2. NOTHING LANDED. flyMotes fades every mote to opacity 0 across the whole
        journey, so the crystal dissolved in mid-air and the counter it was
        flying to never reacted. A gift that evaporates on the way is not a
        gift arriving.
     3. It was one 20px icon travelling in a straight line at a constant size.
        An object thrown across a room arcs, turns, and shrinks as it goes.

     So this is a ceremony with three beats instead of one transition: the
     crystal is BORN at the button (pop, ring, sparks), it FLIES a lofted arc
     shedding a trail, and it LANDS in the wallet (flash, sparks, and the
     crystal row pulses to say how many arrived).
     ⛔ ONE ARC, SAMPLED TWICE. The flight's keyframes and the trail sparks'
     positions both come out of xtalPath, so a spark can never trace a
     different path from the crystal that dropped it.
     ⛔ EVERY NODE IS PARENTED TO <body> AND POSITIONED FIXED. That is the cure
     for fault 1: a repaint of the shop underneath cannot reach the ceremony.
     ⛔ Transform and opacity only, so a flight across the whole screen never
     costs a layout pass.
     ⛔ flyMotes below is UNCHANGED and still carries hearts; it is also the
     fallback here for a browser with no Element.animate. */
  /* ⛔ 46px, AND IT WAS MEASURED RATHER THAN CHOSEN. The first cut flew at
     34px and was rendered frame by frame against the real card
     (harness/browser/out, from the shipped source): beside the module's own
     96px crystal it read as a smudge crossing a dark page, which is the
     complaint this wave exists to answer, not a fix for it. */
  var XTAL_FLY_MS = 780, XTAL_BIRTH_MS = 300, XTAL_GAP_MS = 110, XTAL_FLY_PX = 46;
  /* THE ARC. A quadratic Bezier lofted above the higher of the two points, so
     the crystal is thrown rather than dragged. The loft scales with the
     distance and is clamped at both ends: a short hop must not read as a
     rainbow and a full-screen flight must not flatten into a straight line. */
  function xtalPath(sx,sy,ex,ey){
    var dx=ex-sx, dy=ey-sy, dist=Math.sqrt(dx*dx+dy*dy)||1;
    var lift=Math.min(200, Math.max(66, dist*0.34));
    var cx=(sx+ex)/2, cy=Math.min(sy,ey)-lift;
    return function(t){
      var u=1-t;
      return { x:u*u*sx+2*u*t*cx+t*t*ex, y:u*u*sy+2*u*t*cy+t*t*ey };
    };
  }
  /* smoothstep with a fifth of linear blended back in, so the crystal leaves
     gently and still arrives carrying speed. Pure smoothstep ends at zero
     velocity, which coasts to a stop and gives the landing flash nothing to
     be the impact of. */
  function xtalEase(t){ return t*t*(3-2*t)*0.8 + t*0.2; }
  /* ⛔ ONE PARTICLE SYSTEM. .hoh-pack-particle is the pack opening's own
     system and already reads --pdx/--pdy/--pcol/--pdur, and PALETTES.mythic
     already IS Jan's brand pair, so this adds a caller rather than a second
     copy of either. The only new thing is the layer: fixed at a viewport
     point on <body>, so nothing that repaints can take it away. */
  function xtalSparks(x,y,n,spread,life){
    if(motionReduced())return;
    try{
      var fx=document.createElement('span');
      fx.className='ffshop-xfx ffshop-xfx--free';
      fx.setAttribute('aria-hidden','true');
      fx.style.left=x+'px'; fx.style.top=y+'px';
      var cols=PALETTES.mythic, html='';
      n=n||12; spread=spread||30; life=life||760;
      for(var i=0;i<n;i++){
        var a=(Math.PI*2*i)/n+Math.random()*0.6, d=spread*(0.55+Math.random()*0.8);
        html+='<span class="hoh-pack-particle" style="--pdx:'+(Math.cos(a)*d).toFixed(0)+'px;--pdy:'+(Math.sin(a)*d).toFixed(0)+'px;--pcol:'+cols[i%cols.length]+';--pdur:'+((life*0.65+Math.random()*life*0.5)|0)+'ms"></span>';
      }
      fx.innerHTML=html;
      document.body.appendChild(fx);
      setTimeout(function(){ try{ fx.remove(); }catch(_){ } }, life+700);
    }catch(_){ }
  }
  /* the shockwave. One ring at the source says a thing was made, one at the
     wallet says it was taken in. */
  function xtalRing(x,y,size,ms){
    if(motionReduced())return;
    try{
      var r=document.createElement('span');
      r.className='ffshop-xring'; r.setAttribute('aria-hidden','true');
      r.style.left=x+'px'; r.style.top=y+'px';
      r.style.setProperty('--xr',(size||58)+'px');
      r.style.setProperty('--xrms',(ms||520)+'ms');
      document.body.appendChild(r);
      setTimeout(function(){ try{ r.remove(); }catch(_){ } },(ms||520)+250);
    }catch(_){ }
  }
  /* ⛔ THE INVENTORY, NOT MERELY THE CORNER. Jan asked for it to fly "to your
     inventory", and the pill carries a dedicated crystal row
     (#hoh-tk-header-xtal-row, built in hoh-tickets.js), so the crystal lands
     in the very count it is about to change. That row is hidden while you
     hold none; claimDailyCrystal and buyCrystals both sync BEFORE they
     resolve, so by the time a flight starts a first crystal's row already
     exists. If it somehow does not, the pill itself is the honest target,
     because near the number beats nowhere. */
  function xtalLandingEl(){
    function live(el){
      if(!el||!el.isConnected)return null;
      var r=el.getBoundingClientRect();
      return (r.width>0&&r.height>0)?el:null;
    }
    return live(document.getElementById('hoh-tk-header-xtal-icon'))
        || live(document.getElementById('hoh-tk-header-xtal-row'))
        || live(cornerPill());
  }
  /* one crystal, born at the source, arcing to the wallet, landing in it */
  function xtalOne(sx,sy,ex,ey,delay,landedCb){
    var W=window.HOHWallet, half=XTAL_FLY_PX/2;
    var node=document.createElement('span');
    node.className='ffshop-xfl'; node.setAttribute('aria-hidden','true');
    /* the box is the icon's own size and its top-left is offset by half of
       it, so the icon's CENTRE is what follows the arc and what scale()
       shrinks toward. No percentage transform on an svg, which is the one
       place this centring could have resolved against a different box. */
    node.style.left=(sx-half)+'px'; node.style.top=(sy-half)+'px';
    node.style.width=XTAL_FLY_PX+'px'; node.style.height=XTAL_FLY_PX+'px';
    /* icon(size,false) drops the wallet's inline glow so the flight's own,
       brighter one in hoh-shop.css is not beaten by an inline style. */
    node.innerHTML=
      '<span class="ffshop-xfl-b" style="animation-delay:'+delay+'ms">'+
        '<span class="ffshop-xfl-s" style="animation-delay:'+delay+'ms">'+
          W.icon(XTAL_FLY_PX,false)+
        '</span>'+
      '</span>';
    document.body.appendChild(node);
    var arc=xtalPath(sx,sy,ex,ey), over=false;
    function land(){
      if(over)return; over=true;
      try{ node.remove(); }catch(_){ }
      xtalRing(ex,ey,72,460);
      xtalSparks(ex,ey,14,30,620);
      if(landedCb) landedCb();
    }
    /* THE FLIGHT STARTS AFTER THE BIRTH POP HAS PLAYED. Beginning both at once
       is most of what made the old one read as an icon sliding away: the
       player never saw the thing they had just been given before it left. */
    setTimeout(function(){
      var STEPS=18, frames=[], k, tt, p;
      for(k=0;k<=STEPS;k++){
        tt=k/STEPS; p=arc(xtalEase(tt));
        frames.push({ offset:tt, transform:'translate3d('+(p.x-sx).toFixed(1)+'px,'+
          (p.y-sy).toFixed(1)+'px,0) scale('+(1-0.56*tt).toFixed(3)+')' });
      }
      var anim=null;
      try{ if(node.animate) anim=node.animate(frames,{duration:XTAL_FLY_MS,easing:'linear',fill:'forwards'}); }catch(_){ anim=null; }
      if(anim){
        anim.onfinish=land;
        /* ⛔ onfinish does not arrive if the tab is backgrounded mid-flight,
           and a crystal frozen over the page forever is far worse than one
           that misses its landing flash. */
        setTimeout(land, XTAL_FLY_MS+500);
      }else{
        /* no Element.animate: the mote transition still crosses the screen and
           still ends at the wallet, it simply travels in a straight line. */
        node.style.transition='transform '+XTAL_FLY_MS+'ms cubic-bezier(.3,.1,.3,1)';
        void node.offsetWidth;
        node.style.transform='translate3d('+(ex-sx).toFixed(1)+'px,'+(ey-sy).toFixed(1)+'px,0) scale(.54)';
        setTimeout(land, XTAL_FLY_MS+40);
      }
      /* THE TRAIL, dropped at the crystal's own position five times along the
         way. Same arc, same easing, so the sparks sit exactly on the path
         rather than near it. */
      for(var q=1;q<=5;q++)(function(fr){
        setTimeout(function(){
          var pp=arc(xtalEase(fr));
          xtalSparks(pp.x,pp.y,4,17,430);
        }, fr*XTAL_FLY_MS);
      })(q/6);
    }, delay+XTAL_BIRTH_MS);
  }
  function flyCrystals(fromEl, n, done){
    var W = window.HOHWallet;
    var to = xtalLandingEl();
    /* ⛔ R600: the Shop repaints on hoh:wallet-synced, so the button that was
       pressed can be DETACHED by the time its purchase resolves. A detached
       node measures 0x0, and the crystal would fly out of the top-left corner
       of the screen. The purchase is still real and still happens; only the
       flight is skipped, because a wrong animation is worse than none.
       ⛔ R571: motionReduced() is asked NOW, not the parse-time `reduced`
       this used to read, which is stale for a device that reports the
       setting late. */
    if(!to || !W || !W.icon || motionReduced() || !fromEl || !fromEl.isConnected){ if(done) done(); return; }
    var f=fromEl.getBoundingClientRect(), t=to.getBoundingClientRect();
    if(!f.width || !f.height){ if(done) done(); return; }
    var sx=f.left+f.width/2, sy=f.top+f.height/2;
    var ex=t.left+t.width/2,  ey=t.top+t.height/2;
    var got=Math.max(1, n|0);
    /* ⛔ SIX IN THE AIR AT MOST, however many were bought. Twelve crystals
       should read as a purchase, not a hailstorm. The bubble at the far end
       still states the true number, so the cap costs the player nothing. */
    var flights=Math.min(6, got), landed=0;
    /* the burst and the ring at the source are the "it happened"; the flight
       is the "it is yours". Both are sized to be seen from a sofa: the first
       cut's 34px spread and 64px ring were invisible in a rendered frame. */
    xtalSparks(sx,sy,20,52,820);
    xtalRing(sx,sy,104,600);
    for(var i=0;i<flights;i++) xtalOne(sx,sy,ex,ey,i*XTAL_GAP_MS,function(){
      if(++landed<flights) return;
      /* THE COUNTER REACTS. Mirrors the hearts arrival rather than inventing
         a second language for the same event; see hoh-tickets.js. */
      try{ if(window.hohPulseCrystalsBumped) window.hohPulseCrystalsBumped(got); }catch(_){ }
      if(done) done();
    });
  }
  /* the shared carrier. `glyph` is any markup; hearts pass a heart, crystals
     pass the crystal. */
  function flyMotes(fromEl, toEl, n, glyph, done){
    try{
      var f=fromEl&&fromEl.getBoundingClientRect(), t=toEl&&toEl.getBoundingClientRect();
      if(!f||!t){ if(done) done(); return; }
      for(var i=0;i<n;i++){
        (function(i){
          var m=document.createElement('span');
          m.className='ffshop-mote'; m.innerHTML=glyph;
          m.style.left=(f.left+f.width/2+(Math.random()*24-12))+'px';
          m.style.top=(f.top+f.height/2+(Math.random()*16-8))+'px';
          document.body.appendChild(m);
          setTimeout(function(){
            m.style.transform='translate('+(t.left+t.width/2-f.left-f.width/2)+'px,'+(t.top+t.height/2-f.top-f.height/2)+'px) scale(.45)';
            m.style.opacity='0';
          }, 20+i*45);
          setTimeout(function(){ try{ m.remove(); }catch(_){} }, 1200+i*45);
        })(i);
      }
      if(done) setTimeout(done, 520);
    }catch(_){ if(done) done(); }
  }
  /* every existing caller keeps working unchanged. Jan, 2026-09-27: hearts
     no longer travel to or from the corner wallet. They pop at whichever end
     is not the wallet (hohHeartBurst in hoh-wallet.js), and `done` still
     arrives when the old flight's did. flyMotes still carries crystals. */
  function flyHearts(fromEl, toEl, n, done){
    function isWallet(el){ return !!(el && el.closest && el.closest('#hoh-tk-header,#hoh-heart-remaining')); }
    var at = isWallet(fromEl) ? toEl : fromEl;
    try{ if(at && !isWallet(at) && typeof window.hohHeartBurst === 'function') window.hohHeartBurst(at, { n: n }); }catch(_){ }
    if(done) setTimeout(done, 520);
  }
  function reqId(p){ return p+'-'+Date.now().toString(36)+'-'+Math.random().toString(36).slice(2,9); }
  /* R769 ladder (mirrors hohGetRarity and the R769D SQL ff_tier_for):
     Common 0–49, Uncommon 50–199, Rare 200–499, Epic 500–999,
     Legendary 1000–1199, Mythic 1200+. */
  function tierFor(h){ h=h||0; if(h>=1200)return'mythic'; if(h>=1000)return'legendary'; if(h>=500)return'epic'; if(h>=200)return'rare'; if(h>=50)return'uncommon'; return'common'; }
  function heroById(id){
    try{
      var all=(typeof window.hohGetAllCharacters==='function')?(window.hohGetAllCharacters()||[]):[];
      for(var i=0;i<all.length;i++){ if(String(all[i].id)===String(id)) return all[i]; }
    }catch(_){}
    return null;
  }
  /* R254: the card's rarity follows the character's CURRENT hearts. Both the
     Hall pool (heroById) and the fresh shop snapshot (get_shop_state, refetched
     on every Shop open) carry hearts; take the higher of the two so a card can
     never DISPLAY a lower rarity than the character currently has (hearts only
     climb). The server cashes at the live DB hearts regardless, so a spare copy
     always sells at the up-to-date rarity. */
  function liveHearts(heroId, snap){
    var hero = heroById(heroId != null ? heroId : (snap && snap.character_id));
    return Math.max((hero && hero.hearts) || 0, (snap && snap.hearts) || 0);
  }
  /* R259: shop images inherit the SAME manual crop the owner set in the Hall
     (hohImgObjPos returns the object-position for a character's primary image),
     so a hero re-framed in the Hall is framed identically on pack cards, tiles
     and the full card — no more heads/feet cut off. */
  function imgPos(id){
    try { return (id != null && typeof window.hohImgObjPos === 'function') ? (window.hohImgObjPos(id, 'primary') || '50% 50%') : '50% 50%'; }
    catch(_){ return '50% 50%'; }
  }
  function imgStyle(id){
    try { return (id != null && typeof window.hohImgStyle === 'function') ? window.hohImgStyle(id, 'primary') : ('object-position:' + imgPos(id) + ';'); }
    catch(_){ return 'object-position:50% 50%;'; }
  }
  function toast(msg){ try{ if(window.hohShowHallToast){ window.hohShowHallToast(msg); return; } }catch(_){} try{ console.log('[Shop]', msg); }catch(_){} }
  function heartSvg(){ return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-8-5.3-10.5-10C-0.5 6.5 3 2.5 7 3.5c2.2.6 3.6 2 5 4 1.4-2 2.8-3.4 5-4 4-1 7.5 3 5.5 7.5C22 15.7 12 21 12 21z"/></svg>'; }
  function ticketSvg(){ return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 1 0 0 4v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 1 0 0-4z"/></svg>'; }

  /* ---------------- server calls ---------------- */

  /* v36 ROOT CAUSE, AND IT EXPLAINS EVERY "NOTHING HAPPENED" REPORT.
     Ten places in this theme end a flow by calling window.hohShowHeartToast,
     every one of them guarded with typeof === 'function'. That function is
     DEFINED NOWHERE. Grepped the whole theme, js and liquid: hohShowHallToast
     exists, in hoh-hall-saved.js, and hohShowHeartToast never has. So every
     one of those messages has been swallowed by its own guard since the day it
     was written, and the pack collect in particular would open the shuffle
     overlay, resolve correctly, close, say its piece into the void and hand
     back to a screen that moved on. From the sofa that is indistinguishable
     from a broken button.
     This does not invent a global that other files would then depend on. It is
     a local channel with three rungs: the real toast if it ever appears, the
     Hall toast that genuinely exists today, and failing both, one built here
     out of inline styles so it cannot be defeated by a missing stylesheet.
     A message the player never sees is not a message. */
  var _sayEl = null, _sayT = null;
  function packSay(title, body){
    var msg = String(body || title || '').trim();
    if(!msg) return;
    try{ if(typeof window.hohShowHeartToast === 'function'){ window.hohShowHeartToast(title || 'The packs held', msg); return; } }catch(_){}
    try{ if(typeof window.hohShowHallToast === 'function'){ window.hohShowHallToast((title ? title + '. ' : '') + msg); return; } }catch(_){}
    try{
      if(!_sayEl){
        _sayEl = document.createElement('div');
        _sayEl.setAttribute('role','status');
        _sayEl.setAttribute('aria-live','polite');
        /* above the pack modal (100070) so it is never buried, and clear of a
           phone's home indicator. Inline because this must work even if the
           stylesheet failed to load, which is one of the ways a player ends up
           needing to be told something in the first place. */
        _sayEl.style.cssText = 'position:fixed;left:50%;bottom:calc(22px + env(safe-area-inset-bottom,0px));'+
          'transform:translateX(-50%) translateY(10px);z-index:100120;max-width:min(420px,calc(100vw - 28px));'+
          'box-sizing:border-box;padding:12px 16px;border-radius:12px;pointer-events:none;'+
          'background:rgba(14,11,19,.96);border:1px solid rgba(201,168,76,.45);color:#f0e6d2;'+
          'font-family:"Cormorant Garamond",Georgia,serif;font-size:15px;line-height:1.4;text-align:center;'+
          'box-shadow:0 14px 34px rgba(0,0,0,.6);opacity:0;transition:opacity .22s ease,transform .22s ease';
        document.body.appendChild(_sayEl);
      }
      _sayEl.innerHTML = (title ? '<b style="display:block;font-family:\'Cinzel\',serif;font-size:9.5px;letter-spacing:1.6px;text-transform:uppercase;color:#e8c674;margin-bottom:5px">'+
        String(title).replace(/[&<>"]/g,'')+'</b>' : '') + String(msg).replace(/[&<>"]/g,'');
      _sayEl.style.display = 'block';
      requestAnimationFrame(function(){
        try{ _sayEl.style.opacity='1'; _sayEl.style.transform='translateX(-50%) translateY(0)'; }catch(_){}
      });
      try{ clearTimeout(_sayT); }catch(_){}
      _sayT = setTimeout(function(){
        try{
          _sayEl.style.opacity='0'; _sayEl.style.transform='translateX(-50%) translateY(10px)';
          setTimeout(function(){ try{ _sayEl.style.display='none'; }catch(_){} }, 260);
        }catch(_){}
      }, 5200);
    }catch(_){}
  }
  try{ window.hohPackSay = packSay; }catch(_){}
  function rpc(name, params){
    var c = sb();
    if(!c || typeof c.rpc !== 'function') return Promise.resolve({ error:{ message:'no client' } });
    return c.rpc(name, params).then(function(res){ return res; }, function(e){ return { error:e }; });
  }
  var authFailed = false;
  function fetchState(){
    var scope = captureShopAccount();
    return rpc('get_shop_state', { user_email: scope.email, user_pin: scope.pin }).then(function(res){
      /* A sign-out or account switch can happen while Supabase is answering. */
      if(!shopAccountIsCurrent(scope)) return null;
      var d = res && res.data;
      authFailed = !!(d && d.ok !== true && d.status === 'unauthenticated');
      if(d && d.ok){ shopState = d; shopStateEmail = scope.account; shopStatePin = String(scope.pin || ''); }
      else if(shopStateEmail !== scope.account || shopStatePin !== String(scope.pin || '')){ shopState = null; }
      return shopState;
    });
  }

  /* R245: expose the collected cards to the party builder so a party can be
     built from the FramedFantasy cards and own heroes pulled from packs.
     Returns character-like objects, resolving the full hero where loaded
     and falling back to the stored snapshot otherwise. */
  window.hohShopOwnedCards = function(){
    try {
      useShopAccount();
      var coll = (shopState && Array.isArray(shopState.collection)) ? shopState.collection : [];
      var out = [];
      for(var i=0;i<coll.length;i++){
        var c = coll[i]; if(!c || c.character_id == null) continue;
        /* R558: a fully dusted FF portrait is no more playable than a fully
           dusted Hall card. The old filter lived later in Contract UI and only
           applied to non-FF keys, so an empty FF stack could still be seated
           after a batch operation. Keep the public owned-card boundary honest
           for every downstream consumer. */
        /* A few warm-cache and compatibility payloads identify collection
           membership without repeating `copies`. Unknown is not the same as
           zero: only an explicit copy count can prove that every copy was
           dusted. The authoritative refresh still carries the real counts. */
        var hasCopies = c.copies != null;
        var copies = hasCopies ? Math.max(0,c.copies|0) : 1;
        var cashedCount = ((c.cashed_copies != null ? c.cashed_copies : c.cashed) | 0);
        var remaining = Math.max(0, copies - cashedCount);
        if(hasCopies && remaining <= 0) continue;
        var hero = (typeof heroById === 'function') ? heroById(c.character_id) : null;
        /* R422 (Jan): the collection fields RIDE ALONG on a copy (copies,
           cashed, own, ff). The party builder needs them to keep a fully
           dusted card out of the muster — the bare hall hero object carried
           no copy counts, so a dusted card read as playable there. Shallow
           copy: the shared hall row must never grow shop fields in place. */
        var cashed = cashedCount;
        if(hero){ out.push(Object.assign({}, hero, { copies: copies, cashed_copies: cashed, own: (c.own === true), ff: !!c.ff })); }
        else { out.push({ id: c.character_id, name: c.name || 'Card', image_url: c.img || '', image_thumb_url: c.img || '', is_ff_portrait: !!c.ff, ff: !!c.ff, own: (c.own === true), copies: copies, cashed_copies: cashed, _packCard: true }); }
      }
      return out;
    } catch(_){ return []; }
  };
  /* R380b: RAW collection rows for readers that need copies and flags
     (the weekly heart bank). hohShopOwnedCards returns hero shaped objects
     and loses those fields, which quietly zeroed the card half of the bank. */
  window.hohShopCollectionRows = function(){
    try { useShopAccount(); return (shopState && Array.isArray(shopState.collection)) ? shopState.collection : []; }
    catch(_){ return []; }
  };
  /* Load the shop state on demand (e.g. the party picker opening) so pack
     cards are available even if the Shop tab was never visited. */
  window.hohShopEnsureState = function(force){
    useShopAccount();
    if(shopState && !force) return Promise.resolve(shopState);
    try { return fetchState(); } catch(_){ return Promise.resolve(null); }
  };

  /* ---------------- card text resolution ----------------
     FF heroes: card_meta written once by the worker (Claude).
     Everyone else: their own story, trimmed at a sentence boundary, with
     the campaign as the flavor line. Owner edits in card_meta override
     both, for any hero the user owns. */

  function excerpt(s, max){
    s = String(s||'').replace(/\s+/g,' ').trim();
    if(!s) return '';
    if(s.length <= max) return s;
    var cut = s.slice(0, max);
    var stop = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('! '), cut.lastIndexOf('? '));
    if(stop > max*0.45) return cut.slice(0, stop+1).trim();
    var sp = cut.lastIndexOf(' ');
    return (sp>0 ? cut.slice(0, sp) : cut).trim() + '…';
  }
  var GENERIC_LEGEND = 'A brave hero of the Hall.';
  /* ⛔ R568 · THE CAPS ARE THE SERVER'S CAPS, WRITTEN ONCE.
     `save_card_text` (sql/R234) stores `left(legend, 260)` and
     `left(quote, 120)`, so anything longer is silently cut by the database
     rather than refused — the owner would press Save, see success, and find a
     sentence ending mid-word. These two numbers ARE those two numbers, and
     `harness/shop.cjs` reads both sides and fails if they ever disagree.
     Jan set the ceiling from a real card: Johnny Sands' description measures
     258 characters and he called it "the most text that you're allowed to
     have", which is why 260 is where this sits rather than somewhere rounder.
     The quote lands at the bottom of the 120-140 he named, because every
     written quote on a live card so far fits inside 60. */
  /* the button's label lives here so the three "put it back" branches in the
     sweep cannot drift from the one that sets it */
  var CARD_TEXT_BTN = 'Write missing card texts';
  var CARD_LEGEND_MAX = 260;
  var CARD_QUOTE_MAX  = 120;
  /* ROOT CAUSE, FOUND BY MEASURING THE SCREENSHOT. Olivia's card printed 239
     characters of her submitter's backstory. The fallback in cardText only
     ever cut at 200, so that text was not a fallback at all — it was already
     SAVED into card_meta.legend by the god "write from row" tool, which cuts
     at 240. Fixing the fallback alone would have changed nothing on her card,
     because stored meta wins.
     So the trust rule moves up a level:
       * The OWNER'S OWN WORDS always win. meta.edited is the flag the editor
         sets, and nothing here second-guesses a human who typed it.
       * Otherwise a legend that simply REPEATS the hero's story is not card
         text, whoever wrote it there. Comparing the first 80 normalized
         characters catches both cut lengths and any hand-pasted story,
         without needing to know which tool wrote it.
       * Otherwise it is real written card text and it stands. */
  function _norm(s){ return String(s||'').replace(/\s+/g,' ').replace(/[\u2018\u2019]/g,"'").trim().toLowerCase(); }
  function isStoryEcho(legend, story){
    var a = _norm(legend).replace(/[\u2026.]+$/,'').slice(0, 80);
    var b = _norm(story);
    return a.length >= 24 && b.length >= 24 && b.indexOf(a) === 0;
  }
  /* SELF-CAUGHT: rejecting the story echo for DISPLAY was only half the job.
     Every generation gate asked "does card_meta.legend exist?", so Olivia —
     whose legend exists but is her own backstory — counted as already
     written, and both the on-open generator and the god backfill would have
     skipped her forever, leaving her stuck on the neutral line. Display and
     generation ask the SAME question now. */
  function hasRealCardText(hero){
    var meta = (hero && hero.card_meta) || {};
    if(!meta.legend) return false;
    if(meta.edited === true) return true;
    return !isStoryEcho(meta.legend, hero && hero.story);
  }
  /* R423 (Jan): NO CARD PRINTS THE PLAYER'S TYPED STORY ANYMORE.
     Jan's two screenshots showed the same bug wearing two faces. Olivia (an
     FF legendary) carried her submitter's backstory cut mid-word on "a
     Kobold named Rax who...", a flagship card reading like a form field.
     Kassandra (non-FF) carried the same thing in second person, "You are the
     survivor who turned suffering into...", writing addressed to the player
     printed on a trading card about them.
       * An FF portrait shows WRITTEN card text or the neutral line. Until
         the generator has written it, it stays `pending`, so ensureCardMeta
         upgrades it the moment real text exists.
       * A non-FF portrait is GENERIC by design — one line that fits every
         hero in the Hall — UNLESS its owner has written their own, which is
         stored on the character row so everyone holding a copy sees it.
     The campaign flavour line goes the same way for non-FF: it is
     player-typed too, and an empty flavour block simply does not render. */
  function cardText(hero, snap){
    var meta = (hero && hero.card_meta) || {};
    var isFF = hero ? !!hero.is_ff_portrait : !!(snap && snap.ff);
    var owned = meta.edited === true;             /* the owner typed it */
    var t = { title:'', epithet:'', legend:'', quote:'', pending:false };
    if(owned || isFF){
      t.title   = meta.title   || '';
      t.epithet = meta.epithet || '';
    }
    /* ⛔ ============ R568 · A NON-FF CARD MAY NOW CARRY WRITTEN WORDS ============
       Jan: "at all of the cards that say a brave hero of the hall, I want it to
       retroactively populate them", and his screenshots of that line are HEROES
       OF THE HALL cards — non-FF.
       This line used to read `if(owned || isFF)`, so a non-FF card ignored
       `card_meta.legend` entirely unless its owner had typed it. Generating
       text for those cards would have written 1,100 rows nothing rendered.
       ⛔ R423's PROTECTION IS NOT WHAT MOVED. That wave existed because non-FF
       cards were printing the SUBMITTER'S TYPED STORY back at them in second
       person. The thing that catches that is `isStoryEcho` on the very next
       line, and it runs on every card whoever wrote the text. What R423 also
       did — refuse to read the field at all — was a second lock on the same
       door, and it is the one that now has to open.
       THE QUOTE DOES NOT MOVE. Jan: "characters that aren't an FF portrait,
       they should not be able to have a quote. It should just be pretty
       standard with just the description." So the quote is FF-only outright,
       not FF-or-owned: a quote stored on a non-FF card before R438 removed
       that field is a leftover, and printing it now would be honouring a
       control that no longer exists. */
    t.legend = meta.legend || '';
    t.quote  = isFF ? (meta.quote || '') : '';
    if(t.legend && !owned && isStoryEcho(t.legend, hero && hero.story)){
      t.legend = ''; t.title = ''; t.quote = t.quote || '';
    }
    if(!t.legend){
      /* `pending` only ever means "ensureCardMeta is about to fetch this one",
         and that stays FF-only ON PURPOSE: it fires per CARD VIEW, so widening
         it would spend a model call every time anybody opened any of the
         thousand-odd Hall cards. The non-FF fill is a deliberate one-off sweep
         (generateMissingCardText), not a thing that happens by looking. */
      if(isFF) t.pending = true;
      t.legend = GENERIC_LEGEND;
    }
    if(!t.quote && isFF){ t.quote = (hero && hero.campaign) ? String(hero.campaign) : ''; }
    /* ⛔ THE CAP IS ENFORCED AT THE GLASS, not only at the keyboard.
       `save_card_text` cuts at 260/120, but the /card-text worker writes
       `card_meta` itself and this repo does not hold its source, so it can and
       does store longer text than the editor would accept. Jan: "some of the
       very long ones kind of start looking bad." Clamping here means no card
       can ever print past the ceiling regardless of who wrote the row, and
       `excerpt` cuts at a sentence end rather than mid-word. */
    t.legend = excerpt(t.legend, CARD_LEGEND_MAX);
    t.quote  = excerpt(t.quote,  CARD_QUOTE_MAX);
    return t;
  }
  /* ⛔ R568: the one predicate the retro-fill trusts.
     Jan: "Do not overwrite any changes of people who already changed this. So
     if it doesn't say a brave hero of the hall, do not change those."
     `edited:true` is the flag the editor sets, and it is checked FIRST and
     alone — no other condition can talk past it. Everything after it is a way
     of being empty: never written, written as the placeholder, or a paste of
     the submitter's own story, which R423 established is not card text. */
  function needsCardText(hero){
    var meta = (hero && hero.card_meta) || {};
    if(meta.edited === true) return false;
    if(!meta.legend) return true;
    if(_norm(meta.legend) === _norm(GENERIC_LEGEND)) return true;
    return isStoryEcho(meta.legend, hero && hero.story);
  }
  /* R244: after the worker fails once, stop retrying for the rest of the
     page load — every FF card open was refiring a doomed network call.
     The god "Generate" button clears the latch via its health probe. */
  var workerDown = false;
  function ensureCardMeta(heroId, onDone){
    var hero = heroById(heroId);
    if(hasRealCardText(hero)){ if(onDone) onDone(hero.card_meta); return; }
    /* R423 (Jan): the generator is FF-ONLY again. R422 widened it so non-FF
       cards would have real words, but Jan's call is that non-FF cards read
       one generic line their owner can edit — so spending a model call on
       them writes text nobody asked for and nobody can predict. */
    if(!hero || !hero.is_ff_portrait || workerDown){ if(onDone) onDone(null); return; }
    fetch(CARD_TEXT_ENDPOINT, {
      method:'POST', headers:{ 'content-type':'application/json' },
      body: JSON.stringify({ character_id: heroId })
    }).then(function(r){ if(r.status===404){ workerDown = true; } return r.json(); }).then(function(d){
      if(d && d.meta && hero){ hero.card_meta = d.meta; }
      if(onDone) onDone(d && d.meta || null);
    }).catch(function(){ workerDown = true; if(onDone) onDone(null); });
  }

  /* ==================== R323: SPOTLIGHT CARD EXPORTS ==================== */
  /* The FF Spotlight event (hoh-fresh-faces.js) mounts the REAL trading
     card — the exact face, flip, hover tilt, fitters and collector number
     the inspector renders — minus the owner-only tools (drag, edit, cash).
     These two functions are the only public doors; everything they touch
     stays private to this file. */
  /* R558: opened cards have two genuinely different backs. Public Hall cards
     keep the branded physical back; Profile cards turn over to their live
     abilities and controls. This synchronizer makes the hidden face inert as
     well as invisible, so keyboard focus can never disappear behind the card. */
  function syncCardFaceState(cardEl){
    if(!cardEl) return;
    var on = cardEl.classList.contains('on');
    var front = cardEl.querySelector('.ffshop-face.is-front');
    var back = cardEl.querySelector('.ffshop-face.is-back');
    if(front){ front.setAttribute('aria-hidden', on ? 'true' : 'false'); if(on) front.setAttribute('inert',''); else front.removeAttribute('inert'); }
    if(back){ back.setAttribute('aria-hidden', on ? 'false' : 'true'); if(on) back.removeAttribute('inert'); else back.setAttribute('inert',''); }
    var name = cardEl.getAttribute('data-card-name') || 'Hero';
    var abilities = cardEl.classList.contains('ffshop-card-profile');
    var hallFlip = null;
    try{
      var hallStage = cardEl.closest && cardEl.closest('.hoh-hall-card-stage');
      hallFlip = hallStage && hallStage.querySelector('[data-hall-card-flip]');
    }catch(_){}
    /* The Hall's native flip control is the single keyboard/SR action. Keep
       its name in sync even when a pointer tap on the physical card caused
       the flip, and do not tell people to press Enter on a non-focusable
       article. */
    if(hallFlip){
      hallFlip.setAttribute('aria-label', on ? ('Show '+name+' card art') : ('Flip '+name+' card to back'));
    }
    /* The Profile card has a real, plainly named control directly beneath it.
       Do not make the article itself a second pseudo-button: its ability face
       contains real buttons, and button semantics on the parent would flatten
       those descendants for assistive technology. Pointer taps may still flip
       the physical object, while keyboard/SR players use that external control. */
    cardEl.setAttribute('aria-label', name + (on
      ? (abilities ? ' abilities' : (hallFlip ? ' card back' : ' card back. Press Enter to show the card art.'))
      : (abilities ? ' card art' : (hallFlip ? ' card front' : ' card front. Press Enter to show the card back.'))));
    /* ⛔ R572 · BELT AND BRACES FOR THE WEBKIT STALE PAINT. The real cure is
       in the stylesheet: both faces are laid out at all times now, so a flip
       no longer asks for a first paint of a new subtree. This is the second
       lock on the same door, and it sits here because this function is the
       ONE place every face change passes through, whichever of the two flips
       ran. Reading a geometry property forces the pending style change to be
       flushed rather than left for whenever WebKit next feels like it, which
       is the documented remedy for a face that changes in the DOM seconds
       before it changes on screen. One read per flip, on one element. */
    try{ var _live = on ? back : front; if(_live) void _live.offsetHeight; }catch(_){}
  }
  window.hohShopCardHtml = function(heroId, options){
    try {
      options = options || {};
      var _cn = ffCollectorNo(heroId);
      return '<div class="ffshop-cardbox">' +
          '<div class="ffshop-tiltwrap">' + fullCardHtml(heroId, null, options) + '</div>' +
        '</div>' +
        /* ⛔ R576 (Jan): "do not track the non FF cards so dont show 2/1109 or
           whatever on non ff cards, also that # is wrong for those non ff cards
           as well, that card i sent was one of the last submitted yet it says
           2/1109."
           HE IS RIGHT, AND IT WAS WORSE THAN WRONG, IT WAS BACKWARDS. hallCollNo
           took the hero's RAW INDEX in window.hohGetAllCharacters() with no sort
           at all, and that list arrives newest-first, so the newest hero in the
           Hall was numbered 1 and the oldest was numbered 1109. Measured on live
           data: Mavis, submitted 2026-08-24, sat at index 1 and printed exactly
           the "2 / 1109" in Jan's screenshot, while her true position among Hall
           heroes oldest-first is 1000 of 1001. The total was wrong too, counting
           the 108 FramedFantasy portraits inside a line labelled "Heroes of the
           Hall". And because it is a position in a shared list, EVERY hero's
           number changed the moment anybody else submitted. That is not a
           collector number, so it is gone rather than patched.
           ⛔ THE FF NUMBER IS NOT AFFECTED AND WAS VERIFIED SEPARATELY:
           ffCollectorNo filters to FF portraits and sorts created_at ASCENDING,
           so #1 is the oldest and a number never moves. Measured live: Ember
           (March) is 1 of 108 and Juniper (August) is 108 of 108. */
        (options.showCollectorNumber === false || !_cn ? '' :
          '<div class="ffshop-collno"><span class="ffshop-collno-num"><b>'+_cn.n+'</b> / '+_cn.total+'</span><span class="ffshop-collno-lbl">FramedFantasy portraits</span></div>');
    } catch(_){ return ''; }
  };
  /* ⛔ HEROPOLISH (Jan, 2026-09-25) · AN OPENED CARD'S LEAN AND MOTE, ONE COPY FOR THE HALL AND THE HERO PAGE.
     Jan, on the hero page's card lightbox: "make sure it also has the same effects as the hall aka the
     orange mote for the mouse, subtle tilt effects". This is the Hall's own lean and mote (R573, R582,
     R742), moved here word for word from hohBindHallTilt in hoh-section-core.js so both pages run the
     same code: the Hall's card view calls it through hohBindHallTilt, the hero page through its card
     lightbox (hoh-hero-page.js bindCardLean). hoh-shop.js is the one file both have loaded by the time a
     card opens.
     hohCardLean(stage, o) binds and answers a handle whose release() undoes all of it, or null when
     nothing binds:
       · reduced motion (the card stays still and the arrow stays an arrow);
       · a coarse pointer. ⛔ R582: nothing on the card reacts to touch; Jan's phone could not carry a
         live transform on this element, so a phone gets a still picture;
       · any pointer without hover.
     o.moteParent  where the mote mounts (default document.body). It is fixed to the viewport and moved
                   by clientX/clientY, so the stage's live 3D rotation can never skew it; its parent must
                   have no transformed ancestor, or position:fixed stops meaning the viewport.
     o.moteClass   its class (default 'hoh-card-mote', hall-of-heroes.css)
     o.hostClass   the class the stage wears while the mote stands in for the arrow (default
                   'hoh-card-mote-host'); it goes on at the first move, never on entry, so there is never a
                   frame with no arrow and no mote
     ⛔ THE LEAN IS WRITTEN TO THE STAGE, NEVER TO .ffshop-tiltwrap. Both pages scale the card on the
     tiltwrap with transform !important (R563), which throws any write there away. And the stage must
     not be running a fill-mode CSS animation that ends on a transform, or that end state beats the
     inline lean (the hero page takes its entrance class off when the entrance ends). */
  window.hohCardLean = function(stage, o){
    o = o || {};
    if(!stage) return null;
    var mq = function(q){ try{ return !!(window.matchMedia && window.matchMedia(q).matches); }catch(_){ return false; } };
    if(mq('(prefers-reduced-motion: reduce)') || mq('(pointer: coarse)') || !mq('(hover:hover) and (pointer:fine)')) return null;
    var MOUSE_MAX = 5, host = o.hostClass || 'hoh-card-mote-host';
    var s = { raf: 0, tx: 0, ty: 0, cx: 0, cy: 0, mote: null, moteOn: 0, dead: false };
    function clamp(v, lo, hi){ return v < lo ? lo : (v > hi ? hi : v); }
    function draw(){
      s.raf = 0;
      /* self-healing: a stage that left the page takes its lean and its mote with it */
      if(!document.body.contains(stage)){ release(); return; }
      /* a low pass, so a hand that is never quite still does not jitter the card */
      s.cx += (s.tx - s.cx) * 0.18;
      s.cy += (s.ty - s.cy) * 0.18;
      stage.style.transform = 'perspective(1000px) rotateX(' + s.cy.toFixed(2) + 'deg) rotateY(' + s.cx.toFixed(2) + 'deg)';
      if(Math.abs(s.tx - s.cx) > 0.04 || Math.abs(s.ty - s.cy) > 0.04) queue();
    }
    function queue(){ if(!s.raf && !s.dead) s.raf = requestAnimationFrame(draw); }
    /* R742 (Jan): over the card the arrow becomes a warm mote of light. It rides the lean's own
       pointermove, so hovering the card costs one listener and one rAF. */
    try{
      s.mote = document.createElement('span');
      s.mote.className = o.moteClass || 'hoh-card-mote';
      s.mote.setAttribute('aria-hidden', 'true');
      (o.moteParent || document.body || document.documentElement).appendChild(s.mote);
    }catch(_){ s.mote = null; }
    function move(e){
      if(s.mote){
        s.mote.style.transform = 'translate3d(' + e.clientX + 'px,' + e.clientY + 'px,0)';
        if(!s.moteOn){ s.moteOn = 1; s.mote.classList.add('is-in'); stage.classList.add(host); }
      }
      var r = stage.getBoundingClientRect();
      if(!r.width || !r.height) return;
      s.tx = clamp(((e.clientX - r.left) / r.width - 0.5) * 2 * MOUSE_MAX, -MOUSE_MAX, MOUSE_MAX);
      s.ty = clamp(-((e.clientY - r.top) / r.height - 0.5) * 2 * MOUSE_MAX, -MOUSE_MAX, MOUSE_MAX);
      queue();
    }
    function leave(){
      /* the arrow comes back the moment the pointer leaves the card */
      if(s.mote){ s.moteOn = 0; s.mote.classList.remove('is-in'); }
      try{ stage.classList.remove(host); }catch(_){ }
      s.tx = 0; s.ty = 0; queue();
    }
    /* a card that closes while the pointer is still on it must never leave a light behind, or a page
       with no cursor */
    function release(){
      if(s.dead) return;
      s.dead = true;
      try{ if(s.raf) cancelAnimationFrame(s.raf); }catch(_){ }
      s.raf = 0;
      try{ stage.removeEventListener('pointermove', move); stage.removeEventListener('pointerleave', leave); }catch(_){ }
      try{ if(s.mote && s.mote.parentNode) s.mote.parentNode.removeChild(s.mote); }catch(_){ }
      try{ stage.classList.remove(host); stage.style.transform = ''; }catch(_){ }
    }
    stage.addEventListener('pointermove', move, { passive: true });
    stage.addEventListener('pointerleave', leave, { passive: true });
    return { stage: stage, release: release };
  };
  window.hohShopWireCard = function(wrap){
    try {
      if(!wrap) return;
      var cardEl = wrap.querySelector('.ffshop-card');
      if(!cardEl) return;
      var fineHover = false;
      try{ fineHover = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches); }catch(_){}
      /* R339: INSIDE THE DECK, THE DECK OWNS THE TRANSFORM.
         The hover tilt and the flip's `wrap.style.transform='none'` both write
         an INLINE transform onto the tiltwrap -- and inline beats the class
         rule that scales the card down to the deck footprint. Left alone, the
         first mouse move over a deck card would snap it back to full 380x650
         and blow the layout apart. The tilt is a nicety on a single opened
         card; the scale is load-bearing here, so the tilt stands down. */
      var inDeck = false;
      try{ inDeck = !!(wrap.closest && wrap.closest('.ffshop-deck-card')); }catch(_){}
      if(inDeck) fineHover = false;
      /* ⛔ R563: THE SAME RULE, ONE STAGE FURTHER. `.hoh-hall-card-stage
         .ffshop-tiltwrap` sets `transform:scale(...)!important` in
         hall-of-heroes.css, so on the Hall's opened card every pointermove was
         doing a getBoundingClientRect, a rAF and a style write that the
         cascade then threw away. Measured in a browser: the computed transform
         is byte-identical before and after the inline write. That is pure
         main-thread cost on the exact screen whose flip Jan reports as laggy,
         and the R339 precedent above (a deck forces fineHover false for the
         same reason) simply was never extended here. */
      /* HEROPOLISH: the hero page's card lightbox (.hhp-cv-stage) scales its tiltwrap the same way, and
         leans the whole stage with hohCardLean instead, so the wrap's own tilt stands down there too */
      try{ if(wrap.closest && wrap.closest('.hoh-hall-card-stage,.hhp-cv-stage')) fineHover = false; }catch(_){}
      /* R339b: this function is called again to RE-FIT a card once its
         container finally has width. Every call used to add another click
         listener, so a re-fitted card flipped twice per tap and landed back
         where it started. Bind once; re-fit as often as needed. */
      var alreadyWired = !!wrap.__ffCardWired;
      wrap.__ffCardWired = 1;
      var flipping = false;
      /* ⛔ HALLFLIP (Jan, 2026-10-29): "ok you can bring the flips back on mobile keep it cleanly designed and SMOOTH on
         mobile". R578 switched the phone flip off after four waves could not make the squeeze paint on an iPhone: its
         face swap happens mid-animation, inside the modal's backdrop-filter, and WebKit drops that paint (R572, R576).
         The Hall's opened card on a touch screen now turns the way the collector's card beside it already does on every
         iPhone (ff-collector.css .cc-flip): one 3D turn of the whole card, with BOTH faces painted from the start and
         the far one hidden by backface-visibility. Nothing is repainted during the turn, only turned, so there is no
         paint to drop. The Hall's card view (#hoh-app) and, from the same day ("Yes, hero page too"), the hero page's
         card lightbox (.hhp-cv-stage, hoh-hero-page.js drawCard): one card on screen, so two face layers are a few MB. */
      var turn3d = false;
      try{ turn3d = !inDeck && coarsePointer() && !!(wrap.closest && ((document.getElementById('hoh-app') && wrap.closest('#hoh-modal .hoh-hall-card-stage')) || wrap.closest('.hhp-cv-stage'))); }catch(_){}
      if(turn3d){ cardEl.classList.add('ffshop-3d'); wrap.classList.add('ffshop-3d-wrap'); }
      syncCardFaceState(cardEl);
      /* same flip the inspector runs: any tap turns the card, gem and
         monogram keep their tooltips. */
      if(!alreadyWired) cardEl.addEventListener('click', function(e){
        if(flipping) return;
        var control = e.target && e.target.closest ? e.target.closest('button,a,input,select,textarea,[role="button"],[data-card-no-flip]') : null;
        /* The card itself intentionally has button semantics. Only a nested
           control owns the tap; otherwise the whole physical card flips. */
        if(control && control !== cardEl) return;
        if(e.target.closest('.ffshop-gem')) return;
        if(e.target.closest('.ffshop-ffm-wrap')) return;
        flipping = true;
        /* fineHover is already forced false inside a deck, so this can never
           clear the deck's scale -- but say it out loud, because the next
           person to touch this will not have read the comment above. */
        if(fineHover && !inDeck) wrap.style.transform='none';
        /* ⛔ ===== R576 · ON A PHONE THE FLIP IS INSTANT, AND FORCED TO PAINT =====
           Jan, for the third time: "when i press it it flips but it flips to its
           same side not the back side then in like 5 seconds after it flips to
           the back side and no buttons work."
           EVERY EARLIER THEORY IS NOW DISPROVED BY THE LIVE FILES. R571's 1200ms
           latch, R572's display:none back face, and R572's backdrop blur are ALL
           live on framedfantasy.com right now, verified by fetching and reading
           the served CSS, and the fault is unchanged. So it was never any of
           them.
           WHAT THE SYMPTOM ACTUALLY PROVES. `visibility:hidden` already stops an
           element receiving pointer events. So "no buttons work" is not a latch
           and not `inert`: it is the player tapping a face that the DOM has
           already hidden while iOS is still showing its pixels. The class lands,
           hit-testing follows it immediately, and only the PAINT lags. That is a
           WebKit repaint fault, not a logic fault, which is exactly why desktop
           and every emulated phone have always looked fine.
           So the flip stops depending on paint arriving by itself. On a coarse
           pointer there is no animation, no animationstart to wait for, no
           latch and no timers, and the card is then forced to re-raster. A
           display toggle is the heaviest kick available and the one WebKit
           cannot ignore, and it is safe HERE precisely because this branch runs
           no animation for it to restart. One forced reflow, on one card, per
           tap. */
        /* ⛔ ===== R578 · THE FLIP IS OFF ON A PHONE, BY JAN'S CALL =====
           "on mobile the card is still not flipping and extremely laggy, so
           disable flipping the card to the backside on mobile."
           Four waves went at this: R571's latch, R572's back face and blur,
           R576's instant swap and forced re-raster, R577's dead layer hint.
           Every one of them was verified LIVE and every one of them was a real
           bug, and the flip still does not turn on his phone. I cannot
           reproduce iOS Safari, so a fifth guess would just be a fifth guess.
           A control that does not work is worse than no control, so on a coarse
           pointer the card no longer turns at all, and the gesture becomes the
           lean instead, which is what he asked for in the same breath. The back
           face stays reachable everywhere the flip actually works, and the
           dossier behind "Show detailed view" was always the real way in. */
        /* HALLFLIP: the 3D turn is the class change and nothing else (the transition rides on .ffshop-3d.on), so there
           is no latch: a second tap mid-turn simply turns it back. The stage's glow fades out for the turn and back
           once the card lies flat (R586's rule for the squeeze, hall-of-heroes.css .hoh-hall-card-turning, and the
           hero page's own copy in hoh-hero-page.css). */
        if(cardEl.classList.contains('ffshop-3d')){
          cardEl.classList.toggle('on'); syncCardFaceState(cardEl);
          var _st3 = cardEl.closest('.hoh-hall-card-stage,.hhp-cv-stage');
          if(_st3 && !motionReduced()){
            clearTimeout(_st3.__ffTurnT);
            _st3.classList.remove('hoh-hall-card-turning');
            /* a style read (no layout) so a second tap restarts the glow's fade */
            try{ void getComputedStyle(_st3, '::after').opacity; }catch(_){}
            _st3.classList.add('hoh-hall-card-turning');
            _st3.__ffTurnT = setTimeout(function(){ _st3.classList.remove('hoh-hall-card-turning'); }, 680);
          }
          flipping = false;
          return;
        }
        if(coarsePointer()) { flipping = false; return; }
        if(motionReduced()){
          cardEl.classList.toggle('on'); syncCardFaceState(cardEl); flipping=false;
        }else{
          /* ⛔ ============ R563 · ONE CLOCK, NOT TWO ============
             Jan: "when i tap to flip the card over it just lags and bugs out
             and doesnt flip over very consistently."
             ROOT CAUSE, MEASURED IN A BROWSER. The face swap and the cleanup
             were two setTimeouts started at the CLICK, while the squeeze is a
             CSS animation that starts at the first paint after the class
             lands. Nothing held them together. Even idle on a fast machine the
             animation began 18ms late, so the face swapped 15ms BEFORE the
             card was edge-on and the squeeze was cut 16ms short, snapping
             scaleX back from mid-flight. With 120ms of main-thread work after
             the tap, which is routine on a phone, the face swapped 15% into
             the animation, in full view, and the squeeze died at 68%. That is
             the whole report: the busier the thread, the worse it reads, which
             is why it looked intermittent.
             THE FIX IS THE R548 RULE AGAIN: a derived beat needs a derived
             end. Both moments now hang off the animation's OWN events, so the
             card is edge-on when its face changes however late the animation
             starts, and the class is removed when the animation actually ends
             rather than when a guess expires.
             ⛔ THE GUARD IS NOT OPTIONAL. `flipping` blocks every tap while it
             is true, so if the animation never runs at all — reduced motion
             arriving late, a display:none ancestor, a dropped frame budget —
             the timers were the only thing that ever gave the card back. This
             keeps one, long enough never to race a real animation and short
             enough that a card can never be dead to the touch. */
          var _swapped = false, _midT = 0, _endT = 0, _guardT = 0;
          var _swap = function(){
            if(_swapped) return;
            _swapped = true;
            cardEl.classList.toggle('on'); syncCardFaceState(cardEl);
          };
          var _finish = function(){
            clearTimeout(_midT); clearTimeout(_endT); clearTimeout(_guardT);
            cardEl.removeEventListener('animationstart', _onStart);
            cardEl.removeEventListener('animationend', _onEnd);
            _swap();
            cardEl.classList.remove('ffshop-turn');
            flipping = false;
          };
          /* ⛔ ===== R571 · A CARD IS NEVER DEAD TO THE TOUCH =====
             Jan: "i tap the card in the main hall of heroes grid, and it flips
             over at like the 3rd tap and once its flipped over everything is
             laggy, tapping it doesnt flip it back." Desktop is fine.
             REPRODUCED IN A BROWSER ON THE LIVE PAGE, and it is this block.
             R563 anchored both beats to `animationstart`, which is exactly
             right WHEN THE ANIMATION RUNS. When it does not, neither timer is
             ever armed and the only thing that ever finishes the flip is the
             1200ms guard below. Measured with three taps at 1ms / 251ms /
             505ms: the card turned at 1208ms, off tap ONE, and taps two and
             three were eaten by the `flipping` latch. That is "the 3rd tap",
             and it is why the card then ignores him.
             AND THE ANIMATION REALLY CAN BE ABSENT HERE. hoh-shop.css sets
             `.ffshop-card.ffshop-turn{animation:none}` under
             prefers-reduced-motion while `reduced` was read once at parse, so
             a phone with Reduce Motion on took this animated branch against a
             stylesheet that had already deleted the animation. Nothing was
             ever coming.
             THE CURE IS TO STOP WAITING ON AN EVENT THAT MAY NEVER ARRIVE:
             ffshopWillAnimate() asks the cascade directly, and the guard is
             only for the case where an animation exists but its start is lost.
             R563's win is untouched — whenever the animation does run, both
             beats still hang off its own start, so the face still changes
             while the card is edge-on. */
          var _arm = function(_dur){
            /* the timers own the finish from here, so the guard stands down */
            clearTimeout(_midT); clearTimeout(_endT); clearTimeout(_guardT);
            _midT = setTimeout(_swap, _dur / 2);
            _endT = setTimeout(_finish, _dur + 30);
          };
          var _onStart = function(ev){
            if(ev.target !== cardEl || ev.animationName !== 'ffshopInspTurn') return;
            /* the keyframes hide the card between 46% and 54%, so half its
               REAL duration is the one moment a face change is invisible */
            var _dur = 430;
            try{ _dur = Math.max(120, (parseFloat(getComputedStyle(cardEl).animationDuration) || .43) * 1000); }catch(_){}
            /* ⛔ BOTH MOMENTS ARE ANCHORED HERE, NOT ONLY THE SWAP, and the
               reason is measured: `animationend` did NOT fire in the browser
               proof for this card while `animationstart` did. Hanging the
               cleanup on it alone would have left `flipping` true until the
               1200ms guard, which BLOCKS EVERY TAP — turning the lag Jan
               reported into a longer one and making his second complaint,
               that the card stops responding, strictly worse.
               Anchoring both to the animation's own start is what actually
               cures the drift: the old code measured from the CLICK, which ran
               18 to 120ms ahead of the animation. animationend below is kept
               as an EARLY finish for the browsers that do send it. */
            _arm(_dur);
          };
          var _onEnd = function(ev){
            if(ev.target !== cardEl || ev.animationName !== 'ffshopInspTurn') return;
            _finish();
          };
          cardEl.addEventListener('animationstart', _onStart);
          cardEl.addEventListener('animationend', _onEnd);
          cardEl.classList.add('ffshop-turn');
          /* ⛔ R571: ask the cascade whether an animation is actually going to
             run, with the class already on. If it is not, finish NOW rather
             than sitting on the guard: the flip is instant, the latch clears,
             and the very next tap works. */
          if(!ffshopWillAnimate(cardEl)){ _finish(); }
          /* An animation exists but its start could still be dropped. 700ms is
             clear of a real 430ms flip plus the worst start latency R563
             measured (120ms), and far below the 1200ms that reads as a broken
             card. _arm() cancels this the moment a start does arrive. */
          else{ _guardT = setTimeout(_finish, 700); }
        }
      });
      if(!alreadyWired) cardEl.addEventListener('keydown', function(e){
        if(e.target !== cardEl || (e.key !== 'Enter' && e.key !== ' ')) return;
        e.preventDefault();
        cardEl.click();
      });
      /* same hover tilt, fine pointers only so the mobile transform-scale
         is never overwritten. */
      /* PERF-11 (2026-08-06): the tilt used to read getBoundingClientRect on
         EVERY pointermove, immediately after the previous move wrote a
         transform — a forced layout at pointer rate (120Hz on fast
         trackpads). The rect is now cached on pointerenter (which is also
         the honest, pre-tilt box) and the write rides one rAF, so at most
         one transform lands per frame. pointerleave drops the cache so a
         scrolled page re-measures on the next hover. */
      if(fineHover && !alreadyWired){
        var tiltRect=null, tiltRaf=0, tiltX=0, tiltY=0;
        wrap.addEventListener('pointerenter', function(){ tiltRect=null; });
        wrap.addEventListener('pointermove', function(e){
          if(flipping) return;
          if(!tiltRect) tiltRect=wrap.getBoundingClientRect();
          if(!tiltRect.width||!tiltRect.height) return;
          tiltX=(e.clientX-tiltRect.left)/tiltRect.width-0.5; tiltY=(e.clientY-tiltRect.top)/tiltRect.height-0.5;
          if(!tiltRaf){ tiltRaf=requestAnimationFrame(function(){ tiltRaf=0; if(flipping) return; wrap.style.transform='perspective(1000px) rotateY('+(tiltX*3.5)+'deg) rotateX('+(-tiltY*3.5)+'deg)'; }); }
        });
        wrap.addEventListener('pointerleave', function(){ tiltRect=null; if(tiltRaf){ cancelAnimationFrame(tiltRaf); tiltRaf=0; } wrap.style.transform='none'; });
      }
      /* same text fitters, same webfont re-fit. */
      function runFits(){
        fitLine(cardEl.querySelector('.ffshop-fit'), 11);
        fitZone(cardEl.querySelector('.ffshop-fitzone'));
        syncCardBox(cardEl);
      }
      requestAnimationFrame(runFits);
      try{
        if(document.fonts && document.fonts.ready && document.fonts.ready.then){
          document.fonts.ready.then(function(){ if(document.body.contains(cardEl)) runFits(); });
        }
      }catch(_){}
      setTimeout(function(){ if(document.body.contains(cardEl)) runFits(); }, 650);
      /* FF card whose text has not been written yet: fill it in place,
         exactly like the inspector does. */
      var _hid = cardEl.getAttribute('data-hero-id');
      var heroNow = heroById(_hid);
      if(heroNow && heroNow.is_ff_portrait && !(heroNow.card_meta && heroNow.card_meta.legend)){
        ensureCardMeta(_hid, function(meta){
          if(!meta || !document.body.contains(wrap) || !wrap.parentNode) return;
          var tmp = document.createElement('div');
          var _profileBack = cardEl.classList.contains('ffshop-card-profile');
          tmp.innerHTML = '<div class="ffshop-tiltwrap">' + fullCardHtml(_hid, null, {
            back:_profileBack?'abilities':'brand',
            hideHearts:cardEl.getAttribute('data-hide-hearts')==='1'
          }) + '</div>';
          var nw = tmp.firstChild;
          wrap.parentNode.replaceChild(nw, wrap);
          window.hohShopWireCard(nw);
        });
      }
    } catch(_){}
  };

  /* ---------------- fitters (from the approved v8 card) ---------------- */

  function fitLine(el, min){
    if(!el) return;
    el.classList.remove('ffshop-fit-wrap');   /* R381: re-fits start from the one-line state */
    el.style.fontSize='';
    var s=parseFloat(getComputedStyle(el).fontSize)||18, g=0;
    /* PERF-2 (2026-08-06): the old walk was up to 60 rounds of write font,
       force layout, read layout — per line, per card, three runs per card.
       A single line's width scales near linearly with font size, so one
       measurement predicts the landing zone; jump there in one write, then
       finish on the same 0.5px grid as before. Same guarantees: never clips,
       never below `min`, and the loop cap still backstops a bad estimate. */
    var sw=el.scrollWidth, cw=el.clientWidth;
    if(sw>cw && cw>0 && s>min){
      var est=s*cw/sw;
      est=s-0.5*Math.ceil((s-est)/0.5);      /* land on the old walk's grid */
      est=Math.max(min, est);
      if(est<s){ s=est; el.style.fontSize=s+'px'; }
    }
    while(el.scrollWidth>el.clientWidth && s>min && g++<14){ s-=0.5; el.style.fontSize=s+'px'; }
    /* R381 (Jan): the floor can still overflow on epic names like "Ailuin,
       Archmage of Arcane Runes & Sigil Studies" — and nowrap+hidden then
       slices letters with no ellipsis. Past the floor, wrap to two clamped
       lines at a readable size instead. line-clamp draws the ellipsis if even
       two lines can't hold it. */
    if(el.scrollWidth>el.clientWidth+1){
      el.classList.add('ffshop-fit-wrap');
      el.style.fontSize=(min+3)+'px';
    }
  }
  /* R241: the no-clipping guarantee. Shrinks deeper than before, and if
     the floor STILL overflows (long legend + quote + epithet all at
     once), the quote steps aside cleanly and the legend refits. Restores
     anything a previous pass dropped so refits (fonts.ready) start clean. */
  function fitZone(zone){
    if(!zone) return;
    /* R247: the flavor + its divider now live in one .ffshop-flavor-block, so
       the last-resort drop hides the whole block (no orphaned divider). */
    var fblock=zone.querySelector('.ffshop-flavor-block');
    if(fblock) fblock.style.display='';
    function pass(){
      var texts=[].slice.call(zone.querySelectorAll('p,.ffshop-flavor')).filter(function(t){ return t.style.display!=='none' && (!t.closest('.ffshop-flavor-block') || t.closest('.ffshop-flavor-block').style.display!=='none'); });
      if(!texts.length) return true;
      texts.forEach(function(t){ t.style.fontSize=''; });
      var base=texts.map(function(t){ return parseFloat(getComputedStyle(t).fontSize)||14; });
      var scale=1, g=0;
      /* PERF-2 (2026-08-06): same jump-then-walk as fitLine. Wrapped text
         height scales roughly with the square of the font size (fewer lines
         AND shorter lines), so sqrt(available/needed) predicts the landing
         scale. Jump onto the old 0.03 grid, then the original walk finishes
         the job. Floor and clip guarantees unchanged. */
      var sh=zone.scrollHeight, chh=zone.clientHeight;
      if(sh>chh+1 && chh>0){
        var estz=Math.sqrt(chh/sh);
        estz=1-0.03*Math.ceil((1-estz)/0.03);   /* the old walk's grid */
        estz=Math.max(0.54, estz);
        if(estz<1){
          scale=estz;
          texts.forEach(function(t,i){ t.style.fontSize=(base[i]*scale).toFixed(2)+'px'; });
        }
      }
      while(zone.scrollHeight>zone.clientHeight+1 && scale>0.54 && g++<12){
        scale-=0.03;
        texts.forEach(function(t,i){ t.style.fontSize=(base[i]*scale).toFixed(2)+'px'; });
      }
      return zone.scrollHeight<=zone.clientHeight+1;
    }
    if(!pass() && fblock){
      fblock.style.display='none';
      pass();
    }
  }

  /* R251: the inspector card can now grow taller than 650 to fit long text.
     On mobile the tilt wrapper is transform-scaled, and a transform does NOT
     shrink the element's layout box, so the .ffshop-cardbox would reserve the
     full unscaled height and mis-center the card. Measure the real card height
     and set the cardbox to height*scale (scale is 1 on desktop). Runs after the
     fitters, and again when fonts settle, so the measurement is accurate. */
  /* R444: nothing re-fitted the card on resize. Rotate a phone mid-inspect and
     the card kept the portrait scale, which on the wider axis meant the height
     clamp no longer matched and on the narrower axis meant it overflowed
     again. One debounced listener, and it only touches a card that is actually
     mounted. */
  if(!window.__ffCardBoxResize){
    window.__ffCardBoxResize = true;
    var _cbT = null;
    window.addEventListener('resize', function(){
      if(_cbT) clearTimeout(_cbT);
      _cbT = setTimeout(function(){
        try{
          document.querySelectorAll('.ffshop-insp .ffshop-card').forEach(function(el){ syncCardBox(el); });
        }catch(_){}
      }, 120);
    }, { passive:true });
  }
  function syncCardBox(cardEl){
    try {
      if(!cardEl) return;
      var box = cardEl.closest ? cardEl.closest('.ffshop-cardbox') : null;
      if(!box) return;
      /* R339: the deck sizes its own box from --deck-k. This function's job is
         to match the box to the MOBILE viewport scale, which is a different
         scale entirely, and its inline height would win over the deck rule. */
      if(box.closest && box.closest('.ffshop-deck-card')){ box.style.height = ''; return; }
      var vw = window.innerWidth || 380;
      var insp = box.closest ? box.closest('.ffshop-insp') : null;
      var compact = !!(insp && insp.classList.contains('ffshop-insp-compact'));
      /* R444: was `vw > 430`. The CSS scale rule stopped at 430px while the
         narrow single-column rule starts at 700px, leaving a 431 to 700 band
         where a 380px card sat inside a 344px column with nothing to shrink
         it. Foldables, small tablets, landscape phones and split-screen all
         live in that band. Both ends agree on 700 now. */
      if(vw > 700 && !compact){ box.style.height = ''; return; }
      /* A short landscape screen needs a readable list, rather than a scaled
         portrait. This only sizes the upgrades face; art keeps its own ratio. */
      if(compact && box.classList.contains('is-upgrades-view') && vw > (window.innerHeight || 800) && (window.innerHeight || 800) <= 430){
        var upgradeHeight=Math.max(190,(window.innerHeight || 390)-82);
        insp.style.setProperty('--ffcard-k','1');
        insp.style.setProperty('--ffcard-ui-inv','1');
        box.style.setProperty('--ffcard-render-h',upgradeHeight+'px');
        box.style.height=(upgradeHeight+54)+'px';
        box.style.width=Math.min(560,vw-152)+'px';
        return;
      }
      /* Mobile: match the cardbox to the scaled card height. */
      var h = cardEl.offsetHeight;
      /* Guard: a real card is at least the 650 floor. A sub-200 measure means we
         were called before layout settled — skip rather than collapse the box
         (a collapsed box mis-centers the card and jams it under the chrome). */
      if(!h || h < 200) return;
      /* R444 (Jan): "you have to scroll down a lot to see the stats that a lot
         of people won't see."
         The card is 650px tall at scale 1. On a 390px phone the width-only
         scale is 346/380 = 0.91, so the card reserved 592px, and with the 44px
         clearance for the fixed close button the Battlegrounds panel began
         below the fold on every phone. Nothing on screen suggested it existed.
         The scale is clamped by height as well now, so the card never takes
         more than 62% of the viewport and the top of the stats panel always
         peeks above the fold. A visible edge is the only affordance that
         reliably says "there is more here". This mirrors the CSS in
         hoh-shop.css exactly; if one changes the other has to. */
      var compactLandscape = compact && vw > (window.innerHeight || 800) && (window.innerHeight || 800) <= 430;
      var scale = compact
        /* 70px is a dedicated close-button/safe-area gutter; 54px belongs to
           the single external action row and 14px is the bottom pad.
           The previous 86px budget put the unchanged artwork underneath the
           close button on a 320x568 phone. */
        ? Math.min(1, (vw - (compactLandscape ? 152 : 28)) / 380, ((window.innerHeight || 800) - (compactLandscape ? 62 : 138)) / h)
        : Math.min(1, (vw - 44) / 380, (window.innerHeight || 800) * 0.62 / 650);
      if(scale <= 0) return;
      /* R444: JS owns the scale, not just the box, and that is the whole point.
         The CSS carries the same formula for first paint, but a CSS transform
         driven by one number and a box width written by another is exactly how
         the card ends up off centre again. Writing --ffcard-k here means the
         transform and the layout box can never disagree, whatever the two
         formulas round to. It also settles the vh question: CSS vh on iOS is
         the LARGE viewport and ignores the URL bar, while innerHeight is what
         the player can actually see, and innerHeight is the honest one. */
      try {
        if(insp){
          insp.style.setProperty('--ffcard-k', String(scale));
          /* Only the functional ability face consumes this inverse. The card
             art stays pixel-for-pixel the existing physical card, while its
             controls remain at least 44 rendered pixels after the whole card
             is transform-scaled on a short phone. */
          insp.style.setProperty('--ffcard-ui-inv', String(1 / scale));
        }
      } catch(_){}
      var renderedH = Math.round(h * scale);
      box.style.setProperty('--ffcard-render-h', renderedH + 'px');
      box.style.height = (renderedH + (compact ? 54 : 0)) + 'px';
      box.style.width  = Math.round(380 * scale) + 'px';
    } catch(_){}
  }

  /* ---------------- full card markup (v8, ported) ---------------- */

  function gemHtml(tier, hearts, kept, hideHearts){
    var tip = tier.charAt(0).toUpperCase()+tier.slice(1) + (hideHearts ? '' : ' · ' + (hearts||0).toLocaleString() + ' hearts');
    return '<span class="ffshop-gem ffshop-gem-'+tier+'"><em></em><span class="tip">'+esc(tip)+'</span></span>';
  }
  function footHtml(hero, snap){
    var dt = '';
    try{
      var iso = (hero && hero.created_at) || (snap && snap.first_pulled_at);
      if(iso){ var d=new Date(iso); dt=d.toLocaleString('en-US',{month:'short'})+' '+d.getFullYear(); }
    }catch(_){}
    var player = esc((hero && hero.player_name) || '');
    /* R381b (Jan): the monogram AND its "Portrait by Jan" credit are for
       FramedFantasy portraits ONLY. On a player's own non FF card both were
       false claims — a card that is not Jan's work cannot carry her mark. */
    var _cover = hero && hero.artwork_cover;
    var _isFF = _cover
      ? (_cover.kind !== 'ai' && _cover.artist === 'FramedFantasy')
      : !!((hero && hero.is_ff_portrait) || (snap && snap.ff));
    var _mid = _isFF
      ? ('<span class="ffshop-ffm-wrap" tabindex="0" aria-label="Portrait by Jan from FramedFantasy">' +
          '<img class="ffshop-ffm" src="'+FF_LOGO+'" alt="FramedFantasy">' +
          '<span class="ffshop-ffm-tip">Portrait by Jan from FramedFantasy</span>' +
        '</span>')
      : '';
    return '<div class="ffshop-cfoot"><span class="l">'+esc(dt)+'</span>' +
      _mid +
      '<span class="r">'+player+'</span></div>';
  }
  function backFaceHtml(){
    return '<div class="ffshop-face is-back ffshop-cb">' +
      '<span class="ffshop-cd ffshop-cd1"></span><span class="ffshop-cd ffshop-cd2"></span>' +
      '<span class="ffshop-cd ffshop-cd3"></span><span class="ffshop-cd ffshop-cd4"></span>' +
      '<div class="stack"><img src="'+FF_LOGO+'" alt="FramedFantasy"><div class="hoh">Hall of Heroes</div></div></div>';
  }
  function abilityBackFaceHtml(name){
    return '<div class="ffshop-face is-back ffshop-ability-back" aria-hidden="true" inert>' +
      '<div class="ffshop-ability-shell">' +
        '<div class="ffshop-ability-top">' +
          '<img class="ffshop-ability-logo" src="'+FF_LOGO+'" alt="" aria-hidden="true">' +
          /* R566 (Jan): "switch the position of mythic hero with the name ...
             make it larger so the name is the label at the center."
             The hero tier used to lead the battle panel BELOW this block, so
             the loudest words on the card back named a category hundreds of
             cards share while the one thing that identifies THIS card was a
             small line above it. The tier is an eyebrow over the name now.
             ⛔ THE NAME DOES NOT MOVE. It stays exactly where it has always
             been in the DOM, and only this empty span is new, because the tier
             is painted asynchronously by paintLevel() and that call can fail,
             be gated off, or never run at all (the Quick Review card replaces
             this whole mount and has no battle panel). An empty span collapses
             to nothing via `:empty`, so every one of those paths still renders
             logo → name exactly as before. Moving the NAME into the async
             block instead would have made a hero nameless whenever its
             abilities failed to load. */
          '<span class="ffshop-ability-tier"></span>' +
          '<b>'+esc(name || 'Hero')+'</b>' +
        '</div>' +
        '<div class="ffshop-ability-mount"><div class="ffshop-ability-loading" role="status"><span aria-hidden="true"></span>Loading abilities</div></div>' +
      '</div>' +
      '<section class="ffshop-upgrades-shell" data-card-upgrades-face hidden inert aria-label="'+esc(name || 'Hero')+' card upgrades">' +
        '<header class="ffshop-upgrades-top">' +
          '<span>Card progression</span>' +
          '<b>'+esc(name || 'Hero')+'</b>' +
        '</header>' +
        '<div class="ffshop-upgrades-mount">' +
          '<div class="ffshop-upgrades-loading" role="status"><i aria-hidden="true"></i><span>Loading upgrades</span></div>' +
        '</div>' +
      '</section>' +
    '</div>';
  }
  /* R247: the title / description / flavor render as reorderable blocks. The
     owner can drag them; the order persists in card_meta.order and shows for
     everyone. cardBlockOrder keeps any saved order, appends any missing
     defaults, then filters to the blocks that actually have content. */
  var CARD_BLOCKS = ['title','legend','quote'];
  function cardBlockOrder(meta, has){
    var saved = (meta && Array.isArray(meta.order)) ? meta.order : null;
    var seq = [];
    if(saved){
      saved.forEach(function(k){ if(CARD_BLOCKS.indexOf(k)!==-1 && seq.indexOf(k)===-1) seq.push(k); });
    }
    CARD_BLOCKS.forEach(function(k){ if(seq.indexOf(k)===-1) seq.push(k); });
    return seq.filter(function(k){ return has[k]; });
  }
  function cardBlockHtml(k, t){
    if(k==='title')  return '<div class="ffshop-rblock ffshop-kw" data-block="title">'+esc(t.title)+'</div>';
    if(k==='legend') return '<div class="ffshop-rblock ffshop-legend-block" data-block="legend"><p>'+esc(t.legend)+'</p></div>';
    if(k==='quote')  return '<div class="ffshop-rblock ffshop-flavor-block" data-block="quote"><span class="ffshop-fbar" aria-hidden="true"></span><div class="ffshop-flavor">'+esc(t.quote)+'</div></div>';
    return '';
  }
  function cardBlocksHtml(t, meta){
    var has = { title: !!t.title, legend: !!t.legend, quote: !!t.quote };
    return '<div class="ffshop-rblocks">' +
      cardBlockOrder(meta, has).map(function(k){ return cardBlockHtml(k, t); }).join('') +
    '</div>';
  }
  /* R271: portrait fallback for the opened card. A flaky mobile network used to
     leave the art area blank ("the card didn't load all the way"). On an image
     error we drop the broken img and paint a subtle lettered placeholder inside
     the same art frame, so the card always reads as fully rendered. */
  window.hohShopArtFail = function(img){
    try{
      if(!img) return; img.onerror = null;
      var p = img.parentNode; if(!p) return;
      p.classList.add('ffshop-art-failed');
      var letter = img.getAttribute('data-letter') || '';
      if(letter && !p.querySelector('.ffshop-art-letter')){
        var s = document.createElement('span');
        s.className = 'ffshop-art-letter';
        s.textContent = letter;
        p.appendChild(s);
      }
      img.style.display = 'none';
    }catch(_){}
  };
  function artImgHtml(img, heroId, name){
    var letter = String(name || '?').charAt(0).toUpperCase();
    /* R444 (Jan): "normal heroes, their cards do not load properly ... right
       now it just says Hero and it doesn't even show an image."
       This returned '' for an empty url, which is a different failure from the
       one R271 covered. R271 paints a lettered placeholder from the <img>'s
       onerror, so with NO <img> at all there is nothing to fire and nothing to
       paint. Worse, .ffshop-art is an aspect-ratio:1/1 flex item with flex:none
       inside a min-height:650px column, so an EMPTY art div resolves to
       fit-content 0 and the frame vanishes entirely. .ffshop-rules is
       flex:1 0 auto, so it then swallowed the whole 650px floor. That is the
       giant empty purple panel with one line of text in it.
       An empty url now paints the same placeholder a broken url does, so the
       art frame always holds its square and the card keeps its proportions
       whatever the data does. */
    if(!img) return '<span class="ffshop-art-letter">' + esc(letter) + '</span>';
    return '<img src="'+esc(img)+'" alt="" decoding="async" data-letter="'+esc(letter)+'" onerror="hohShopArtFail(this)" style="'+esc(imgStyle(heroId))+'">';
  }
  /* R380 (2026-08-06, Jan): an own non FF card carries no written legend.
     Its story block tells the single use truth instead, in plain words. The
     rest of the card is a normal card: art, name, gem, stats, rolls. */
  /* R381b (Jan): "Ephemeral" is the keyword, spoken like a card mechanic.
     The FramedFantasy upsell line is gone — the card explains its rule and
     stops. No brand talk on the face of someone's own hero. */
  function ownCardBlockHtml(heroId){
    /* R391 (Jan): the dust has a second road back — repair with hearts at the
       hero's rarity price. The button renders ONLY while the card actually
       needs it, so a playable card keeps the clean two line rule. */
    /* v39: this block only ever renders for a non FF card in the player's own
       satchel (see the _nonFF branch in fullCardHtml), so ownership is already
       settled by the time we get here. Say so, instead of letting the author
       email decide it wrongly. */
    var rep = null;
    try { rep = (typeof window.hohRepairInfo === 'function') ? window.hohRepairInfo(heroId, true) : null; } catch(_){}
    var needs = !!(rep && rep.need);
    return '<div class="ffshop-block ffshop-ownrule">' +
      '<div class="ffshop-block-title">Ephemeral</div>' +
      '<p>This card is not strong enough to hold its form. It turns to dust after one battle.</p>' +
      (needs
        ? '<p>Pull it again from a pack, or repair it here.</p>' +
          '<button type="button" class="ffshop-repair-btn" data-repair-id="' + esc(String(heroId)) + '">Repair &middot; ' + rep.cost + ' &#9829;&#xFE0E;</button>'
        : '<p>Pull it again from a pack to fight again.</p>') +
    '</div>';
  }

  /* R422 (Jan): the non-FF card's counter — its number among EVERY hero in
     the Hall, because "4 / 103 FramedFantasy portraits" is the wrong club
     for a card that is not in it. Counts what the client already holds
     (hohGetAllCharacters); silent when the pool is not loaded yet. */
  /* ⛔ R576: hallCollNo IS DELETED, NOT DISABLED, so nobody restores a call to
     it. It numbered a hero by its RAW INDEX in a newest-first list, so the
     newest hero was #1, the oldest was #1109, the total counted FramedFantasy
     portraits inside a "Heroes of the Hall" line, and every hero's number moved
     whenever anyone else submitted. If a real Hall number is ever wanted it has
     to filter to non-FF and sort created_at ASCENDING, the way ffCollectorNo
     already does. */
  function fullCardHtml(heroId, snap, options){
    options = options || {};
    var profileBack = options.back === 'abilities';
    var hideHearts = profileBack || !!options.hideHearts;
    /* ⛔ R574: a hero being SUBMITTED has no id yet, so the preview on the
       "Your card" step cannot look itself up. Letting the caller hand in the
       hero object is the whole of what a real preview needs, and it is one
       line: everything below already reads from `hero`, so the preview is the
       genuine card renderer rather than a lookalike that drifts from it.
       heroById stays the default, so every existing caller is untouched. */
    var hero = options.hero || heroById(heroId);
    /* FITEDITOR: options.framingId paints another id's framing on the art (the
       add-hero form's '__hoh_new_primary' for a picture with no row yet) */
    var name = (hero && hero.name) || (snap && snap.name) || 'Hero';
    var hearts = liveHearts(heroId, snap);
    var tier = tierFor(hearts);
    /* ⛔ RARITYMATCH (Jan, 2026-10-03: "some borders arent updated and they retain the
       old borders while the rarity got updated"). A Hall hero's rarity is the stored
       hero-page tier (hohHallTier: the grid border, the rarity filter, the sort, the
       detailed view), but an opened card read the old hearts ladder here, so Kiki
       Orimura (253 hearts, stored Legendary) opened as Rare and a 1,200-heart hero
       that is not an FF portrait opened as Mythic. The public card view and the hero
       page's card hand in the tier they show (options.tier) and the card wears it.
       Every other caller (the collection, packs, Battlegrounds) keeps the hearts
       ladder, which is the card economy's own server-side tier. */
    if(options.tier && TIER_COLOR[options.tier]) tier = options.tier;
    var img = (hero && (hero.image_url || hero.image_thumb_url)) || (snap && snap.img) || '';
    var race = (hero && hero.race) || '', cls = (hero && hero.character_class) || '';
    var type = [race, cls].filter(Boolean).join(' · ');
    var t = cardText(hero, snap);
    var meta = hero && hero.card_meta;
    /* R380: in the collection a non FF card is your own hero by construction. */
    var _nonFF = !(hero && hero.is_ff_portrait) && !(snap && snap.ff);
    var front;
    /* R422 (Jan): THE FRAME IS THE TREATMENT. Every FramedFantasy portrait
       wears the blended full-art face at EVERY rarity now — the format
       itself says FF, always cleaner than the classic layout — while the
       holo sweep (markup, here) and the rainbow rim (CSS, keyed off the
       mythic tier class) stay the mythic tier's crown. A non-FF card of
       any rarity keeps the classic layout with REAL rolled blocks — R422
       killed the ephemeral rule, so it is a full citizen of the
       collection, just never in the house style. */
    if(!_nonFF){
      front =
        '<div class="ffshop-face is-front ffshop-fa"><div class="ffshop-fa-in">' +
          '<div class="ffshop-artfull ffshop-flipzone">'+artImgHtml(img, options.framingId || heroId, name)+'</div>' +
          (tier === 'mythic' ? '<div class="ffshop-holo"></div>' : '') +
          /* R266: the "Mythic" corner label was removed at Jan's request. */
          '<div class="ffshop-lower ffshop-fitzone">' +
            '<div class="ffshop-cname ffshop-fit">'+esc(name)+'</div>' +
            (t.epithet?'<div class="ffshop-epithet">'+esc(t.epithet)+'</div>':'') +
            (type?'<div class="ffshop-type">'+esc(type)+'</div>':'') +
            '<div class="ffshop-gemline">'+gemHtml(tier, hearts, null, hideHearts)+'</div>' +
            cardBlocksHtml(t, meta) +
            footHtml(hero, snap) +
          '</div>' +
        '</div></div>';
    } else {
      front =
        '<div class="ffshop-face is-front ffshop-gt">' +
          '<div class="ffshop-art ffshop-flipzone">'+artImgHtml(img, options.framingId || heroId, name)+'</div>' +
          '<div class="ffshop-nameband">' +
            '<div class="ffshop-cname ffshop-fit">'+esc(name)+'</div>' +
            (t.epithet?'<div class="ffshop-epithet">'+esc(t.epithet)+'</div>':'') +
          '</div>' +
          (type?'<div class="ffshop-typerow"><s></s><span class="ffshop-type">'+esc(type)+'</span><s></s></div>':'') +
          '<div class="ffshop-gemrow">'+gemHtml(tier, hearts, null, hideHearts)+'</div>' +
          '<div class="ffshop-rules ffshop-fitzone">' +
            cardBlocksHtml(t, meta) +
          '</div>' +
          footHtml(hero, snap) +
        '</div>';
    }
    /* R268: tier class + --rar drive the opened card's rarity edge glow (CSS). */
    /* R422: ffshop-card-eph is gone — no card is a ghost anymore. */
    var _cardRar = TIER_COLOR[tier] || TIER_COLOR.common;
    var back = profileBack ? abilityBackFaceHtml(name) : backFaceHtml();
    var shortFfName = !_nonFF && String(name).trim().length <= 22;
    return '<article class="ffshop-card ffshop-card-'+tier+(profileBack?' ffshop-card-profile':'')+(shortFfName?' ffshop-card-short-name':'')+'" tabindex="'+(profileBack?'-1':'0')+'" data-card-name="'+esc(name)+'" data-hero-id="'+esc(String(heroId))+'" data-hide-hearts="'+(hideHearts?'1':'0')+'" style="--rar:'+_cardRar+'">'+front+back+'</article>';
  }

  /* R247: persist the block order to card_meta (optimistic locally, then the
     server via save_card_text's p_order). Owner-gated on the server too. */
  function saveCardOrder(heroId, order){
    var hero = heroById(heroId);
    if(hero){ hero.card_meta = Object.assign({}, hero.card_meta||{}, { order: order.slice() }); }
    try {
      rpc('save_card_text', { user_email: email(), user_pin: pin(), p_character_id: heroId, p_order: order });
    } catch(_){}
  }
  /* R247: pointer-based drag-to-rearrange for the card's text blocks. Pointer
     events unify mouse + touch; a 5px threshold separates a drag from a tap;
     the block reorders live by comparing the pointer against sibling
     midpoints (no transform-baseline math, so it can't drift). setDragging
     tells the card to suppress its flip + hover-tilt while a drag is live. */
  function wireCardBlockDrag(cardEl, heroId, setDragging){
    var container = cardEl.querySelector('.ffshop-rblocks');
    if(!container) return;
    var initial = [].slice.call(container.querySelectorAll('.ffshop-rblock'));
    if(initial.length < 2) return; /* nothing to reorder */
    container.classList.add('ffshop-rblocks-draggable');
    var active = null, startY = 0, started = false, pid = null, startOrder = null;
    function curOrder(){
      return [].slice.call(container.querySelectorAll('.ffshop-rblock'))
        .map(function(b){ return b.getAttribute('data-block'); });
    }
    function onMove(e){
      if(!active) return;
      if(!started){
        if(Math.abs(e.clientY - startY) < 5) return;
        started = true;
        if(setDragging) setDragging(true);
        active.classList.add('ffshop-rblock-dragging');
        container.classList.add('ffshop-rblocks-dragging');
      }
      var y = e.clientY, before = null;
      var sibs = [].slice.call(container.children).filter(function(b){
        return b !== active && b.classList && b.classList.contains('ffshop-rblock');
      });
      for(var i=0;i<sibs.length;i++){
        var r = sibs[i].getBoundingClientRect();
        if(y < r.top + r.height/2){ before = sibs[i]; break; }
      }
      if(before){ if(active.nextSibling !== before) container.insertBefore(active, before); }
      else if(container.lastElementChild !== active){ container.appendChild(active); }
    }
    function onUp(){
      if(!active) return;
      var wasDrag = started;
      active.classList.remove('ffshop-rblock-dragging');
      container.classList.remove('ffshop-rblocks-dragging');
      active.removeEventListener('pointermove', onMove);
      active.removeEventListener('pointerup', onUp);
      active.removeEventListener('pointercancel', onUp);
      try { active.releasePointerCapture(pid); } catch(_){}
      active = null; started = false;
      if(wasDrag){
        /* Only persist when the order actually changed, so a drag that lands
           back where it started doesn't fire a needless save_card_text RPC. */
        var order = curOrder();
        if(!startOrder || order.join('|') !== startOrder.join('|')) saveCardOrder(heroId, order);
      }
      /* Clear the drag flag on the next tick so the click that follows
         pointerup is still suppressed. */
      setTimeout(function(){ if(setDragging) setDragging(false); }, 0);
    }
    initial.forEach(function(block){
      block.addEventListener('pointerdown', function(e){
        if(e.button != null && e.button !== 0) return; /* primary / touch only */
        active = block; startY = e.clientY; started = false; pid = e.pointerId; startOrder = curOrder();
        try { block.setPointerCapture(e.pointerId); } catch(_){}
        block.addEventListener('pointermove', onMove);
        block.addEventListener('pointerup', onUp);
        block.addEventListener('pointercancel', onUp);
      });
    });
  }

  /* ---------------- inspector overlay ---------------- */

  /* Keep the card inspector single-instance. Removing only the backdrop left
     its body-level close button and Escape listener alive, which meant a warm
     double tap could stack two cards and orphan invisible handlers. Every
     replacement path goes through the inspector's own teardown when present. */
  function closeShopInspectors(){
    try{
      document.querySelectorAll('.ffshop-pour').forEach(function(pour){
        try{ pour.remove(); }catch(_){}
      });
      document.querySelectorAll('.ffshop-insp').forEach(function(old){
        if(old && typeof old.__ffshopClose === 'function') old.__ffshopClose();
        else try{ old.remove(); }catch(_){}
      });
      document.querySelectorAll('body > .ffshop-insp-x').forEach(function(x){
        try{ x.remove(); }catch(_){}
      });
    }catch(_){}
  }

  /* One cold-state wait owns one completion. Both the network callback and
     the deadline call finishInspectorColdWait(), so the first one wins and a
     late second answer cannot stack another overlay. The legacy boolean and
     queued-id fields remain mirrored because older theme snippets may inspect
     them, but the object identity is the race boundary. */
  function cancelInspectorColdWait(wait){
    if(wait){
      wait.done = true;
      try{ if(wait.timer) clearTimeout(wait.timer); }catch(_){}
      wait.timer = 0;
    }
    if(!wait || window.__hohInspWait === wait){
      try{
        window.__hohInspWait = null;
        window.__hohInspFetched = false;
        window.__hohInspQueuedHero = '';
      }catch(_){}
    }
  }
  function finishInspectorColdWait(wait){
    if(!wait || wait.done) return false;
    var heroId = String(wait.heroId || '');
    wait.done = true;
    try{ if(wait.timer) clearTimeout(wait.timer); }catch(_){}
    wait.timer = 0;
    if(window.__hohInspWait === wait){
      try{
        window.__hohInspWait = null;
        window.__hohInspFetched = false;
        window.__hohInspQueuedHero = '';
      }catch(_){}
    }
    if(!heroId || !shopAccountIsCurrent(wait.scope)) return false;
    try{ window.hohShopInspect(heroId, true); return true; }catch(_){ return false; }
  }
  function emitShopInspectorLifecycle(kind, heroId, overlay){
    try{
      document.dispatchEvent(new CustomEvent('hoh:shop-inspector-' + kind, {
        detail: { heroId:String(heroId || ''), inspector:overlay || null }
      }));
    }catch(_){}
  }

  function collEntry(heroId){
    var coll = (shopState && shopState.collection) || [];
    for(var i=0;i<coll.length;i++){ if(String(coll[i].character_id)===String(heroId)) return coll[i]; }
    return null;
  }
  /* ⛔ R586 (Jan): "inside the personal hall, on both mobile and desktop add
     the little arrows as well so people can sort through their cards as well
     with the fade effect, etc."
     The same gesture the public Hall got at R583, including the wrap, so
     neither direction ever reaches a dead end.
     THE LIST IS READ FROM THE GRID AT THE MOMENT AN ARROW IS PRESSED, never
     cached at open. The collection re-renders on a filter, a sort, a dust, a
     pack opening and an account switch, so a list captured when the card
     opened would step onto a card that is no longer on the page.
     It also scopes ITSELF rather than needing a caller to say where it came
     from: the open hero must be among the grid's own visible tiles, so an
     inspector opened from the party picker, a deep link or a toast simply
     shows no arrows instead of borrowing some other surface's order. */
  function inspNavList(heroId){
    try{
      var grid = document.querySelector('#ffshop-grid-all');
      if(!grid) return [];
      var tiles = grid.querySelectorAll('.ffshop-tile[data-hero-id]');
      var out = [], seen = {}, found = false, want = String(heroId);
      for(var i=0;i<tiles.length;i++){
        var id = tiles[i].getAttribute('data-hero-id');
        if(!id || seen[id]) continue;
        /* A tile filtered out of the current view is still in the DOM in some
           collection modes. Stepping onto a card the player cannot see reads
           as the arrow skipping one at random, so hidden tiles are not in the
           order at all. */
        if(tiles[i].offsetParent === null) continue;
        seen[id] = 1;
        if(String(id) === want) found = true;
        out.push(id);
      }
      return found ? out : [];
    }catch(_){ return []; }
  }
  function inspNavIndex(list, heroId){
    for(var i=0;i<list.length;i++){ if(String(list[i]) === String(heroId)) return i; }
    return -1;
  }
  /* Which way the NEXT overlay should arrive from. It is set immediately
     before the reopen and consumed by the opener, so a normal open can never
     inherit a stale direction. */
  var _inspNavIn = '';
  window.hohShopInspectNav = function(dir){
    var overlay = document.querySelector('.ffshop-insp');
    if(!overlay || overlay.__ffshopNavBusy) return;
    var heroId = overlay.getAttribute('data-insp-hero') || '';
    var list = inspNavList(heroId);
    var i = inspNavIndex(list, heroId);
    if(i < 0 || list.length < 2) return;
    /* ⛔ The double modulo is the whole wrap: a bare `%` on -1 gives -1 in
       JavaScript, not the last index, so stepping back from the first card
       would read `list[-1]` and quietly do nothing. */
    var next = list[((i + dir) % list.length + list.length) % list.length];
    if(String(next) === String(heroId)) return;
    overlay.__ffshopNavBusy = true;
    try{
      var inner = overlay.querySelector('.ffshop-insp-inner');
      if(inner) inner.classList.add(dir > 0 ? 'ffshop-inspout-left' : 'ffshop-inspout-right');
    }catch(_){}
    _inspNavIn = dir > 0 ? 'right' : 'left';
    /* ⛔ A FIXED STEP, NEVER `transitionend`. R583 paid for this on the Hall:
       the event never fires when the property does not actually change, which
       is every reduced-motion visitor and any frame the compositor drops, and
       the card would then sit behind a latch that only a reload clears. The
       overlay is replaced wholesale here, so the latch dies with it either
       way. */
    setTimeout(function(){
      try{ closeShopInspectors(); }catch(_){}
      try{ window.hohShopInspect(next, true); }
      catch(_){ _inspNavIn = ''; }
    }, 170);
  };
  window.hohShopInspect = function(heroId, skipStateFetch){
    useShopAccount();
    /* The inspector API takes a hero id, while battle and party surfaces use
       typed card keys. Normalize at the boundary as a second line of defence
       so one missed caller can never create another FF-works/non-FF-fails
       split. */
    heroId = String(heroId == null ? '' : heroId).replace(/^(ff|own|hall):/, '');
    if(!heroId){ toast('That card could not open.'); return; }
    /* The inspector can open from surfaces that never visited the Shop tab
       (the Battlegrounds party picker). Without shop state the card face and
       the Battlegrounds section render blank, so fetch it first, once, then
       re-enter. Proceeds regardless of outcome, so a failed fetch still opens
       the card from the hall pool. */
    if(!skipStateFetch && !shopState && !authFailed && email() && pin()){
      /* Coalesce rapid taps while the one state read is in flight. The latest
         requested card wins, and no half-loaded generic overlay is opened. */
      if(window.__hohInspFetched && window.__hohInspWait && !window.__hohInspWait.done){
        window.__hohInspQueuedHero = heroId;
        window.__hohInspWait.heroId = heroId;
        toast('Opening card…');
        return;
      }
      window.__hohInspFetched = true;
      var requestedScope = captureShopAccount();
      var inspectorWait = {
        done:false,
        timer:0,
        heroId:heroId,
        scope:requestedScope
      };
      window.__hohInspWait = inspectorWait;
      window.__hohInspQueuedHero = heroId;
      toast('Opening card…');
      /* R444: the latch used to be set and never cleared. One flaky
         get_shop_state on a phone and EVERY inspect for the rest of that page
         load opened with no snapshot and no retry, which is half of why the
         broken card was a mobile-only report. Clear it whenever the fetch did
         not actually produce state, so the next open tries once more. */
      inspectorWait.timer = setTimeout(function(){
        finishInspectorColdWait(inspectorWait);
      }, INSPECTOR_COLD_WAIT_MS);
      try{
        fetchState().then(function(){
          finishInspectorColdWait(inspectorWait);
        }, function(){
          finishInspectorColdWait(inspectorWait);
        });
      }catch(_){
        finishInspectorColdWait(inspectorWait);
      }
      return;
    }
    var snap = collEntry(heroId);
    var hero = heroById(heroId);
    /* R423 BUG, caught while widening the edit button past FF cards: with
       nobody signed in, email() is '' and a hero row with no author is also
       '', so '' === '' made EVERY ownerless hero read as "yours". It only
       ever gated the FF-owner edit button before, which hid the damage.
       Both sides must be a real address now. */
    var _me = String(email() || '').trim().toLowerCase();
    /* R440: heroById reads hohGetAllCharacters, which FILTERS OUT hidden
       heroes (R432). So an author whose hero Jan has hidden pending a new
       image had no hero row here, no author address, and therefore no way to
       edit the card text — permanently, not transiently. Authorship is asked
       of the unfiltered pool; being hidden is a moderation state, not a
       change of who wrote it. Falls back to the filtered row so nothing
       breaks if the full accessor is unavailable. */
    var _authorHero = hero;
    try {
      if(typeof window.hohGetAllCharactersFull === 'function'){
        var _allF = window.hohGetAllCharactersFull() || [];
        for(var _i = 0; _i < _allF.length; _i++){
          if(String(_allF[_i].id) === String(heroId)){ _authorHero = _allF[_i]; break; }
        }
      }
    } catch(_){}
    var _author = String((_authorHero && (_authorHero.auth_email || _authorHero.email)) || '').trim().toLowerCase();
    var own = !!(snap && snap.own) || (!!_me && !!_author && _me === _author);
    /* R438 (Jan): "remember ONLY the owner of the card can change its flavor
       text." `own` above is an OR with snap.own, which comes out of
       get_shop_state's collection payload — a function whose body is not in
       this repo, so what it means by "own" cannot be read here. Card text is
       written to characters.card_meta and is therefore GLOBAL: whatever the
       author types, every collector of that card sees. That is the one action
       in this panel where being wrong about ownership edits somebody else's
       hero, so it stops sharing a flag with dust prices and ticket rows and
       tests authorship on its own. Fails closed: an unloaded hero row means an
       empty author, which means no button. */
    var _isAuthor = !!_me && !!_author && _me === _author;
    var inspectorScope = captureShopAccount();
    var returnFocus = document.activeElement;
    closeShopInspectors();
    var overlay = document.createElement('div');
    overlay.className = 'ffshop-insp ffshop-insp-compact';
    overlay.setAttribute('role','dialog');
    overlay.setAttribute('aria-modal','true');
    overlay.setAttribute('aria-label', ((hero && hero.name) || (snap && snap.name) || 'Hero') + ' card');
    /* R444: stamp which hero this overlay is showing, so the
       hoh:characters-loaded repaint below can re-open the SAME card once the
       hall pool finally lands. Nothing else reads it. */
    try{ overlay.setAttribute('data-insp-hero', String(heroId)); }catch(_){}
    /* R297: inline z so the inspector clears the Battlegrounds focus layer
       (100030) no matter which stylesheet vintage is deployed. */
    try{ overlay.style.zIndex = '100084'; }catch(_){}
    /* R423 (Jan): ONE ROW, ONE DUST BUTTON, NO ARITHMETIC TO READ.
       Jan's screenshot had two buttons side by side — "CASH 1 EXTRA · ~1
       HEARTS" and "TURN TO HEARTS · ALL 2 COPIES · ~2 ♥" — which is the
       same verb twice, asking the player to work out the difference between
       cashing spares and cashing the lot before they can press anything.
       It is one button now that turns ONE copy into hearts, and how many
       you hold is a quiet label rather than a second control. Press it
       twice to dust twice. The old "last copy" warning came off the label
       too: the price is the fact that matters, and the confirm below is
       where the consequence belongs.
       FF cards get the button as well now (R423 SQL) — they are simply
       worth far more per copy. */
    var _isFF = !!((hero && hero.is_ff_portrait) || (snap && snap.ff));
    var _left = snap ? Math.max(0, (snap.copies|0) - (snap.cashed|0)) : 0;
    /* R423: any OWNER can write their card's text, not just FF owners. A
       non-FF card carries one generic line by design, and this is the door
       that replaces it — the edit lands in card_meta, so everyone who holds
       a copy sees the owner's words. */
    var _per = 0;
    if(snap && _left > 0){
      _per = TIER_VALUE[tierFor(liveHearts(heroId, snap))] * (_isFF?10:1) * (own?2:1);
      /* R431 (Jan): "dont write a ~5 like its around 5 hearts just say its 5
         hearts." The tilde was hedging against a number that is not actually
         uncertain — this multiplies the same tier table, FF multiplier and
         owner multiplier the server's ff_card_value uses, so it lands on the
         exact figure. Hedging a known number just makes the player distrust
         it. The server's answer is still what the wallet banks. */
    }
    /* R558c: the physical back is for abilities and rerolls only. Every
       collection action lives in one unscaled, honest-sized rail beneath the
       card. The art side exposes only View abilities; ability-only controls use
       real `hidden`, so they cannot become invisible keyboard stops. */
    function cardActionRailHtml(){
      var edit = _isAuthor
        ? '<button type="button" class="ffshop-card-control ffshop-card-edit" data-card-front-action="1" data-act="edit" aria-label="Edit card text">'+
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 16-.8 4.8L8 20l10.7-10.7-4-4zM13.7 6.3l4 4"/></svg>'+
            '<span class="ffshop-control-long">Edit</span><span class="ffshop-control-short">Edit</span></button>'
        : '';
      /* ⛔ R599 (Jan): "the hearts thing change it to dust instead of whatever
         text you got on there and make it a trash icon."
         It used to read "Hearts · 60", which names the REWARD and not the
         ACTION. The action is destroying a card you own. A trash can and the
         word Dust say what actually happens, which is this project's own rule
         that a button states what it does.
         ⛔ THE NUMBER IS NOT LOST, it moves to where a number belongs: the
         hover tip and the aria-label both still state exactly how many Hearts
         the card is worth, and the confirm sheet after the tap states it again
         before anything is destroyed. */
      var dust = (snap && _left > 0)
        ? '<button type="button" class="ffshop-card-control ffshop-card-dust" data-card-back-action="1" data-act="dustall" hidden'+
            ' data-tip="Turn one copy into '+_per+' Hearts. You hold '+_left+'."'+
            ' aria-label="Dust one of '+_left+' owned '+(_left===1?'copy':'copies')+' for '+_per+' Hearts">'+
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16"/><path d="M10 4h4M9 7v12M15 7v12"/><path d="M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13"/></svg>'+
            '<span class="ffshop-control-long">Dust</span><span class="ffshop-control-short">Dust</span></button>'
        : '';
      /* The inspector star stays owned by this stable cardbox rather than the
         disposable gear mount, but it now PAINTS inside the ability face at the
         bottom centre. That keeps the exact favourite state/subscription/focus
         behaviour while putting the action beside the result it bookmarks. */
      var favorite = '<div class="ffshop-card-favorite-slot" data-card-back-action="1" hidden>'+
        '<button type="button" class="ffshop-card-control ffshop-card-favorite" data-act="favorite" aria-pressed="false" aria-label="Favorite this hero">'+
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 2.6 5.3 5.9.9-4.3 4.2 1 5.9-5.2-2.8-5.2 2.8 1-5.9-4.3-4.2 5.9-.9Z"/></svg></button></div>';
      /* One external row, now with direct doors to both functional card faces.
         A player can move Art -> Abilities or Art -> Upgrades without cycling
         through a hidden state. Face-specific Edit/Dust controls still use real
         `hidden`, so they never become invisible keyboard stops. */
      return favorite+'<div class="ffshop-card-controls" role="group" aria-label="Card actions">'+
        '<button type="button" class="ffshop-card-control ffshop-card-view-toggle" data-card-view-toggle="1" aria-label="View abilities">'+
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 7h10v10H7z"/><path d="M4 11V5a1 1 0 0 1 1-1h6M20 13v6a1 1 0 0 1-1 1h-6"/></svg>'+
          '<span class="ffshop-control-long" data-card-view-label="long">Abilities</span><span class="ffshop-control-short" data-card-view-label="short">Abilities</span></button>'+
        '<button type="button" class="ffshop-card-control ffshop-card-upgrades-toggle" data-card-upgrades-toggle="1" aria-label="View upgrades">'+
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.25 19 8.2l-2.15 2.45L12 6.55l-4.85 4.1L5 8.2 12 2.25Zm0 7.1 6 5.1-2.15 2.4L12 13.6l-3.85 3.25L6 14.45l6-5.1Zm0 6.65 4.1 3.45L12 22.6l-4.1-3.15L12 16Z"/></svg>'+
          '<span class="ffshop-control-long" data-card-upgrades-label="long">Upgrades</span><span class="ffshop-control-short" data-card-upgrades-label="short">Upgrades</span></button>'+
        edit+dust+
      '</div>';
    }
    function cardBoxHtml(){
      return '<div class="ffshop-tiltwrap">' + fullCardHtml(heroId, snap, { back:'abilities' }) + '</div>' + cardActionRailHtml();
    }
    /* R586: only offered when this card really is one of several on the grid
       behind it, so the arrows never appear with nowhere to go. */
    var _navOn = inspNavList(heroId).length > 1;
    overlay.innerHTML =
      (_navOn
        ? '<button type="button" class="ffshop-inspnav ffshop-inspnav-prev" aria-label="Previous card"></button>'+
          '<button type="button" class="ffshop-inspnav ffshop-inspnav-next" aria-label="Next card"></button>'
        : '') +
      '<div class="ffshop-insp-inner">' +
        '<div class="ffshop-cardbox">'+cardBoxHtml()+'</div>' +
      '</div>';
    var closed = false;
    var hallFavoriteUnsub = null;
    overlay.__ffshopClose = close;
    /* R586: held here so close() can take the resize listener back off. */
    var _inspNavResize = null;
    document.body.appendChild(overlay);
    if(_navOn){
      /* ⛔ R586 · THE ARROWS ARE ANCHORED TO THE CARD, NOT THE OVERLAY, which
         is the same mistake R585 had to undo on the Hall. The overlay is the
         whole window, so an arrow pinned to its edge sits a few hundred pixels
         away from a 380px card on a desktop and looks correct only on a phone,
         where the card nearly fills the screen and the two happen to coincide.
         The card is transform-SCALED, so its layout box is not its visible
         box; getBoundingClientRect reports what the eye actually sees, and
         that measured edge is published as a variable both arrows read. The
         card is centred, so one number serves both sides. */
      var _syncInspNav = function(){
        try{
          var box = overlay.querySelector('.ffshop-cardbox');
          if(!box) return;
          var o = overlay.getBoundingClientRect(), b = box.getBoundingClientRect();
          if(!b.width) return;
          overlay.style.setProperty('--ffshop-insp-edge', Math.round(b.left - o.left) + 'px');
        }catch(_){}
      };
      /* One frame later: the card is scaled to fit after layout, so measuring
         in this tick reads its unscaled 380px box and parks both arrows in the
         wrong place until the first resize. */
      try{ requestAnimationFrame(_syncInspNav); }catch(_){ _syncInspNav(); }
      _inspNavResize = function(){ _syncInspNav(); };
      try{ window.addEventListener('resize', _inspNavResize); }catch(_){ _inspNavResize = null; }
      try{
        var _pv = overlay.querySelector('.ffshop-inspnav-prev');
        var _nx = overlay.querySelector('.ffshop-inspnav-next');
        /* stopPropagation because a tap on the overlay's own background
           closes the card, and the arrows sit on that background */
        if(_pv) _pv.addEventListener('click', function(e){ e.stopPropagation(); window.hohShopInspectNav(-1); });
        if(_nx) _nx.addEventListener('click', function(e){ e.stopPropagation(); window.hohShopInspectNav(1); });
      }catch(_){}
    }
    /* R586: arrive from the side the step came from, then forget the
       direction so an ordinary open is never animated by a stale value. */
    if(_inspNavIn){
      try{
        var _inn = overlay.querySelector('.ffshop-insp-inner');
        if(_inn) _inn.classList.add(_inspNavIn === 'right' ? 'ffshop-inspin-right' : 'ffshop-inspin-left');
      }catch(_){}
      _inspNavIn = '';
    }
    function paintInspectorFavorite(){
      var btn=overlay.querySelector('[data-act="favorite"]');
      if(!btn)return;
      var ready=typeof window.hohHallIsSaved==='function' &&
        (typeof window.hohHallToggleHero==='function' ||
         (typeof window.hohHallForceSaveHero==='function'&&typeof window.hohHallRemoveHero==='function'));
      if(!ready){
        btn.disabled=true;btn.setAttribute('aria-label','Favorites are still loading');btn.setAttribute('title','Favorites are still loading');
        return;
      }
      var saved=!!window.hohHallIsSaved(heroId);
      btn.disabled=false;
      btn.classList.toggle('is-saved',saved);
      btn.setAttribute('aria-pressed',saved?'true':'false');
      btn.setAttribute('aria-label',saved?'Remove this hero from favorites':'Favorite this hero');
      btn.setAttribute('title',saved?'Remove favorite':'Favorite');
    }
    function toggleInspectorFavorite(btn){
      if(!btn||btn.disabled)return;
      try{
        if(typeof window.hohHallToggleHero==='function')window.hohHallToggleHero(heroId);
        else if(window.hohHallIsSaved(heroId))window.hohHallRemoveHero(heroId);
        else window.hohHallForceSaveHero(heroId,'star');
        paintInspectorFavorite();
        if(!reduced&&btn.animate)btn.animate([{transform:'scale(1)'},{transform:'scale(1.09)'},{transform:'scale(1)'}],{duration:260,easing:'ease-out'});
      }catch(_){toast('Favorites are unavailable right now.');paintInspectorFavorite();}
    }
    paintInspectorFavorite();
    try{if(typeof window.hohHallSubscribe==='function')hallFavoriteUnsub=window.hohHallSubscribe(paintInspectorFavorite);}catch(_){hallFavoriteUnsub=null;}
    function guardInspectorAccount(e){
      if(shopAccountIsCurrent(inspectorScope)) return;
      if(e){ e.preventDefault(); e.stopPropagation(); if(e.stopImmediatePropagation) e.stopImmediatePropagation(); }
      close();
      toast('The signed-in account changed. Open the card again for this account.');
    }
    overlay.addEventListener('pointerdown',guardInspectorAccount,true);
    overlay.addEventListener('click',guardInspectorAccount,true);
    overlay.addEventListener('keydown',guardInspectorAccount,true);
    var appViewport = document.getElementById('hoh-app');
    var appHadInert = !!(appViewport && appViewport.hasAttribute('inert'));
    var appHadAria = !!(appViewport && appViewport.hasAttribute('aria-hidden'));
    var appAria = appViewport ? appViewport.getAttribute('aria-hidden') : null;
    var bodyOverflow = document.body.style.overflow;
    try{
      document.body.style.overflow = 'hidden';
      if(appViewport){ appViewport.setAttribute('inert',''); appViewport.setAttribute('aria-hidden','true'); }
    }catch(_){ }
    /* Tilt is a hover effect: only on fine pointers, so the wrapper's
       responsive scale on small screens is never overwritten. */
    var fineHover = false;
    try{ fineHover = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches); }catch(_){}
    function canTiltInspector(){
      /* Compact Profile cards use transform for required viewport scaling.
         An inline hover transform would override that scale in short desktop
         windows and landscape phones, so card-first inspectors never tilt. */
      return !overlay.classList.contains('ffshop-insp-compact') && fineHover && (window.innerWidth || 0) > 700;
    }
    /* Shared wiring for the full card: animated flip on click (gem
       excluded), subtle hover tilt on the wrapper, text fitters. Used
       for the first render AND the post-generation refresh. */
    function wireInspCard(wrap){
      var cardEl = wrap.querySelector('.ffshop-card');
      if(!cardEl) return;
      var viewToggle = null, upgradesToggle = null, cardBox = null;
      try{
        cardBox=wrap.closest('.ffshop-cardbox');
        viewToggle = cardBox.querySelector('[data-card-view-toggle]');
        upgradesToggle = cardBox.querySelector('[data-card-upgrades-toggle]');
      }catch(_){}
      var flipping = false;
      function currentCardView(){
        if(!cardEl.classList.contains('on')) return 'art';
        return cardBox && cardBox.classList.contains('is-upgrades-view') ? 'upgrades' : 'abilities';
      }
      function paintCardView(view){
        var showBack = view !== 'art';
        var showUpgrades = view === 'upgrades';
        var label = showBack ? 'View card art' : 'View abilities';
        if(viewToggle){
          viewToggle.setAttribute('aria-label', label);
          var longLabel=viewToggle.querySelector('[data-card-view-label="long"]');
          var shortLabel=viewToggle.querySelector('[data-card-view-label="short"]');
          if(longLabel)longLabel.textContent=showBack?'Art':'Abilities';
          if(shortLabel)shortLabel.textContent=showBack?'Art':'Abilities';
        }
        if(upgradesToggle){
          var upgradeLabel=showUpgrades?'View abilities':'View upgrades';
          var readyCount=Math.max(0,parseInt(upgradesToggle.getAttribute('data-card-upgrades-ready'),10)||0);
          upgradesToggle.setAttribute('aria-label',upgradeLabel+(readyCount?'. '+readyCount+' '+(readyCount===1?'reward':'rewards')+' ready.':''));
          var upgradeLong=upgradesToggle.querySelector('[data-card-upgrades-label="long"]');
          var upgradeShort=upgradesToggle.querySelector('[data-card-upgrades-label="short"]');
          if(upgradeLong)upgradeLong.textContent=showUpgrades?'Abilities':'Upgrades';
          if(upgradeShort)upgradeShort.textContent=showUpgrades?'Abilities':'Upgrades';
        }
        if(cardBox){
          cardBox.classList.toggle('is-ability-view',showBack&&!showUpgrades);
          cardBox.classList.toggle('is-upgrades-view',showUpgrades);
          cardBox.querySelectorAll('[data-card-back-action]').forEach(function(control){control.hidden=!showBack;});
          cardBox.querySelectorAll('[data-card-front-action]').forEach(function(control){control.hidden=showBack;});
          var favoriteSlot=cardBox.querySelector('.ffshop-card-favorite-slot');
          if(favoriteSlot)favoriteSlot.hidden=!showBack||showUpgrades;
        }
        var abilityShell=cardEl.querySelector('.ffshop-ability-shell');
        var upgradeShell=cardEl.querySelector('[data-card-upgrades-face]');
        if(abilityShell){abilityShell.hidden=showUpgrades;if(showUpgrades)abilityShell.setAttribute('inert','');else abilityShell.removeAttribute('inert');}
        if(upgradeShell){upgradeShell.hidden=!showUpgrades;if(showUpgrades)upgradeShell.removeAttribute('inert');else upgradeShell.setAttribute('inert','');}
        syncCardFaceState(cardEl);
        syncCardBox(cardEl);
        if(showBack){
          var cardName=cardEl.getAttribute('data-card-name')||'Hero';
          cardEl.setAttribute('aria-label',cardName+(showUpgrades?' upgrades':' abilities'));
          paintInspectorFavorite();
        }
      }
      paintCardView(currentCardView());
      function turnCard(view, focusCard){
        if(view!=='art'&&view!=='abilities'&&view!=='upgrades')view='abilities';
        var current=currentCardView();
        if(flipping || current===view) return;
        var crossesPhysicalFace=(current==='art')!==(view==='art');
        if(!crossesPhysicalFace){paintCardView(view);return;}
        flipping = true;
        if(canTiltInspector()) wrap.style.transform='none';
        else wrap.style.removeProperty('transform');
        function swap(){
          cardEl.classList.toggle('on', view!=='art');
          paintCardView(view);
          if(focusCard){ try{ cardEl.focus({ preventScroll:true }); }catch(_){ try{ cardEl.focus(); }catch(__){} } }
        }
        /* ⛔ R576: the inspector's flip gets the identical treatment, because it
           is the identical fault on a second surface. R563 already learned that
           fixing one of these two flips and not the other leaves half the bug
           on the site. */
        if(coarsePointer()){
          swap();
          forceCardRepaint(cardEl);
          flipping = false;
          return;
        }
        if(motionReduced()){ swap(); flipping=false; return; }
        /* ⛔ R563 · THE SECOND COPY OF THE SAME BUG, found by the guard rather
           than by reading. The inspector runs its own flip and had the same two
           setTimeouts started at the CLICK, drifting from an animation that
           starts at the first paint after the class lands. Same cure: both
           moments hang off the animation's own start, animationend finishes
           early where it fires, and a guard releases the latch if the animation
           never runs at all. See the fuller note in hohShopWireCard. */
        var _sw = false, _mT = 0, _eT = 0, _gT = 0;
        var _once = function(){ if(_sw) return; _sw = true; swap(); };
        var _fin = function(){
          clearTimeout(_mT); clearTimeout(_eT); clearTimeout(_gT);
          cardEl.removeEventListener('animationstart', _st);
          cardEl.removeEventListener('animationend', _en);
          _once();
          cardEl.classList.remove('ffshop-turn');
          flipping = false;
        };
        /* ⛔ R571 · AND THE SECOND COPY OF THE R571 BUG TOO. Same shape, same
           cure, same reason it must be fixed in both: R563 fixed one flip and
           the guard found the other. See the long note in hohShopWireCard. */
        var _armT = function(_d){
          clearTimeout(_mT); clearTimeout(_eT); clearTimeout(_gT);
          _mT = setTimeout(_once, _d / 2);
          _eT = setTimeout(_fin, _d + 30);
        };
        var _st = function(ev){
          if(ev.target !== cardEl || ev.animationName !== 'ffshopInspTurn') return;
          var _d = 430;
          try{ _d = Math.max(120, (parseFloat(getComputedStyle(cardEl).animationDuration) || .43) * 1000); }catch(_){}
          _armT(_d);
        };
        var _en = function(ev){
          if(ev.target !== cardEl || ev.animationName !== 'ffshopInspTurn') return;
          _fin();
        };
        cardEl.addEventListener('animationstart', _st);
        cardEl.addEventListener('animationend', _en);
        cardEl.classList.add('ffshop-turn');
        if(!ffshopWillAnimate(cardEl)){ _fin(); }
        else{ _gT = setTimeout(_fin, 700); }
      }
      cardEl.addEventListener('click', function(e){
        var rb = e.target.closest('.ffshop-repair-btn');
        if(rb && !rb.disabled){
          e.stopPropagation();
          rb.disabled = true;
          var rbOld = rb.innerHTML;
          rb.textContent = 'Repairing…';
          try {
            window.hohRepairCard(heroId).then(function(r){
              if(r && r.ok){
                rb.textContent = 'Repaired · ready to fight';
                rb.classList.add('is-done');
                setTimeout(function(){ closeShopInspectors(); try{ window.hohShopInspect(heroId); }catch(_){} }, 900);
              } else { rb.innerHTML = rbOld; rb.disabled = false; }
            });
          } catch(_){ rb.innerHTML = rbOld; rb.disabled = false; }
          return;
        }
        if(e.target.closest('[data-card-flip]')){ e.stopPropagation(); turnCard('art', true); return; }
        if(e.target.closest('button,a,input,select,textarea,[role="button"],[tabindex]:not(.ffshop-card)')) return;
        if(e.target.closest('.ffshop-gem')) return;
        if(e.target.closest('.ffshop-ffm-wrap')) return;
        turnCard(currentCardView()==='art'?'abilities':'art', false);
      });
      cardEl.addEventListener('keydown', function(e){
        if(e.target !== cardEl || (e.key !== 'Enter' && e.key !== ' ')) return;
        e.preventDefault();
        turnCard(currentCardView()==='art'?'abilities':'art', false);
      });
      if(viewToggle){
        viewToggle.addEventListener('click', function(e){
          e.preventDefault(); e.stopPropagation();
          /* Keep focus on the real control whose label updates in place. */
          turnCard(currentCardView()==='art'?'abilities':'art', false);
        });
      }
      if(upgradesToggle){
        upgradesToggle.addEventListener('click',function(e){
          e.preventDefault();e.stopPropagation();
          turnCard(currentCardView()==='upgrades'?'abilities':'upgrades',false);
        });
      }
      if(fineHover){
        /* PERF-11: same cached-rect + rAF batching as the shelf tilt above. */
        var tiltRect2=null, tiltRaf2=0, tiltX2=0, tiltY2=0;
        wrap.addEventListener('pointerenter', function(){ tiltRect2=null; });
        wrap.addEventListener('pointermove', function(e){
          if(!canTiltInspector()){ wrap.style.removeProperty('transform'); return; }
          if(flipping) return;
          if(!tiltRect2) tiltRect2=wrap.getBoundingClientRect();
          if(!tiltRect2.width||!tiltRect2.height) return;
          tiltX2=(e.clientX-tiltRect2.left)/tiltRect2.width-0.5; tiltY2=(e.clientY-tiltRect2.top)/tiltRect2.height-0.5;
          if(!tiltRaf2){ tiltRaf2=requestAnimationFrame(function(){ tiltRaf2=0; if(flipping) return; wrap.style.transform='perspective(1000px) rotateY('+(tiltX2*3.5)+'deg) rotateX('+(-tiltY2*3.5)+'deg)'; }); }
        });
        wrap.addEventListener('pointerleave', function(){ tiltRect2=null; if(tiltRaf2){ cancelAnimationFrame(tiltRaf2); tiltRaf2=0; } wrap.style.removeProperty('transform'); });
      }
      /* R241: fit once now, again when the webfonts land (they change
         text metrics after the first measure — the clipping root cause),
         and once more as a late safety net. */
      function runFits(){
        fitLine(cardEl.querySelector('.ffshop-fit'), 11);
        fitZone(cardEl.querySelector('.ffshop-fitzone'));
        syncCardBox(cardEl);
      }
      requestAnimationFrame(runFits);
      try{
        if(document.fonts && document.fonts.ready && document.fonts.ready.then){
          document.fonts.ready.then(function(){ if(document.body.contains(cardEl)) runFits(); });
        }
      }catch(_){}
      setTimeout(function(){ if(document.body.contains(cardEl)) runFits(); }, 650);
    }
    wireInspCard(overlay.querySelector('.ffshop-tiltwrap'));
    overlay.addEventListener('click', function(e){ if(e.target===overlay) close(); });
    var xb = null;
    function close(){
      if(closed) return;
      closed = true;
      try{if(typeof hallFavoriteUnsub==='function')hallFavoriteUnsub();}catch(_){ }
      hallFavoriteUnsub=null;
      try{if(typeof overlay.__ffProgressionCleanup==='function')overlay.__ffProgressionCleanup();}catch(_){ }
      overlay.__ffProgressionCleanup=null;
      try{ document.querySelectorAll('.ffshop-pour').forEach(function(pour){ pour.remove(); }); }catch(_){}
      try{ overlay.remove(); }catch(_){}
      try{ if(xb) xb.remove(); }catch(_){}
      /* R586: the overlay is thrown away on every step between cards, so a
         listener left on `window` would outlive it and accumulate one dead
         handler per arrow press for the life of the page. */
      try{ if(_inspNavResize) window.removeEventListener('resize', _inspNavResize); }catch(_){}
      _inspNavResize = null;
      document.removeEventListener('keydown', onKey);
      try{
        document.body.style.overflow = bodyOverflow;
        if(appViewport){
          if(!appHadInert) appViewport.removeAttribute('inert');
          if(appHadAria) appViewport.setAttribute('aria-hidden', appAria);
          else appViewport.removeAttribute('aria-hidden');
        }
        if(returnFocus && document.body.contains(returnFocus) && typeof returnFocus.focus === 'function') returnFocus.focus({ preventScroll:true });
      }catch(_){ }
      emitShopInspectorLifecycle('closed', heroId, overlay);
    }
    function dialogFocusables(){
      var nodes = Array.prototype.slice.call(overlay.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'));
      if(xb && !xb.disabled) nodes.unshift(xb);
      return nodes.filter(function(n){ return !n.hasAttribute('inert') && n.getClientRects && n.getClientRects().length; });
    }
    function onKey(e){
      if(e.key==='Escape'){ e.preventDefault(); close(); return; }
      /* R586: the same step on a keyboard. Guarded on _navOn so the keys stay
         inert on a card that has no neighbours, and skipped while the player
         is typing, because the card text editor lives inside this dialog and
         an arrow key there means move the caret. */
      if(_navOn && (e.key==='ArrowLeft' || e.key==='ArrowRight')){
        var _ae = document.activeElement, _tn = _ae ? (_ae.tagName||'').toLowerCase() : '';
        if(_tn!=='input' && _tn!=='textarea' && _tn!=='select' && !(_ae && _ae.isContentEditable)){
          e.preventDefault();
          window.hohShopInspectNav(e.key==='ArrowLeft' ? -1 : 1);
          return;
        }
      }
      if(e.key!=='Tab') return;
      var f = dialogFocusables();
      if(!f.length){ e.preventDefault(); return; }
      var first=f[0], last=f[f.length-1];
      if(e.shiftKey && document.activeElement===first){ e.preventDefault(); last.focus(); }
      else if(!e.shiftKey && document.activeElement===last){ e.preventDefault(); first.focus(); }
    }
    /* v24c ROOT CAUSE (Jan's iPhone screenshot, no way to exit): the ✕ was
       position:fixed INSIDE the overlay — and the overlay wears
       backdrop-filter, which on iOS Safari makes it the containing block
       for fixed descendants, so the ✕ scrolled away with the card. It now
       lives on <body>, truly viewport-fixed, and close() removes it. */
    try{ var _ox = document.querySelector('body > .ffshop-insp-x'); if(_ox) _ox.remove(); }catch(_){}
    xb = document.createElement('button');
    xb.type = 'button'; xb.id='ffshop-insp-close'; xb.className = 'ffshop-insp-x'; xb.setAttribute('aria-label','Close card');
    /* iOS requires this fixed control outside the filtered overlay for correct
       viewport positioning. aria-owns keeps it inside the dialog's accessible
       ownership tree even though its visual containing block is <body>. */
    overlay.setAttribute('aria-owns',xb.id);
    xb.innerHTML = '✕';
    xb.addEventListener('click', function(e){ e.stopPropagation(); close(); });
    document.body.appendChild(xb);
    document.addEventListener('keydown', onKey);
    function bindInspectorActions(){
      if(overlay.__ffActionsWired)return;
      overlay.__ffActionsWired=1;
      /* Delegate from the stable dialog. Card-text generation can replace the
         complete cardbox, but it cannot duplicate or strand this listener. */
      overlay.addEventListener('click',function(e){
        var b=e.target.closest('[data-act]');if(!b||!overlay.contains(b))return;
        var act=b.getAttribute('data-act');
        if(act==='favorite'){e.preventDefault();e.stopPropagation();toggleInspectorFavorite(b);}
        if(act==='edit'){e.preventDefault();e.stopPropagation();openEdit(heroId,close);}
        if(act==='dustall'){e.preventDefault();e.stopPropagation();openSingleDustReview(heroId,b,close);}
      });
    }
    bindInspectorActions();
    /* FF card with no generated text yet: generate in the background,
       then refresh the open inspector in place (wrapper + X rebuilt so
       the parallax and close wiring stay whole). */
    var heroNow = heroById(heroId);
    if((heroNow && heroNow.is_ff_portrait || (snap && snap.ff)) && !hasRealCardText(heroNow)){
      ensureCardMeta(heroId, function(meta){
        if(!meta || !document.body.contains(overlay)) return;
        var box = overlay.querySelector('.ffshop-cardbox');
        if(box){
          var oldCard=box.querySelector('.ffshop-card');
          var oldView=oldCard&&oldCard.classList.contains('on')
            ? (box.classList.contains('is-upgrades-view')?'upgrades':'abilities') : 'art';
          var focusAction=document.activeElement&&document.activeElement.closest?document.activeElement.closest('[data-act],[data-card-view-toggle],[data-card-upgrades-toggle]'):null;
          var focusKey=focusAction?(focusAction.getAttribute('data-act')||(focusAction.hasAttribute('data-card-upgrades-toggle')?'upgrades':'view')):'';
          box.innerHTML = cardBoxHtml();
          var newCard=box.querySelector('.ffshop-card');
          if(oldView!=='art'&&newCard)newCard.classList.add('on');
          box.classList.toggle('is-upgrades-view',oldView==='upgrades');
          box.classList.toggle('is-ability-view',oldView==='abilities');
          wireInspCard(box.querySelector('.ffshop-tiltwrap'));
          paintInspectorFavorite();
          _battleExtras(overlay, heroId, snap, own, inspectorScope, close);
          if(focusKey){
            requestAnimationFrame(function(){
              var next=focusKey==='view'?box.querySelector('[data-card-view-toggle]')
                :(focusKey==='upgrades'?box.querySelector('[data-card-upgrades-toggle]'):box.querySelector('[data-act="'+focusKey+'"]'));
              if(next&&!next.hidden&&!next.disabled)try{next.focus({preventScroll:true});}catch(_){try{next.focus();}catch(__){}}
            });
          }
        }
      });
    }
    _battleExtras(overlay, heroId, snap, own, inspectorScope, close);
    setTimeout(function(){
      try{ var firstAction = overlay.querySelector('[data-card-view-toggle]'); if(firstAction) firstAction.focus({ preventScroll:true }); }catch(_){ }
    }, 0);
    /* This fires only after the real overlay, close affordance, card wiring,
       and Battlegrounds panel are ready. Callers can stop loading indicators
       or pause a fight from this event instead of guessing with a timer. */
    emitShopInspectorLifecycle('opened', heroId, overlay);
  };

  /* Card progression is its own face, not a seventh row squeezed beneath the
     two rolled abilities. The data module owns eligibility and receipts; this
     renderer only makes its saved truth legible and keeps claims sequential. */
  function cardProgressionGuideKey(scope){
    var raw=String((scope&&scope.account)||'local'), hash=5381;
    for(var i=0;i<raw.length;i++)hash=((hash<<5)+hash)^raw.charCodeAt(i);
    return 'hoh-card-progression-guide-v1:'+(hash>>>0).toString(36);
  }
  function cardProgressionGuideSeen(scope){
    try{return localStorage.getItem(cardProgressionGuideKey(scope))==='1';}catch(_){return false;}
  }
  function markCardProgressionGuideSeen(scope){
    try{localStorage.setItem(cardProgressionGuideKey(scope),'1');}catch(_){}
  }
  function cardProgressionIcon(row){
    var paths={
      card_20:'M6 4h10v14H6z M10 8h8v13H10 M8 8h4 M8 11h4',
      class_move_20:'m5 19 7-7 M9 6l3-3 9 9-3 3z M4 15l5 5 M14 4l6 6',
      card_30:'M4 7h9v13H4z M8 4h9v13 M12 2h9v13 M6 11h5 M6 14h5',
      ability_35:'m7 3 4 7-4 7-4-7z m10 4 4 7-4 7-4-7z',
      card_40:'M5 9h14v12H5z M5 9 3 3l6 3 3-4 3 4 6-3-2 6 M8 14h8 M8 17h8',
      ultimate_60:'m12 2 2.5 6.5L21 11l-6.5 2.5L12 21l-2.5-7.5L3 11l6.5-2.5z M18 3l3 3 M3 3l3 3',
      card_50:'M12 21V11 M12 15C3 15 3 6 3 6s9-1 9 9 M12 11c0-8 9-8 9-8s1 9-9 9',
      card_70:'M12 3a9 9 0 1 1-8 5 M12 6v6l4 2 M2 3h7 M1 7h5',
      card_100:'M12 12C5 1 0 9 4 14c4 5 8-2 8-2s4-7 8-2c4 5-1 13-8 2'
    };
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="'+(paths[row.id]||paths.card_20)+'"/></svg>';
  }
  /* R791 (Cortez): a claim the database refused showed PostgREST's own
     check-constraint sentence in the red alert box. Every claim and quest
     failure now goes through the contract's playerLine: copy written for a
     player is kept, machine text becomes the plain line plus a short code. */
  function progressionErrorLine(err,fallback,context){
    var D=window.HOHContractData;
    if(D&&typeof D.playerLine==='function'){try{return D.playerLine(err,{context:context||'claim',line:fallback});}catch(_){}}
    var t=String(err&&err.message||'');
    var rpc=!!(err&&err.__hohRpcName&&String(err.__hohRpcCode||'')!=='P0001');
    return (!rpc&&/^[A-Z][^_{}<>]*[.!?]$/.test(t)&&t.indexOf(' ')>0)?t:fallback;
  }
  function progressionAttentionCount(rows){
    // Eligible quests stay visible after acceptance, through retries and until the reward is claimed.
    return (rows||[]).filter(function(row){return row&&((row.earned&&row.state!=='claimed')||row.state==='quest');}).length;
  }
  function cardUpgradeAttention(cardKey){
    try{var D=window.HOHContractData, state=D&&D.cardProgressionFor&&D.cardProgressionFor(cardKey);
      return state&&state.status==='ready'?progressionAttentionCount(state.milestones):0;
    }catch(_){return 0;}
  }
  function updateGridUpgradeAttention(){
    document.querySelectorAll('.ffshop-tile[data-card-key]').forEach(function(tile){
      var count=(tile.hasAttribute('data-spent')||tile.classList.contains('ffshop-tile-uncoll'))?0:cardUpgradeAttention(tile.getAttribute('data-card-key'));
      tile.classList.toggle('has-upgrade-ready',count>0);
      var badge=tile.querySelector('.ffshop-tile-upgrade');
      if(count&&!badge){badge=document.createElement('span');badge.className='ffshop-tile-upgrade';badge.setAttribute('aria-hidden','true');badge.textContent='!';tile.appendChild(badge);}
      if(!count&&badge)badge.remove();
      var name=tile.querySelector('.ffshop-tile-name');
      tile.setAttribute('aria-label',(name?name.textContent:'Hero')+(count?'. '+count+' '+(count===1?'upgrade or quest':'upgrades or quests')+' to finish.':''));
    });
  }
  function cardProgressionUnavailable(overlay,title,copy){
    var host=overlay&&overlay.querySelector?overlay.querySelector('.ffshop-upgrades-mount'):null;
    if(!host)return;
    try{if(typeof overlay.__ffProgressionCleanup==='function')overlay.__ffProgressionCleanup();}catch(_){}
    overlay.__ffProgressionCleanup=null;
    host.removeAttribute('aria-busy');
    host.innerHTML='<div class="ffshop-upgrades-error" role="status"><span class="ffshop-upgrades-error-icon" aria-hidden="true">!</span>'+
      '<b>'+esc(title||'Upgrades unavailable')+'</b><p>'+esc(copy||'Close this card and try again.')+'</p></div>';
  }
  function mountCardProgression(overlay,cardKey,levelHint,inspectorScope,eagerLoad,heroHint){
    var host=overlay&&overlay.querySelector?overlay.querySelector('.ffshop-upgrades-mount'):null;
    if(!host)return;
    var title=overlay.querySelector('.ffshop-upgrades-top>b');
    if(title){
      var nameLength=title.textContent.trim().length;
      title.style.setProperty('--ff-upgrade-name-size',(nameLength<=14?28:nameLength<=26?23:nameLength<=44?20:nameLength<=65?18:16)+'px');
    }
    try{if(typeof overlay.__ffProgressionCleanup==='function')overlay.__ffProgressionCleanup();}catch(_){}
    var destroyed=false, busyId='', bulkBusy=false, bulkTotal=0, bulkDone=0, claimError='', liveMessage='', lastState=null, pageIndex=0, justClaimed='', guideVisible=null, loadInFlight=false, locatedReward=false;
    var shortMq=null;
    /* R743 (Jan): short means short LANDSCAPE. A portrait phone under browser
       chrome is under 740px too, and it was getting two rows a page with a
       pager and dead space below. Portrait renders the whole list; the CSS
       for the same query lets the mount scroll if it ever outgrows the card. */
    try{shortMq=window.matchMedia&&window.matchMedia('(max-height:740px) and (orientation:landscape)');}catch(_){shortMq=null;}
    function isShort(){return !!(shortMq&&shortMq.matches);}
    function cleanMilestones(state){
      var rows=state&&Array.isArray(state.milestones)?state.milestones.slice():[];
      var stateLevel=Math.max(1,parseInt(state&&state.level,10)||parseInt(levelHint,10)||1);
      var hero=(typeof heroById==='function'?heroById(String(cardKey).replace(/^(ff|own):/,'')):null)||heroHint;
      var cls=hero&&(hero.character_class||hero.cls||hero.class), engine=window.HOHContract;
      var moves=cls&&engine&&engine.classMovesFor?engine.classMovesFor(cls):null;
      return rows.map(function(row,index){
        row=row||{};
        var s=String(row.state||'locked');
        if(s!=='ready'&&s!=='claimed'&&s!=='quest')s='locked';
        var rowLevel=Math.max(1,parseInt(row.level,10)||1);
        return {
          id:String(row.id||('level-'+(row.level||index))),
          level:rowLevel,
          title:String(moves&&row.kind==='move'?moves[1].name:moves&&row.kind==='ultimate'?moves[2].name:row.title||'Card upgrade'),
          summary:String(moves&&row.kind==='move'?moves[1].copy:moves&&row.kind==='ultimate'?moves[2].copy:row.summary||''), kind:String(row.kind||'craft'), quest:row.quest||null, prerequisite:!!row.prerequisite,
          state:s,
          earned:row.earned===true||(row.earned==null&&s!=='claimed'&&s!=='quest'&&(s==='ready'||stateLevel>=rowLevel)),
          remaining:Math.max(0,parseInt(row.remaining,10)||0),
          order:index
        };
      }).sort(function(a,b){return (a.level-b.level)||(a.order-b.order);});
    }
    function paintUpgradeAttention(rows){
      var toggle=overlay&&overlay.querySelector?overlay.querySelector('[data-card-upgrades-toggle]'):null;
      if(!toggle)return;
      var count=progressionAttentionCount(rows);
      toggle.classList.toggle('has-unclaimed-upgrades',count>0);
      toggle.setAttribute('data-card-upgrades-ready',String(count));
      var badge=toggle.querySelector('.ffshop-upgrade-count');
      if(count&&!badge){badge=document.createElement('b');badge.className='ffshop-upgrade-count';badge.setAttribute('aria-hidden','true');toggle.appendChild(badge);}
      if(badge){badge.textContent=count?String(count):'';badge.hidden=!count;}
      var showing=!!(toggle.closest('.ffshop-cardbox')&&toggle.closest('.ffshop-cardbox').classList.contains('is-upgrades-view'));
      var action=showing?'View abilities':'View upgrades';
      toggle.setAttribute('aria-label',action+(count?'. '+count+' '+(count===1?'upgrade or quest':'upgrades or quests')+' to finish.':''));
    }
    function readState(){
      var D=window.HOHContractData;
      if(!D||typeof D.cardProgressionFor!=='function')return {status:'error',level:levelHint||1,milestones:[]};
      try{return D.cardProgressionFor(cardKey)||{status:'loading',level:levelHint||1,milestones:[]};}
      catch(_){return {status:'error',level:levelHint||1,milestones:[]};}
    }
    function requestProgressionLoad(force){
      var D=window.HOHContractData;
      if(loadInFlight||!D||typeof D.loadCardXp!=='function')return false;
      loadInFlight=true;
      Promise.resolve(D.loadCardXp(!!force)).then(function(){
        if(destroyed)return;loadInFlight=false;lastState=null;paint();
      },function(){
        if(destroyed)return;loadInFlight=false;lastState=null;paint();
      });
      return true;
    }
    function focusProgressionRow(id){
      if(!id)return;
      requestAnimationFrame(function(){
        if(destroyed)return;
        var rows=host.querySelectorAll('[data-card-progression-row]'), target=null;
        for(var i=0;i<rows.length;i++)if(rows[i].getAttribute('data-card-progression-row')===id){target=rows[i];break;}
        if(target)try{
          var list=target.closest('.ffshop-upgrade-list');
          if(list){
            var rowRect=target.getBoundingClientRect(),listRect=list.getBoundingClientRect();
            var inverse=listRect.height?list.offsetHeight/listRect.height:1;
            if(rowRect.top<listRect.top||rowRect.height>=listRect.height)list.scrollTop+=(rowRect.top-listRect.top)*inverse;
            else if(rowRect.bottom>listRect.bottom)list.scrollTop+=(rowRect.bottom-listRect.bottom)*inverse;
          }
          target.focus({preventScroll:true});
        }catch(_){}
      });
    }
    function rowHtml(row,index,firstOpen){
      var state=row.state, canClaim=state==='ready'&&!busyId&&!bulkBusy;
      var pending=busyId===row.id, isUltimate=row.kind==='ultimate', isTrial=isUltimate||row.kind==='move', goal=isUltimate?((row.quest&&row.quest.target_round)||20):10, reward=isUltimate?'ultimate':'signature move';
      var stateClass=(state==='claimed'?'claimed':state==='quest'?'quest':row.earned?'ready':'locked')+(row.id===justClaimed?' is-just-claimed':'');
      var action='', detail='', q=row.quest||{}, questStatus=q.status, attemptFailed=false;
      if(state==='claimed')action='<span class="ffshop-upgrade-state is-claimed"><b aria-hidden="true">&#10003;</b> Unlocked</span>';
      else if(pending)action='<button type="button" class="ffshop-upgrade-claim" disabled>Saving…</button>';
      else if(canClaim)action='<button type="button" class="ffshop-upgrade-claim" data-card-progression-claim="'+esc(row.id)+'" aria-label="'+(isUltimate?'Unlock':'Claim')+' '+esc(row.title)+'">'+(isUltimate?'Unlock':'Claim')+'</button>';
      else if(state==='quest')action='<span class="ffshop-upgrade-state">'+(questStatus==='active'?(attemptFailed?'Try again':'Quest active'):questStatus==='available'?'Quest ready':questStatus==='error'?'Reconnect':'Checking…')+'</span>';
      else if(row.prerequisite&&isUltimate)action='<span class="ffshop-upgrade-state">Unlock move 2</span>';
      else if(row.earned)action='<span class="ffshop-upgrade-state is-earned">'+(isUltimate?'Claim move 2':'Ready next')+'</span>';
      else action='<span class="ffshop-upgrade-state is-locked">Level '+row.level+'</span>';
      if(isTrial&&state!=='claimed'&&row.remaining===0){
        var progress=Math.max(0,Math.min(goal,q.best_round|0));
        if(row.prerequisite)detail='<b>Your signature comes first</b><p>'+(questStatus==='complete'?'Your saved solo record already fulfils this trial. Claim your second class move to unlock this reward.':'Claim your second class move first. Your best solo round will still count toward this trial.')+'</p>';
        else if(questStatus==='available')detail='<b>Your solo challenge</b><p>Beat Round '+goal+' with this hero as your only main party member. Any bench is allowed. Your previous best counts.</p><button type="button" data-card-ultimate-accept="'+(isUltimate?'ultimate':'class_move')+'"'+(busyId||bulkBusy?' disabled':'')+'>Accept quest</button>';
        else if(questStatus==='active')detail='<b>Beat Round '+goal+' solo</b><p>Keep this hero as your only main party member. Any bench is allowed. Completed rounds stay saved, even if you lose or leave.</p><div class="ffshop-quest-progress" role="progressbar" aria-label="Solo trial progress" aria-valuemin="0" aria-valuemax="'+goal+'" aria-valuenow="'+progress+'"><i style="width:'+(progress/goal*100)+'%"></i></div><span>Best completed: Round '+progress+' of '+goal+'</span><button type="button" data-card-ultimate-play="'+(isUltimate?'ultimate':'class_move')+'"'+(busyId||bulkBusy?' disabled':'')+'>Open solo battle setup</button>';
        else if(questStatus==='complete')detail='<b>Solo challenge complete</b><p>Your saved solo record fulfils this trial. Your '+reward+' is ready to unlock.</p>';
        else detail='<p>'+ (questStatus==='error'?'Trial progress could not be checked. Your saved progress is safe.':'Checking your saved solo trial…')+'</p>'+(questStatus==='error'?'<button type="button" data-card-progression-retry>Retry</button>':'');
      }
      var label=isUltimate?'Ultimate':row.kind==='move'?'Class move':row.kind==='crystal'?'Crystal slot':'Card craft';
      var summary=isUltimate?'Unleash the full power of your class.':row.kind==='move'?'Bring your signature move into battle.':row.summary;
      return '<div class="ffshop-upgrade-row is-'+stateClass+' reward-'+esc(row.kind)+'" role="listitem" tabindex="-1" data-card-progression-row="'+esc(row.id)+'">'+
        '<span class="ffshop-upgrade-icon" aria-hidden="true">'+cardProgressionIcon(row)+'</span>'+
        '<span class="ffshop-upgrade-copy"><small>Level '+row.level+' · '+label+'</small><b>'+esc(row.title)+'</b>'+
          (summary?'<span>'+esc(summary)+'</span>':'')+'</span>'+
        '<span class="ffshop-upgrade-action">'+action+'</span>'+
        (detail?'<div class="ffshop-ultimate-quest">'+detail+'</div>':'')+
      '</div>';
    }
    function paint(next,focusId){
      if(destroyed||!document.body.contains(host))return;
      var state=next||readState();
      lastState=state;
      var status=String((state&&state.status)||'loading');
      if(status==='loading'){
        paintUpgradeAttention([]);
        host.setAttribute('aria-busy','true');
        host.innerHTML='<div class="ffshop-upgrades-loading" role="status"><i aria-hidden="true"></i><span>Loading upgrades</span></div>';
        if(eagerLoad)requestProgressionLoad(false);
        return;
      }
      host.removeAttribute('aria-busy');
      if(status==='error'){
        paintUpgradeAttention([]);
        host.innerHTML='<div class="ffshop-upgrades-error" role="alert"><span class="ffshop-upgrades-error-icon" aria-hidden="true">!</span>'+
          '<b>Upgrades could not be verified</b><p>Your card is safe. Reconnect, then try again.</p>'+
          '<button type="button" data-card-progression-retry>Retry upgrades</button></div>';
        return;
      }
      var rows=cleanMilestones(state);
      if(!rows.length){
        paintUpgradeAttention([]);
        host.innerHTML='<div class="ffshop-upgrades-error" role="status"><span class="ffshop-upgrades-error-icon" aria-hidden="true">!</span>'+
          '<b>Upgrade path unavailable</b><p>Close this card and try again in a moment.</p></div>';
        return;
      }
      paintUpgradeAttention(rows);
      var level=Math.max(1,parseInt(state.level,10)||parseInt(levelHint,10)||1);
      var claimed=rows.filter(function(row){return row.state==='claimed';}).length;
      var earned=rows.filter(function(row){return row.earned&&row.kind!=='ultimate';}).length;
      var firstOpen=-1;
      for(var oi=0;oi<rows.length;oi++)if(rows[oi].state!=='claimed'){firstOpen=oi;break;}
      var first=rows[0], guideEligible=first&&first.level===10&&first.state==='ready';
      if(guideVisible===null&&guideEligible){
        guideVisible=!cardProgressionGuideSeen(inspectorScope);
      }
      if(!guideEligible)guideVisible=false;
      var guide=guideVisible
        ? '<aside class="ffshop-upgrades-guide" role="note"><span><b>Your first craft is ready.</b> Claim it here. When several are ready, use Claim all.</span>'+
          '<button type="button" data-card-progression-guide-dismiss aria-label="Dismiss upgrade guide">Got it</button></aside>' : '';
      /* Keep every milestone in one scrollable list on phone and desktop. */
      var ranges=[[0,rows.length]], pages=[];
      ranges.forEach(function(range){if(range[0]<rows.length)pages.push(range);});
      if(pageIndex>=pages.length)pageIndex=Math.max(0,pages.length-1);
      var pageMarkup=pages.map(function(range,pi){
        var pageRows='';
        for(var ri=range[0];ri<Math.min(range[1],rows.length);ri++)pageRows+=rowHtml(rows[ri],ri,firstOpen);
        return '<div class="ffshop-upgrade-page'+(pi===pageIndex?' is-current':'')+'" data-card-upgrade-page="'+pi+'"'+
          (isShort()&&pi!==pageIndex?' aria-hidden="true" inert':'')+'>'+pageRows+'</div>';
      }).join('');
      var pager=pages.length>1?'<div class="ffshop-upgrade-pager" role="group" aria-label="Upgrade pages">'+
        '<button type="button" data-card-upgrade-page-step="-1" aria-label="Previous upgrades"'+(pageIndex===0?' disabled':'')+'>&larr;<span>Previous</span></button>'+
        '<span aria-live="polite">'+(pageIndex+1)+' of '+pages.length+'</span>'+
        '<button type="button" data-card-upgrade-page-step="1" aria-label="Next upgrades"'+(pageIndex===pages.length-1?' disabled':'')+'><span>Next</span>&rarr;</button></div>':'';
      var bulkAction=(earned>1||bulkBusy)
        ? '<button type="button" class="ffshop-upgrades-claim-all" data-card-progression-claim-all'+(bulkBusy?' disabled':'')+
          ' aria-label="'+(bulkBusy?'Claiming earned upgrades':'Claim all '+earned+' earned upgrades')+'">'+
          (bulkBusy?'Claiming '+Math.min(bulkDone+1,bulkTotal)+' of '+bulkTotal+'…':'Claim all '+earned)+'</button>' : '';
      var celebration=justClaimed==='class_move_20'?'<div class="ffshop-ultimate-unlocked" role="status"><b>Signature move unlocked</b><span>Your hero now uses both class moves.</span></div>':justClaimed==='ultimate_60'?'<div class="ffshop-ultimate-unlocked" role="status"><b>Ultimate unlocked</b><span>Your hero now uses all three class moves.</span></div>':'';
      var soloBest=state.highest_solo_round;
      host.innerHTML='<div class="ffshop-upgrades-summary"><span>Level <b>'+level+'</b></span><span class="ffshop-upgrades-solo" title="Highest completed round with this hero as your only main party member. Any bench is allowed.">Highest solo run: <b>'+(soloBest==null?'&mdash;':Math.max(0,soloBest|0))+'</b></span><span class="ffshop-upgrades-summary-actions"><span><b>'+claimed+'</b> of '+rows.length+' unlocked</span></span></div>'+
        celebration+guide+(claimError?'<div class="ffshop-upgrades-claim-error" role="alert">'+esc(claimError)+'</div>':'')+
        '<div class="ffshop-upgrade-list" role="list">'+pageMarkup+'</div>'+pager+
        (bulkAction?'<div class="ffshop-upgrades-footer">'+bulkAction+'</div>':'')+
        '<span class="ffshop-upgrade-live" aria-live="polite" aria-atomic="true">'+esc(liveMessage||(justClaimed?'Upgrade claimed.':''))+'</span>';
      var nextReward=rows.filter(function(row){return row.state==='ready'||(row.state==='quest'&&row.quest&&(row.quest.status==='available'||row.quest.status==='complete'));})[0];
      if(!busyId&&!bulkBusy&&nextReward&&(!locatedReward||earned===0)){focusId=nextReward.id;locatedReward=true;}
      if(focusId)focusProgressionRow(focusId);
      if(justClaimed){
        setTimeout(function(){
          if(destroyed)return;
          var claimedRows=host.querySelectorAll('.is-just-claimed');
          for(var ci=0;ci<claimedRows.length;ci++)claimedRows[ci].classList.remove('is-just-claimed');
          justClaimed='';
        },900);
      }
    }
    function claimAllEarned(D,state){
      if(destroyed)return Promise.resolve(state);
      if(!shopAccountIsCurrent(inspectorScope))return Promise.reject(new Error('The signed-in account changed. Open the card again for this account.'));
      var rows=cleanMilestones(state), next=null;
      for(var i=0;i<rows.length;i++)if(rows[i].state==='ready'&&rows[i].kind!=='ultimate'){next=rows[i];break;}
      if(!next)return Promise.resolve(state);
      busyId=next.id;
      liveMessage='Claiming upgrade '+Math.min(bulkDone+1,bulkTotal)+' of '+bulkTotal+'.';
      paint(state);
      return Promise.resolve(D.claimCardProgression(cardKey,next.id)).then(function(updated){
        if(destroyed)return updated;
        bulkDone++;justClaimed=next.id;guideVisible=false;markCardProgressionGuideSeen(inspectorScope);
        var verified=updated&&updated.milestones?updated:readState();
        return claimAllEarned(D,verified);
      });
    }
    function onClick(e){
      var dismiss=e.target.closest('[data-card-progression-guide-dismiss]');
      if(dismiss){e.preventDefault();markCardProgressionGuideSeen(inspectorScope);guideVisible=false;paint(lastState);return;}
      var retry=e.target.closest('[data-card-progression-retry]');
      if(retry){
        e.preventDefault();claimError='';lastState=null;
        host.setAttribute('aria-busy','true');
        host.innerHTML='<div class="ffshop-upgrades-loading" role="status"><i aria-hidden="true"></i><span>Checking saved upgrades</span></div>';
        if(!requestProgressionLoad(true))paint();
        return;
      }
      var step=e.target.closest('[data-card-upgrade-page-step]');
      if(step&&!step.disabled){e.preventDefault();pageIndex=Math.max(0,pageIndex+(parseInt(step.getAttribute('data-card-upgrade-page-step'),10)||0));paint(lastState);return;}
      var claimAll=e.target.closest('[data-card-progression-claim-all]');
      if(claimAll&&!claimAll.disabled&&!busyId&&!bulkBusy){
        e.preventDefault();e.stopPropagation();
        if(!shopAccountIsCurrent(inspectorScope)){
          try{if(overlay.__ffshopClose)overlay.__ffshopClose();}catch(_){}
          toast('The signed-in account changed. Open the card again for this account.');
          return;
        }
        var bulkD=window.HOHContractData, bulkState=lastState||readState(), bulkRows=cleanMilestones(bulkState);
        bulkTotal=bulkRows.filter(function(row){return row.earned&&row.kind!=='ultimate';}).length;
        if(!bulkD||typeof bulkD.claimCardProgression!=='function'||!bulkTotal){claimError='Upgrades are still loading. Try again.';paint(bulkState);return;}
        bulkBusy=true;bulkDone=0;busyId='';claimError='';liveMessage='Claiming earned upgrades.';guideVisible=false;markCardProgressionGuideSeen(inspectorScope);
        paint(bulkState);
        claimAllEarned(bulkD,bulkState).then(function(updated){
          if(destroyed)return;
          if(bulkDone<bulkTotal)throw new Error('The remaining earned upgrades could not be verified.');
          var completed=bulkDone;
          bulkBusy=false;busyId='';liveMessage=completed+' '+(completed===1?'upgrade':'upgrades')+' claimed.';
          paint(updated&&updated.milestones?updated:null,justClaimed);
        }).catch(function(err){
          if(destroyed)return;
          var completed=bulkDone;
          bulkBusy=false;busyId='';liveMessage='';
          claimError=(completed?'Claim all stopped after '+completed+' '+(completed===1?'upgrade':'upgrades')+'. ':'')+
            progressionErrorLine(err,'That upgrade could not be claimed. Nothing was spent.');
          paint(null,justClaimed);
        });
        return;
      }
      var questAccept=e.target.closest('[data-card-ultimate-accept]');
      if(questAccept&&!questAccept.disabled&&!busyId&&!bulkBusy){
        e.preventDefault();e.stopPropagation();
        if(!shopAccountIsCurrent(inspectorScope)){claimError='Account changed. Open this card again.';paint();return;}
        var questData=window.HOHContractData;
        if(!questData||!questData.acceptCardUltimateQuest){claimError='Quest is unavailable. Reload and try again.';paint();return;}
        var questKind=questAccept.getAttribute('data-card-ultimate-accept')==='class_move'?'class_move':'ultimate', questId=questKind==='class_move'?'class_move_20':'ultimate_60', goal=questKind==='class_move'?10:((questData.cardUltimateQuestFor&&questData.cardUltimateQuestFor(cardKey,'ultimate').target_round)||20);
        busyId=questId;claimError='';paint(lastState);
        Promise.resolve(questData.acceptCardUltimateQuest(cardKey,questKind)).then(function(updated){
          if(destroyed)return;busyId='';liveMessage='Quest saved. Beat Round '+goal+' solo. Your previous best counts.';paint(updated,questId);
        }).catch(function(err){if(destroyed)return;busyId='';claimError=progressionErrorLine(err,'Quest could not be saved. Try again.','quest');paint(null,questId);});
        return;
      }
      var questPlay=e.target.closest('[data-card-ultimate-play]');
      if(questPlay&&!questPlay.disabled&&!busyId&&!bulkBusy){
        e.preventDefault();e.stopPropagation();
        if(!shopAccountIsCurrent(inspectorScope)){claimError='Account changed. Open this card again.';paint();return;}
        if(typeof window.hohEnterBattlegrounds!=='function'){claimError='Battle setup is still loading. Try again.';paint();return;}
        var questCard=overlay.querySelector('[data-card-name]');
        try{localStorage.setItem('hoh-party-launch',JSON.stringify({keys:[cardKey],name:questCard&&questCard.getAttribute('data-card-name')||'Your hero',ultimateQuest:cardKey,questKind:questPlay.getAttribute('data-card-ultimate-play')==='class_move'?'class_move':'ultimate',account:inspectorScope.account,createdAt:Date.now()}));}
        catch(_){claimError='This device could not save the solo setup. Allow storage and try again.';paint();return;}
        try{if(overlay.__ffshopClose)overlay.__ffshopClose();}catch(_){}
        window.hohEnterBattlegrounds();return;
      }
      var claim=e.target.closest('[data-card-progression-claim]');
      if(!claim||claim.disabled||busyId)return;
      e.preventDefault();e.stopPropagation();
      if(!shopAccountIsCurrent(inspectorScope)){
        try{if(overlay.__ffshopClose)overlay.__ffshopClose();}catch(_){}
        toast('The signed-in account changed. Open the card again for this account.');
        return;
      }
      var D=window.HOHContractData, id=claim.getAttribute('data-card-progression-claim');
      if(!D||typeof D.claimCardProgression!=='function'){claimError='Upgrades are still loading. Try again.';paint(lastState);return;}
      busyId=id;claimError='';liveMessage='Claiming upgrade.';paint(lastState);
      Promise.resolve(D.claimCardProgression(cardKey,id)).then(function(updated){
        if(destroyed)return;
        busyId='';justClaimed=id;liveMessage=id==='ultimate_60'?'Ultimate unlocked. Your hero now uses all three class moves.':'Upgrade unlocked.';guideVisible=false;markCardProgressionGuideSeen(inspectorScope);
        paint(updated&&updated.milestones?updated:null,id);
      }).catch(function(err){
        if(destroyed)return;
        busyId='';liveMessage='';claimError=progressionErrorLine(err,'That upgrade could not be claimed. Nothing was spent.');
        paint(lastState,id);
      });
    }
    function onChanged(e){
      var d=e&&e.detail||{}, changed=String(d.cardKey||d.card_key||'');
      if(changed&&changed!==String(cardKey))return;
      if(d.to!=null)levelHint=Math.max(parseInt(levelHint,10)||1,parseInt(d.to,10)||1);
      if(!busyId&&!bulkBusy)liveMessage='';
      claimError='';lastState=null;paint();
    }
    function onViewport(){paint(lastState);}
    host.addEventListener('click',onClick);
    document.addEventListener('hoh:card-level-changed',onChanged);
    document.addEventListener('hoh:card-progression-changed',onChanged);
    try{if(shortMq&&shortMq.addEventListener)shortMq.addEventListener('change',onViewport);else if(shortMq&&shortMq.addListener)shortMq.addListener(onViewport);}catch(_){}
    overlay.__ffProgressionCleanup=function(){
      if(destroyed)return;destroyed=true;
      try{host.removeEventListener('click',onClick);}catch(_){}
      try{document.removeEventListener('hoh:card-level-changed',onChanged);document.removeEventListener('hoh:card-progression-changed',onChanged);}catch(_){}
      try{if(shortMq&&shortMq.removeEventListener)shortMq.removeEventListener('change',onViewport);else if(shortMq&&shortMq.removeListener)shortMq.removeListener(onViewport);}catch(_){}
    };
    paint();
  }

  /* R558b/c: short viewports cannot hold level controls and two ability rolls
     at honest 44px sizes on one continuous face. Split that ONE back into two
     small pages whenever height is <=740px; collection actions now remain in
     the unscaled rail beneath the card rather than becoming a third page.
     The wrappers remain display:contents on taller phones and desktop. The
     external View abilities control remains the only front/back control; this
     pager only changes what is visible ON the ability face. */
  function setupShortLandscapeAbilityPages(overlay, sec){
    try{
      var shell = overlay && overlay.querySelector ? overlay.querySelector('.ffshop-ability-shell') : null;
      var gear = sec && sec.querySelector ? sec.querySelector('.ffshop-battle-gear') : null;
      if(!shell || !sec || !gear || shell.__ffLandscapePages) return;

      var levelPage = document.createElement('div');
      levelPage.className = 'ffshop-ability-page ffshop-ability-page-level';
      levelPage.setAttribute('data-ability-panel','level');
      var abilityPage = document.createElement('div');
      abilityPage.className = 'ffshop-ability-page ffshop-ability-page-abilities';
      abilityPage.setAttribute('data-ability-panel','abilities');
      var compactIdentity = document.createElement('div');
      compactIdentity.className = 'ffshop-battle-power ffshop-ability-identity';
      var mount = shell.querySelector('.ffshop-ability-mount');
      shell.insertBefore(compactIdentity,mount || null);

      /* Preserve the live nodes rather than cloning them. Their XP/ticket
         listeners and the Contract gear mount keep the exact same references.
         A live identity mirror outside the pages keeps every short-screen view
         reading Name → Hero tier before its current section. */
      Array.prototype.slice.call(sec.children).forEach(function(child){
        if(child === gear) abilityPage.appendChild(child);
        else levelPage.appendChild(child);
      });
      sec.appendChild(levelPage);
      sec.appendChild(abilityPage);

      var pages = [
        { key:'level', label:'Level', shortLabel:'Level', icon:'<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v18M7 8l5-5 5 5"/></svg>' },
        { key:'abilities', label:'Abilities', shortLabel:'Skills', icon:'<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 2.1 5.3L20 10l-4.5 3.6.2 5.9L12 16.8l-3.7 2.7.2-5.9L4 10l5.9-1.7z"/></svg>' }
      ];
      var pager = document.createElement('div');
      pager.className = 'ffshop-landscape-pager';
      pager.setAttribute('role','group');
      pager.setAttribute('aria-label','Ability card sections');
      pager.style.setProperty('--ff-land-page-count', String(pages.length));
      pager.innerHTML = pages.map(function(page){
        return '<button type="button" data-ability-page="'+page.key+'" aria-label="'+page.label+'" aria-pressed="'+(page.key==='abilities'?'true':'false')+'">'+
          page.icon+'<span aria-hidden="true">'+page.shortLabel+'</span></button>';
      }).join('');
      shell.appendChild(pager);
      shell.classList.add('ffshop-ability-paged');
      shell.setAttribute('data-ff-landscape-page','abilities');
      shell.__ffLandscapePages = true;

      function showPage(key, focus){
        if(!pages.some(function(page){ return page.key === key; })) key = 'abilities';
        shell.setAttribute('data-ff-landscape-page',key);
        pager.querySelectorAll('[data-ability-page]').forEach(function(btn){
          var on = btn.getAttribute('data-ability-page') === key;
          btn.setAttribute('aria-pressed',on?'true':'false');
          if(on && focus){ try{ btn.focus({preventScroll:true}); }catch(_){ try{ btn.focus(); }catch(__){} } }
        });
      }
      pager.addEventListener('click',function(e){
        var btn=e.target.closest('[data-ability-page]');
        if(!btn)return;
        e.preventDefault();e.stopPropagation();showPage(btn.getAttribute('data-ability-page'),false);
      });
      pager.addEventListener('keydown',function(e){
        if(['ArrowLeft','ArrowRight','Home','End'].indexOf(e.key)===-1)return;
        var buttons=Array.prototype.slice.call(pager.querySelectorAll('[data-ability-page]'));
        var at=buttons.indexOf(document.activeElement); if(at<0)return;
        e.preventDefault();
        if(e.key==='Home')at=0;
        else if(e.key==='End')at=buttons.length-1;
        else at=(at+(e.key==='ArrowRight'?1:-1)+buttons.length)%buttons.length;
        showPage(buttons[at].getAttribute('data-ability-page'),true);
      });
    }catch(_){ }
  }

  /* Battlegrounds section on the trading card: battle gear (actives/passives)
     and the card's LEVEL. Two level sources:
       - You OWN this FF portrait (it's your submitted character): the card
         INHERITS your profile hero level. Feeding EXP here levels the profile
         hero (hohGrantCharacterXp), so it's one shared level, growable from
         Your Hall or from here.
       - You pulled it as a collectible: it levels FROM SCRATCH per account
         (card_xp / giveCardXp), starting at level 1.
     Either way, level band -> engine die. Rarity (the gem) stays hall hearts.
     Renders only when the event is live (section flag or god mode), FF only. */
  function _battleExtras(overlay, heroId, snap, own, inspectorScope, closeInspector){
    var inner = overlay && overlay.querySelector ? (overlay.querySelector('.ffshop-ability-mount') || overlay.querySelector('.ffshop-insp-inner')) : null;
    function unavailable(msg, retryable){
      if(!inner) return;
      inner.innerHTML = '<div class="ffshop-ability-empty" role="'+(retryable?'alert':'status')+'" aria-live="polite" aria-atomic="true">'+
        '<b>Abilities unavailable</b><span>'+esc(msg || 'Close this card and try again.')+'</span>'+
        (retryable?'<button type="button" data-ability-retry>Retry abilities</button>':'')+'</div>';
      /* A failure before card identity is known must not leave the dedicated
         Upgrades face spinning forever. Once progression mounted successfully,
         its own state remains independent of an ability-panel failure. */
      if(!overlay.__ffProgressionCleanup)cardProgressionUnavailable(overlay,'Upgrades unavailable',msg||'Close this card and try again.');
      var retry=inner.querySelector('[data-ability-retry]');
      if(retry)retry.addEventListener('click',function(){
        retry.disabled=true;
        inner.innerHTML='<div class="ffshop-ability-loading" role="status" aria-live="polite"><span aria-hidden="true"></span>Loading abilities</div>';
        _battleExtras(overlay,heroId,snap,own,inspectorScope,closeInspector);
      });
    }
    function clearLoading(){
      if(!inner) return;
      var loading = inner.querySelector('.ffshop-ability-loading');
      if(loading){ try{ loading.remove(); }catch(_){ } }
    }
    try{
      var live = !!window.HOH_CONTRACT_ENABLED || (typeof window.hohIsGodMode==='function' && window.hohIsGodMode());
      /* R380 AUDIT FIX: leveling and tickets are PROFILE features and must
         not vanish when the Battlegrounds flag is off (the old grid that
         carried them is retired). Only the gear rolls stay behind the flag. */
      if(!inner) return;
      if(!window.HOHContractUI || !window.HOHContract){ unavailable('The ability panel is still loading.',true); return; }
      if(!live && !own){ unavailable('Abilities unlock when the Battlegrounds are available.'); return; }
      var hero = heroById(heroId) || {};
      /* fallback identity: the Battlegrounds' own collection cache (loaded via
         get_shop_state) knows the card even when the hall pool lookup misses. */
      var alt = null;
      try{
        var oc = (window.HOHContractData && window.HOHContractData.ownedCards && window.HOHContractData.ownedCards()) || [];
        for(var oi=0; oi<oc.length; oi++){
          if(String(oc[oi].id || oc[oi].character_id) === String(heroId)){ alt = oc[oi]; break; }
        }
      }catch(_){}
      /* R380 (2026-08-06, Jan): own non FF cards get the FULL battle panel.
         They roll, they level, they take tickets, they save their stats. */
      var _isFF = !!(hero.is_ff_portrait || (snap && snap.ff) || (alt && alt.is_ff_portrait));
      var cardKey = (_isFF ? 'ff:' : 'own:') + heroId;
      var ownNonFF = !_isFF && !!own;
      /* R422 (Jan): the panel's gate is the COLLECTION, not authorship — a
         stranger's non-FF card you pulled fights like anything else, so it
         rolls and levels like anything else. Only a non-FF card you neither
         authored nor collected stays a picture. */
      var _collRow2 = null;
      try {
        var _lc2 = (shopState && shopState.collection) || [];
        for(var _ci2 = 0; _ci2 < _lc2.length; _ci2++){
          if(String(_lc2[_ci2].character_id) === String(heroId)){ _collRow2 = _lc2[_ci2]; break; }
        }
      } catch(_){}
      var _collected2 = !!(_collRow2 && (((_collRow2.copies | 0) - ((_collRow2.cashed_copies != null ? _collRow2.cashed_copies : _collRow2.cashed) | 0)) > 0)) ||
                        !!(snap && (((snap.copies | 0) - ((snap.cashed_copies != null ? snap.cashed_copies : snap.cashed) | 0)) > 0));
      if(!_isFF && !ownNonFF && !_collected2){
        unavailable('This card is not in your collection.');
        cardProgressionUnavailable(overlay,'Upgrades locked','Collect this card before claiming its progression rewards.');
        return;
      }
      /* R380: an OWN hero whose card is not in the collection shows the panel
         locked. The left side is the portrait as always; this right side says
         what is missing and how to get it, and offers nothing else. */
      if(own && shopState){
        var _lockRow = null;
        try {
          var _lc = (shopState && shopState.collection) || [];
          for(var _li = 0; _li < _lc.length; _li++){
            if(String(_lc[_li].character_id) === String(heroId)){ _lockRow = _lc[_li]; break; }
          }
        } catch(_){}
        if(!_lockRow || (((_lockRow.copies | 0) - ((_lockRow.cashed_copies != null ? _lockRow.cashed_copies : _lockRow.cashed) | 0)) <= 0)){
          var lockSec = document.createElement('div');
          lockSec.className = 'ffshop-insp-battle ffshop-battle-locked';
          /* R391 (Jan): the locked panel names BOTH roads back for an own non
             FF hero — pull from a pack, or repair right here for hearts. */
          var _lri = null;
          try { _lri = (ownNonFF && typeof window.hohRepairInfo === 'function') ? window.hohRepairInfo(heroId) : null; } catch(_){}
          lockSec.innerHTML =
            '<div class="ffshop-battle-head"><span>&#9876; Battlegrounds</span></div>' +
            '<div class="ffshop-battle-lockmsg"><b>Not collected</b>' +
            ((_lri && _lri.need)
              ? '<span>Pull this card from a pack, or repair it for hearts.</span></div>' +
                '<button type="button" class="ffshop-repair-btn" data-lock-repair="1" style="margin-top:10px">Repair &middot; ' + _lri.cost + ' &#9829;&#xFE0E;</button>'
              : '<span>Pull this card from a pack to play it in the Battlegrounds.</span></div>');
          clearLoading();
          inner.appendChild(lockSec);
          cardProgressionUnavailable(overlay,'Upgrades locked','Collect this card before claiming its progression rewards.');
          var _lrb = lockSec.querySelector('[data-lock-repair]');
          if(_lrb) _lrb.addEventListener('click', function(){
            if(_lrb.disabled || typeof window.hohRepairCard !== 'function') return;
            _lrb.disabled = true;
            _lrb.textContent = 'Repairing…';
            window.hohRepairCard(heroId).then(function(r){
              if(r && r.ok){
                _lrb.textContent = 'Repaired';
                _lrb.classList.add('is-done');
                /* reopen so the battle side unlocks in front of them */
                setTimeout(function(){
                  closeShopInspectors();
                  try{ window.hohShopInspect(heroId); }catch(_){}
                }, 650);
              } else {
                _lrb.innerHTML = 'Repair &middot; ' + _lri.cost + ' &#9829;&#xFE0E;';
                _lrb.disabled = false;
              }
            });
          });
          return;
        }
      }
      var E = window.HOHContract;
      var sec = document.createElement('div');
      sec.className = 'ffshop-insp-battle';
      /* CARD LEVEL system — the profile hero level mechanic, per account per
         card (card_xp). Hearts become EXP (1 ♥ = 10 XP) through
         HOHContractData.giveCardXp; a fresh card starts at level 1 and levels
         from scratch. Rarity (the gem on the card above) stays the hero's
         HALL hearts and is untouched here. Power = level band = engine die. */
      var D2 = window.HOHContractData;
      /* R380: a non FF card lives under its own: key in the contract data
         layer (R371/R372), so rolls and gear resolve to the right row.
         R422: EVERY non-FF card keys own:, the author does not matter — the
         server names cards by the live is_ff_portrait flag alone, and the
         old ownNonFF test would have filed a stranger's card under ff:. */
      var chr = heroById(heroId) || alt || {};
      /* owner of an FF portrait → the card inherits the PROFILE hero level and
         feeding EXP here levels that profile hero. Needs the profile level API. */
      var XPH = (D2 && D2.XP_PER_HEART) || 10;
      /* R380: any OWN hero levels the profile character, FF portrait or not.
         The old is_ff_portrait requirement predates own cards being playable. */
      var isOwner = !!own && typeof window.hohGrantCharacterXp === 'function' && typeof window.hohCharLevel === 'function';
      var canXp = isOwner || !!(D2 && D2.giveCardXp);
      /* current {level, xp, cost} from whichever source owns this card */
      function readState(){
        if(isOwner){
          var lv = (window.hohCharLevel(chr) | 0) || 1;
          var xp = (typeof window.hohCharXp === 'function' ? (window.hohCharXp(chr) | 0) : 0) || 0;
          var cost = (typeof window.hohXpForLevel === 'function') ? window.hohXpForLevel(lv + 1) : (D2 ? D2.xpCost(lv) : 200);
          return { level: lv, xp: xp, cost: cost };
        }
        var st = (D2 && D2.cardXpFor) ? D2.cardXpFor(cardKey) : { level: 1, xp: 0 };
        return { level: st.level, xp: st.xp, cost: D2 ? D2.xpCost(st.level) : 200 };
      }
      var cur = readState();
      mountCardProgression(overlay,cardKey,cur.level,inspectorScope,isOwner,chr);
      var HEART = '♥';
      function walletNow(){ return (typeof window.heartsRemainingToday === 'function') ? (window.heartsRemainingToday() | 0) : 0; }
      /* R311: the duplicate wallet chip is gone — the Hall's own corner counter
         is lifted above this card instead (see liftWallet below), so there is
         one heart total on screen, in the place players already look. */
      sec.innerHTML =
        '<div class="ffshop-battle-head"><span>' + (live ? '⚔ Battlegrounds' : 'Level up') + '</span></div>' +
        '<div class="ffshop-battle-power"></div>' +
        '<div class="ffshop-battle-xprow">' +
          '<div class="ffshop-battle-xpbar" role="progressbar" aria-label="Level progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" aria-valuetext="Loading level progress"><span class="ffshop-battle-xpfill"></span></div>' +
          (canXp ? '<button type="button" class="ffshop-battle-plus ffshop-battle-plus--min" aria-label="Choose Hearts to add" title="Choose Hearts to add">+</button>' : '') +
        '</div>' +
        '<div class="ffshop-battle-next" role="status" aria-live="polite" aria-atomic="true"></div>' +
        '<div class="ffshop-battle-feedback" role="status" aria-live="polite" aria-atomic="true" hidden></div>' +
        /* R378 (2026-08-06): POTENTIAL. R381: one quiet small caps line in the
           same voice as the Next level line, and the give pill exists ONLY
           while unspent tickets are held. Zero tickets renders no control. */
        (_collected2 ?
          '<div class="ffshop-battle-potrow" id="ffshopPotRow">' +
            '<span class="ffshop-battle-potline" id="ffshopPotLine"></span>' +
          '</div>' +
          '<div class="ffshop-battle-ticketrow" id="ffshopTicketRow" hidden>' +
            '<button type="button" class="ffshop-battle-giveticket" id="ffshopGiveTicket" data-tipcolor="#c98cff" data-tip="Improves this card&#39;s Crystal tier-up odds. Only a hero you submitted also gains a raffle entry and can gain a level." aria-label="Give a ticket. Improves this card&#39;s Crystal tier-up odds. Only a hero you submitted can gain a level.">Give a ticket</button>' +
          '</div>' : '') +
        '<div class="ffshop-battle-gear"></div>' +
        /* R381 (Jan): regenerate hides even in god mode until the debug menu
           switch (hoh-god-regen) turns it on. */
        ((typeof window.hohIsGodMode==='function' && window.hohIsGodMode() &&
          (function(){ try { return localStorage.getItem('hoh-god-regen') === '1'; } catch(_){ return false; } })())
          ? '<button type="button" class="ffshop-battle-regen">↻ Regenerate card text</button>' : '');
      clearLoading();
      inner.appendChild(sec);
      setupShortLandscapeAbilityPages(overlay, sec);
      /* R378: paint + wire the potential row. Live counts, no page reload. */
      if(_collected2){
        (function(){
          function tkApplied(){
            /* R735: tier-up odds count every ticket on the card, the
               submitter's raffle tickets plus guest tickets from other
               players. The raffle count alone stays in row.tickets. */
            var row = heroById(heroId) || {};
            var n = row.tickets; if(n == null) n = row.new_tickets;
            return Math.max(0, (n | 0) + (row.guest_tickets | 0));
          }
          function tkHeld(){ return (typeof window.hohGetUnspentTickets === 'function') ? (window.hohGetUnspentTickets() | 0) : 0; }
          function paintPot(){
            /* self-cleaning: once the inspector closes, drop the listeners */
            if(!document.body.contains(sec)){
              document.removeEventListener('hoh:ticket-applied', paintPot);
              document.removeEventListener('hoh:unspent-tickets-changed', paintPot);
              document.removeEventListener('hoh:gear-changed', paintPot);
              return;
            }
            var lineEl = sec.querySelector('#ffshopPotLine');
            var btn = sec.querySelector('#ffshopGiveTicket');
            var btnRow = sec.querySelector('#ffshopTicketRow');
            if(!lineEl || !btn || !btnRow) return;
            var t = tkApplied();
            var gear={}; try{gear=(D2&&D2.gearFor)?(D2.gearFor(cardKey)||{}):{};}catch(_){gear={};}
            function pctFor(slot){
              var held=gear[slot]; if(!held)return null;
              if(D2&&typeof D2.abilityTierChancePct==='function')return D2.abilityTierChancePct(held.r|0,t,_isFF);
              if(typeof window.hohAbilityTierPct==='function')return window.hohAbilityTierPct(held.r|0,t,_isFF);
              return null;
            }
            var a=pctFor('active'),p=pctFor('passive');
            /* R793 (Jan, 2026-09-22): raffle tickets are "not part of
               Battlegrounds". In the live Hall this card never mentions them
               and never offers to give one (that happens only on the hero's
               own page in the main Hall). The odds line keeps its numbers. */
            var r793Tk=(typeof window.hohR793Live==='function')&&window.hohR793Live();
            var tkNote=function(word){ return (t>0&&!r793Tk)?(' &middot; '+t+' ticket'+(t===1?'':'s')+' '+word):''; };
            if(a==null&&p==null){
              lineEl.innerHTML='First ability rolls are free'+tkNote('ready for future tier-ups');
            }else if(a!=null&&p!=null&&a===p){
              lineEl.innerHTML='Next ability tier <b>'+a+'%</b>'+tkNote('applied');
            }else{
              var odds=[]; if(a!=null)odds.push('Active <b>'+a+'%</b>'); if(p!=null)odds.push('Passive <b>'+p+'%</b>');
              lineEl.innerHTML='Tier-up odds &middot; '+odds.join(' &middot; ')+tkNote('applied');
            }
            var held = r793Tk ? 0 : tkHeld();
            if(held > 0){
              btnRow.hidden = false;
              btn.disabled = false;
              /* v39 (Jan): the count is gone. The control only renders when
                 held > 0 (see the branch this sits in), so "3 held" was
                 telling the player something the control's own presence
                 already said, in a longer string that pushed the line wide. */
              btn.textContent = 'Give a ticket';
            } else {
              btnRow.hidden = true;
            }
          }
          var gtBtn = sec.querySelector('#ffshopGiveTicket');
          if(gtBtn && !gtBtn.__wired){
            gtBtn.__wired = 1;
            gtBtn.addEventListener('click', function(){
              if(typeof window.hohApplyTicket !== 'function') return;
              if(typeof window.hohR793Live==='function' && window.hohR793Live()) return;
              gtBtn.disabled = true;
              var kicked=false;
              try { kicked=window.hohApplyTicket(String(heroId), { targetEl: gtBtn }); } catch(_){ kicked=false; }
              if(!kicked){ gtBtn.disabled=false; paintPot(); return; }
              setTimeout(paintPot, 900);
            });
          }
          if(!sec.__potListeners){
            sec.__potListeners = 1;
            document.addEventListener('hoh:ticket-applied', paintPot);
            document.addEventListener('hoh:unspent-tickets-changed', paintPot);
            document.addEventListener('hoh:gear-changed', paintPot);
          }
          paintPot();
        })();
      }
      /* R311: bring the REAL wallet forward while the card is open, then put it
         back exactly as it was. z 100085 clears the inspector (100084) but sits
         under any dialog (100086+), so a confirm still covers it. */
      function cornerPill(){
        return document.getElementById('hoh-tk-header') || document.getElementById('hoh-heart-remaining');
      }
      var _pill = cornerPill(), _pillZ = null, _pillPos = null;
      function liftWallet(){
        if(!_pill || _pill.__hohLifted) return;   /* never bank the lifted value */
        _pill.__hohLifted = 1;
        _pillZ = _pill.style.zIndex; _pillPos = _pill.style.position;
        try{
          if(getComputedStyle(_pill).position === 'static') _pill.style.position = 'relative';
          _pill.style.setProperty('z-index', '100085', 'important');
        }catch(_){}
      }
      function dropWallet(){
        if(!_pill || !_pill.__hohLifted) return;
        try{
          if(_pillZ) _pill.style.zIndex = _pillZ; else _pill.style.removeProperty('z-index');
          if(_pillPos) _pill.style.position = _pillPos; else _pill.style.removeProperty('position');
        }catch(_){}
        try{ delete _pill.__hohLifted; }catch(_){ _pill.__hohLifted = 0; }
      }
      /* R558: Profile's compact card view is deliberately just the physical
         card and its one external action row. Keep the Hall wallet behind that modal
         instead of floating it over the card art. Exact roll and dust prices
         remain printed beside the actions on the ability side. */
      if(!overlay.classList.contains('ffshop-insp-compact')) liftWallet();
      /* restore the moment this card leaves the page, however it was closed */
      try{
        /* childList on BODY ONLY — the overlay is a direct child, so a subtree
           observer would fire on every DOM change during play for no reason. */
        var _obs = new MutationObserver(function(){
          if(!document.body.contains(overlay)){
            dropWallet(); _obs.disconnect();
          }
        });
        _obs.observe(document.body, { childList:true });
      }catch(_){ }
      function paintWallet(){ try{ if(typeof window.hohRenderHeaderCounter === 'function') window.hohRenderHeaderCounter(); }catch(_){} }
      var feedback=sec.querySelector('.ffshop-battle-feedback');
      function paintFeedback(msg,isError,retryFn){
        if(!feedback)return;
        feedback.hidden=!msg;
        feedback.classList.toggle('is-error',!!isError);
        feedback.innerHTML=msg?'<span>'+esc(msg)+'</span>'+(retryFn?'<button type="button">Retry</button>':''):'';
        var retry=feedback.querySelector('button');
        if(retry)retry.addEventListener('click',retryFn);
      }
      function paintLevel(){
        /* v2 (Jan's dice law): the DIE is the card's RARITY — the gem, the
           hero's real hall hearts. Levels only feed the team's power. */
        var rHearts = chr.hearts || 0;
        var rTier = E.tierOf(rHearts);
        /* ⛔ R661 (Jan): "make sure you also fix the copy across everything
           like the character cards and just take off like rolls d20 with
           advantage stuff like that, everyone all rolls a d20 regardless of
           rarity." This card front was still promising the OLD dice on LIVE,
           because R654 made the version an explicit argument and this call
           forgot it, so dieOf fell back to the rarity ladder and a common
           read "Rolls a d8". The card is not inside a run - it describes what
           happens if you take this hero into a fight NOW - so the live asset's
           own version is the honest reader, and the legacy branch is kept so
           an older engine asset still reads correctly rather than silently. */
        var ev = E.ENGINE_VERSION | 0;
        var oneDie = (typeof E.oneDieAt === 'function') && E.oneDieAt(ev);
        var die = E.dieOf({ hearts: rHearts }, ev);
        var adv = !oneDie && (typeof E.hasAdv === 'function') && E.hasAdv({ hearts: rHearts }, ev);
        var rbon = (oneDie && typeof E.rarBonusOf === 'function')
          ? (E.rarBonusOf({ hearts: rHearts }, ev) | 0) : 0;
        var CC = window.HOHContractContent || {};
        var RN = CC.RARITY_NAMES || [], RC = CC.RARITY_COLORS || [];
        var atCap = cur.level >= 100 || !isFinite(cur.cost);
        /* R311: the rarity leads as its own coloured label, and the die and
           level read underneath it as one quiet line. */
        var rName = (RN[rTier] || 'COMMON');
        var rCol = RC[rTier] || '#e8c674';
        var rarHtml =
          '<span class="ffshop-battle-rar' + (rTier >= 5 ? ' is-mythic' : '') + '" style="--rc:' + rCol + '">' +
            rName.charAt(0) + rName.slice(1).toLowerCase() + ' Hero</span>';
        var dieHtml =
          '<span class="ffshop-battle-die">Rolls a <b>d' + die + '</b>' +
            (adv ? ' <i>with advantage</i>' : '') +
            (rbon > 0 ? ' <b>+' + rbon + '</b> damage' : '') +
            ' &middot; <b>Level ' + cur.level + '</b>' + (atCap ? ' · the summit' : '') + '</span>';
        var identityHtml = rarHtml + dieHtml;
        /* ⛔ ====== R566 · THE TIER LEADS, THE NAME CARRIES ======
           Jan: "switch the position of mythic hero with the name ... make it
           larger so the name is the label at the center."
           The swap is done HERE rather than by moving nodes around after the
           fact, because this function REPAINTS `.ffshop-battle-power` on every
           level change and every heart poured in. Anything injected into that
           element from outside would survive exactly until the next `+`. One
           source, so the swap cannot come undone mid-session.
           The decision is the SPAN'S EXISTENCE, never a hard-coded surface
           name: if the shell offered a tier slot, the tier goes up there and
           the panel opens on the die line; if it did not (an older cached
           face, or some future layout that mounts this panel on its own), the
           panel renders exactly what it always did. Fail-open in both
           directions — the tier can never be shown twice, and it can never be
           shown nowhere.
           The landscape mirror keeps the full pair on purpose: at
           `max-height:740px` the shell's own top is squeezed to 34px and its
           tier is hidden in CSS, so the mirror is the only place the tier can
           read on that view. */
        var topTier = null;
        try{ topTier = overlay.querySelector('.ffshop-ability-back .ffshop-ability-tier'); }catch(_){ topTier = null; }
        if(topTier) topTier.innerHTML = rarHtml;
        var el = sec.querySelector('.ffshop-battle-power');
        if(el) el.innerHTML = topTier ? dieHtml : identityHtml;
        var compactIdentity=overlay.querySelector('.ffshop-ability-identity');
        if(compactIdentity)compactIdentity.innerHTML=identityHtml;
        var plus=sec.querySelector('.ffshop-battle-plus');
        if(plus){plus.hidden=atCap;plus.disabled=atCap;}
        var pct=atCap?100:Math.max(0,Math.min(100,(cur.xp/cur.cost)*100));
        var bar=sec.querySelector('.ffshop-battle-xpbar');
        var fill = sec.querySelector('.ffshop-battle-xpfill');
        if(fill) fill.style.width=pct.toFixed(1)+'%';
        if(bar){
          bar.setAttribute('aria-valuenow',String(Math.round(pct)));
          bar.setAttribute('aria-valuetext',atCap
            ? 'Level 100, maximum level.'
            : 'Level '+cur.level+', '+Math.max(0,cur.xp)+' of '+Math.max(1,cur.cost)+' experience.');
        }
        var nx = sec.querySelector('.ffshop-battle-next');
        if(nx){
          if(atCap){ nx.innerHTML = '<b>Level 100 &middot; Max level</b> &middot; levels add team power'; }
          else {
            var need = Math.max(1, Math.ceil((cur.cost - cur.xp) / XPH));
            nx.innerHTML = 'Next level · <b>' + need + ' ♥</b> · levels add team power';
          }
        }
      }
      var gearHost = sec.querySelector('.ffshop-battle-gear');
      function mountGear(){
        if(!gearHost) return;
        if(!live){
          gearHost.style.display='flex';
          gearHost.innerHTML='<div class="ffshop-ability-empty ffshop-ability-disabled" role="status"><b>Ability rolls are offline</b><span>Your level progress is still available. Return when the Battlegrounds reopen to view or roll abilities.</span></div>';
          return;
        }   /* R380: rolls stay behind the flag */
        var gearCls = chr.character_class || 'Fighter';
        window.HOHContractUI.mountGearPanel(gearHost, cardKey, {
          character_class: gearCls, cls: gearCls.split(' ')[0], race:chr.race || '', hearts: chr.hearts || 0
        });
      }
      /* Paint cached progress immediately so the face never opens blank, then
         verify a collector card before its spend control becomes available. */
      paintLevel();
      mountGear();
      function loadCardLevel(force){
        var plus=sec.querySelector('.ffshop-battle-plus');
        if(plus)plus.disabled=true;
        paintFeedback('Checking saved level progress…',false);
        Promise.resolve().then(function(){return D2.loadCardXp(!!force);}).then(function(){
          if(D2.cardXpLoadFailed && D2.cardXpLoadFailed())throw new Error('level progress unavailable');
          cur=readState();paintLevel();paintFeedback('',false);
        },function(){
          if(plus)plus.disabled=true;
          paintFeedback('Level progress could not be verified. Reconnect and retry before adding Hearts.',true,function(){loadCardLevel(true);});
        });
      }
      if(!isOwner && D2 && D2.loadCardXp)loadCardLevel();
      /* one give path, two backends: owner → profile hero (grant_character_xp),
         collector → this card (give_card_xp). Both deduct + refund on their own
         side and return the new {level, xp, cost}. */
      function doGive(hearts){
        if(isOwner){
          return Promise.resolve(window.hohGrantCharacterXp(chr.id || heroId, hearts)).then(function(res){
            if(!res || res.ok !== true) throw new Error((res && res.reason) || 'xp failed');
            return { level: res.level, xp: res.xp, cost: (res.xp_to_next != null ? res.xp_to_next : cur.cost), leveled_up: !!res.leveled_up };
          });
        }
        return D2.giveCardXp(cardKey, hearts).then(function(res){
          return { level: res.level, xp: res.xp, cost: (res.xp_to_next != null ? res.xp_to_next : cur.cost), leveled_up: !!res.leveled_up };
        });
      }
      var hb = sec.querySelector('.ffshop-battle-plus');
      var busy = false;
      function heartsToFinish(){ return Math.max(1, Math.ceil((cur.cost - cur.xp) / XPH)); }
      /* R311: the level-up moment — the bar runs all the way to full, the panel
         flashes, gold shards burst off the button, then the bar resets to the
         new level's progress. Reduced-motion players just get the numbers. */
      function levelCeremony(done){
        var reduced = false;
        try{ reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); }catch(_){}
        var fill = sec.querySelector('.ffshop-battle-xpfill');
        if(reduced || !fill){ done(); return; }
        fill.style.width = '100%';
        fill.classList.add('is-full');
        sec.classList.add('ffshop-battle-levelup');
        try{
          var r = (hb || sec).getBoundingClientRect();
          for(var k = 0; k < 14; k++){
            var sp = document.createElement('span');
            sp.className = 'ffshop-burst';
            var ang = (k / 14) * Math.PI * 2, d = 34 + Math.random() * 46;
            sp.style.left = (r.left + r.width / 2) + 'px';
            sp.style.top = (r.top + r.height / 2) + 'px';
            sp.style.setProperty('--bx', (Math.cos(ang) * d).toFixed(0) + 'px');
            sp.style.setProperty('--by', (Math.sin(ang) * d).toFixed(0) + 'px');
            document.body.appendChild(sp);
            (function(el2){ setTimeout(function(){ try{ el2.remove(); }catch(_){} }, 950); })(sp);
          }
        }catch(_){}
        setTimeout(function(){
          try{ fill.classList.remove('is-full'); sec.classList.remove('ffshop-battle-levelup'); }catch(_){}
          done();
        }, 480);
      }
      function give(hearts, after){
        if(busy) return;
        /* R335: belt and braces for the same race — never spend into a card
           that has already left the page. */
        try{ if(!document.body.contains(sec)) return; }catch(_){}
        busy = true;
        paintFeedback('',false);
        /* R314: the spend has to be VISIBLE — hearts leave the corner wallet
           and stream into the XP bar, the same flight the rest of the Hall
           uses. It was never wired here, so levelling felt like nothing left
           your bank. Fired immediately (not after the round trip) so the
           motion tracks the tap. */
        try{
          var target = sec.querySelector('.ffshop-battle-xpbar') || hb || sec;
          var from = cornerPill();
          var n = Math.max(3, Math.min(12, hearts));
          /* only fly what the bank can actually pay, so a rejected spend never
             shows hearts leaving a wallet that keeps its balance. */
          if(from && target && !motionReduced() && walletNow() >= hearts && typeof flyHearts === 'function')
            flyHearts(from, target, n);
        }catch(_){}
        doGive(hearts).then(function(r){
          busy = false;
          paintWallet();                       /* the corner counter shows the spend */
          var apply = function(){
            var levelBefore=cur.level;
            cur = { level: r.level, xp: r.xp, cost: r.cost };
            paintLevel();
            if(isOwner&&cur.level!==levelBefore){
              try{document.dispatchEvent(new CustomEvent('hoh:card-level-changed',{detail:{cardKey:cardKey,from:levelBefore,to:cur.level}}));}catch(_){}
            }
            if(after) after();
          };
          if(r.leveled_up){
            if(hb&&!motionReduced()){ try{ hb.animate([{transform:'scale(1)'},{transform:'scale(1.25)'},{transform:'scale(1)'}],{duration:320}); }catch(_){} }
            levelCeremony(apply);
          } else apply();
        }).catch(function(e2){
          busy = false; if(after) after();
          /* R335: the double-tap path races the XP bar to 100% before the round
             trip so the spend is felt instantly, and its comment promised "a
             failure repaints the true value". It never did -- so a rejected
             level-up left the bar pinned full and the card read as complete
             while nothing had been spent. paintLevel rewrites the inline
             width, which is exactly what undoes it. */
          try{ paintLevel(); }catch(_){}
          paintFeedback('Hearts were not added. Check your connection and try again.',true);
          try{
            if(typeof window.hohShowHeartToast === 'function' && /not enough|insufficient|out of/i.test(String((e2 && e2.message) || '')))
              window.hohShowHeartToast("You're out of hearts.", 'Explore the Hall to find more.');
          }catch(_){}
        });
      }
      /* One visible action opens one explicit amount picker. The old delayed
         single/double-tap split disagreed with its label, was unreachable from
         a keyboard, and could spend a full level when the user expected one
         Heart. Nothing mutates until Give is confirmed in the picker. */
      if(hb){
        function openPourMenu(){
          if(busy || hb.disabled || cur.level >= 100 || !isFinite(cur.cost))return;
          var prev = document.querySelector('.ffshop-pour'); if(prev){ try{ prev.remove(); }catch(_){ } }
          var wallet = walletNow();
          if(wallet <= 0){
            try{ if(typeof window.hohShowHeartToast === 'function') window.hohShowHeartToast("You're out of hearts.", 'Explore the Hall to find more.'); }catch(_){}
            return;
          }
          var ov = document.createElement('div');
          ov.className = 'ffshop-pour';
          ov.innerHTML = '<div class="ffshop-pour-in" role="dialog" aria-modal="true" aria-label="Pour hearts">'+
            '<div class="ffshop-pour-t">POUR HEARTS INTO THIS HERO</div>'+
            '<div class="ffshop-pour-s">You hold <b>'+wallet+'</b> ♥ · <b>'+(isFinite(cur.cost)?heartsToFinish():'—')+'</b> to the next level</div>'+
            '<input type="number" class="ffshop-pour-n" min="1" max="'+wallet+'" value="'+Math.min(wallet, isFinite(cur.cost)?heartsToFinish():wallet)+'" aria-label="Hearts to give">'+
            '<div class="ffshop-pour-chips">'+
              '<button type="button" data-pn="100">+100</button>'+
              '<button type="button" data-pn="500">+500</button>'+
              '<button type="button" data-pn="all">All '+wallet+'</button>'+
            '</div>'+
            '<div class="ffshop-pour-acts">'+
              '<button type="button" class="ffshop-pour-go">Give ♥</button>'+
              '<button type="button" class="ffshop-pour-x">Cancel</button>'+
            '</div></div>';
          document.body.appendChild(ov);
          var nEl = ov.querySelector('.ffshop-pour-n');
          var outerClose=document.getElementById('ffshop-insp-close');
          try{ overlay.setAttribute('inert','');overlay.setAttribute('aria-hidden','true');if(outerClose)outerClose.disabled=true; }catch(_){}
          /* The picker lives on <body> so it can clear the transformed card,
             which also means the inspector's capture-phase account guard does
             not receive its events. Reassert that boundary here: a picker
             opened under Account A must never debit whichever account happens
             to be active when Give is pressed. */
          function guardPourAccount(e){
            if(!inspectorScope || shopAccountIsCurrent(inspectorScope))return false;
            if(e){e.preventDefault();e.stopPropagation();if(e.stopImmediatePropagation)e.stopImmediatePropagation();}
            try{ov.remove();}catch(_){}
            try{
              if(typeof closeInspector==='function')closeInspector();
              else closeShopInspectors();
            }catch(_){}
            toast('The signed-in account changed. Open the card again for this account.');
            return true;
          }
          ov.addEventListener('pointerdown',guardPourAccount,true);
          ov.addEventListener('click',guardPourAccount,true);
          ov.addEventListener('keydown',guardPourAccount,true);
          ov.addEventListener('input',guardPourAccount,true);
          ov.addEventListener('change',guardPourAccount,true);
          ov.querySelectorAll('[data-pn]').forEach(function(b){
            b.onclick = function(){
              if(guardPourAccount())return;
              var v = b.getAttribute('data-pn');
              nEl.value = (v === 'all') ? wallet : Math.min(wallet, (parseInt(nEl.value,10)||0) + parseInt(v,10));
            };
          });
          function focusables(){
            return Array.prototype.slice.call(ov.querySelectorAll('button:not([disabled]),input:not([disabled])'));
          }
          function close(){
            try{ ov.remove(); }catch(_){}
            try{ overlay.removeAttribute('inert');overlay.removeAttribute('aria-hidden');if(outerClose)outerClose.disabled=false; }catch(_){}
            try{ if(document.body.contains(hb))hb.focus({preventScroll:true}); }catch(_){}
          }
          ov.addEventListener('keydown',function(e){
            if(e.key==='Escape'){e.preventDefault();e.stopPropagation();close();return;}
            if(e.key!=='Tab')return;
            var f=focusables();if(!f.length){e.preventDefault();return;}
            var first=f[0],last=f[f.length-1];
            if(e.shiftKey&&document.activeElement===first){e.preventDefault();last.focus();}
            else if(!e.shiftKey&&document.activeElement===last){e.preventDefault();first.focus();}
            e.stopPropagation();
          });
          ov.querySelector('.ffshop-pour-x').onclick = close;
          ov.onclick = function(e){ if(e.target === ov) close(); };
          ov.querySelector('.ffshop-pour-go').onclick = function(){
            if(guardPourAccount())return;
            /* Wallet state can change in another tab while this confirmation
               is open. The live value, not the opening snapshot, is the final
               authority for both the zero-wallet state and the clamp. */
            var liveWallet=walletNow();
            if(liveWallet<=0){
              close();
              try{if(typeof window.hohShowHeartToast==='function')window.hohShowHeartToast("You're out of hearts.",'Explore the Hall to find more.');}catch(_){}
              return;
            }
            var n = Math.max(1, Math.min(liveWallet, parseInt(nEl.value, 10) || 0));
            close();
            try{
              var f0 = sec.querySelector('.ffshop-battle-xpfill');
              if(f0){ f0.style.transition=motionReduced()?'none':'width .45s cubic-bezier(.2,.8,.3,1)'; f0.style.width='100%'; }
            }catch(_){}
            give(n);
          };
          setTimeout(function(){try{nEl.focus({preventScroll:true});nEl.select();}catch(_){}},0);
        }
        hb.addEventListener('click',function(e){e.preventDefault();e.stopPropagation();openPourMenu();});
      }
      /* R296 god tool: rebuild this card's text from the hero's saved data. */
      var rg = sec.querySelector('.ffshop-battle-regen');
      if(rg) rg.addEventListener('click', function(e){
        e.stopPropagation();
        if(typeof window.hohShopRegenCardText === 'function') window.hohShopRegenCardText(heroId);
      });
    }catch(_){ unavailable('The ability panel could not load. Try again.',true); }
  }

  /* ==================== R558: SAFE BATCH DUSTING ====================
     The old toolbar action fanned out one cash_in_cards request per hero and
     animated the grid before it knew which calls committed. That cannot power
     rarity rules or selection safely: a half-failed fan-out leaves the review,
     collection and Heart payout disagreeing. The R558 RPC plans, locks and
     commits the whole batch once. This client supplies a calm preview, then
     treats the immutable request receipt as the only payout authority. */
  var DUST_PENDING_KEY = 'hoh-dust-batch-pending-v1';
  var DUST_CLAIMS_KEY = 'hoh-dust-batch-claims-v1';
  var DUST_HEART_BANK_MAX = 9999;
  var DUST_RARITY = ['Common','Uncommon','Rare','Epic','Legendary','Mythic'];
  var dustBusyOwner = null;
  function dustBusyTake(scope){
    var token={account:dustAccount(scope),generation:scope&&scope.generation,at:Date.now()};
    dustBusyOwner=token; busy=true; return token;
  }
  function dustBusyRelease(token){
    if(dustBusyOwner!==token) return false;
    dustBusyOwner=null; busy=false; return true;
  }

  function dustJsonRead(key){
    try{
      var raw = localStorage.getItem(key);
      if(raw == null || raw === '') return {};
      var value = JSON.parse(raw);
      return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
    }catch(_){ return null; }
  }
  function dustJsonWrite(key, value){
    try{
      if(!value || typeof value !== 'object') return false;
      var raw = JSON.stringify(value);
      localStorage.setItem(key, raw);
      return localStorage.getItem(key) === raw;
    }catch(_){ return false; }
  }
  function dustAccount(scope){ return String(scope && scope.account || shopAccount() || '').trim().toLowerCase(); }
  function dustScopedKey(base,scope){ return base+':'+encodeURIComponent(dustAccount(scope)); }
  function dustJsonRemove(key){
    try{ localStorage.removeItem(key); return localStorage.getItem(key)==null; }
    catch(_){ return false; }
  }
  function dustPendingRead(scope){
    var row=dustJsonRead(dustScopedKey(DUST_PENDING_KEY,scope));
    return row&&row.payload?row:null;
  }
  function dustRemaining(c){
    return Math.max(0, (c && c.copies | 0) - ((c && c.cashed_copies != null ? c.cashed_copies : c && c.cashed) | 0));
  }
  function dustFav(id){
    try{ return typeof window.hohHallIsSaved === 'function' && !!window.hohHallIsSaved(id); }
    catch(_){ return false; }
  }
  function dustPerCopy(c){
    return (TIER_VALUE[tierFor(liveHearts(c.character_id, c))] || 1) * (c.ff ? 10 : 1) * (c.own ? 2 : 1);
  }
  function dustSelection(){
    var account = shopAccount();
    var st = window.__ffDustSelection;
    if(!st || st.account !== account) st = window.__ffDustSelection = { account:account, active:false, ids:{} };
    if(!st.ids || typeof st.ids !== 'object') st.ids = {};
    return st;
  }
  function dustSelectionIds(){
    var st = dustSelection(), out = [];
    for(var id in st.ids) if(Object.prototype.hasOwnProperty.call(st.ids,id) && st.ids[id]) out.push(id);
    return out;
  }
  function dustClearSelection(){
    var st = dustSelection(); st.active = false; st.ids = {};
  }

  function closeDustAccountUi(nextAccount){
    try{
      /* Close the topmost child first so each nested dialog restores the
         inert/scroll state it captured from its parent, in reverse order. */
      Array.prototype.slice.call(document.querySelectorAll('.ffshop-dust-confirm')).reverse().forEach(function(m){
        if(typeof m.__dustClose === 'function') m.__dustClose();
        else try{ m.remove(); }catch(_){}
      });
    }catch(_){}
    window.__ffDustSelection = { account:String(nextAccount || shopAccount() || '').trim().toLowerCase(), active:false, ids:{} };
  }

  function dustDialog(label, html){
    var m = document.createElement('div');
    m.className = 'ffshop-dust-confirm ffshop-dust-manager';
    m.innerHTML = '<div class="ffshop-dust-box ffshop-dust-manager-box" role="dialog" aria-modal="true" tabindex="-1" aria-label="'+esc(label)+'">'+html+'</div>';
    var prior = document.activeElement;
    /* Nested flow: the rapid-roll queue can open the established dust review
       above itself. Only the topmost dialog may remain accessible. */
    var under = Array.prototype.slice.call(document.querySelectorAll('.ffshop-dust-confirm')).map(function(el){
      return {el:el,inert:el.hasAttribute('inert'),aria:el.getAttribute('aria-hidden')};
    });
    /* A review opened from another review must be visually above it as well
       as semantically modal. The rapid-roll queue owns 100087, so its Dust
       confirmation becomes 100088 rather than hiding behind an inert card. */
    var topZ=100085;
    under.forEach(function(s){
      try{topZ=Math.max(topZ,parseInt(getComputedStyle(s.el).zIndex,10)||0);}catch(_){}
    });
    if(under.length)m.style.zIndex=String(topZ+1);
    under.forEach(function(s){try{s.el.setAttribute('inert','');s.el.setAttribute('aria-hidden','true');}catch(_){}});
    var prevOverflow = document.body.style.overflow;
    var app = document.getElementById('hoh-app');
    var appHadInert = !!(app&&app.hasAttribute('inert'));
    var appHadAria = !!(app&&app.hasAttribute('aria-hidden'));
    var appAria = app ? app.getAttribute('aria-hidden') : null;
    document.body.appendChild(m); document.body.style.overflow = 'hidden';
    try{ if(app){ app.setAttribute('inert',''); app.setAttribute('aria-hidden','true'); } }catch(_){}
    requestAnimationFrame(function(){ m.classList.add('on'); });
    function close(force){
      if(!m.parentNode) return;
      if(!force&&m.classList.contains('is-busy')) return;
      try{ m.remove(); }catch(_){}
      document.body.style.overflow = prevOverflow || '';
      document.removeEventListener('keydown', keys, true);
      try{
        if(app){
          if(!appHadInert) app.removeAttribute('inert');
          if(appHadAria) app.setAttribute('aria-hidden',appAria);
          else app.removeAttribute('aria-hidden');
        }
      }catch(_){}
      under.forEach(function(s){
        try{
          if(!s.inert)s.el.removeAttribute('inert');
          if(s.aria==null)s.el.removeAttribute('aria-hidden');else s.el.setAttribute('aria-hidden',s.aria);
        }catch(_){}
      });
      try{ if(prior && document.body.contains(prior)) prior.focus(); }catch(_){}
    }
    function focusable(){ return Array.prototype.slice.call(m.querySelectorAll('button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])')); }
    function keys(e){
      /* Only the visually top dialog owns Escape/Tab. A nested dust review
         marks the rapid queue inert; letting the parent capture Escape first
         would close both layers and corrupt their restoration order. */
      if(m.hasAttribute('inert'))return;
      if(e.key === 'Escape'){ e.preventDefault(); close(); return; }
      if(e.key !== 'Tab') return;
      var list = focusable(); if(!list.length){ e.preventDefault(); return; }
      var first=list[0], last=list[list.length-1];
      if(e.shiftKey && document.activeElement===first){ e.preventDefault(); last.focus(); }
      else if(!e.shiftKey && document.activeElement===last){ e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', keys, true);
    m.addEventListener('click', function(e){ if(e.target===m && !m.classList.contains('is-busy')) close(); });
    m.__dustClose = function(){ close(true); };
    setTimeout(function(){
      var list=focusable(), target=list[0] || m.querySelector('.ffshop-dust-manager-box');
      if(target) try{ target.focus(); }catch(_){}
    }, 0);
    return m;
  }

  function dustWarm(){
    var D = window.HOHContractData;
    if(!D) return Promise.resolve({ gear:false, xp:false });
    var gear = D.loadGear
      ? Promise.resolve().then(function(){ return D.loadGear(true); }).then(function(){
          return !(D.gearLoadFailed && D.gearLoadFailed());
        },function(){ return false; })
      : Promise.resolve(false);
    var xp = D.loadCardXp
      ? Promise.resolve().then(function(){ return D.loadCardXp(); }).then(function(){
          return !(D.cardXpLoadFailed && D.cardXpLoadFailed());
        },function(){ return false; })
      : Promise.resolve(false);
    return Promise.all([gear,xp]).then(function(ok){ return { gear:ok[0], xp:ok[1] }; });
  }

  function dustPlan(mode, maxRarity, selectedIds){
    var cs = collectionSource();
    var built = collMetaFor(cs), meta = built.meta, byId = {}, selected = {};
    cs.allCards.forEach(function(c){ byId[String(c.character_id)] = c; });
    (selectedIds || []).forEach(function(id){ selected[String(id)] = 1; });
    var cards = [], skipped = { unrolled:0, pending:0, above:0, unavailable:0 };
    var sourceIds = (mode === 'manual' || mode === 'single') ? Object.keys(selected) : Object.keys(byId);
    sourceIds.forEach(function(id){
      var c = byId[id], m = meta[id] || {}, left = dustRemaining(c), copies = left;
      if(!c || left < 1){ skipped.unavailable++; return; }
      if(mode === 'extras'){
        copies = Math.max(0, left - 1);
        if(!copies) return;
        if(m.pending){ skipped.pending++; return; }
        if(m.malformed){ skipped.unavailable++; return; }
      } else if(m.pending){ skipped.pending++; return;
      } else if(m.malformed){ skipped.unavailable++; return;
      } else if(mode === 'single'){
        copies = 1;
      } else if(mode === 'threshold'){
        if(!m.rolled){ skipped.unrolled++; return; }
        if((m.ability | 0) > (maxRarity | 0)){ skipped.above++; return; }
      }
      var per=dustPerCopy(c),requestedCopies=copies;
      /* One server receipt must fit in the Hall's device wallet eventually.
         Very large duplicate stacks are split into repeatable safe batches. */
      copies=Math.min(copies,Math.max(1,Math.floor(DUST_HEART_BANK_MAX/Math.max(1,per))));
      var cardRarity=Math.max(0,Math.min(5,m.cardRarity|0));
      var highAbility=!!m.rolled && (m.ability | 0) >= 3;
      var highCard=cardRarity>=3;
      var high=highAbility||highCard;
      var fav = dustFav(id);
      cards.push({
        id:id, card:c, meta:m, name:c.name || (heroById(id)||{}).name || 'Hero',
        img:c.img || heroImg(id) || '', copies:copies, per:per,
        hearts:copies * per, deferredCopies:Math.max(0,requestedCopies-copies),
        high:high, highAbility:highAbility, highCard:highCard, favorite:fav,
        cardRarity:cardRarity,
        include:!(high || fav)
      });
    });
    return { mode:mode, max:maxRarity, cards:cards, skipped:skipped,
      selectedIds:Object.keys(selected), allCards:cs.allCards, scope:captureShopAccount() };
  }

  /* The mass-dust builder keeps card rarity and rolled-ability rarity as two
     visibly separate dimensions. It only chooses the exact IDs that advance
     to review; the RPC still receives manual mode and the review's full server-
     checked fingerprints, so a client-side filter can never authorize dust. */
  function dustFilterCandidates(filters){
    filters=filters||{};
    var cs=collectionSource(),built=collMetaFor(cs),meta=built.meta,ids=[];
    var skipped={pending:0,unrolled:0,ability:0,card:0};
    var maxAbility=Math.max(0,Math.min(5,parseInt(filters.maxAbility,10)||0));
    var maxCard=Math.max(0,Math.min(5,parseInt(filters.maxCard,10)||0));
    cs.allCards.forEach(function(c){
      var id=String(c.character_id),m=meta[id]||{};
      if(dustRemaining(c)<1)return;
      if(m.pending){skipped.pending++;return;}
      if(m.malformed){skipped.pending++;return;}
      /* R733 (Jan): "Keep unrolled cards" is a real choice now, on by default.
         While it is on, cards with no confirmed abilities never match. Off, an
         unrolled card is judged by its card rarity alone (it has no ability
         rarity to check) and reaches review labelled Unrolled, where the R558
         manual-mode RPC already accepts it with max_rarity null. */
      if(filters.keepUnrolled&&!m.rolled){skipped.unrolled++;return;}
      if((m.ability|0)>maxAbility){skipped.ability++;return;}
      if((m.cardRarity|0)>maxCard){skipped.card++;return;}
      if(filters.keepEpicAbility&&m.rolled&&(m.ability|0)>=3){skipped.ability++;return;}
      if(filters.keepEpicCard&&(m.cardRarity|0)>=3){skipped.card++;return;}
      ids.push(id);
    });
    return {ids:ids,skipped:skipped};
  }

  function dustPlanTotals(plan){
    var heroes=0,copies=0,hearts=0,protectedCount=0;
    plan.cards.forEach(function(c){
      if(c.include){ heroes++; copies += c.copies; hearts += c.hearts; }
      else protectedCount++;
    });
    return { heroes:heroes,copies:copies,hearts:hearts,protectedCount:protectedCount };
  }
  function dustCapacityMessage(totals){
    if(!totals||!totals.copies)return '';
    if(totals.hearts>DUST_HEART_BANK_MAX)return 'This review is worth '+totals.hearts+' Hearts, above the 9,999-Heart wallet limit. Exclude some cards and dust them in a smaller batch.';
    var room=Math.max(0,DUST_HEART_BANK_MAX-heartBank());
    if(totals.hearts>room)return 'Your wallet has room for '+room+' more '+(room===1?'Heart':'Hearts')+'. Spend Hearts or exclude some cards before dusting this batch.';
    return '';
  }

  function dustModeIcon(kind){
    if(kind === 'select') return '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="4" width="6" height="6" rx="1"/><rect x="14" y="4" width="6" height="6" rx="1"/><rect x="4" y="14" width="6" height="6" rx="1"/><path d="m14.5 17 2 2 4-5"/></svg>';
    if(kind === 'extras') return '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="5" width="11" height="14" rx="2"/><path d="M4 15V7a2 2 0 0 1 2-2M10 9h5M10 13h5"/></svg>';
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 9.8 8.8 4 11l5.8 2.2L12 19l2.2-5.8L20 11l-5.8-2.2z"/><path d="M19 4v4M17 6h4"/></svg>';
  }
  function dustRarityOptions(kind){
    var noun=kind==='ability'?'ability':'card';
    return DUST_RARITY.map(function(name,rank){
      var copy=rank===0?'Common '+noun+' only':(rank===5?'Any '+noun+' rarity':name+' '+noun+' or lower');
      return '<option value="'+rank+'"'+(rank===2?' selected':'')+'>'+copy+'</option>';
    }).join('');
  }

  function enterDustSelection(host, modal){
    if(modal && modal.__dustClose) modal.__dustClose();
    var st = dustSelection(); st.active = true; st.ids = {};
    syncDustSelectionUI(host);
    var grid = host && host.querySelector('#ffshop-grid-all');
    if(grid) try{ grid.scrollIntoView({behavior:reduced?'auto':'smooth',block:'start'}); }catch(_){ }
    packSay('Select cards','Tap any cards you want to review. Nothing is dusted until the final confirmation.');
    setTimeout(function(){
      var first=grid&&grid.querySelector('.ffshop-tile:not([style*="display: none"])');
      if(first) try{ first.focus({preventScroll:true}); }catch(_){ try{ first.focus(); }catch(__){} }
    },reduced?0:180);
  }

  function openDustManager(host){
    var pendingRow=dustPendingRead({account:shopAccount()});
    if(pendingRow&&pendingRow.payload){
      recoverPendingDust();
      packSay('Finishing your saved dust receipt','The earlier request is being checked before another batch can begin.');
      return;
    }
    if(busy){ packSay('One moment','Another card action is still finishing.'); return; }
    var menuScope=captureShopAccount();
    var loading = dustDialog('Dust cards','<div class="ffshop-dust-loading" role="status" aria-live="polite"><span class="ffshop-dust-orb" aria-hidden="true"></span><b>Reading your cards</b><span>Checking rolls and protected favorites…</span></div>');
    loading.__dustScope=menuScope;
    dustWarm().then(function(ready){
      if(!loading.parentNode) return;
      if(!shopAccountIsCurrent(menuScope)){ loading.__dustClose(); return; }
      var extras=dustPlan('extras',null,[]);
      var exCopies=extras.cards.reduce(function(total,card){return total+Math.max(0,card.copies|0);},0);
      var rollsReady=!!(ready&&ready.gear);
      var canSelect=!!(host&&host.querySelector('.ffshop-collsec #ffshopSelectBar'));
      var box=loading.querySelector('.ffshop-dust-manager-box');
      box.classList.add('is-menu');
      box.innerHTML =
        '<button type="button" class="ffshop-dust-close" aria-label="Close dust menu">&times;</button>'+
        '<div class="ffshop-dust-menu-scroll">'+
        '<div class="ffshop-dust-spark" aria-hidden="true">&#10022;</div>'+
        '<div class="ffshop-dust-title">Mass dust cards</div>'+
        '<div class="ffshop-dust-sub">Choose which cards are safe to dust. Both rarity rules apply.</div>'+
        '<div class="ffshop-dust-builder'+(rollsReady?'':' is-paused')+'">'+
          '<div class="ffshop-dust-section-title"><span>Dust cards with&hellip;</span><em>Both rules apply</em></div>'+
          '<div class="ffshop-dust-filters">'+
            '<label class="ffshop-dust-filter ffshop-dust-filter-card"><span><b>Card rarity</b><small>The card frame rarity</small></span><select data-dust-filter="card"'+(rollsReady?'':' disabled')+'>'+dustRarityOptions('card')+'</select></label>'+
            '<label class="ffshop-dust-filter ffshop-dust-filter-ability"><span><b>Rolled ability rarity</b><small><strong>Abilities, not cards.</strong> Uses the higher Active or Passive roll</small></span><select data-dust-filter="ability"'+(rollsReady?'':' disabled')+'>'+dustRarityOptions('ability')+'</select></label>'+
          '</div>'+
          '<fieldset class="ffshop-dust-guards"'+(rollsReady?'':' disabled')+'><legend>Extra protections</legend>'+
            '<p>These keep cards even when they match the rules above.</p>'+
            '<label><input type="checkbox" data-dust-keep="unrolled" checked><span aria-hidden="true"></span><b>Keep unrolled cards<small>No confirmed abilities yet</small></b></label>'+
            '<label><input type="checkbox" data-dust-keep="ability" checked><span aria-hidden="true"></span><b>Keep Epic+ abilities<small>Epic, Legendary, or Mythic Active/Passive rolls</small></b></label>'+
            '<label><input type="checkbox" data-dust-keep="card" checked><span aria-hidden="true"></span><b>Keep Epic+ cards<small>Epic, Legendary, or Mythic frames</small></b></label>'+
          '</fieldset>'+
          '<p class="ffshop-dust-filter-law">Every remaining copy of each match will appear in review.</p>'+
        '</div>'+
        '<div class="ffshop-dust-section-title ffshop-dust-other-title"><span>Other ways</span></div>'+
        '<div class="ffshop-dust-modes ffshop-dust-modes-secondary">'+
          '<button type="button" class="ffshop-dust-mode" data-dust-mode="extras"'+(rollsReady&&extras.cards.length?'':' disabled')+'>'+dustModeIcon('extras')+'<span><b>Spare copies</b><small>'+(rollsReady?(extras.cards.length?(exCopies+' spare '+(exCopies===1?'copy':'copies')+' ready for review'):'No verified spare copies right now'):'Ability rolls could not be verified')+'</small></span><i aria-hidden="true">›</i></button>'+
          '<button type="button" class="ffshop-dust-mode" data-dust-mode="select"'+(rollsReady&&canSelect?'':' disabled')+'>'+dustModeIcon('select')+'<span><b>Select cards manually</b><small>'+(rollsReady?(canSelect?'Choose several cards directly from your grid':'Available from the Profile card grid'):'Ability rolls could not be verified')+'</small></span><i aria-hidden="true">›</i></button>'+
        '</div>'+
        (!rollsReady?'<div class="ffshop-dust-load-error" role="alert"><span>We couldn&rsquo;t verify ability rolls, so dusting is paused. Your cards are safe.</span><button type="button" data-dust-retry="1">Retry</button></div>':'')+
        '</div>'+
        '<div class="ffshop-dust-menu-footer">'+
          '<div class="ffshop-dust-builder-summary" aria-live="polite"><b data-dust-match-count>0 cards match</b><span data-dust-match-detail>Checking your filters&hellip;</span></div>'+
          '<button type="button" class="ffshop-dust-review-filter" data-dust-review-filter disabled>Review matching cards</button>'+
        '</div>'+
        '';
      var close=box.querySelector('.ffshop-dust-close'); if(close) close.onclick=loading.__dustClose;
      box.querySelectorAll('[data-dust-mode]').forEach(function(btn){
        btn.addEventListener('click',function(){
          var mode=btn.getAttribute('data-dust-mode');
          if(mode==='select'){ enterDustSelection(host,loading); return; }
          loading.__dustClose();
          openDustReview(host,'extras',null,[]);
        });
      });
      function readDustFilters(){
        var card=box.querySelector('[data-dust-filter="card"]'),ability=box.querySelector('[data-dust-filter="ability"]');
        var keepUnrolled=box.querySelector('[data-dust-keep="unrolled"]'),keepAbility=box.querySelector('[data-dust-keep="ability"]'),keepCard=box.querySelector('[data-dust-keep="card"]');
        return {maxCard:card?card.value:2,maxAbility:ability?ability.value:2,
          keepUnrolled:!!(keepUnrolled&&keepUnrolled.checked),keepEpicAbility:!!(keepAbility&&keepAbility.checked),keepEpicCard:!!(keepCard&&keepCard.checked)};
      }
      var currentFilterIds=[];
      function paintDustFilters(){
        var match=dustFilterCandidates(readDustFilters());
        currentFilterIds=match.ids;
        var plan=dustPlan('manual',null,currentFilterIds),totals=dustPlanTotals(plan);
        var count=box.querySelector('[data-dust-match-count]'),detail=box.querySelector('[data-dust-match-detail]'),reviewBtn=box.querySelector('[data-dust-review-filter]');
        if(count)count.textContent=currentFilterIds.length?(currentFilterIds.length+' '+(currentFilterIds.length===1?'card matches':'cards match')):'No cards match';
        if(detail){
          var kept=match.skipped.unrolled+match.skipped.ability+match.skipped.card+match.skipped.pending;
          detail.textContent=currentFilterIds.length
            ? (totals.copies+' '+(totals.copies===1?'copy':'copies')+' selected · '+totals.hearts+' Hearts'+(totals.protectedCount?' · '+totals.protectedCount+' protected '+(totals.protectedCount===1?'match starts':'matches start')+' excluded':'')+(kept?' · '+kept+' protected':''))
            : (kept?kept+' cards are protected':'Try a higher card or rolled ability rarity');
        }
        if(reviewBtn){reviewBtn.disabled=!rollsReady||!currentFilterIds.length;reviewBtn.textContent=currentFilterIds.length?'Review '+currentFilterIds.length+' matching '+(currentFilterIds.length===1?'card':'cards'):'Review matching cards';}
      }
      box.querySelectorAll('[data-dust-filter],[data-dust-keep]').forEach(function(control){control.addEventListener('change',paintDustFilters);});
      var reviewFilter=box.querySelector('[data-dust-review-filter]');
      if(reviewFilter)reviewFilter.addEventListener('click',function(){
        if(!currentFilterIds.length)return;
        loading.__dustClose();
        openDustReview(host,'filter',null,currentFilterIds);
      });
      paintDustFilters();
      var retry=box.querySelector('[data-dust-retry]');
      if(retry) retry.addEventListener('click',function(){ loading.__dustClose(); openDustManager(host); });
      try{ (close || box.querySelector('button')).focus(); }catch(_){ }
    });
  }

  /* ⛔ ====== R566 · THE ONE-SENTENCE DUST CONFIRM ======
     Jan: "when you press dust dont pop up a full menu just ask are you sure
     this heros rarity is X and you can choose ignore forever as a check box
     and then you can literlaly just click left or right and go through all
     your cards quickly."

     THIS DOES NOT REIMPLEMENT DUSTING, and that is the whole design. It
     builds the SAME `dustPlan('single', …)`, so every guard the full review
     enforces still runs — a pending roll is refused, a malformed skill is
     refused, a card with no remaining copy is refused, the 9,999-Heart
     capacity cap still applies, the account is still re-checked at commit
     time — and it hands that plan to the SAME `commitDustBatch`, so the
     server still receives the exact fingerprinted receipt with its immutable
     request id and its idempotent retry. Only the SHEET is different.

     ⛔ THE PROTECTION IS NOT REMOVED, IT IS RESTATED. `dustPlan` starts an
     Epic+ card, an Epic+ ability or a favourite EXCLUDED, which is why a
     Mythic card cannot be dusted by simply pressing a button. Confirming here
     sets `include` for that one card, which is exactly what ticking "Include
     anyway" does in the full review, and the id still travels to the server
     inside `approved_high_ids`. Naming the rarity in the question IS the
     approval — that is what Jan asked for, and it is the same consent the
     long form was collecting with three more taps.

     ⛔ A FAVOURITE ALWAYS ASKS, even with "Don't ask again" on. Rarity is a
     property of the card; a favourite is a decision the player already made
     about THIS hero, and silently undoing somebody's own explicit keep-signal
     is a different thing from letting them move fast through cards they never
     marked. Nothing else re-arms the prompt: the hero's art, name and rarity
     are all on screen when the swipe happens. */
  function dustQuickSkipKey(){ return 'ff-dust-quick-skip:' + (shopAccount() || ''); }
  function dustQuickSkip(){
    try{ return localStorage.getItem(dustQuickSkipKey()) === '1'; }catch(_){ return false; }
  }
  function dustQuickSkipSet(on){
    try{ if(on) localStorage.setItem(dustQuickSkipKey(),'1'); else localStorage.removeItem(dustQuickSkipKey()); }catch(_){ }
  }
  /* `options.onComplete` receives the same result object the full review
     passes, so the caller cannot tell which sheet ran. `options.onRefused`
     fires when nothing could be dusted, so a queue can say why and stay put
     rather than silently doing nothing. */
  function openDustQuickConfirm(host, heroId, options){
    options = options || {};
    var plan = dustPlan('single', null, [String(heroId)]);
    if(!shopAccountIsCurrent(plan.scope)){
      if(options.onRefused) options.onRefused('The signed-in account changed. Nothing was dusted.');
      return;
    }
    var card = plan.cards[0];
    if(!card){
      if(options.onRefused) options.onRefused('This card cannot be dusted while its ability roll is pending or its copy is unavailable.');
      return;
    }
    /* ⛔ commitDustBatch begins `if(busy||!totals.copies) return;` — a silent
       no-op. The full review can afford that because its sheet stays open and
       the player presses again; a swipe cannot, because the card has already
       flown off and nothing would ever come back. Ask first and say so. */
    if(busy){
      if(options.onRefused) options.onRefused('Another Heart action is still finishing. Try this card again in a moment.');
      return;
    }
    var rarity = DUST_RARITY[Math.max(0,Math.min(5,card.cardRarity|0))] || 'Common';
    /* The confirm is the approval step for a protected card, so include it
       here and let the receipt carry it as an approved high id. */
    card.include = true;
    var totals = dustPlanTotals(plan);
    /* ⛔ finishDustBatch deliberately waits out its own flight animation and a
       collection refresh before it resolves — up to about a second. On the
       full review that is invisible, because the sheet stays up with its
       button spinning. Here the card has already flown off, so without this
       the player would be looking at an empty stage with nothing said. */
    function fire(modal){
      if(typeof options.onStart === 'function'){ try{ options.onStart(card.name); }catch(_){ } }
      commitDustBatch(host, modal, plan, dustPlanTotals(plan), null, options);
    }
    /* THE SILENT PATH. Still a real commit through the audited RPC — it just
       does not ask first. A favourite is never silent. */
    if(dustQuickSkip() && !card.favorite){
      var quiet = document.createElement('div');
      quiet.className = 'ffshop-dust-quiet';
      quiet.innerHTML = '<div class="ffshop-dust-error" role="alert" hidden></div>' +
        '<button type="button" class="ffshop-dust-go" hidden></button>';
      quiet.__dustClose = function(){ try{ quiet.remove(); }catch(_){ } };
      /* Attached, not detached: commitDustBatch needs a live node to write
         into, and `onError` below is what carries that message somewhere a
         person can actually read it. */
      document.body.appendChild(quiet);
      var quietOpts = {};
      for(var k in options) if(Object.prototype.hasOwnProperty.call(options,k)) quietOpts[k]=options[k];
      quietOpts.onError = function(msg,definitive){
        try{ quiet.remove(); }catch(_){ }
        if(typeof options.onError==='function'){ try{ options.onError(msg,definitive); }catch(_){ } }
        else if(typeof options.onRefused==='function'){ try{ options.onRefused(msg); }catch(_){ } }
        else toast(msg);
      };
      if(typeof options.onStart === 'function'){ try{ options.onStart(card.name); }catch(_){ } }
      commitDustBatch(host, quiet, plan, dustPlanTotals(plan), null, quietOpts);
      return;
    }
    var sheet = dustDialog('Dust this hero',
      '<button type="button" class="ffshop-dust-close" aria-label="Keep this hero">&times;</button>' +
      '<div class="ffshop-dust-spark" aria-hidden="true">&#10022;</div>' +
      '<div class="ffshop-dust-title ffshop-dustq-title">Are you sure?</div>' +
      '<div class="ffshop-dust-sub ffshop-dustq-sub">This hero is <b>' + esc(rarity) + '</b>. ' +
        'Turning ' + (card.copies === 1 ? 'the copy' : 'a copy') + ' of <b>' + esc(card.name) + '</b> to Hearts pays <b>' +
        totals.hearts + ' &#9829;</b> and cannot be undone.</div>' +
      (card.favorite
        ? '<div class="ffshop-dustq-fav">You favorited this hero, so this one always asks.</div>'
        : '<label class="ffshop-dustq-again"><input type="checkbox" data-dust-quick-skip>' +
          '<span aria-hidden="true"></span><em>Do not ask again on this device</em></label>') +
      '<div class="ffshop-dust-error" role="alert" hidden></div>' +
      '<div class="ffshop-dust-actions ffshop-dustq-actions">' +
        '<button type="button" class="ffshop-dust-cancel">Keep it</button>' +
        '<button type="button" class="ffshop-dust-go">Dust for ' + totals.hearts + ' &#9829;&#xFE0E;</button>' +
      '</div>');
    sheet.__dustScope = plan.scope;
    /* not `box`: the review queue two thousand lines down uses that name for
       its own modal body, and a reader skimming both should never have to work
       out which one is which */
    var sheetBox = sheet.querySelector('.ffshop-dust-manager-box');
    if(sheetBox) sheetBox.classList.add('is-quick');
    function keep(){
      if(sheet.__dustClose) sheet.__dustClose();
      if(options.onKeep) options.onKeep();
    }
    sheet.querySelector('.ffshop-dust-close').onclick = keep;
    sheet.querySelector('.ffshop-dust-cancel').onclick = keep;
    sheet.querySelector('.ffshop-dust-go').onclick = function(){
      var again = sheet.querySelector('[data-dust-quick-skip]');
      /* Written BEFORE the commit on purpose: the player's answer to "do not
         ask again" is about the question, not about whether this particular
         dust succeeded. A failed commit that then asks again next time would
         read as the checkbox not working. */
      if(again && again.checked) dustQuickSkipSet(true);
      fire(sheet);
    };
    try{ sheet.querySelector('.ffshop-dust-cancel').focus(); }catch(_){ }
  }

  function openDustReview(host, mode, maxRarity, selectedIds, options){
    options=options||{};
    var planMode=mode==='filter'?'manual':mode;
    var plan=dustPlan(planMode,maxRarity,selectedIds), totals=dustPlanTotals(plan);
    if(!shopAccountIsCurrent(plan.scope)){
      packSay('Account changed','Open Dust cards again to review this account’s collection.');
      return;
    }
    var title=mode==='extras'?'Dust spare copies':(mode==='single'?'Turn one copy to Hearts':(mode==='filter'?'Review matching cards':(mode==='manual'?'Review selected cards':((maxRarity|0)===2?'Rare ability or lower':'Epic ability or lower'))));
    if(!plan.cards.length){
      var why=mode==='extras'?'There are no verified spare copies to dust. Pending rolls were left alone.':(mode==='single'?'This card cannot be dusted while its ability roll is pending or its copy is unavailable.':(mode==='filter'?'No cards match both rarity rules. Adjust the filters and try again.':(mode==='manual'?'None of those cards can be dusted right now.':'No confirmed rolled cards match that ability rarity. Unrolled and pending cards were left alone.')));
      var empty=dustDialog(title,'<button type="button" class="ffshop-dust-close" aria-label="Close">&times;</button><div class="ffshop-dust-spark" aria-hidden="true">&#10022;</div><div class="ffshop-dust-title">Nothing eligible</div><div class="ffshop-dust-sub">'+why+'</div><div class="ffshop-dust-actions"><button type="button" class="ffshop-dust-cancel">Done</button></div>');
      empty.querySelectorAll('.ffshop-dust-close,.ffshop-dust-cancel').forEach(function(b){ b.onclick=empty.__dustClose; });
      return;
    }
    var rows=plan.cards.map(function(c){
      var abilityRarity=c.meta.rolled ? DUST_RARITY[Math.max(0,Math.min(5,c.meta.ability|0))] : 'Unrolled';
      var cardRarity=DUST_RARITY[Math.max(0,Math.min(5,c.cardRarity|0))];
      var rarity=c.meta.rolled ? abilityRarity+' ability' : 'Unrolled';
      var protectedBy=[]; if(c.highAbility) protectedBy.push('Epic+ ability'); if(c.highCard) protectedBy.push('Epic+ card'); if(c.favorite) protectedBy.push('Favorite');
      var excluded=(!c.include&&protectedBy.length)?'<span class="ffshop-dust-excluded">Excluded</span>':'';
      var warn=(c.highAbility?'<span class="ffshop-dust-risk">Epic+ ability</span>':'')+(c.highCard?'<span class="ffshop-dust-risk">Epic+ card</span>':'');
      var fav=c.favorite?'<span class="ffshop-dust-fav">&#9733; Favorite</span>':'';
      var deferred=c.deferredCopies?(' · '+c.deferredCopies+' '+(c.deferredCopies===1?'copy':'copies')+' left for a later batch'):'';
      var inputLabel='Include '+c.name+' — '+cardRarity+' card, '+rarity+', '+c.copies+' '+(c.copies===1?'copy':'copies')+', '+c.hearts+' hearts'+
        deferred+(protectedBy.length?'. Excluded by default because '+protectedBy.join(' and '):'');
      return '<li class="ffshop-dust-row'+(c.include?' is-included':' is-protected')+'" data-dust-id="'+esc(c.id)+'">'+
        (c.img?'<img src="'+esc(c.img)+'" alt="">':'<span class="ffshop-dust-avatar" aria-hidden="true">&#10022;</span>')+
        '<span class="ffshop-dust-row-copy"><b>'+esc(c.name)+'</b><small>'+c.copies+' '+(c.copies===1?'copy':'copies')+' · '+c.hearts+' hearts'+deferred+'</small><span><span class="ffshop-dust-rarity-chip is-card">Card &middot; '+cardRarity+'</span><span class="ffshop-dust-rarity-chip is-ability">Ability &middot; '+abilityRarity+'</span>'+excluded+warn+fav+'</span></span>'+
        '<label class="ffshop-dust-check"><input type="checkbox" data-dust-include="'+esc(c.id)+'" aria-label="'+esc(inputLabel)+'"'+(c.include?' checked':'')+'><span aria-hidden="true"></span><em>'+((c.high||c.favorite)?'Include anyway':'Include')+'</em></label>'+
      '</li>';
    }).join('');
    var skipped=plan.skipped;
    var skippedN=skipped.unrolled+skipped.pending+skipped.above+skipped.unavailable;
    var review=dustDialog(title,
      '<button type="button" class="ffshop-dust-close" aria-label="Close review">&times;</button>'+
      '<div class="ffshop-dust-spark" aria-hidden="true">&#10022;</div><div class="ffshop-dust-title">'+title+'</div>'+
      '<div class="ffshop-dust-sub ffshop-dust-review-intro">'+(mode==='single'?'Review the exact copy below. Epic+ cards, Epic+ abilities, and favorites stay excluded until you deliberately include them.':(mode==='filter'?'Review each match below. Epic+ cards, Epic+ abilities, and favorites start excluded for safety.':'Uncheck anything you want to keep. Check a protected hero only when you mean to include it.'))+'</div>'+
      '<div class="ffshop-dust-summary" aria-live="polite"><span><b data-dust-heroes>'+totals.heroes+'</b><small data-dust-heroes-label>'+(totals.heroes===1?'hero':'heroes')+'</small></span><span><b data-dust-copies>'+totals.copies+'</b><small data-dust-copies-label>'+(totals.copies===1?'copy':'copies')+'</small></span><span><b data-dust-hearts>'+totals.hearts+'&#9829;</b><small>you receive</small></span></div>'+
      '<ul class="ffshop-dust-list">'+rows+'</ul>'+
      (skippedN?'<div class="ffshop-dust-skipped">'+skippedN+' '+(skippedN===1?'card was':'cards were')+' left out because '+[
        skipped.unrolled?(skipped.unrolled+' unrolled'):'',skipped.pending?(skipped.pending+' pending'):'',skipped.above?(skipped.above+' above the threshold'):'',skipped.unavailable?(skipped.unavailable+' unavailable'):''
      ].filter(Boolean).join(', ')+'.</div>':'')+
      '<div class="ffshop-dust-error" role="alert" hidden></div>'+
      '<div class="ffshop-dust-actions ffshop-dust-review-actions"><button type="button" class="ffshop-dust-cancel">'+(mode==='single'?'Keep it':'Cancel')+'</button><button type="button" class="ffshop-dust-go">'+(mode==='single'?'Turn ':'Dust ')+'<span data-dust-final>'+totals.copies+'</span> '+(totals.copies===1?'copy':'copies')+' for <span data-dust-final-hearts>'+totals.hearts+'</span> &#9829;&#xFE0E;</button></div>');
    review.__dustScope=plan.scope;
    var reviewBox=review.querySelector('.ffshop-dust-manager-box'); if(reviewBox) reviewBox.classList.add('is-review');
    function recalc(){
      plan.cards.forEach(function(c){
        var input=review.querySelector('[data-dust-include="'+c.id+'"]'); c.include=!!(input&&input.checked);
        var row=review.querySelector('[data-dust-id="'+c.id+'"]'); if(row){
          row.classList.toggle('is-included',c.include); row.classList.toggle('is-protected',!c.include);
          var excluded=row.querySelector('.ffshop-dust-excluded'); if(excluded) excluded.hidden=c.include;
        }
      });
      var t=dustPlanTotals(plan);
      review.querySelector('[data-dust-heroes]').textContent=t.heroes;
      review.querySelector('[data-dust-heroes-label]').textContent=t.heroes===1?'hero':'heroes';
      review.querySelector('[data-dust-copies]').textContent=t.copies;
      review.querySelector('[data-dust-copies-label]').textContent=t.copies===1?'copy':'copies';
      review.querySelector('[data-dust-hearts]').innerHTML=t.hearts+'&#9829;';
      review.querySelector('[data-dust-final]').textContent=t.copies;
      review.querySelector('[data-dust-final-hearts]').textContent=t.hearts;
      var capacity=dustCapacityMessage(t),error=review.querySelector('.ffshop-dust-error');
      if(capacity){error.textContent=capacity;error.dataset.dustCapacity='1';error.hidden=false;}
      else if(error&&error.dataset.dustCapacity==='1'){error.textContent='';error.removeAttribute('data-dust-capacity');error.hidden=true;}
      var go=review.querySelector('.ffshop-dust-go'); go.disabled=!t.copies||!!capacity;
      go.setAttribute('aria-label',capacity|| (t.copies?(mode==='single'?'Turn ':'Dust ')+t.copies+' '+(t.copies===1?'copy':'copies')+' for '+t.hearts+' hearts':'Choose at least one card'));
      return t;
    }
    review.querySelectorAll('[data-dust-include]').forEach(function(i){ i.addEventListener('change',recalc); });
    review.querySelector('.ffshop-dust-close').onclick=review.__dustClose;
    review.querySelector('.ffshop-dust-cancel').onclick=review.__dustClose;
    review.querySelector('.ffshop-dust-go').onclick=function(){ commitDustBatch(host,review,plan,recalc(),null,options); };
    recalc();
  }

  function openSingleDustReview(heroId, btn, closeInspector){
    if(busy) return;
    var scope=captureShopAccount(), old=btn&&btn.innerHTML;
    if(btn){btn.disabled=true;btn.setAttribute('aria-busy','true');btn.textContent='Checking ability…';}
    var D=window.HOHContractData;
    var verify=D&&D.loadGear
      ? Promise.resolve().then(function(){return D.loadGear(true);}).then(function(){return !(D.gearLoadFailed&&D.gearLoadFailed());},function(){return false;})
      : Promise.resolve(false);
    verify.then(function(ok){
      if(btn){btn.disabled=false;btn.removeAttribute('aria-busy');btn.innerHTML=old;}
      if(!shopAccountIsCurrent(scope)){toast('The signed-in account changed. Nothing was dusted.');return;}
      if(!ok){toast('Abilities could not be verified. Your card is safe. Retry before dusting.');return;}
      if(typeof closeInspector==='function')closeInspector();
      openDustReview(document,'single',null,[String(heroId)]);
    });
  }

  function dustPendingSave(scope,payload,response){
    var row={payload:payload,response:response||null,at:Date.now()};
    if(!dustJsonWrite(dustScopedKey(DUST_PENDING_KEY,scope),row)) throw new Error('This device could not save the dust request. Nothing was submitted.');
  }
  function dustPendingClear(scope,requestId){
    var key=dustScopedKey(DUST_PENDING_KEY,scope),row=dustJsonRead(key);
    if(row&&row.payload&&row.payload.request_id!==requestId)return false;
    return dustJsonRemove(key);
  }
  function dustHeartWalletLock(work){
    if(typeof window.hohWithHeartWalletLock==='function')return window.hohWithHeartWalletLock(work);
    var lockName='hoh-heart-wallet-v2',leaseKey='hoh-heart-wallet-lease-v2';
    try{if(navigator.locks&&typeof navigator.locks.request==='function')return navigator.locks.request(lockName,function(){return Promise.resolve().then(work);});}catch(_){ }
    /* Standalone fixtures and older cached pages may not have hoh-tickets yet.
       Use the same global verified lease so dust can never race a BG debit. */
    var token=reqId('heart-wallet'),tries=0;
    function fail(code,message){var e=new Error(message);e.__dustWalletLock=code;return e;}
    function release(){var mine=dustJsonRead(leaseKey);try{if(mine&&mine.token===token)localStorage.removeItem(leaseKey);}catch(_){ }}
    function acquire(){
      var now=Date.now(),cur=dustJsonRead(leaseKey);
      if(!cur||Number(cur.until||0)<now){
        if(!dustJsonWrite(leaseKey,{token:token,until:now+120000}))return Promise.reject(fail('storage','The Heart wallet lock could not be saved.'));
      }
      return new Promise(function(resolve){setTimeout(resolve,35+Math.floor(Math.random()*25));}).then(function(){
        var mine=dustJsonRead(leaseKey);
        if(mine&&mine.token===token)return Promise.resolve().then(work).then(function(value){release();return value;},function(error){release();throw error;});
        if(++tries<80)return new Promise(function(resolve){setTimeout(resolve,50);}).then(acquire);
        throw fail('busy','Another Heart action is still finishing.');
      });
    }
    return acquire();
  }
  function dustHeartRaw(){try{return String(localStorage.getItem('hoh-heart-bank-v1')||'0');}catch(_){return '';}}
  function dustCreditMarker(requestId,after){return String(after)+'|hoh:dust:'+encodeURIComponent(String(requestId||''));}
  function dustCreditMarked(requestId,after){return dustHeartRaw()===dustCreditMarker(requestId,after);}
  function dustCreditApply(requestId,delta,reason,expectedBefore){
    var before=heartBank(),raw=dustHeartRaw(),after=before+delta,marker=dustCreditMarker(requestId,after);
    if(before!==Number(expectedBefore)||raw!==String(expectedBefore))throw new Error('Heart balance changed before the dust receipt landed.');
    if(after>DUST_HEART_BANK_MAX){var capError=new Error('Your dust reward is waiting because the Heart wallet is full. Spend Hearts, then reopen Profile.');capError.__dustPending=true;throw capError;}
    try{ if(!window.__hohHeartHint && window.hohHeartHint) window.hohHeartHint(reason); }catch(_){ }
    localStorage.setItem('hoh-heart-bank-v1',marker);
    if(dustHeartRaw()!==marker)throw new Error('The Heart receipt could not be written.');
    return after;
  }
  function dustCreditNotify(delta){
    try{document.dispatchEvent(new CustomEvent('hoh:hearts-bumped',{detail:{gained:delta}}));}catch(_){ }
    try{if(typeof window.hohRenderHeaderCounter==='function')window.hohRenderHeaderCounter();}catch(_){ }
    updateHeartChip();
  }
  function dustCreditNormalize(requestId,after){
    var marker=dustCreditMarker(requestId,after),raw=dustHeartRaw();
    if(raw===String(after))return true;
    if(raw!==marker)return false;
    try{localStorage.setItem('hoh-heart-bank-v1',String(after));return dustHeartRaw()===String(after);}catch(_){return false;}
  }
  function claimDustReward(scope,requestId,delta){
    delta=Math.max(0,Math.min(1000000000,Number(delta)||0));
    var account=dustAccount(scope), key=String(requestId||''), claimStore=dustScopedKey(DUST_CLAIMS_KEY,scope);
    if(!delta) return Promise.resolve({first:false,amount:0});
    if(!account||key.length<8) return Promise.reject(new Error('That dust receipt has an invalid identity. Hearts were not added.'));
    function pending(message){ var e=new Error(message); e.__dustPending=true; return e; }
    if(delta>DUST_HEART_BANK_MAX)return Promise.reject(pending('This dust receipt is larger than the 9,999-Heart wallet limit. The cards and reward are preserved for support recovery.'));
    function assertCurrent(){
      if(!shopAccountIsCurrent(scope)) throw pending('Return to this account and reopen Profile to safely add its dusted Hearts.');
    }
    function save(bucket){ if(!dustJsonWrite(claimStore,bucket)) throw pending('Your cards were dusted, but this device could not save the Heart receipt. Reopen Profile to recover it.'); }
    function attempt(wait){
      assertCurrent();
      var bucket=dustJsonRead(claimStore); if(!bucket) throw pending('The saved Heart receipt journal is unreadable. Nothing was added twice.');
      if(!bucket||typeof bucket!=='object'||Array.isArray(bucket)) throw pending('The saved Heart receipt journal is invalid. Nothing was added twice.');
      var old=bucket[key], now=Date.now();
      if(old && Number(old.delta)!==delta) throw pending('The server receipt amount changed unexpectedly. Nothing was added.');
      if(old && old.state==='done'){
        dustCreditNormalize(key,old.after|0);
        return Promise.resolve({first:false,amount:delta});
      }
      if(old && old.state==='credited'){
        assertCurrent();
        var cur=heartBank();
        var marked=dustCreditMarked(key,old.after|0);
        if(!marked&&(cur!==(old.before|0)||dustHeartRaw()!==String(old.before|0))) throw pending('Your Heart balance changed during receipt recovery. Nothing was added twice; reopen Profile after other Heart actions finish.');
        if(!marked&&(old.after|0)>DUST_HEART_BANK_MAX)throw pending('Your dust reward is waiting because the Heart wallet is full. Spend Hearts, then reopen Profile.');
        var first=false;
        if(!marked){ assertCurrent(); dustCreditApply(key,delta,'earn_dust_batch',old.before|0); first=true; }
        if((parseInt(dustHeartRaw(),10)||0)!==(old.after|0)) throw pending('The Heart receipt could not be verified. Reopen Profile to recover it.');
        assertCurrent();
        old.state='done'; old.at=now; bucket[key]=old; save(bucket);
        dustCreditNormalize(key,old.after|0);
        dustCreditNotify(delta);
        return Promise.resolve({first:first,amount:delta,recovered:true});
      }
      if(old && old.state==='claiming' && now-Number(old.at||0)<2000 && wait<40){
        return new Promise(function(resolve,reject){ setTimeout(function(){ Promise.resolve().then(function(){return attempt(wait+1);}).then(resolve,reject); },50); });
      }
      if(heartBank()+delta>DUST_HEART_BANK_MAX)throw pending('Your dust reward is waiting because the Heart wallet is full. Spend Hearts, then reopen Profile.');
      var token=reqId('claim');
      bucket[key]={state:'claiming',token:token,delta:delta,at:now}; save(bucket);
      return new Promise(function(resolve,reject){ setTimeout(function(){
        try{
          assertCurrent();
          var b2=dustJsonRead(claimStore); if(!b2) throw pending('The Heart receipt became unreadable. Nothing was added.');
          var mine=b2[key];
          if(!mine||mine.token!==token||mine.state!=='claiming'){ Promise.resolve().then(function(){return attempt(wait+1);}).then(resolve,reject); return; }
          assertCurrent();
          var before=heartBank(), after=before+delta;
          mine.state='credited'; mine.before=before; mine.after=after; mine.at=Date.now(); b2[key]=mine; save(b2);
          assertCurrent();
          dustCreditApply(key,delta,'earn_dust_batch',before);
          if((parseInt(dustHeartRaw(),10)||0)!==after) throw pending('The Heart receipt could not be verified. Reopen Profile to recover it.');
          assertCurrent();
          var b3=dustJsonRead(claimStore); if(!b3) throw pending('The Heart receipt needs recovery. Reopen Profile.');
          var done=b3[key];
          if(!done||done.token!==token||done.state!=='credited') throw pending('The Heart receipt needs recovery. Reopen Profile.');
          done.state='done'; done.at=Date.now(); b3[key]=done; save(b3);
          dustCreditNormalize(key,after);
          dustCreditNotify(delta);
          resolve({first:true,amount:delta});
        }catch(e){ reject(e); }
      },0); });
    }
    return dustHeartWalletLock(function(){return attempt(0);}).catch(function(e){
      var lockCode=e&&(e.__dustWalletLock||e.code);
      if(lockCode)throw pending(lockCode==='busy'||lockCode==='heart_wallet_busy'?'Another Heart action is still finishing. Reopen Profile to recover this dust receipt.':'The shared Heart wallet lock is unavailable. Reopen Profile to recover this dust receipt.');
      throw e;
    });
  }

  /* ⛔ ============ R567 · THE APPROVAL THE SERVER NEVER ASKED FOR ============
     Jan: "nothing lets me dust anything cause 'A protected ability changed
     while you reviewed it. Nothing was dusted; reopen the review.'"

     THAT SENTENCE IS `approval_mismatch`, and the cause is right here.
     `approved_high_ids` is a SERVER contract field, and the server's rule
     (sql/R558B-atomic-batch-dust.sql) is narrow and symmetric:

       -- an included row with max_rarity >= 3 MUST be approved
       -- and an approved id whose max_rarity is null or < 3 is ITSELF a mismatch

     `max_rarity` there is the ROLLED ABILITY rarity — the same number this
     function puts in `reviewed` two lines below. But the id list was built
     from `c.high`, which the client computes as `highAbility || highCard`,
     and `highCard` is the CARD FRAME's rarity. Those are different numbers
     about different things.

     So every Epic+ / Legendary / Mythic CARD holding a Common or Rare ability
     sent an approval the server had not asked for, and the server refused the
     whole batch. A mythic frame with a common roll is the single most common
     card in a collection that has been rolling for a while, which is why Jan
     could not dust ANYTHING.

     ⛔ THE BUG IS OLDER THAN THE SCREEN THAT EXPOSED IT. In the full review an
     Epic+ card starts EXCLUDED, so reaching this line required deliberately
     ticking "Include anyway" — rare enough that it read as a server hiccup.
     R566 pointed the quick confirm at the same path for every card in the
     review queue, which turned a latent edge case into a total blocker. The
     fix belongs here either way, and it repairs the full review too.

     ⛔ `c.high` IS STILL CORRECT FOR WHAT IT IS FOR. It decides whether a card
     starts excluded and wears a warning chip, and a Mythic frame absolutely
     should. Two jobs, two rules: the UI protection keeps the card rarity, the
     server approval mirrors the server. They are computed from one shared
     `maxRarityOf` below so they can never drift apart again. */
  function dustRpcPayload(plan){
    /* the ONE definition of the number the receipt carries and the server
       judges — never re-derived at either call site */
    function maxRarityOf(c){ return (!c.meta || !c.meta.rolled) ? null : (c.meta.ability | 0); }
    function needsApproval(c){ var r = maxRarityOf(c); return r !== null && r >= 3; }
    var excluded=[],approved=[];
    plan.cards.forEach(function(c){
      if(!c.include) excluded.push(c.id);
      else if(needsApproval(c)) approved.push(c.id);
    });
    /* This is the exact review receipt, not a request to rescan the account.
       Every object corresponds to one visible review row and fingerprints the
       copies, ability rarity, key and payout the player actually saw. The RPC
       aborts the whole batch if any included row changes under its lock; cards
       absent from this array are categorically outside the transaction. */
    var reviewed=plan.cards.map(function(c){
      return {
        id:String(c.id), copies:c.copies|0,
        max_rarity:maxRarityOf(c),
        per_copy:c.per|0,
        card_key:((c.card&&c.card.ff)?'ff:':'own:')+String(c.id)
      };
    });
    return {
      mode:plan.mode,
      character_ids:reviewed,
      max_rarity:plan.mode==='threshold'?(plan.max|0):null,
      approved_high_ids:approved,
      excluded_ids:excluded,
      request_id:reqId('dust-batch')
    };
  }
  function dustRpcCall(scope,payload){
    return rpc('hoh_dust_cards_batch',{
      p_email:scope.email,p_pin:scope.pin,p_mode:payload.mode,
      p_character_ids:payload.character_ids,p_max_rarity:payload.max_rarity,
      p_approved_high_ids:payload.approved_high_ids,p_excluded_ids:payload.excluded_ids,
      p_request_id:payload.request_id
    });
  }
  function dustErrorMessage(res){
    var d=res&&res.data, status=d&&d.status, raw=res&&res.error&&res.error.message;
    if(status==='payout_too_large') return 'That review is worth more than the 9,999-Heart wallet limit. Exclude some cards and dust them in a smaller batch.';
    if(status==='request_conflict') return 'This saved request no longer matches its first review. Nothing new was dusted.';
    if(status==='approval_mismatch') return 'A protected ability changed while you reviewed it. Nothing was dusted; reopen the review.';
    if(status==='review_changed') return 'A card changed while this review was open. Nothing was dusted; close this and review the current cards again.';
    if(status==='invalid_review'||status==='duplicate_review'||status==='review_scope_mismatch') return 'That review is no longer valid. Nothing was dusted; open Dust cards again.';
    if(status==='too_many_cards'||status==='too_many_ids') return 'That batch is too large to process safely. Select fewer cards.';
    if(status==='missing_auth'||status==='unauthenticated') return 'Your sign-in changed. Sign in again before dusting.';
    if(raw&&(/hoh_dust_cards_batch|schema cache|could not find/i.test(raw))) return 'Batch dusting needs the R558 database update before it can be used.';
    return raw||'The cards could not be dusted. Your saved request is safe to retry.';
  }
  function dustFailureIsDefinitive(res){
    var d=res&&res.data, status=String(d&&d.status||'');
    /* A named server refusal proves the RPC completed without this request
       committing. It must not be kept in the ambiguous-response journal or it
       would block every later batch. Transport loss remains ambiguous and
       therefore keeps the exact request id for an idempotent retry. */
    if(d&&d.ok===false&&status) return true;
    var raw=String(res&&res.error&&res.error.message||'');
    return /hoh_dust_cards_batch|schema cache|could not find|does not exist|unauthor|invalid jwt/i.test(raw);
  }
  function finishDustBatch(scope,payload,d){
    return claimDustReward(scope,d.request_id,d.hearts).then(function(){
      dustPendingClear(scope,d.request_id);
      if(!shopAccountIsCurrent(scope)) return d;
      var processed=Array.isArray(d.processed)?d.processed:[], grid=document.querySelector('#ffshop-grid-all'), pill=cornerPill();
      processed.forEach(function(row,i){
        var tile=grid&&grid.querySelector('.ffshop-tile[data-hero-id="'+row.character_id+'"]');
        if(tile&&!reduced) setTimeout(function(){
          if((row.remaining_copies|0)<=0)tile.classList.add('ffshop-tile-dusting');
          if(pill)flyHearts(tile,pill,4);
        },i*55);
      });
      if(payload.mode!=='extras'&&processed.length){
        var gone={}; processed.forEach(function(r){if((r.remaining_copies|0)<=0)gone[String(r.character_id)]=1;});
        try{ window.__ffPB=(window.__ffPB||[]).filter(function(k){return !gone[String(k).replace(/^(ff|own|hall):/,'')];}); }catch(_){ }
      }
      dustClearSelection();
      var skipped=(d.skipped||[]).length;
      if(d.cards_dusted>0) packSay('Cards turned to Hearts',d.copies_dusted+' '+(d.copies_dusted===1?'copy':'copies')+' became '+d.hearts+' Hearts.'+(skipped?' '+skipped+' protected or changed '+(skipped===1?'card was':'cards were')+' left alone.':''));
      else packSay('Nothing was dusted','Every card was protected, changed, or no longer eligible.');
      return new Promise(function(resolve){ setTimeout(function(){
        fetchState().then(function(){
          var D=window.HOHContractData;
          return D&&D.loadCollection ? D.loadCollection(true) : null;
        }).then(function(){ refreshCollectionEverywhere(); resolve(d); },function(){ refreshCollectionEverywhere(); resolve(d); });
      },reduced?20:Math.min(750,processed.length*55+260)); });
    });
  }
  function commitDustBatch(host,modal,plan,totals,savedPayload,options){
    options=options||{};
    if(busy||!totals.copies) return;
    var scope=plan&&plan.scope, error=modal.querySelector('.ffshop-dust-error'), go=modal.querySelector('.ffshop-dust-go');
    var capacity=dustCapacityMessage(totals);
    if(capacity){if(error){error.textContent=capacity;error.dataset.dustCapacity='1';error.hidden=false;}go.disabled=true;return;}
    if(error)error.removeAttribute('data-dust-capacity');
    if(!scope||!shopAccountIsCurrent(scope)){
      if(error){ error.textContent='The signed-in account changed. Nothing was dusted; close this review and open Dust cards again.'; error.hidden=false; }
      go.disabled=true;
      return;
    }
    var payload=savedPayload||dustRpcPayload(plan);
    var pendingRow=dustPendingRead(scope);
    if(pendingRow&&pendingRow.payload&&pendingRow.payload.request_id!==payload.request_id){
      if(error){ error.textContent='Finish the earlier saved dust receipt before starting another batch.'; error.hidden=false; }
      recoverPendingDust(); return;
    }
    var busyToken=dustBusyTake(scope);
    modal.classList.add('is-busy'); go.disabled=true; go.setAttribute('aria-busy','true'); go.innerHTML='<span class="ffshop-dust-spinner" aria-hidden="true"></span>Checking cards…'; if(error) error.hidden=true;
    modal.querySelectorAll('[data-dust-include],.ffshop-dust-close,.ffshop-dust-cancel').forEach(function(el){ el.disabled=true; });
    try{ dustPendingSave(scope,payload,null); }
    catch(e){
      dustBusyRelease(busyToken); modal.classList.remove('is-busy'); go.disabled=false; go.textContent='Try again';
      modal.querySelectorAll('[data-dust-include],.ffshop-dust-close,.ffshop-dust-cancel').forEach(function(el){ el.disabled=false; });
      if(error){error.textContent=e.message;error.hidden=false;} return;
    }
    dustRpcCall(scope,payload).then(function(res){
      var d=res&&res.data;
      if(!(d&&d.ok)) throw {__dustResponse:res,__dustDefinitive:dustFailureIsDefinitive(res),message:dustErrorMessage(res)};
      dustPendingSave(scope,payload,d);
      return finishDustBatch(scope,payload,d);
    }).then(function(result){
      dustBusyRelease(busyToken); if(modal.__dustClose) modal.__dustClose();
      if(typeof options.onComplete==='function'){
        try{options.onComplete(result);}catch(_){ }
      }
    },function(err){
      dustBusyRelease(busyToken); modal.classList.remove('is-busy'); go.removeAttribute('aria-busy'); go.disabled=false; go.textContent='Retry safely';
      modal.querySelectorAll('.ffshop-dust-close,.ffshop-dust-cancel').forEach(function(el){ el.disabled=false; });
      var _msg=(err&&err.message)||dustErrorMessage(err&&err.__dustResponse);
      if(error){ error.textContent=_msg; error.hidden=false; }
      /* ⛔ R566: the sheet is not always on screen. The quick confirm's silent
         path commits with no visible dialog at all, so writing the failure
         into a hidden `.ffshop-dust-error` and stopping there would lose it
         completely — the player would swipe a card away and simply never learn
         it was still theirs. Additive and optional: every existing caller
         passes no onError and behaves exactly as before. */
      if(typeof options.onError==='function'){ try{ options.onError(_msg,!!(err&&err.__dustDefinitive)); }catch(_){ } }
      /* An ambiguous network response must retry the SAME immutable request
         id. Creating a fresh one here could commit a second batch after the
         first response was merely lost in transit. */
      if(err&&err.__dustDefinitive){
        dustPendingClear(scope,payload.request_id);
        go.textContent='Close and review again';
        go.onclick=function(){ if(modal.__dustClose) modal.__dustClose(); };
      }else{
        go.onclick=function(){ commitDustBatch(host,modal,plan,recalcTotalsForRetry(plan),payload,options); };
      }
    });
  }

  function recalcTotalsForRetry(plan){ return dustPlanTotals(plan); }

  function recoverPendingDust(){
    var account=shopAccount(); if(!account||window.__ffDustRecovering===account) return;
    var row=dustPendingRead({account:account}); if(!row||!row.payload) return;
    window.__ffDustRecovering=account;
    var scope=captureShopAccount(), busyToken=dustBusyTake(scope), work=row.response?Promise.resolve({data:row.response}):dustRpcCall(scope,row.payload);
    work.then(function(res){
      var d=res&&res.data;
      if(!(d&&d.ok)){
        var failure=new Error(dustErrorMessage(res)); failure.__dustDefinitive=dustFailureIsDefinitive(res); throw failure;
      }
      if(!row.response) dustPendingSave(scope,row.payload,d);
      return finishDustBatch(scope,row.payload,d);
    }).then(function(){ window.__ffDustRecovering=''; dustBusyRelease(busyToken); },function(e){
      window.__ffDustRecovering=''; dustBusyRelease(busyToken);
      if(e&&e.__dustDefinitive) dustPendingClear(scope,row.payload.request_id);
      if(shopAccountIsCurrent(scope)) packSay(e&&e.__dustDefinitive?'Review the cards again':'Dust receipt waiting',(e&&e.message)||'Reopen Profile to retry the saved receipt.');
    });
  }

  function syncDustSelectionUI(host){
    if(!host) return;
    var st=dustSelection(), grid=host.querySelector('#ffshop-grid-all'), bar=host.querySelector('#ffshopSelectBar');
    if(!grid||!bar) return;
    var ids=dustSelectionIds(), selected={}; ids.forEach(function(id){selected[id]=1;});
    var sec=grid.closest('.ffshop-collsec'); if(sec) sec.classList.toggle('is-selecting',!!st.active);
    grid.querySelectorAll('.ffshop-tile').forEach(function(t){
      var on=!!selected[String(t.getAttribute('data-hero-id'))];
      t.classList.toggle('is-dust-selected',on); t.setAttribute('aria-pressed',on?'true':'false');
      if(st.active){
        var tileName=t.querySelector('.ffshop-tile-name');
        t.setAttribute('aria-label',(on?'Remove ':'Select ')+((tileName&&tileName.textContent)||'card')+' for dust review');
      }
      else { t.removeAttribute('aria-pressed'); t.removeAttribute('aria-label'); }
    });
    bar.hidden=!st.active;
    var count=bar.querySelector('#ffshopSelectCount'), review=bar.querySelector('#ffshopSelectReview');
    if(count) count.textContent=ids.length+' selected';
    if(review) review.disabled=!ids.length;
  }

  /* ==================================================================
     R560 · RAPID ABILITY REVIEW
     One verified card at a time, using the exact existing trading card and
     the same roll/dust/favourite authorities as the individual inspector.
     Only MISSING slots are submitted: a rapid review can never turn into a
     surprise 150-Heart reroll. PENDING/malformed rows fail closed.
     ================================================================== */
  var abilityQueueRollBusy=false;
  /* ⛔ R596 · ONE BURST, THE SHOP'S OWN PARTICLES. hoh-shop.css already drives
     .hoh-pack-particle from --pdx/--pdy/--pcol/--pdur, which is what the pack
     opening uses, so this adds a caller rather than a second particle system.
     The colours are PALETTES.mythic, which already IS Jan's brand pair, so the
     crystal and the game's own mythic celebration cannot drift apart. */
  /* one buy, whatever route reached it */
  function buyCrystals(qty, btn, opts){
    var W = window.HOHWallet; if(!W) return Promise.resolve();
    if(btn) btn.disabled = true;
    return W.buyCrystals(qty, opts || {}).then(function(r){
      if(r && r.ok){
        /* ⛔ R599: the crystal FLIES to the wallet. A purchase that only changes
           a number somewhere else asks the player to go and check; a crystal
           that travels from the button they pressed to the counter it landed in
           answers the question before it is asked.
           ⛔ R648: the TRUE count is handed over, not a pre-capped one. The cap
           on how many fly at once belongs inside flyCrystals with the reason
           for it, and the arrival bubble states the real number either way. */
        var bought=Math.max(1,Number(r.qty)||Number(qty)||1);
        if(btn){ try{ flyCrystals(btn, bought); }catch(_){ } }
        toast(bought === 1 ? 'One Mythic Crystal bought.' : bought + ' Mythic Crystals bought.');
      } else if(r && r.status === 'insufficient_hearts'){
        toast('Your Heart balance changed. No crystals were bought; choose an amount within the updated maximum.');
      } else if(r && r.status === 'locked'){
        toast('Open ' + W.unlockPacks() + ' packs to unlock crystals.');
      } else if(r && r.status === 'account_changed'){
        toast('Your account changed before the purchase finished. Reopen the Shop for the current account.');
      } else if(r && r.status === 'price_unavailable'){
        toast('Crystal purchases are temporarily unavailable. No Hearts were spent.');
      } else if(r && r.status === 'offline'){
        /* A lost reply is not proof that the transaction failed. Never say
           "nothing was spent" here: the database may have committed it. */
        toast('The purchase could not be confirmed. Refresh your wallet before choosing another amount.');
      } else if(r && r.status === 'pending_purchase'){
        toast('A previous crystal purchase still needs checking. Open the quantity menu to verify it safely.');
      } else if(r && r.status === 'invalid_reply'){
        toast('The purchase reply could not be verified. Check this saved purchase before trying again.');
      } else if(r && r.status === 'wallet_unverified'){
        toast('Your Heart balance is still being confirmed. No crystal purchase was sent; try again once it settles.');
      } else {
        toast('That purchase could not be completed. Nothing was spent.');
      }
      try{ if(hostEl) renderShop(hostEl); }catch(_){ }
      return r;
    });
  }

  /* ⛔ R597 · THE QUANTITY SHEET, reached only by the double press. */
  function crystalQuantitySheet(){
    var W = window.HOHWallet; if(!W) return;
    var openedFor=typeof W.accountEmail==='function'?W.accountEmail():'';
    function limits(){
      var price=Number(W.crystalPrice()),have=Math.max(0,Number(W.hearts())||0);
      var valid=isFinite(price)&&Math.floor(price)===price&&price>0;
      return {price:price,have:have,valid:valid,affordable:valid?Math.max(0,Math.floor(have/price)):0};
    }
    function messageSheet(html){
      var note=dustDialog('Mythic Crystals',html),close=note&&note.querySelector('.ffshop-dust-close');
      if(close)close.onclick=note.__dustClose;
      return note;
    }
    var pending=typeof W.pendingCrystalPurchase==='function'?W.pendingCrystalPurchase(openedFor):null;
    var first=limits(),price=first.price,have=first.have,affordable=first.affordable;
    /* A zero/malformed price has no finite Heart-based maximum. Fail closed
       rather than turning it into an unbounded free-purchase slider. */
    if(!first.valid&&!pending){
      messageSheet(
        '<button type="button" class="ffshop-dust-close" aria-label="Close">&times;</button>'+
        '<div class="ffshop-xsheet">'+
          '<div class="ffshop-xsheet-t">Purchases unavailable</div>'+
          '<div class="ffshop-xsheet-q"><span class="ffshop-xsheet-icon">'+W.icon(30)+'</span></div>'+
          '<div class="ffshop-xsheet-cost" role="status">The crystal price could not be verified. No Hearts can be spent until it is restored.</div>'+
        '</div>');
      return;
    }
    /* Never offer a quantity the current purse cannot reach. R605 deliberately
       has no old 12-item ceiling: the far-right stop is every whole crystal
       affordable from the Heart balance, while a sub-price remainder stays. */
    /* ⛔ R600: with fewer hearts than one crystal costs, the old floor of 1
       drew a working slider and a gold Buy button that the server would always
       refuse. A control that cannot succeed should not look like it can. Say
       what is missing instead. */
    if(affordable < 1&&!pending){
      messageSheet(
        '<button type="button" class="ffshop-dust-close" aria-label="Close">&times;</button>'+
        '<div class="ffshop-xsheet">'+
          '<div class="ffshop-xsheet-t">Not enough hearts</div>'+
          '<div class="ffshop-xsheet-q"><span class="ffshop-xsheet-icon">'+W.icon(30)+'</span></div>'+
          '<div class="ffshop-xsheet-cost">One crystal costs <b>'+price+'</b> hearts. You hold <b>'+have+'</b>.</div>'+
          '<p class="ffshop-xhow-hint">Explore the Hall pays hearts every day, and dusting a spare card pays more.</p>'+
        '</div>');
      return;
    }
    var start=pending?pending.qty:Math.min(affordable,4);
    var startMax=pending?pending.qty:affordable;
    var m = dustDialog('Mythic Crystals',
      '<button type="button" class="ffshop-dust-close" aria-label="Close">&times;</button>'+
      '<div class="ffshop-xsheet">'+
        '<div class="ffshop-xsheet-t">How many crystals</div>'+
        '<div class="ffshop-xsheet-q"><span class="ffshop-xsheet-icon">'+W.icon(30)+'</span><b id="ffshop-xqty">'+start+'</b></div>'+
        '<input type="range" id="ffshop-xrange" min="1" max="'+startMax+'" value="'+start+'" aria-label="How many crystals to buy">'+
        '<div class="ffshop-xsheet-cost" id="ffshop-xcost" role="status" aria-live="polite" aria-atomic="true"></div>'+
        '<button type="button" class="ffshop-open-btn ffshop-open-btn--primary" id="ffshop-xgo">Buy '+start+'<small>'+(start*price)+' hearts</small></button>'+
      '</div>');
    var box = m && m.querySelector ? m.querySelector('.ffshop-dust-box') : null;
    var range = document.getElementById('ffshop-xrange');
    var go = document.getElementById('ffshop-xgo');
    var statusMessage=pending?'A previous purchase was not confirmed. Its receipt is saved; checking it cannot charge twice.':'';
    var submitting=false,submissionIsCheck=!!pending;
    function sameAccount(){return !openedFor||typeof W.accountEmail!=='function'||W.accountEmail()===openedFor;}
    function paint(){
      if(!range||!go)return;
      if(!sameAccount()){
        range.disabled=true;go.disabled=true;
        var changed=document.getElementById('ffshop-xcost');
        if(changed)changed.textContent='The signed-in account changed. Close and reopen the Shop before buying.';
        return;
      }
      /* A possibly committed request comes BEFORE current affordability. A
         background sync may already show zero Hearts because this exact buy
         landed; disabling Check in that state would strand its receipt. Keep
         the original quantity/cost visible and replay its id unchanged. */
      if(pending){
        var pn=pending.qty,pc=Math.max(0,Number(pending.cost)||0);
        range.min=String(pn);range.max=String(pn);range.value=String(pn);range.disabled=true;
        var pq=document.getElementById('ffshop-xqty');if(pq)pq.textContent=String(pn);
        var pendingCopy=document.getElementById('ffshop-xcost');
        if(pendingCopy)pendingCopy.innerHTML=submitting
          ? '<span>'+(submissionIsCheck?'Checking the saved receipt&hellip;':'Confirming this purchase&hellip;')+'</span><br><b>'+pc.toLocaleString()+
            '</b> Hearts for '+pn.toLocaleString()+' crystal'+(pn===1?'':'s')+'.'
          : '<span>'+statusMessage+'</span><br><b>'+pc.toLocaleString()+
            '</b> Hearts requested for '+pn.toLocaleString()+' crystal'+(pn===1?'':'s')+'. You may close this and check later.';
        go.disabled=submitting;
        go.innerHTML=submitting
          ? (submissionIsCheck?'Checking&hellip;':'Buying&hellip;')+'<small>please wait</small>'
          : 'Check purchase<small>same saved request</small>';
        return;
      }
      var now=limits();price=now.price;have=now.have;affordable=now.affordable;
      if(!now.valid||affordable<1){
        range.max='1';range.value='1';range.disabled=true;go.disabled=true;
        var empty=document.getElementById('ffshop-xcost');
        if(empty)empty.innerHTML=now.valid
          ? 'One crystal costs <b>'+price.toLocaleString()+'</b> Hearts. You now hold <b>'+have.toLocaleString()+'</b>.'
          : 'The crystal price cannot be verified. No Hearts were spent.';
        return;
      }
      range.max=String(affordable);range.disabled=!!pending;
      var n=Math.max(1,Math.min(affordable,parseInt(range.value,10)||1));range.value=String(n);
      var cost=n*price,remainder=Math.max(0,have-cost);
      var q = document.getElementById('ffshop-xqty'); if(q) q.textContent = String(n);
      var c = document.getElementById('ffshop-xcost');
      if(c)c.innerHTML=(statusMessage?'<span>'+statusMessage+'</span><br>':'')+
        '<b>'+cost.toLocaleString()+'</b> Hearts of your '+have.toLocaleString()+
        (n===affordable?' &middot; maximum affordable':'')+' &middot; '+remainder.toLocaleString()+' remain';
      go.disabled=false;
      go.innerHTML=(pending?'Check purchase':'Buy '+n)+'<small>'+cost.toLocaleString()+' Hearts</small>';
    }
    if(range) range.oninput=function(){statusMessage='';paint();};
    paint();
    if(go) go.onclick = function(){
      if(submitting)return;
      if(!sameAccount()){paint();return;}
      submissionIsCheck=!!pending;
      if(!pending){
        var now=limits(),n=Math.max(1,parseInt(range.value,10)||1);
        if(!now.valid||n>now.affordable){statusMessage='Your Heart balance changed. Choose an amount within the updated maximum.';paint();return;}
        pending={qty:n,cost:n*now.price,id:reqId('crystal-buy')};
      }
      submitting=true;paint();
      /* Reuse the logical request after an uncertain reply. R605 replays the
         settled two-leg receipt, so "Check purchase" can never charge twice. */
      buyCrystals(pending.qty,go,{requestId:pending.id}).then(function(r){
        submitting=false;
        if(r&&r.ok){pending=null;try{m.__dustClose();}catch(_){ }return;}
        if(r&&r.status==='pending_purchase'&&r.pending)pending=r.pending;
        else if(!r||['offline','invalid_reply','account_changed'].indexOf(r.status)<0)pending=null;
        statusMessage=r&&r.status==='insufficient_hearts'
          ? 'Your Heart balance changed. No crystals were bought; the maximum is updated below.'
          : r&&r.status==='offline'
            ? 'Not confirmed. Check this same purchase before choosing a new amount.'
            : r&&r.status==='invalid_reply'
              ? 'The reply was incomplete. Check this same saved purchase; do not start another.'
            : r&&r.status==='account_changed'
              ? 'The signed-in account changed. No result was applied to this Shop.'
              : 'The purchase did not complete. Your current balance is shown below.';
        paint();
      });
    };
    function walletChanged(){
      if(!m||!m.parentNode){
        document.removeEventListener('hoh:hearts-bumped',walletChanged);
        document.removeEventListener('hoh:wallet-synced',walletChanged);
        return;
      }
      if(!pending)statusMessage='';paint();
    }
    try{
      document.addEventListener('hoh:hearts-bumped',walletChanged);
      document.addEventListener('hoh:wallet-synced',walletChanged);
    }catch(_){ }
    var closeSheet=m&&m.__dustClose;
    if(m&&closeSheet)m.__dustClose=function(){
      try{document.removeEventListener('hoh:hearts-bumped',walletChanged);document.removeEventListener('hoh:wallet-synced',walletChanged);}catch(_){ }
      closeSheet();
    };
    var x = box && box.querySelector('.ffshop-dust-close');
    if(x) x.onclick = m.__dustClose;
  }

  /* ⛔ R603 · THE PANEL WAS TELLING PLAYERS SOMETHING FALSE.
     Jan: "crystals should NOT be able to tier it down". He was right to
     challenge it, and the fix was not a code change: THE GAME ALREADY WORKS
     THAT WAY and always has. The reroll model is

         rarity = current;  if (rng < chance) rarity = current + 1;

     a single upward chance per tier with no downward branch anywhere, and the
     reply carries `floor: current`. A reroll stays or it climbs. I had written
     "or one tier down" into the explainer and it was simply untrue, which is
     the worst thing an explainer can be: a player reading it would have been
     afraid to spend a crystal for a reason that does not exist.
     The FramedFantasy-and-Mythic line comes out at Jan's instruction. The
     tier cap it described is still real in the code (non-FF clamps one tier
     lower), it just no longer appears here.
     R708: a submitted owner's first Active now starts at exact Epic and the
     receipt-backed crystal path may keep that tier or improve it. */
  function crystalHowPanel(){
    var W = window.HOHWallet; if(!W) return;
    var D = window.HOHContractData;
    /* the ladder is the whole point of the panel: it shows the floor, and it
       shows that the only direction is up */
    var TIERS = [['Common','#cfc7da'],['Uncommon','#7ee6a0'],['Rare','#8fc0ff'],
                 ['Epic','#d6a4ff'],['Legendary','#ffd27a'],['Mythic','#ef94ff']];
    var ladder = TIERS.map(function(t,i){
      return '<span class="ffshop-xrung'+(i===2?' is-here':'')+'" style="--rc:'+t[1]+'">'+
        '<i></i><small>'+t[0]+'</small></span>';
    }).join('');
    var oddsRows = TIERS.slice(0, -1).map(function(t,i){
      return '<tr data-crystal-tier="'+i+'"><th scope="row">'+t[0]+' &rarr; '+TIERS[i+1][0]+'</th>'+
        '<td data-crystal-base></td><td data-crystal-boost></td></tr>';
    }).join('');
    function row(icon, title, body){
      return '<div class="ffshop-xrow"><span class="ffshop-xrow-i" aria-hidden="true">'+icon+'</span>'+
        '<span class="ffshop-xrow-t"><b>'+title+'</b><span>'+body+'</span></span></div>';
    }
    var SVG = {
      free:  '<svg viewBox="0 0 24 24"><path d="M12 3v18M3 12h18"/></svg>',
      up:    '<svg viewBox="0 0 24 24"><path d="M12 20V5M6 11l6-6 6 6"/></svg>',
      lock:  '<svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>',
      price: '<svg viewBox="0 0 24 24"><path d="M12 21s-7-4.4-7-10.2C5 7.6 7 6 9.3 6c1.2 0 2.2.5 2.7 1.4C12.5 6.5 13.5 6 14.7 6 17 6 19 7.6 19 10.8 19 16.6 12 21 12 21Z"/></svg>'
    };
    var m = dustDialog('How crystals work',
      '<button type="button" class="ffshop-dust-close" aria-label="Close">&times;</button>'+
      '<div class="ffshop-xhow">'+
        '<div class="ffshop-xhow-hero">'+W.icon(64)+
          '<p class="ffshop-xhow-lead">One crystal rerolls one ability on one card.</p></div>'+
        '<div class="ffshop-xladder" role="img" aria-label="A reroll holds your current tier as a floor and can only climb from it.">'+
          ladder+'<span class="ffshop-xladder-arrow" aria-hidden="true"></span></div>'+
        '<p class="ffshop-xladder-cap">Your tier is the floor. A reroll never falls below it.</p>'+
        '<div class="ffshop-xodds">'+
          '<label class="ffshop-xodds-kind">Card type<select data-crystal-kind><option value="regular">Regular Hall card</option><option value="ff">FramedFantasy portrait</option></select></label>'+
          '<label class="ffshop-xodds-tickets">Tickets on this hero <output data-crystal-count>10</output>'+
            '<input type="range" min="0" max="50" step="1" value="10" data-crystal-tickets aria-label="Preview tickets applied to a hero"></label>'+
          '<table class="ffshop-xodds-table"><caption>Chance to climb one tier per reroll</caption>'+
            '<thead><tr><th scope="col">Upgrade</th><th scope="col">No tickets</th><th scope="col" data-crystal-heading>10 tickets</th></tr></thead><tbody>'+oddsRows+'</tbody></table>'+
          '<p class="ffshop-xodds-example" data-crystal-example aria-live="polite"></p>'+
        '</div>'+
        row(SVG.free,  'The first roll is free',
            'An empty slot always rolls for nothing. A crystal only pays to reroll an ability you already have.')+
        row(SVG.up,    'Tickets improve your odds',
            'Each ticket adds 2% of the base chance, up to double at 50 tickets. Apply tickets from the hero\'s card. Tickets from other players count too.')+
        row(SVG.lock,  'Your submitted hero starts Epic',
            'Its first Active ability is always Epic. A crystal can keep that tier or move it up one.')+
        row(SVG.price, 'Where crystals come from',
            '<b>'+W.crystalPrice()+' hearts</b> each in the Shop, and one free every day.')+
      '</div>');
    m.classList.add('ffshop-crystal-help');
    m.querySelector('.ffshop-dust-close').onclick = m.__dustClose;
    var ticketsInput = m.querySelector('[data-crystal-tickets]');
    var kindInput = m.querySelector('[data-crystal-kind]');
    function paintOdds(){
      var tickets = Math.max(0, Math.min(50, parseInt(ticketsInput.value, 10) || 0));
      var isFF = kindInput.value === 'ff';
      var ready = D && typeof D.abilityTierChancePct === 'function';
      function chance(tier, count){
        return Math.min(100, Math.max(0, D.abilityTierChancePct(tier, count, isFF)));
      }
      m.querySelector('[data-crystal-count]').textContent = String(tickets);
      m.querySelector('[data-crystal-heading]').textContent = tickets+' ticket'+(tickets === 1 ? '' : 's');
      Array.prototype.forEach.call(m.querySelectorAll('[data-crystal-tier]'), function(row){
        var tier = parseInt(row.getAttribute('data-crystal-tier'), 10);
        row.hidden = tier === 4 && !isFF;
        row.querySelector('[data-crystal-base]').textContent = ready ? chance(tier, 0)+'%' : '—';
        row.querySelector('[data-crystal-boost]').textContent = ready ? chance(tier, tickets)+'%' : '—';
      });
      m.querySelector('[data-crystal-example]').textContent = ready
        ? 'Rare → Epic: '+chance(2, 0)+'% becomes '+chance(2, tickets)+'% with '+tickets+' ticket'+(tickets === 1 ? '' : 's')+'. Preview only; no tickets are spent.'
        : 'Upgrade chances are still loading. Reopen this guide in a moment.';
    }
    ticketsInput.addEventListener('input', paintOdds);
    kindInput.addEventListener('change', paintOdds);
    paintOdds();
  }

  /* ---------- R597 · THE MYTHIC CRYSTAL MODULE ---------- */
  /* ⛔ EVERY NUMBER COMES FROM THE WALLET, WHICH MIRRORS THE SERVER. The lock,
     the price, the held count and whether today's free crystal is still there
     are all decided by the database and read here. A grey button is a courtesy;
     hoh_crystal_buy and hoh_crystal_claim_daily enforce the same rules
     themselves, so this surface cannot be talked past. */
  function crystalModuleHtml(){
    var W = window.HOHWallet;
    if(!W) return '';
    var unlocked = W.unlocked(), held = W.crystals(), spent = W.crystalsSpent();
    var need = Math.max(0, W.unlockPacks() - W.packsOpened());
    var price = W.crystalPrice();
    var dailyReady = W.dailyCrystalReady();
    var pending = unlocked&&typeof W.pendingCrystalPurchase==='function'?W.pendingCrystalPurchase():null;
    var art = '<span class="ffshop-xtal-art'+(unlocked?'':' is-locked')+'">'+
      W.icon(96, unlocked)+
      /* ambient sparks only on the unlocked module. ⛔ A LOCKED THING THAT
         SHIMMERS IS ASKING TO BE TAPPED, and this one cannot be. */
      (unlocked
        ? '<span class="ffshop-xspark s1" aria-hidden="true"></span>'+
          '<span class="ffshop-xspark s2" aria-hidden="true"></span>'+
          '<span class="ffshop-xspark s3" aria-hidden="true"></span>'+
          '<span class="ffshop-xspark s4" aria-hidden="true"></span>'
        : '')+
      '</span>';
    var buy = unlocked
      ? (pending
        ? '<button type="button" class="ffshop-open-btn ffshop-open-btn--primary ffshop-xbuy" data-xtal-buy="1" data-xtal-pending="1">Check purchase<small>'+
            pending.qty.toLocaleString()+' crystal'+(pending.qty===1?'':'s')+' &middot; '+pending.cost.toLocaleString()+' hearts</small></button>'
        : '<button type="button" class="ffshop-open-btn ffshop-open-btn--primary ffshop-xbuy" data-xtal-buy="1">Buy a Crystal<small>'+price+' hearts</small></button>')
      : '<button type="button" class="ffshop-open-btn ffshop-open-btn--primary is-locked ffshop-xbuy-locked" disabled>Buy a Crystal<small>unlocks at '+W.unlockPacks()+' packs &middot; '+need+' to go</small></button>';
    /* ⛔ THE DAILY STAYS VISIBLE WHILE LOCKED. A reward nobody can see is a
       reward nobody works toward, and hiding it means a new player never learns
       the free crystal exists. Visible and out of reach beats absent.
       R737 (Jan): the daily control is the same in every shelf column: one
       --daily button with ready, claimed (is-claimed), and locked (is-locked)
       states. No FREE ribbon, no green; the label and small say what you get. */
    var daily = unlocked
      ? (dailyReady
        ? '<button type="button" class="ffshop-open-btn ffshop-open-btn--daily" data-xtal-daily="1">Claim Daily Crystal<small>one a day &middot; saved to Wallet</small></button>'
        : '<button type="button" class="ffshop-open-btn ffshop-open-btn--daily is-claimed" disabled>Crystal claimed<small>today &middot; '+dailyResetText()+'</small></button>')
      : '<button type="button" class="ffshop-open-btn ffshop-open-btn--daily is-locked ffshop-xbuy-locked" disabled>Claim Daily Crystal<small>unlocks at '+W.unlockPacks()+' packs</small></button>';
    /* the tracker Jan asked for: what you hold and what you have spent */
    var stat = unlocked
      ? '<div class="ffshop-item-stat"><b>'+held+'</b> held &middot; <b>'+spent+'</b> spent</div>'
      : '<div class="ffshop-item-stat">Crystals reroll a rolled ability. <b>'+W.packsOpened()+'</b> of '+W.unlockPacks()+' packs opened</div>';
    return '<div class="ffshop-item ffshop-item--xtal'+(unlocked?'':' is-locked')+'" id="ffshop-xtal-mod">'+
      '<div class="ffshop-item-art">'+art+'</div>'+
      '<div class="ffshop-item-name">Mythic Crystals</div>'+
      '<div class="ffshop-item-sub">One crystal rerolls one ability. Rarity can climb.</div>'+
      '<button type="button" class="ffshop-how-link" data-xtal-how="1">How crystals work</button>'+
      '<div class="ffshop-btns">'+buy+daily+'</div>'+
      stat+
    '</div>';
  }
  function bindCrystalBuyButton(buyBtn){
    if(!buyBtn)return;
    /* Recovery must not hide behind the ordinary double-press shortcut. A
       saved request may already have charged the account, so the primary
       action says Check purchase and one click, tap, Enter, or Space opens the
       exact-id receipt sheet. Native button keyboard behaviour supplies the
       click; the normal single/double gesture remains unchanged otherwise. */
    if(buyBtn.hasAttribute('data-xtal-pending')){
      buyBtn.onclick=function(){crystalQuantitySheet();};
      return;
    }
    var lastTap = 0, singleTimer = 0;
    buyBtn.onclick = function(e){
      var now = (e && e.timeStamp) || Date.now();
      if(now - lastTap < 320){
        clearTimeout(singleTimer); lastTap = 0;
        crystalQuantitySheet();
        return;
      }
      lastTap = now;
      singleTimer = setTimeout(function(){ buyCrystals(1, buyBtn); }, 320);
    };
  }
  /* ⛔ R648 · THE BURST LEFT THE BUTTON, AND THAT WAS A BUG FIX.
     This used to append its particle layer INSIDE `el`. Every caller here is
     a button that its own success handler then repaints away in the same
     tick, so on the free daily claim the layer was destroyed before a single
     frame of it painted, and a button with overflow of its own would have
     clipped whatever survived. It spawns at the element's centre on the
     body-level layer now, which is the same fix flyCrystals needed and the
     same helper, so there is still exactly one particle system here. */
  function crystalBurst(el,n){
    /* same reason as flyCrystals: a repaint can detach the button first, and
       a detached node measures 0x0 at the top-left corner of the screen */
    if(motionReduced()||!el||!el.isConnected)return;
    try{
      var r=el.getBoundingClientRect();
      if(!r.width||!r.height)return;
      xtalSparks(r.left+r.width/2, r.top+r.height/2, n||12, 30, 780);
    }catch(_){ }
  }
  function abilityQueueFreshPending(row){
    return !!(row&&row.id==='PENDING'&&row.prevId==null&&row.prevR==null&&(row.price|0)===0);
  }
  /* ⛔ ====== R609 · A CARD WITH NO LIVE HERO CANNOT BE ROLLED ======
     Jan: "there's also characters that I purposely have removed from the hall
     using my debug privileges ... right now those characters are still showing
     up in the ability re-roll. And when you try to click roll both abilities,
     it just says roll did not finish."

     BOTH HALVES ARE ONE BUG, AND IT IS A DEAD FILTER.
     `collectionSource` has carried an `_isHiddenHero` guard since R432, and its
     own comment states the rule: "Present AND hidden is the only condition that
     removes a tile", checked against `hohGetAllCharactersFull()` so a
     not-yet-loaded hero is never mistaken for a hidden one. That was sound when
     it was written. It is not now: the boot fetch in hoh-section-core.js reads
        .eq('approved',true).or('is_hidden.is.null,is_hidden.eq.false')
     so a hidden row is EXCLUDED FROM THE QUERY. `allCharacters` therefore holds
     no hidden hero for anybody, `_hiddenIds` builds empty every single time,
     and that filter has never removed one card since the day the query was
     tightened. Present-and-hidden became unreachable.

     The failed roll is the same fact one layer down. `heroById` reads the
     visible pool, so an unresolvable card hands `rollCurrent` a `heroLike` of
     `{character_class:'Fighter', race:''}` — a guess, not the hero — and the
     ability id picked from that guess is what the server refuses. "That roll
     did not finish" is the client's catch-all for a refusal it never explains.

     So the queue asks one honest question before it offers a card: can this
     client resolve a live, visible hero behind it? Three sources answer, in
     descending confidence, and NOTHING is judged until the Hall pool has
     actually loaded — the exact hazard R432 named, kept. */
  function abilityQueueHeroState(id){
    var key=String(id||'');
    if(!key)return 'gone';
    var full=[];
    try{ full=(typeof window.hohGetAllCharactersFull==='function')?(window.hohGetAllCharactersFull()||[]):[]; }catch(_){ full=[]; }
    /* Nothing loaded yet: refuse to judge rather than blank the queue. */
    if(!full.length)return 'unknown';
    for(var i=0;i<full.length;i++){
      var row=full[i];
      if(!row||String(row.id)!==key)continue;
      return row.is_hidden===true ? 'hidden' : 'live';
    }
    /* The lazily loaded hidden set (R170) is only present when a god-mode
       review has asked for it. When it is here it names the reason exactly. */
    try{
      var hid=window.__hohHidden;
      if(Array.isArray(hid)){
        for(var h=0;h<hid.length;h++){ if(hid[h]&&String(hid[h].id)===key)return 'hidden'; }
      }
    }catch(_){ }
    /* Loaded pool, no row: hidden, deleted, or never approved. All three mean
       the same thing to this screen — there is no hero to roll an ability for. */
    return 'gone';
  }
  function abilityQueueCandidates(){
    var D=window.HOHContractData, cs=collectionSource(), cards=[], blocked={pending:0,malformed:0,noHero:0}, slots=0,recoveries=0;
    if(!D)return {cards:cards,blocked:blocked,slots:0,recoveries:0};
    cs.allCards.forEach(function(c){
      var id=String(c&&c.character_id||''), authoritative=id&&collEntry(id);
      /* Profile can temporarily synthesize a just-submitted row while its
         collection refetch catches up. Rolling waits for the server row. */
      if(!id||!authoritative||dustRemaining(authoritative)<1)return;
      /* R609: the hero gate, before any gear is read. A card the Hall cannot
         resolve is counted and named, never silently dropped and never offered
         a roll that is built on a guessed class. */
      var heroState=abilityQueueHeroState(id);
      if(heroState==='hidden'||heroState==='gone'){blocked.noHero++;return;}
      var key=(authoritative.ff?'ff:':'own:')+id, g={}, gm={};
      try{g=D.gearFor(key)||{};gm=D.gearMetaFor?D.gearMetaFor(key)||{}:{};}catch(_){return;}
      var unsafePending=['active','passive'].some(function(slot){
        var row=gm[slot];return !!(row&&row.id==='PENDING'&&!abilityQueueFreshPending(row));
      });
      /* A zero-price PENDING row with no predecessor is a fresh rarity whose
         response was interrupted. It belongs in the queue so the atomic
         first-roll door can replay and finalize it; every other PENDING shape
         remains protected and excluded. */
      if(unsafePending){blocked.pending++;return;}
      var malformed=false;
      ['active','passive'].forEach(function(slot){
        var row=gm[slot];
        if(row&&row.id!=='PENDING'&&(!String(row.id||'').trim()||row.r==null||!isFinite(row.r)||row.r<0||row.r>5))malformed=true;
      });
      if(malformed){blocked.malformed++;return;}
      var missing=[];
      if(!(g.active&&g.active.id&&g.active.id!=='PENDING'))missing.push('active');
      /* R630: a second slot below level 30 is not missing, it is not open.
         ⛔ IT HAS TO BE FILTERED HERE TOO, not only in the queue's own
         missing(). This function decides which cards ENTER Quick Review and
         how many slots it promises in its own header. Without this a level-3
         card whose one ability is already rolled would be pulled into the
         queue for a slot it cannot fill, and the header would count a roll
         that can never happen. */
      if(!(g.passive&&g.passive.id&&g.passive.id!=='PENDING')){
        var _lv=1;
        try{ _lv=Math.max(1,(D.effectiveLevel?D.effectiveLevel(key):1)|0); }catch(_){ _lv=1; }
        if(_lv>=30)missing.push('passive');
      }
      /* An atomic crystal reply can be lost after the transaction commits.
         Its durable request journal is an ACTIONABLE saved result, even when
         both visible slots are already filled. Keep that card in Quick Review
         so the promised Verify door can actually be reached; it replays the
         same receipt and cannot charge a second crystal. */
      var recovering=[];
      if(D.crystalRollPending){
        ['active','passive'].forEach(function(slot){
          try{if(D.crystalRollPending(key,slot))recovering.push(slot);}catch(_){ }
        });
      }
      if(!missing.length&&!recovering.length)return;
      var hero=heroById(id)||{}, hearts=Math.max(hero.hearts||0,authoritative.hearts||0);
      slots+=missing.length;
      recoveries+=recovering.length;
      cards.push({
        id:id,key:key,snap:authoritative,hero:hero,missing:missing,recovering:recovering,
        cardRarity:tierRank(tierFor(hearts)),cardTier:tierFor(hearts),
        name:hero.name||authoritative.name||'Hero'
      });
    });
    return {cards:cards,blocked:blocked,slots:slots,recoveries:recoveries};
  }

  function openAbilityRollQueue(host){
    if(abilityQueueRollBusy){packSay('Roll still saving','Wait for the current ability to finish before reopening Quick review.');return;}
    if(busy){packSay('One moment','Another card action is still finishing.');return;}
    var old=document.querySelector('.ffshop-rollq');
    if(old&&typeof old.__dustClose==='function')old.__dustClose();
    var scope=captureShopAccount(), D=window.HOHContractData;
    if(!scope.account||!scope.pin){packSay('Sign in first','Your ability rolls are saved to your Hall account.');return;}
    if(!D||!D.loadGear){packSay('Abilities are still loading','Try the quick review again in a moment.');return;}
    var modal=dustDialog('Roll abilities','<div class="ffshop-dust-loading ffshop-rollq-loading" role="status" aria-live="polite"><span class="ffshop-dust-orb" aria-hidden="true"></span><b>Checking your ability rolls</b><span>Verifying every card before the review begins…</span></div>');
    modal.classList.add('ffshop-rollq');
    var box=modal.querySelector('.ffshop-dust-manager-box');
    box.classList.add('ffshop-rollq-box');
    /* The visible status line is replaced whenever the card repaints. Keep a
       single live region outside that disposable subtree so a landed result
       is announced once and can never vanish mid-utterance. */
    var queueLive=document.createElement('div'),queueLiveSeq=0;
    queueLive.className='visually-hidden ffshop-rollq-live';
    queueLive.setAttribute('role','status');queueLive.setAttribute('aria-live','polite');queueLive.setAttribute('aria-atomic','true');
    modal.appendChild(queueLive);
    function queueAnnounce(text){
      var seq=++queueLiveSeq;queueLive.textContent='';
      requestAnimationFrame(function(){try{if(seq===queueLiveSeq&&queueLive.isConnected)queueLive.textContent=text||'';}catch(_){ }});
    }
    function guard(){
      if(!modal.isConnected)return false;
      if(shopAccountIsCurrent(scope))return true;
      if(modal.__dustClose)modal.__dustClose();
      packSay('Account changed','Open Roll abilities again for this account.');return false;
    }
    function load(){
      if(!guard())return;
      box.innerHTML='<div class="ffshop-dust-loading ffshop-rollq-loading" role="status" aria-live="polite" tabindex="-1"><span class="ffshop-dust-orb" aria-hidden="true"></span><b>Checking your ability rolls</b><span>Verifying every card before the review begins…</span></div>';
      var loading=box.querySelector('.ffshop-rollq-loading');
      requestAnimationFrame(function(){try{if(loading&&loading.isConnected)loading.focus({preventScroll:true});}catch(_){ }});
      /* ⛔ R638: THIS SCREEN GATES ON A LEVEL IT NEVER LOADED. The second
         ability slot opens at level 30, and abilityQueueCandidates asks
         D.effectiveLevel for it. That reads the card-XP cache, which nothing
         on this path fills, so an unwarmed or failed cache answers level 1
         for every card and every unrolled Passive is filtered out. The screen
         then paints its SUCCESS state, "Every card is rolled", over a
         collection of level-40 heroes with every Passive still open. A
         false negative wearing a success face is worse than an error.
         The gear half of the same question was already loaded and verified
         here; the level half is now held to the identical standard. */
      Promise.all([fetchState(),D.loadGear(true),
                   D.loadCardXp?D.loadCardXp(true):null]).then(function(){
        if(!modal.parentNode||!guard())return;
        /* loadGear resolves after recording a verification failure. Throwing
           from this fulfilment callback bypasses the sibling rejection
           handler and strands the sheet on Checking. Route the resolved-fail
           state to the same recoverable surface as a rejected request. */
        if(D.gearLoadFailed&&D.gearLoadFailed()){showLoadError();return;}
        if(D.cardXpLoadFailed&&D.cardXpLoadFailed()){showLoadError();return;}
        showSetup(abilityQueueCandidates());
      },function(){showLoadError();});
    }
    function showLoadError(){
      if(!modal.parentNode)return;
      box.innerHTML='<button type="button" class="ffshop-dust-close" aria-label="Close">&times;</button><div class="ffshop-rollq-state" role="alert"><span class="ffshop-rollq-state-icon" aria-hidden="true">&#8635;</span><h3>Abilities and levels could not be verified</h3><p>Your cards are safe. Reconnect and retry before rolling anything.</p><button type="button" class="ffshop-rollq-primary" data-rollq-retry>Retry</button></div>';
      box.querySelector('.ffshop-dust-close').onclick=modal.__dustClose;
      var retry=box.querySelector('[data-rollq-retry]');retry.onclick=load;
      requestAnimationFrame(function(){try{if(retry&&retry.isConnected)retry.focus({preventScroll:true});}catch(_){ }});
    }
    function showSetup(result){
      var cards=result.cards, blocked=(result.blocked.pending+result.blocked.malformed);
      /* R609: a card whose hero has left the Hall is a different fact from a
         card that is protected mid-recovery, so it gets its own sentence. Both
         are counted out loud, because a queue that silently shrinks reads as a
         queue that lost your cards. */
      var gone=result.blocked.noHero|0;
      var goneNote=gone?(gone+' card'+(gone===1?' is':'s are')+' waiting on a hero that is no longer in the Hall, so '+(gone===1?'it':'they')+' cannot be rolled.'):'';
      if(!cards.length){
        box.innerHTML='<button type="button" class="ffshop-dust-close" aria-label="Close">&times;</button><div class="ffshop-rollq-state"><span class="ffshop-rollq-state-icon is-done" aria-hidden="true">&#10022;</span><h3>Every card is rolled</h3><p>'+ (blocked?blocked+' card'+(blocked===1?' is':'s are')+' waiting for a safe roll recovery. Nothing uncertain was changed.':'There are no unrolled ability slots in your verified collection.') + (goneNote?' '+goneNote:'') +'</p><button type="button" class="ffshop-rollq-primary" data-rollq-done>Done</button></div>';
        box.querySelectorAll('.ffshop-dust-close,[data-rollq-done]').forEach(function(b){b.onclick=modal.__dustClose;});
        queueAnnounce(blocked?'Abilities verified. Some saved cards still need a safe recovery.':'Abilities verified. Every card is rolled.');
        var done=box.querySelector('[data-rollq-done]');
        requestAnimationFrame(function(){try{if(done&&done.isConnected)done.focus({preventScroll:true});}catch(_){ }});
        return;
      }
      box.innerHTML=
        '<button type="button" class="ffshop-dust-close" aria-label="Close">&times;</button>'+
        '<div class="ffshop-rollq-setup">'+
          '<img src="'+FF_LOGO+'" alt="" aria-hidden="true" class="ffshop-rollq-logo">'+
          '<div class="ffshop-rollq-eyebrow">Quick ability review</div><h3>Roll your cards</h3>'+
          '<p class="ffshop-rollq-lead">'+(result.recoveries?'Verify saved crystal results first, then roll any missing Active and Passive abilities.':'One card at a time. Roll every missing Active and Passive ability, then favorite, dust, or move on.')+'</p>'+
          '<div class="ffshop-rollq-summary" aria-label="'+cards.length+' '+(cards.length===1?'card':'cards')+', '+result.slots+' unrolled '+(result.slots===1?'ability':'abilities')+', and '+result.recoveries+' saved '+(result.recoveries===1?'result':'results')+' to verify"><b>'+cards.length+'</b><span>'+(cards.length===1?'card':'cards')+'</span><i></i><b>'+result.slots+'</b><span>unrolled</span>'+(result.recoveries?'<i></i><b>'+result.recoveries+'</b><span>saved to verify</span>':'')+'</div>'+
          /* ⛔ ====== R567 · THE CHOICE IS THE START ======
             Jan: "instead of making them click start rolling remove that
             button and whatever they click on to select just opens the roll
             your cards."
             The radio group and the button asked for the same decision twice.
             Nothing on this screen happens BETWEEN picking an order and
             starting — there is no second field to fill, nothing to review,
             no cost to confirm — so the button was a second tap that could
             only ever repeat the first. Picking an order IS the start.
             ⛔ THEY ARE BUTTONS NOW, NOT RADIOS. A radio that navigates is a
             lie to every assistive technology: it announces "1 of 2 selected"
             and a keyboard user arrowing through the group to hear the second
             option would have already launched the first. Two buttons say
             what they do, and Enter, Space and a tap all mean the same thing. */
          '<div class="ffshop-rollq-order" role="group" aria-label="Choose your starting order">'+
            '<div class="ffshop-rollq-order-lede">Choose your starting order</div>'+
            '<button type="button" data-rollq-start="high"><span><b>Highest rarity first</b><small>Review your rarest card frames first</small></span><em aria-hidden="true">&#8595;</em></button>'+
            '<button type="button" data-rollq-start="low"><span><b>Lowest rarity first</b><small>Clear common card frames first</small></span><em aria-hidden="true">&#8593;</em></button>'+
          '</div>'+
          (blocked?'<div class="ffshop-rollq-note">'+blocked+' uncertain '+(blocked===1?'card is':'cards are')+' protected and left out.</div>':'')+
          (goneNote?'<div class="ffshop-rollq-note">'+goneNote+'</div>':'')+
        '</div>';
      box.querySelector('.ffshop-dust-close').onclick=modal.__dustClose;
      box.querySelectorAll('[data-rollq-start]').forEach(function(btn){
        btn.onclick=function(){
          /* one guard for the whole screen: a double tap, or a tap landing on
             the second option while the first is already building the queue,
             must not sort and mount twice */
          if(box.__rollqStarting)return;
          box.__rollqStarting=1;
          var dir=btn.getAttribute('data-rollq-start')==='low'?1:-1;
          cards.sort(function(a,b){return dir*(a.cardRarity-b.cardRarity)||String(a.name).localeCompare(String(b.name));});
          runQueue(cards,result.slots+result.recoveries);
        };
      });
      queueAnnounce('Abilities verified. Choose which rarity to review first.');
      var firstOrder=box.querySelector('[data-rollq-start]');
      requestAnimationFrame(function(){try{if(firstOrder&&firstOrder.isConnected)firstOrder.focus({preventScroll:true});}catch(_){ }});
    }

    function runQueue(cards,totalSlots){
      var state={cards:cards,index:0,position:0,total:cards.length,totalSlots:totalSlots,rolling:false,advancing:false,
        rolled:{},favorites:0,dusted:0,skipped:0,error:'',status:'',suppressClickUntil:0};
      box.classList.add('is-running');
      var resizeTimer=0;
      function onQueueResize(){
        if(!modal.isConnected){window.removeEventListener('resize',onQueueResize);return;}
        if(resizeTimer)clearTimeout(resizeTimer);
        resizeTimer=setTimeout(function(){if(state.fitQueueCard)state.fitQueueCard(true);},90);
      }
      window.addEventListener('resize',onQueueResize,{passive:true});
      var closeQueue=modal.__dustClose;
      modal.__dustClose=function(){
        if(resizeTimer)clearTimeout(resizeTimer);window.removeEventListener('resize',onQueueResize);
        if(closeQueue)closeQueue();
      };
      function current(){return state.cards[state.index]||null;}
      function gear(card){try{return D.gearFor(card.key)||{};}catch(_){return {};}}
      /* ============ R630 (Jan) · ONE ABILITY UNTIL LEVEL 30 ============
         "I actually want the 2nd ability slot to be locked so each character
         can only roll 1 ability until they hit level 30."

         THE GATE IS THE LEVEL THE HERO FIGHTS AT, `D.effectiveLevel`, and not
         either of the two numbers the screens have been showing separately.
         That is deliberate: a lock that reads a different level from the one
         printed above it is a lock the player cannot predict.

         ⛔ THE LOCK IS ON ROLLING, NEVER ON HOLDING. A card that already
         carries a second ability keeps it and keeps rerolling it, whatever
         its level. Taking away something a player already earned is not what
         "can only roll 1" asks for, and there is no way to give it back. */
      var SECOND_SLOT = 'passive';
      /* R645: read from the ENGINE, the referee that enforces it. This number
         used to live here and again in hoh-contract-ui.js and nowhere else, so
         the screen and the fight could disagree about when a second ability
         starts working. */
      var SECOND_SLOT_LEVEL = (function () {
        try { return (window.HOHContract && window.HOHContract.SECOND_SLOT_LEVEL | 0) || 30; }
        catch (_) { return 30; }
      })();
      function cardLevel(card){
        try{ return Math.max(1, (D.effectiveLevel ? D.effectiveLevel(card.key) : 1) | 0); }
        catch(_){ return 1; }
      }
      /* ⛔ R645 (Jan): "if they have two abilities, just literally lock the
         second one until they hit the correct level." The line here read
         `already held: never revoked`, which is the opposite of the ruling and
         meant an under-levelled card with an old-system second ability showed
         it as live while the engine ran it. The ability is never taken away.
         It is inert until the level, and the card says so on its face. */
      function slotLocked(card, slot){
        if(slot !== SECOND_SLOT) return false;
        return cardLevel(card) < SECOND_SLOT_LEVEL;
      }
      function meta(card){try{return D.gearMetaFor?D.gearMetaFor(card.key)||{}:{};}catch(_){return {};}}
      function missing(card){
        var g=gear(card),gm=meta(card);
        if((gm.active&&gm.active.id==='PENDING'&&!abilityQueueFreshPending(gm.active))||
           (gm.passive&&gm.passive.id==='PENDING'&&!abilityQueueFreshPending(gm.passive)))return null;
        var out=[];
        if(!(g.active&&g.active.id&&g.active.id!=='PENDING'))out.push('active');
        /* R630: a locked second slot is not MISSING, it is not open yet, so
           the free roll never tries to fill it and the queue never counts a
           card as incomplete for a slot the player cannot have. */
        if(!(g.passive&&g.passive.id&&g.passive.id!=='PENDING')&&!slotLocked(card,'passive'))out.push('passive');
        return out;
      }
      function recovering(card){
        var out=[];
        if(!D.crystalRollPending)return out;
        ['active','passive'].forEach(function(slot){
          try{if(D.crystalRollPending(card.key,slot))out.push(slot);}catch(_){ }
        });
        return out;
      }
      function rarityName(r){
        var C=window.HOHContractContent||{};return (C.RARITY_NAMES&&C.RARITY_NAMES[r|0])||DUST_RARITY[Math.max(0,Math.min(5,r|0))]||'Common';
      }
      /* R564: `held` is an optional override. Two concurrent rolls each end
         with a full get_card_skills read-back that REPLACES the shared gear
         cache, so the slower reply can land a snapshot taken before the other
         slot was written. Painting from the roll's own answer removes that
         ordering race outright instead of hoping the replies interleave
         kindly. Passing nothing keeps the cache read. */
      function slotMarkup(card,slot,held){
        var C=window.HOHContractContent||{},label=slot==='active'?'Active':'Passive';
        var lead=slot==='active'?'Your first rolled ability. Its effect explains when it triggers.':'Your second rolled ability. Its effect explains when it triggers.';
        var labelMarkup='<span class="ffshop-rollq-slot-label" data-tip="'+esc(lead)+'">'+label+'</span>';
        var _pending=!!(D&&D.crystalRollPending&&D.crystalRollPending(card.key,slot));
        if(held===undefined)held=gear(card)[slot];
        /* R630: the second slot before level 30. It says what opens it and
           where this hero stands. A held ability keeps its name while locked. */
        if(slotLocked(card,slot)){
          var lv=cardLevel(card), need=Math.max(0,SECOND_SLOT_LEVEL-lv);
          var lockedName=held&&held.id&&held.id!=='PENDING'?(C.passiveName?C.passiveName(held.id):held.id):'';
          return labelMarkup+
            '<div class="ffshop-rollq-empty is-locked" aria-label="Second ability opens at level '+SECOND_SLOT_LEVEL+'. This hero is level '+lv+'.">'+
              '<i class="ffshop-rollq-lock" aria-hidden="true">&#128274;</i>'+
              '<span><b>'+(lockedName?esc(lockedName):'Opens at Level '+SECOND_SLOT_LEVEL)+'</b><small>'+(lockedName?'Locked until Level '+SECOND_SLOT_LEVEL:need+' more '+(need===1?'level':'levels'))+'</small></span>'+
            '</div>'+
            (_pending
              ? '<button type="button" class="ffshop-rollq-re" data-rollq-re="'+slot+'" aria-label="Verify the saved ability reroll. No second Crystal will be spent.">Verify</button>'
              : '<span class="ffshop-rollq-re is-locked" aria-hidden="true">Lv '+SECOND_SLOT_LEVEL+'</span>');
        }
        if(!held&&_pending){
          return labelMarkup+'<div class="ffshop-rollq-empty"><span>Saved roll waiting</span></div>'+
            '<button type="button" class="ffshop-rollq-re" data-rollq-re="'+slot+'" aria-label="Verify the saved ability reroll. No second Crystal will be spent.">Verify</button>';
        }
        if(!held)return labelMarkup+'<div class="ffshop-rollq-empty"><i aria-hidden="true"></i><span>Unrolled</span></div>';
        var r=Math.max(0,Math.min(5,held.r|0)),color=(C.RARITY_COLORS&&C.RARITY_COLORS[r])||'#c8a45e';
        var name=slot==='passive'?(C.passiveName?C.passiveName(held.id):held.id):(C.skillName?C.skillName(held.id):held.id);
        var desc=slot==='passive'?(C.passiveDesc?C.passiveDesc(held.id):''):(C.skillDesc?C.skillDesc(held.id):'');
        /* ⛔ R596 (Jan): "you should be able to spend hearts in this menu to also
           refresh your abilities just like if you opened the cards profile with
           the refresh icon". These two rows have NEVER had a reroll: the
           interactive one only ever existed on the card profile, and this
           screen rendered name and rarity and stopped. His arrows pointed
           straight at them.
           It is the same control, the same price of one Mythic Crystal and the
           same server call, so a player does not learn two different things.
           R708: submitted owners use the same receipt-backed crystal control;
           direct/free rerolls remain forbidden in the data and SQL layers. */
        var _own = !!(card && card.snap && card.snap.own);
        var _xtal = (window.HOHWallet && window.HOHWallet.icon) ? window.HOHWallet.icon(17) : '';
        var _held = (window.HOHWallet && window.HOHWallet.crystals) ? window.HOHWallet.crystals() : 0;
        var _runLocked = !!(D && D.runLocksAbilities && D.runLocksAbilities());
        var _re = _pending
          ? '<button type="button" class="ffshop-rollq-re" data-rollq-re="'+slot+'" data-tip="Finish the saved roll. Its receipt cannot charge twice." aria-label="Verify the saved '+label+' ability reroll. No second crystal will be spent.">Verify '+_xtal+'</button>'
          : (_held >= 1
            ? '<button type="button" class="ffshop-rollq-re" data-rollq-re="'+slot+'" aria-label="'+(_own?'Upgrade your submitted hero’s ':'Reroll ')+label+' ability for one Mythic Crystal. Rarity stays the same or improves.">&times;1 '+_xtal+'</button>'
            : '<button type="button" class="ffshop-rollq-re is-broke" disabled data-tip="Crystal needed. Buy one in the Shop, or claim your free daily." aria-label="'+(_own?'Upgrade your submitted hero’s ':'Reroll ')+label+' ability. One Mythic Crystal needed.">&times;1 '+_xtal+'</button>');
        if(_runLocked&&!_pending)_re='<button type="button" class="ffshop-rollq-re is-locked" disabled aria-label="Ability locked until this run ends">After run</button>';
        return labelMarkup+'<div class="hoc-skill ffshop-rollq-skill" tabindex="0" aria-label="'+esc(label+' slot, '+(name||'Ability')+', '+rarityName(r)+'. '+(desc||lead))+'" style="--rc:'+color+'" data-tip="'+esc(desc||lead)+'"><span class="hoc-skill-dot"></span><span><b>'+esc(name||'Ability')+'</b><small style="color:'+color+'">'+esc(rarityName(r))+'</small></span></div>'+_re;
      }
      function isFav(card){try{return typeof window.hohHallIsSaved==='function'&&window.hohHallIsSaved(card.id);}catch(_){return false;}}
      function setFace(showAbilities){
        var cardEl=box.querySelector('.ffshop-rollq-card'),cardbox=box.querySelector('.ffshop-rollq-cardbox');
        if(!cardEl)return;
        cardEl.classList.toggle('on',showAbilities);cardbox.classList.toggle('is-ability-view',showAbilities);
        var front=cardEl.querySelector('.is-front'),back=cardEl.querySelector('.is-back');
        if(front){front.setAttribute('aria-hidden',showAbilities?'true':'false');if(showAbilities)front.setAttribute('inert','');else front.removeAttribute('inert');}
        if(back){back.setAttribute('aria-hidden',showAbilities?'false':'true');if(showAbilities)back.removeAttribute('inert');else back.setAttribute('inert','');}
        /* ⛔ R567: the `data-rollq-back-only` dance is retired with the rail.
           Favorite now lives INSIDE the card's ability face, so turning the
           card to its art hides it the same way it hides everything else on
           that face (the back takes `inert` two lines above) — no second
           mechanism needed. The two side controls stay live on both faces on
           purpose: dusting or skipping a hero is a decision about the HERO,
           not about which side of the card is showing, and blinking them out
           mid-flip would make the sides jump. */
      }
      function paintCard(message){
        var card=current();if(!card){paintDone();return;}
        if(!guard())return;
        var miss=missing(card);
        if(miss===null){state.error='This card has a roll still being recovered. It was skipped safely.';state.skipped++;state.position++;state.index++;paintCard();return;}
        var recover=recovering(card);
        var heroLike={character_class:card.hero.character_class||'Fighter',cls:String(card.hero.character_class||'Fighter').split(' ')[0],race:card.hero.race||'',hearts:card.hero.hearts||card.snap.hearts||0};
        var rollLabel=miss.length===2?'Roll both abilities':(miss[0]==='active'?'Roll Active ability':'Roll Passive ability');
        var finished=!miss.length&&!recover.length,fav=isFav(card);
        var statusText=message||state.status||state.error||(recover.length?'Choose Verify on the saved crystal result. No second crystal will be spent.':(finished?'':'First ability rolls are free.'));
        box.innerHTML=
          '<div class="ffshop-rollq-head"><div><span>Ability review</span><b>'+(state.position+1)+' of '+state.total+'</b></div><button type="button" class="ffshop-dust-close" aria-label="Close and pause">&times;</button></div>'+
          /* ⛔ ====== R567 · THE RAIL IS GONE, THE SIDES ARE THE CONTROLS ======
             Jan: "remove the dust and next button and have subtle icons on
             either side of the card so they know what it does intuitively",
             and "just have favorite appear under the 2 abilities instead of
             off the card, so there are no 3 pill buttons on this screen".
             R566 put a red DUST wash on the left and a NEXT chip on the right
             that only appeared DURING a drag — so the gesture had to be
             discovered before its own signpost would show. These are the same
             two answers, standing still: quiet at rest, brightening as the
             card is dragged toward them, and CLICKABLE, so a mouse, a thumb
             and a keyboard all reach the same two moves without a pill rail
             competing with the card for the screen.
             The dust side is absent, not disabled, on an unrolled card — the
             same rule the swipe and the old button already followed, because
             dusting before rolling throws away the free rolls this queue
             exists to collect. One control on that card is honest: there is
             only one thing to do with it. */
          '<div class="ffshop-rollq-stage">'+
            (finished
              ? '<button type="button" class="ffshop-rollq-side ffshop-rollq-side-dust" data-rollq-dust'+
                ' aria-label="Dust this hero for Hearts"><svg viewBox="0 0 24 24" aria-hidden="true">'+
                '<path d="M12 3.5l1.9 4.9 4.9 1.9-4.9 1.9L12 17.1l-1.9-4.9L5.2 10.3l4.9-1.9z"/>'+
                '<path d="M18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z"/></svg>'+
                '<span class="ffshop-rollq-side-cap">Dust</span></button>'
              : '')+
            '<div class="ffshop-rollq-cardbox is-ability-view"><div class="ffshop-tiltwrap">'+fullCardHtml(card.id,card.snap,{back:'abilities',hideHearts:true})+'</div></div>'+
            '<button type="button" class="ffshop-rollq-side ffshop-rollq-side-next" data-rollq-next'+
              ' aria-label="'+(finished?'Next card':'Skip this card')+'"><svg viewBox="0 0 24 24" aria-hidden="true">'+
              '<path d="M9 5l7 7-7 7"/></svg>'+
              '<span class="ffshop-rollq-side-cap">'+(finished?'Next':'Skip')+'</span></button>'+
          '</div>'+
          '<div class="ffshop-rollq-status">'+esc(statusText)+'</div>'+
          ((D.runLocksAbilities&&D.runLocksAbilities())
            ? '<div class="ffshop-rollq-status ffshop-rollq-run-lock" role="note">Abilities stay locked until your run ends. '+
              (typeof modal.__dustClose==='function'&&typeof window.hohContractUnlock==='function'
                ? '<button type="button" class="ffshop-rollq-re" data-rollq-resume>Resume your run</button>'
                : 'Close this card to resume your run.')+'</div>' : '');
        if(message)queueAnnounce(statusText);
        var resumeRun=box.querySelector('[data-rollq-resume]');
        if(resumeRun)resumeRun.onclick=function(){modal.__dustClose();window.hohContractUnlock();};
        var cardEl=box.querySelector('.ffshop-card');cardEl.classList.add('ffshop-rollq-card','on');cardEl.removeAttribute('tabindex');
        /* ⛔ ====== R562 · THE FACE BELONGS ON THE ABILITY SIDE ======
           Jan, of this screen on a phone: "show the card art at least with the
           rarity border around it under the name at least and make the stats
           and abilities a bit smaller and lower so theres space for the card
           art ... it should look minimal and clean not this terribly formatted
           thing right now."
           The ability side carried a logo, a name, and then a large empty gap
           before the panel, because the shell centres a 1fr mount whose panel
           is shorter than the space it is given. Reviewing 306 heroes one at a
           time with no face on screen also means the one thing that says WHICH
           hero this is was a word, and the button to see them was a round trip
           that loses your place.
           The art is a THUMBNAIL here, not the card: it identifies the hero and
           carries the rarity in its border, and it fills the dead space that
           made the panel look unplanned. It is aria-hidden because the name
           directly above it already names the hero, so a screen reader gains
           nothing from a second identical label.
           Scoped to `.ffshop-rollq-card` in CSS, so the Hall's own card profile
           (the same ability back) is untouched by this. */
        try {
          var _abTop=cardEl.querySelector('.ffshop-ability-top');
          if(_abTop && !cardEl.querySelector('.ffshop-rollq-face')){
            var _heroRow=heroById(card.id)||{};
            /* ⛔ R609 · THE SAME FILE THE HALL FRAMES, OR THE CROP IS A GUESS.
               Jan: "it's not inheriting the reframe of the image that is being
               used in the main hall ... reframe their image so it sticks in the
               square, but it's not inheriting the reframe."
               The object-position IS inherited — `artImgHtml` has always asked
               `imgPos(heroId)`, which is the Hall's own `hohImgObjPos`. What was
               not inherited is the FILE. The Hall card renders
               `image_thumb_url || image_url` (hoh-card-render.js) and this
               thumbnail is a server-side derivative with its own crop; this
               review face asked for `image_url` FIRST, the untouched original.
               An object-position is a PERCENTAGE, so the identical number
               against a differently cropped source lands somewhere else — the
               owner drags their hero's face into the square in the Hall and
               sees it sitting wrong here. Same order as the Hall now, so the
               same percentage means the same thing. It is also the smaller
               file, on the one screen a player pages through hundreds of. */
            var _artSrc=_heroRow.image_thumb_url||_heroRow.image_url||(card.snap&&card.snap.img)||'';
            var _fig=document.createElement('div');
            _fig.className='ffshop-rollq-face';
            _fig.setAttribute('aria-hidden','true');
            _fig.innerHTML=artImgHtml(_artSrc,card.id,(_heroRow.name||(card.snap&&card.snap.name)||'Hero'));
            _abTop.parentNode.insertBefore(_fig,_abTop.nextSibling);
          }
        } catch(_){}
        var mount=cardEl.querySelector('.ffshop-ability-mount');
        /* R566 (Jan): "remove the 'both first rolls are free'" and "remove
           'Both abilities saved. Favorite, dust, or continue.' as well."
           Both were saying, ON the card, a thing the status line under the
           card already said — and the status line is the one that can also
           report a roll's actual result, so it is the one that survives. The
           free note in particular repeated the standing line verbatim, which
           is two sentences of screen furniture on a surface Jan is trying to
           get through 304 times. Removing them is also what pays for the
           larger art below. */
        if(mount)mount.innerHTML='<div class="ffshop-rollq-panel"><div class="ffshop-rollq-panel-title">Stats and abilities</div><div class="ffshop-rollq-slots"><div class="hoc-slotrow ffshop-rollq-slot" data-rollq-slot="active">'+slotMarkup(card,'active')+'</div><div class="hoc-slotrow ffshop-rollq-slot" data-rollq-slot="passive">'+slotMarkup(card,'passive')+'</div></div>'+(miss.length
          ? '<button type="button" class="ffshop-rollq-roll" data-rollq-roll><span aria-hidden="true">&#10022;</span>'+rollLabel+'</button>'
          /* R567 (Jan): "just have favorite appear under the 2 abilities
             instead of off the card". It keeps the old visibility rule — it
             belongs to a card whose abilities are settled, which is the moment
             a player decides whether this one is worth keeping. On an unrolled
             card the roll button owns that spot, and it is the only thing
             worth pressing there. */
          : (recover.length?'':'<button type="button" class="ffshop-rollq-fav'+(fav?' is-on':'')+'" data-rollq-favorite'+
            ' aria-pressed="'+(fav?'true':'false')+'" aria-label="'+(fav?'Remove favorite':'Favorite this hero')+'">'+
            '<span class="ffshop-rollq-fav-star" aria-hidden="true">'+(fav?'&#9733;':'&#9734;')+'</span>'+
            (fav?'Favorited':'Favorite')+'</button>'))+'</div>';
        /* The exact physical card is allowed to grow beyond its historical
           650px floor when an owner wrote long card text. Fit that content,
           measure the real card, then reduce only its outer queue scale so a
           tall front can never spill behind the action rail on a phone. The
           stage keeps the same rendered footprint; only the scale variable
           changes, which also preserves the inverse-sized functional targets
           on the ability side. */
        state.fitQueueCard=function(resetBase){
          var liveCard=box.querySelector('.ffshop-rollq-card');
          if(!liveCard||!liveCard.isConnected)return;
          fitLine(liveCard.querySelector('.ffshop-fit'),11);fitZone(liveCard.querySelector('.ffshop-fitzone'));
          var stage=box.querySelector('.ffshop-rollq-stage'),cardbox=box.querySelector('.ffshop-rollq-cardbox'),tilt=cardbox&&cardbox.querySelector(':scope > .ffshop-tiltwrap');
          if(!stage||!cardbox||!tilt)return;
          var front=liveCard.querySelector('.ffshop-face.is-front');
          /* every pass starts from an uncompensated card, or the second call
             would measure the first call's own correction and grow forever */
          if(front)front.style.removeProperty('min-height');
          if(resetBase){
            stage.style.removeProperty('--ffrq-k');stage.style.removeProperty('height');stage.__ffrqBase=0;
            cardbox.style.removeProperty('width');cardbox.style.removeProperty('height');tilt.style.removeProperty('transform');
          }
          var natural=Math.max(650,liveCard.offsetHeight||650),base=stage.__ffrqBase||(parseFloat(getComputedStyle(stage).getPropertyValue('--ffrq-k'))||.78);
          stage.__ffrqBase=base;
          var target=650*base,scale=Math.min(base,target/natural),rendered=Math.round(natural*scale);
          stage.style.height=rendered+'px';stage.style.setProperty('--ffrq-k',String(scale));
          cardbox.style.width=Math.round(380*scale)+'px';cardbox.style.height=rendered+'px';
          tilt.style.transform='scale('+scale+')';
          /* ⛔ ====== R609 · THE BACK FACE CANNOT MAKE THE CARD TALLER ======
             Jan: "if the name is long or something, then it kind of pushes the
             favorite button past the card ... the favorite icon is currently
             cut off." And: "we can make the box a little bit longer ... remove
             that and extend the box on the inside."

             THE CLIP IS STRUCTURAL. `.ffshop-face.is-back` is
             `position:absolute; inset:0` — R572 made both faces permanently
             laid out to cure a WebKit paint fault and that must stay — so only
             the FRONT sizes the card, and `liveCard.offsetHeight` above is the
             front's height and nothing else. The ability shell is
             `height:100%; overflow:hidden`, so when the ability side needs more
             room than the front happens to occupy, the shell CUTS it, and the
             last thing in that column is the Favorite button.
             A two line hero name is enough: about 28 unscaled pixels onto a
             column that already lands within a few of the 650px floor at the
             phone's scale. KASSANDRA DELIA MIRIEL wraps and ordinary names do
             not, which is exactly why this read as random.

             ⛔ THE SCALE IS DELIBERATELY NOT RECOMPUTED HERE. Every functional
             size on this face is written `calc(Npx / var(--ffrq-k))` so touch
             targets survive the card's own scaling — so changing k changes the
             content height, which changes the overflow, which changes k.
             Growing the card at a FIXED scale settles in one pass.
             ⛔ AND IT GROWS BY HEIGHT ONLY, NEVER BY SCALE. Raising --ffrq-k
             would widen the card too, and the phone's .68 is not an arbitrary
             number: `.ffshop-rollq-side-dust/-next` are centred in the gutter
             `(100% - 380px * k) / 4`, which collapses to zero at about k=.69 on
             a 375px screen and puts Dust and Next on top of the artwork. A
             taller card at the same width leaves both controls exactly where
             they are. */
          try{
            var shell=liveCard.querySelector('.ffshop-ability-shell');
            var want=natural;
            if(shell&&liveCard.classList.contains('on')){
              var overflow=Math.max(0,shell.scrollHeight-shell.clientHeight);
              var panel=shell.querySelector('.ffshop-rollq-panel');
              if(panel)overflow=Math.max(overflow,panel.scrollHeight-panel.clientHeight);
              if(overflow>0)want=natural+overflow+2;
            }
            /* Then spend whatever the sheet genuinely has left on the card. The
               review box is content-sized under a max-height, and on a phone it
               was finishing about 120px short of that ceiling — dead space
               under a panel Jan was calling cramped. Reading the cap rather
               than hard-coding a taller card is what keeps this correct on a
               small Android, in landscape, and while iOS Safari's chrome slides
               in and out (onQueueResize re-fits). The 1.14 ceiling stops a very
               tall screen from stretching the card into a slab. */
            var cap=0;
            try{ cap=parseFloat(getComputedStyle(box).maxHeight)||0; }catch(_){ cap=0; }
            if(cap>0&&scale>0){
              var chrome=Math.max(0,box.scrollHeight-stage.offsetHeight);
              var room=cap-chrome-6;
              if(room>0)want=Math.max(want,Math.min(room/scale,650*1.14));
            }
            want=Math.max(650,Math.round(want));
            if(front&&want>natural){
              front.style.minHeight=want+'px';
              var grownRendered=Math.round(want*scale);
              stage.style.height=grownRendered+'px';cardbox.style.height=grownRendered+'px';
            }
          }catch(_){ }
        };
        requestAnimationFrame(function(){if(state.fitQueueCard)state.fitQueueCard(false);});
        /* ⛔ R566 (Jan): "have the next card slide in with an animation."
           CONSUMED ON READ, and only ever SET by the two moves that actually
           reach a different card (advance and a completed dust). paintCard
           also runs on a favourite toggle, a roll landing and every error
           message, and sliding the same card in again on each of those would
           turn ordinary feedback into a flinch.
           It enters from the RIGHT whichever way the last card left, because
           the queue only ever moves forward — an entrance that mirrored the
           exit would read as going back a card after a right swipe. The two
           never overlap: paintCard has already replaced the node. */
        if(state.enter){
          state.enter=false;
          if(!reduced){
            var _inbox=box.querySelector('.ffshop-rollq-cardbox');
            if(_inbox&&_inbox.animate){
              try{_inbox.animate([{transform:'translateX(46px)',opacity:0},{transform:'translateX(0)',opacity:1}],
                {duration:210,easing:'cubic-bezier(.22,1,.36,1)'});}catch(_){ }
            }
          }
        }
        try{if(document.fonts&&document.fonts.ready&&document.fonts.ready.then)document.fonts.ready.then(function(){if(state.fitQueueCard)state.fitQueueCard(false);});}catch(_){ }
        setTimeout(function(){if(state.fitQueueCard)state.fitQueueCard(false);},650);
        box.querySelector('.ffshop-dust-close').onclick=modal.__dustClose;
        /* R564: the View card art button is retired. Tapping the card still
           turns it, so the front face is reachable without a button for a
           thing the review face already shows. */
        cardEl.onclick=function(e){if(Date.now()<state.suppressClickUntil||e.target.closest('button,[data-tip],a,input,select'))return;setFace(!cardEl.classList.contains('on'));};
        var roll=box.querySelector('[data-rollq-roll]');if(roll)roll.onclick=function(e){e.stopPropagation();rollCurrent(card,heroLike);};
        var favBtn=box.querySelector('[data-rollq-favorite]');if(favBtn)favBtn.onclick=function(){toggleFavorite(card);};
        var dustBtn=box.querySelector('[data-rollq-dust]');if(dustBtn)dustBtn.onclick=function(){dustCurrent(card);};
        /* ⛔ R596 · DELEGATED FOR REAL, ONCE, ON THE BOX. paintSlot replaces a
           row's innerHTML after every roll, so a handler bound to the button
           itself dies with the node and the SECOND reroll on a card would do
           nothing. The first cut of this bound directly while its own comment
           claimed otherwise. One listener on the container, matched by
           attribute, survives every repaint. */
        if(!box.__rollqReBound){
          box.__rollqReBound=true;
          box.addEventListener('click',function(e){
            var rb=e.target&&e.target.closest?e.target.closest('[data-rollq-re]'):null;
            if(!rb||rb.disabled)return;
            e.stopPropagation();
            var cur=current();if(!cur)return;
            var hl={character_class:cur.hero.character_class||'Fighter',
                    cls:String(cur.hero.character_class||'Fighter').split(' ')[0],
                    race:cur.hero.race||'',hearts:cur.hero.hearts||cur.snap.hearts||0};
            rerollSlot(cur,hl,rb.getAttribute('data-rollq-re'));
          });
        }
        box.querySelector('[data-rollq-next]').onclick=function(){advance(false);};
        wireSwipe();
        setFace(true);
        /* A recovery-only card opens on its Verify action, not Skip. That is
           the reason the card was returned to the queue. */
        var recoveryFocus=recover.length?box.querySelector('[data-rollq-re]'):null;
        try{(recoveryFocus||roll||box.querySelector('[data-rollq-next]')).focus({preventScroll:true});}catch(_){ }
      }
      function paintSlot(card,slot,held){
        var row=box.querySelector('[data-rollq-slot="'+slot+'"]');if(row)row.innerHTML=slotMarkup(card,slot,held);
        return row;
      }
      /* ⛔ R596 · REROLLING ONE ALREADY-ROLLED SLOT, from this screen.
         Separate from rollCurrent, which fills EMPTY slots for free. This is
         the paid move and it must never be mistaken for the free one: it goes
         out with payWith 'crystal', so the data layer takes a crystal and the
         free-only path is not involved at all.
         It shares abilityQueueRollBusy with rollCurrent on purpose. Two rolls
         on one card at once is exactly the race the server lock exists to
         stop, and the screen should not be inviting it. */
      function rerollSlot(card,heroLike,slot){
        if(state.rolling||abilityQueueRollBusy||!guard())return;
        var D=window.HOHContractData;
        if(!D||typeof D.rollSlot!=='function')return;
        /* ⛔ R643 · JAN'S RULE, SAID OUT LOUD BEFORE ANYTHING IS SPENT.
           "Abilities lock for a run." The run already simulates the abilities
           the party STARTED with, so a reroll mid-run provably cannot change
           the fight the player is in, and until now nothing said so: they
           could spend a Mythic Crystal on it and see no effect. rollSlot
           refuses this too, but a refusal a player only meets AFTER tapping is
           a worse answer than a sentence before they do. */
        var _rl=D.runLocksAbilities&&D.runLocksAbilities();
        var pendingReceipt=!!(D.crystalRollPending&&D.crystalRollPending(card.key,slot));
        if(slotLocked(card,slot)&&!pendingReceipt){
          var levelMessage='This ability opens at Level '+SECOND_SLOT_LEVEL+'.';
          var levelStatus=box.querySelector('.ffshop-rollq-status');
          if(levelStatus)levelStatus.textContent=levelMessage;
          queueAnnounce(levelMessage);
          return;
        }
        if(_rl&&!pendingReceipt){
          var _m=_rl==='unlimited'
            ?'Your abilities are locked until this Battlegrounds run ends. Finish or leave the run to roll again.'
            :'Your abilities are locked until this climb ends. Finish or leave the climb to roll again.';
          var lk=box.querySelector('.ffshop-rollq-status');
          if(lk)lk.textContent=_m;
          queueAnnounce(_m);
          return;
        }
        if(!pendingReceipt&&(!window.HOHWallet||window.HOHWallet.crystals()<1)){
          var l0=box.querySelector('.ffshop-rollq-status');
          if(l0)l0.textContent='Crystal needed. Buy one in the Shop, or claim your free daily.';
          queueAnnounce('Crystal needed. Buy one in the Shop, or claim your free daily.');
          return;
        }
        state.rolling=true;abilityQueueRollBusy=true;state.error='';
        var btn=box.querySelector('[data-rollq-re="'+slot+'"]'),restoreFocus=document.activeElement===btn;
        /* the burst fires on the PRESS, not on the answer: on a phone the gap
           can be a second and this is what says the tap landed */
        if(btn){ try{ crystalBurst(btn); }catch(_){ } btn.disabled=true;btn.setAttribute('aria-busy','true'); }
        box.querySelectorAll('.ffshop-rollq-side,[data-rollq-favorite],[data-rollq-re]').forEach(function(b){b.disabled=true;});
        var say=function(t){var l=box.querySelector('.ffshop-rollq-status');if(l)l.textContent=t;queueAnnounce(t);};
        var UI=window.HOHContractUI||{},row=box.querySelector('[data-rollq-slot="'+slot+'"]');
        var stop=UI.spinReel?UI.spinReel(row,slot):function(){return Promise.resolve();};
        var abilityName=function(res){
          var C=window.HOHContractContent||{};
          return (slot==='passive'?(C.passiveName?C.passiveName(res.id):res.id):(C.skillName?C.skillName(res.id):res.id))||'ability';
        };
        say('Rolling '+(slot==='active'?'Active':'Passive')+' ability…');
        var landedRes=null,rollRequest,recoverySettled=false,recoveryMessage='';
        try{rollRequest=D.rollSlot(card.key,slot,heroLike,'crystal');}
        catch(startError){rollRequest=Promise.reject(startError);}
        var chain=Promise.resolve(rollRequest).then(function(res){
          landedRes=res;
          var verify=D.loadGear?D.loadGear(true):Promise.resolve();
          try{verify.catch(function(){});}catch(_){ }
          return Promise.resolve(stop(res)).then(function(){
            return UI.rollCeremony?UI.rollCeremony(row,res,card.key):null;
          }).then(function(){return verify;}).then(function(){
            if(D.gearLoadFailed&&D.gearLoadFailed())throw new Error('Abilities could not be verified after rolling.');
            return res;
          });
        }).then(function(res){
          if(!guard())throw new Error('account changed');
          /* Keep the roll's answer authoritative for its own row; the verified
             cache safely refreshes the sibling and its affordability state. */
          paintSlot(card,slot,{id:res.id,r:res.rarity|0});
          paintSlot(card,slot==='active'?'passive':'active');
          var floor=res.floor==null?(res.rarity|0):(res.floor|0);
          var up=res.tiered_up==null?(res.rarity|0)>floor:!!res.tiered_up;
          recoveryMessage=up?'Tier up: '+rarityName(res.rarity)+' '+abilityName(res)+'.'
                :'Rarity unchanged: '+rarityName(res.rarity)+' '+abilityName(res)+'.';
          recoverySettled=pendingReceipt;
          if(!pendingReceipt)say(recoveryMessage);
        },function(e){
          return Promise.resolve(stop(null)).catch(function(){}).then(function(){
            var refresh=D.loadGear?D.loadGear(true):Promise.resolve();
            return Promise.resolve(refresh).catch(function(){}).then(function(){
              if(!guard())return;
              if(D.gearLoadFailed&&D.gearLoadFailed()){
                showLoadError();
                return;
              }
              paintSlot(card,slot);
              paintSlot(card,slot==='active'?'passive':'active');
              if(landedRes){
                var recoveredFloor=landedRes.floor==null?(landedRes.rarity|0):(landedRes.floor|0);
                var recoveredUp=landedRes.tiered_up==null?(landedRes.rarity|0)>recoveredFloor:!!landedRes.tiered_up;
                say(recoveredUp?'Tier up: '+rarityName(landedRes.rarity)+' '+abilityName(landedRes)+'.'
                      :'Rarity unchanged: '+rarityName(landedRes.rarity)+' '+abilityName(landedRes)+'.');
                return;
              }
              var m=String((e&&e.message)||'');
              var precise=D.abilityRollErrorMessage&&D.abilityRollErrorMessage(e);
              if(precise)say(precise);
              else if(/locked.*run|locked.*climb|abilities stay locked/i.test(m))say('Abilities stay locked for your run. Close this card and resume your run before rerolling.');
              else if(/no crystals|crystal needed/i.test(m))say('Crystal needed. Buy one in the Shop, or claim your free daily.');
              else if(/receipt could not be verified/i.test(m))say('This crystal roll may already be saved. Reopen Quick review and choose Verify before trying another roll.');
              else if(/conflicts with its receipt|contact support/i.test(m))say('This saved crystal request needs support before another can begin. No new roll was started.');
              else if(/downgrade|rarity|verified|integrity/i.test(m))say('Your original ability was kept because that result could not be verified.');
              else if(/account changed/i.test(m))say('The account changed while this was saving. Reopen Quick review to verify the card.');
              else if(/network|fetch|offline|connection/i.test(m))say('The connection changed while this was saving. Reopen Quick review to verify the saved result.');
              else if(/being upgraded|update is installed/i.test(m))say('Crystal rerolls are temporarily unavailable while the Hall update finishes. Nothing was spent.');
              else say('The reroll could not be confirmed. Reopen Quick review to check the saved ability before trying again.');
            });
          });
        });
        var unlock=function(){
          state.rolling=false;abilityQueueRollBusy=false;
          if(recoverySettled){state.status=recoveryMessage;paintCard(state.status);}
          box.querySelectorAll('.ffshop-rollq-side,[data-rollq-favorite]').forEach(function(b){b.disabled=false;});
          box.querySelectorAll('[data-rollq-re]').forEach(function(b){
            if(!b.classList.contains('is-broke')&&!b.classList.contains('is-locked'))b.disabled=false;
          });
          if(restoreFocus&&!recoverySettled){
            var r2=box.querySelector('[data-rollq-re="'+slot+'"]:not([disabled])');
            if(r2){try{r2.focus({preventScroll:true});}catch(_){ }}
            else{
              var rr=box.querySelector('[data-rollq-slot="'+slot+'"]');
              var fallback=rr&&rr.querySelector('.ffshop-rollq-skill,.hoc-skill');
              if(!fallback)fallback=box.querySelector('[data-rollq-retry]');
              if(fallback){try{if(!fallback.hasAttribute('tabindex')&&fallback.tagName!=='BUTTON')fallback.setAttribute('tabindex','-1');fallback.focus({preventScroll:true});}catch(_){ }}
            }
          }
        };
        chain.then(unlock,function(e){
          try{console.error('[quick review] reroll cleanup:',e);}catch(_){ }
          unlock();
        });
      }
      function rollCurrent(card,heroLike){
        if(state.rolling||abilityQueueRollBusy||!guard())return;
        var todo=missing(card);if(!todo||!todo.length){paintCard();return;}
        state.rolling=true;abilityQueueRollBusy=true;state.error='';
        var slotName=function(slot){return slot==='active'?'Active':'Passive';};
        var btn=box.querySelector('[data-rollq-roll]');if(btn){btn.disabled=true;btn.setAttribute('aria-busy','true');btn.innerHTML='<span class="ffshop-rollq-spinner" aria-hidden="true"></span>Rolling '+(todo.length===2?'abilities':slotName(todo[0]))+'…';}
        box.querySelectorAll('.ffshop-rollq-side,[data-rollq-favorite]').forEach(function(action){action.disabled=true;});
        var saved=[];
        var abilityName=function(slot,res){
          var C=window.HOHContractContent||{};
          return (slot==='passive'?(C.passiveName?C.passiveName(res.id):res.id):(C.skillName?C.skillName(res.id):res.id))||'ability';
        };
        var say=function(txt){var live=box.querySelector('.ffshop-rollq-status');if(live)live.textContent=txt;queueAnnounce(txt);};
        say(todo.length===2?'Rolling both abilities…':'Rolling '+slotName(todo[0])+' ability…');
        /* ── R564 · BOTH SLOTS ROLL AS ONE MOMENT ──
           Jan: "when it rolls roll both at the same time". This used to be a
           promise CHAIN: Active drew, landed, celebrated, and only then did
           Passive begin its spin, so a two-slot card took two full ceremonies
           end to end and read as two separate rolls.

           Three properties make the concurrent version safe rather than just
           faster:
           1. rollSlot takes a Web Lock keyed on account+card+SLOT, so two
              slots of one card never contend. Nothing here serialises them.
           2. Nothing lands until every answer is back. Both reels are started
              on the same frame and both are stopped after the barrier below,
              so the two settle together instead of whenever their own request
              happened to return. That is the difference between "started
              together" and "rolled together".
           3. A slot that fails must never abandon its sibling mid-spin with a
              live reel on screen, so each draw resolves to a RESULT OR AN
              ERROR and the barrier is reached either way. */
        var reels=todo.map(function(slot){
          var row=box.querySelector('[data-rollq-slot="'+slot+'"]'),target=row&&row.querySelector('.ffshop-rollq-empty,.ffshop-rollq-skill');
          if(target&&!target.classList.contains('hoc-skill'))target.classList.add('hoc-skill');
          var UI=window.HOHContractUI||{};
          return {slot:slot,row:row,stop:UI.spinReel?UI.spinReel(row,slot):function(){return Promise.resolve();}};
        });
        var land=function(r,res){
          /* The reel lands first. Only after its settle completes do we paint
             the durable result and begin the shared rarity FX, so neither the
             text nor the live region leaks the outcome. The paint is fed the
             roll's OWN answer, never the shared cache (see slotMarkup). */
          return Promise.resolve().then(function(){return r.stop(res||null);}).then(function(){
            if(!res)return null;
            saved.push(r.slot);
            var liveRow=(r.row&&r.row.isConnected)?r.row:box.querySelector('[data-rollq-slot="'+r.slot+'"]');
            var UI=window.HOHContractUI||{};
            var ceremony=UI.rollCeremony?UI.rollCeremony(liveRow,res,card.key):null;
            return Promise.resolve(ceremony).then(function(){
              return paintSlot(card,r.slot,{id:res.id,r:res.rarity});
            });
          });
        };
        /* ⛔ R567 (Jan): "the roll loading is a bit slow idk if we can speed
           that up a bit."
           THE READ-BACK WAS QUEUED BEHIND THE CEREMONY FOR NO REASON. This
           chain used to be: roll → land the reels → play the rarity ceremony →
           THEN start a full `get_card_skills` round trip → then repaint. That
           last round trip is a whole network latency spent staring at
           "Rolling abilities…" with nothing moving, and it was serial purely
           because it was written last.
           It is fired here instead, the moment every `rollSlot` has RESOLVED —
           which is the moment the server has committed, so it can never read a
           roll that has not landed — and it is awaited after the ceremony. On
           any connection slower than the ceremony it now costs nothing at all.
           ⛔ SAFE ONLY BECAUSE OF R564: `land` paints from the roll's OWN
           answer, never from the shared gear cache, so a read-back replacing
           that cache mid-ceremony cannot change what is on screen. If a future
           change makes the ceremony read `gearFor` again, this must go back
           behind it.
           ⛔ The `.catch` below parks the rejection so a slow failure cannot
           raise an unhandled rejection while the ceremony is still playing.
           The ORIGINAL promise stays rejected, so `return verify` still routes
           the failure into the error handler exactly as before. */
        var verify=null;
        var chain=Promise.all(reels.map(function(r){
          return D.rollSlot(card.key,r.slot,heroLike,'free-only').then(
            function(res){return {r:r,res:res};},function(err){return {r:r,err:err};});
        })).then(function(list){
          if(!guard())return Promise.all(list.map(function(d){return land(d.r,null);})).then(function(){throw new Error('account changed');});
          verify=Promise.resolve().then(function(){return D.loadGear(true);}).then(function(){
            if(D.gearLoadFailed&&D.gearLoadFailed())throw new Error('Abilities could not be verified after rolling.');
          });
          try{verify.catch(function(){});}catch(_){ }
          return Promise.all(list.map(function(d){return land(d.r,d.res);})).then(function(){
            var won=list.filter(function(d){return d.res;}),lost=list.filter(function(d){return !d.res;});
            if(won.length){
              /* ⛔ R609 (Jan): "you can remove the text on the bottom that says
                 active, common, steady hand, passive, common, clarity. Just
                 remove that and extend the box on the inside."
                 He is right, and it is the same argument R566 used to delete
                 the two notes ON the card: the two ability rows directly above
                 have just landed with their name, their rarity word and their
                 rarity colour, after a ceremony that played on them. Printing
                 the identical facts as a sentence underneath is the screen
                 saying the same thing twice on the one surface he pages through
                 three hundred times.
                 ⛔ THE ANNOUNCEMENT STAYS. `queueAnnounce` writes to the
                 visually hidden live region, which is the only way a screen
                 reader learns what landed — the coloured rows say nothing to
                 it. Deleting the sentence from the VISIBLE line and keeping the
                 spoken one is the whole change; `state.status` is cleared so
                 the repaint below draws an empty line rather than the old text.
                 A paid crystal reroll keeps its visible sentence on purpose:
                 "Tier up" versus "Rarity unchanged" is the outcome you spent
                 for, and the rows alone cannot tell you which happened. */
              queueAnnounce(won.map(function(d){return slotName(d.r.slot)+' '+rarityName(d.res.rarity)+': '+abilityName(d.r.slot,d.res);}).join('. ')+'.');
              state.status='';
            }
            if(lost.length)throw (lost[0].err||new Error('That roll did not finish.'));
          });
        });
        chain.then(function(){return verify;}).then(function(){
          state.rolling=false;abilityQueueRollBusy=false;state.rolled[card.id]=1;/* R566 (Jan): 'remove "Both abilities saved. Favorite, dust, or
             continue." as well'. say() has already announced the real result
             ("Active Epic: STORED WRATH."), which is both more useful and the
             thing a screen reader should hear. Overwriting it with a generic
             sentence threw that away. Keep the announcement, drop the filler. */
        paintCard(state.status);
        },function(err){
          state.rolling=false;abilityQueueRollBusy=false;
          Promise.resolve(D.loadGear(true)).then(function(){
            if(D.gearLoadFailed&&D.gearLoadFailed()){
              showLoadError();
              return;
            }
            var left=missing(card)||[],partial=saved.length&&left.length;
            /* ⛔ R609 · THE REFUSAL SAYS WHY NOW.
               "That roll did not finish" was the only sentence this branch
               could produce, for every cause there is: no hero behind the card,
               a skill id the server whitelist refuses, a changed account, a
               dropped connection, a database mid-upgrade. Its sibling
               `rerollSlot` has read the server's own message and translated it
               since R596; the free path never learned to, so the one screen Jan
               sweeps three hundred cards through was the one screen that could
               not tell him what went wrong. Same vocabulary, one reader.
               ⛔ AND IT LOGS FLAT. R565 paid for this once: an object logged
               beside a label reads as "Object" in a phone screenshot, which is
               exactly how this report reached me. Card key, slots and the raw
               message go on the line itself. */
            var _m=String((err&&err.message)||'');
            var _why=
              /no crystals|crystal needed/i.test(_m)?'Crystal needed. Buy one in the Shop, or claim your free daily.':
              /receipt could not be verified/i.test(_m)?'This roll may already be saved. Reopen Quick review and choose Verify before trying another.':
              /conflicts with its receipt|contact support/i.test(_m)?'This saved request needs support before another can begin. No new roll was started.':
              /downgrade|rarity|verified|integrity/i.test(_m)?'Your original ability was kept because that result could not be verified.':
              /account changed/i.test(_m)?'The account changed while this was saving. Reopen Quick review on the original account to finish it.':
              /network|fetch|offline|connection/i.test(_m)?'The connection dropped while this was saving. Reopen Quick review to verify the saved result.':
              /being upgraded|update is installed/i.test(_m)?'Ability rolls are paused while the Hall update finishes. Nothing was spent.':
              /bad skill|skill/i.test(_m)?'The Hall refused that ability for this hero. Nothing was spent. Try the roll again.':
              'That roll did not finish. Your saved abilities are safe. Retry the unrolled slot.';
            state.error=partial?(saved.map(function(s){return s.charAt(0).toUpperCase()+s.slice(1);}).join(' and ')+' saved. '+left.map(function(s){return s.charAt(0).toUpperCase()+s.slice(1);}).join(' and ')+' still needs a roll.'):_why;
            try{console.error('[quick review] R609 ROLL REFUSED card='+card.key+' hero='+card.id+' slots='+(todo.join('+'))+' saved='+(saved.join('+')||'none')+' reason='+(_m||'(no message)'));}catch(_){ }
            paintCard(state.error);
          },function(){showLoadError();});
        });
      }
      function toggleFavorite(card){
        if(!guard())return;
        var was=isFav(card);
        try{
          if(typeof window.hohHallToggleHero==='function')window.hohHallToggleHero(card.id);
          else if(was&&typeof window.hohHallRemoveHero==='function')window.hohHallRemoveHero(card.id);
          else if(!was&&typeof window.hohHallForceSaveHero==='function')window.hohHallForceSaveHero(card.id,'star');
          else throw new Error('favorite unavailable');
          if(!was)state.favorites++;else if(state.favorites>0)state.favorites--;
          state.status=was?'Removed from favorites.':'Saved to favorites.';paintCard(state.status);
        }catch(_){state.error='Favorites are unavailable right now.';paintCard(state.error);}
      }
      /* R566: the compact confirm replaces the full review HERE ONLY. Every
         other door into dusting (the collection's Dust cards manager, the
         inspector's single-copy button) still opens the long form, because
         those are deliberate one-off decisions rather than a sweep through
         304 cards. `onKeep`/`onRefused` exist so a swiped card can come back
         instead of being stranded off-screen. */
      /* ⛔ ====== R609 · A DUST YOU CAN SEE PAY OUT ======
         Jan: "if you dust it, it should show the card kind of evaporating and
         turning into hearts and going into your wallet."
         There was NO animation here at all, and the reason is worth recording.
         `finishDustBatch` does fly hearts on a dust — from the collection
         TILE: `grid.querySelector('.ffshop-tile[data-hero-id=...]')`. In Quick
         review that grid is behind a full screen sheet, so on every one of the
         three hundred cards Jan sweeps the payout animation was either firing
         invisibly behind the overlay or not firing at all. The card just
         vanished and a number moved somewhere off screen.
         The stage is the source, never the card, because a SWIPED dust has
         already thrown the card off screen under `fill:forwards` — motes from
         its rect would launch from beyond the bezel. The stage sits exactly
         where the card was and does not move.
         Heart count is a rough visual weight, not a readout: the sheet's own
         "Cards turned to Hearts" message carries the real number. */
      function dustMotes(fromEl){
        try{
          var r=fromEl.getBoundingClientRect();
          if(!r.width||!r.height)return;
          var host=document.createElement('div');
          host.className='ffshop-dustfx';host.setAttribute('aria-hidden','true');
          host.style.left=r.left+'px';host.style.top=r.top+'px';
          host.style.width=r.width+'px';host.style.height=r.height+'px';
          var html='';
          for(var i=0;i<18;i++){
            html+='<i style="--dx:'+((Math.random()*100)|0)+'%;--dy:'+(20+((Math.random()*70)|0))+'%;'+
              '--drift:'+(((Math.random()*54)-27)|0)+'px;--dlift:'+(-40-((Math.random()*70)|0))+'px;'+
              '--ddur:'+((520+Math.random()*420)|0)+'ms;--ddel:'+((Math.random()*180)|0)+'ms"></i>';
          }
          host.innerHTML=html;
          document.body.appendChild(host);
          setTimeout(function(){ try{ host.remove(); }catch(_){ } },1250);
        }catch(_){ }
      }
      function dustAway(paidHearts,flew){
        try{
          var stage=box.querySelector('.ffshop-rollq-stage');
          var cardbox=box.querySelector('.ffshop-rollq-cardbox');
          /* opacity and filter only. A swiped card is mid-flight under its own
             `fill:forwards` transform animation, and animating transform here
             too would fight it and snap the card back to centre first. */
          if(!reduced&&!flew&&cardbox&&cardbox.isConnected&&cardbox.animate){
            try{cardbox.animate([{opacity:1,filter:'blur(0px)'},{opacity:0,filter:'blur(7px)'}],
              {duration:320,easing:'ease-in',fill:'forwards'});}catch(_){ }
          }
          if(!stage||!stage.isConnected)return;
          if(!reduced)dustMotes(stage);
          var pill=cornerPill();
          if(pill&&!reduced)flyHearts(stage,pill,Math.max(3,Math.min(8,Math.round((paidHearts|0)/14)||3)));
        }catch(_){ }
      }
      function dustCurrent(card,restore){
        if(!guard()||state.rolling){ if(restore)restore(); return; }
        var back=function(msg){
          if(!modal.parentNode||!guard())return;
          if(restore)restore();
          if(msg){state.status=msg;paintCard(state.status);}
        };
        openDustQuickConfirm(host||document,card.id,{
          onStart:function(name){
            var live=modal.querySelector('.ffshop-rollq-status');
            if(live)live.textContent='Turning '+name+' to Hearts…';
            queueAnnounce('Turning '+name+' to Hearts…');
          },
          onKeep:function(){ back(''); },
          onRefused:function(why){ if(!modal.parentNode||!guard())return; if(restore)restore(); state.error=why;paintCard(state.error); },
          onComplete:function(result){
            if(!modal.parentNode||!guard())return;
            var processed=(result&&result.processed)||[],did=processed.some(function(r){return String(r.character_id)===card.id;});
            if(!did){ if(restore)restore(); state.status='This card was kept. Continue when you are ready.';paintCard(state.status);return; }
            state.dusted++;state.position++;state.cards.splice(state.index,1);
            if(state.index>=state.cards.length)state.index=state.cards.length;
            state.enter=true;
            /* R609: the payout plays on the card that is leaving, so the
               repaint that destroys that card has to wait for it. `advancing`
               is held across the gap for the same reason `fly` holds it — every
               control on screen still belongs to a card that is already gone
               from `state.cards`, and a fast tap on Next would skip the card
               after it. Cleared immediately before the paint, and the paint
               re-checks the sheet is still open, because 340ms is long enough
               for a close or an account change. */
            dustAway(result&&result.hearts,!!restore);
            if(reduced){paintCard('Card turned to Hearts.');return;}
            state.advancing=true;
            setTimeout(function(){
              state.advancing=false;
              if(!modal.parentNode||!guard())return;
              paintCard('Card turned to Hearts.');
            },340);
          }
        });
      }
      function advance(fromSwipe){
        if(state.rolling||state.advancing)return;
        var card=current(),left=card?missing(card):[],recover=card?recovering(card):[];
        if((left&&left.length)||recover.length)state.skipped++;
        state.position++;state.index++;state.status=fromSwipe?'Kept. Moving to the next card.':'';state.error='';
        state.enter=true;   /* R566: this really is a different card */
        paintCard(state.status);
      }
      /* ⛔ ====== R566 · THE DECK, LEFT AND RIGHT ======
         Jan: "have dust be on the left side just like swiping left and right
         on tinder ... you can literlaly just click left or right and go
         through all your cards quickly, rolling, swiping left (insta dust) or
         clicking the left button thats red, or swiping right to go next or
         pressing the middle to favorite."

         The old gesture only understood ONE direction: a right drag toggled a
         class and, past 72px, advanced. A left drag did nothing at all, and
         nothing followed the finger, so the card gave no sign of what letting
         go would do.

         THREE PROPERTIES MAKE AN INSTANT DUST SAFE TO PUT ON A GESTURE:
         1. THE CARD FOLLOWS THE FINGER, and the destructive direction lights
            up as it goes. You can see "Dust" arriving and slide back to
            cancel. A gesture whose outcome is only legible after release is
            not a confirmation, it is a coin toss.
         2. THE DESTRUCTIVE THROW IS LONGER. Right (harmless, advance) commits
            at 72px, the distance it has always used. Left (permanent) needs
            108px. A brush past the edge of a scroll cannot reach it.
         3. IT IS ARMED ONLY ONCE THE CARD IS ROLLED, which is the same rule
            the Dust button already follows — the button is `hidden` until
            `is-finished`. Dusting a card whose free rolls are unspent throws
            away the very thing this queue exists to collect, so an unrolled
            card springs back and says so.
         Vertical drags are left to the page: the stage is `touch-action:pan-y`
         and the dominance test below keeps a scroll from arming either side. */
      var SWIPE_NEXT_PX=72, SWIPE_DUST_PX=108, SWIPE_ARM_PX=14;
      function wireSwipe(){
        var stage=box.querySelector('.ffshop-rollq-stage');if(!stage)return;
        var cardbox=box.querySelector('.ffshop-rollq-cardbox');
        var down=null,drag=false;
        function dustArmed(){var c=current();return !!(c&&!(missing(c)||[]).length&&!recovering(c).length);}
        function paintDrag(dx){
          if(!cardbox)return;
          var lim=Math.max(-170,Math.min(170,dx));
          cardbox.style.transform='translateX('+lim.toFixed(1)+'px) rotate('+(lim/30).toFixed(2)+'deg)';
          var dustish=dx<0&&dustArmed();
          stage.classList.toggle('is-swipe-dust',dustish&&dx<=-SWIPE_ARM_PX);
          stage.classList.toggle('is-swipe-next',dx>=SWIPE_ARM_PX);
          stage.classList.toggle('is-swipe-ready',dustish?(dx<=-SWIPE_DUST_PX):(dx>=SWIPE_NEXT_PX));
          stage.style.setProperty('--ffrq-swipe',
            Math.min(1,Math.abs(dx)/(dx<0?SWIPE_DUST_PX:SWIPE_NEXT_PX)).toFixed(3));
        }
        function release(){
          stage.classList.remove('is-swipe-dust','is-swipe-next','is-swipe-ready');
          stage.style.removeProperty('--ffrq-swipe');
          if(!cardbox)return;
          if(reduced||!cardbox.animate){cardbox.style.removeProperty('transform');return;}
          /* spring back from wherever the finger left it, never from a
             hard-coded start, or a short drag snaps instead of easing */
          var from=cardbox.style.transform||'translateX(0) rotate(0deg)';
          cardbox.style.removeProperty('transform');
          try{cardbox.animate([{transform:from},{transform:'translateX(0) rotate(0deg)'}],{duration:200,easing:'cubic-bezier(.22,1,.36,1)'});}catch(_){ }
        }
        /* `then` receives a `restore` it can call to bring the card back, for
           the one path where flying out does not mean the card is gone: the
           player answered "Keep it" to the dust confirm. Without it the card
           would still be off-screen with the sheet closed over an empty
           stage. */
        function fly(dir,then){
          state.suppressClickUntil=Date.now()+280;
          stage.classList.remove('is-swipe-dust','is-swipe-next','is-swipe-ready');
          stage.style.removeProperty('--ffrq-swipe');
          var unlock=function(){box.querySelectorAll('.ffshop-rollq-side,[data-rollq-favorite]').forEach(function(b){b.disabled=false;});};
          if(reduced||!cardbox||!cardbox.animate){
            if(cardbox)cardbox.style.removeProperty('transform');
            then(function(){ if(cardbox)cardbox.style.removeProperty('transform'); unlock(); });
            return;
          }
          state.advancing=true;
          box.querySelectorAll('.ffshop-rollq-side,[data-rollq-favorite]').forEach(function(b){b.disabled=true;});
          var from=cardbox.style.transform||'translateX(0) rotate(0deg)';
          var to='translateX('+(dir*330)+'px) rotate('+(dir*7)+'deg)';
          /* fill:forwards holds the card off-screen after the animation ends.
             That is deliberate: the next thing on screen is either a NEW card
             (paintCard replaces this node outright) or the dust confirm, and
             a card that snapped back into place under the sheet would read as
             the swipe having failed. */
          var a=cardbox.animate([{transform:from,opacity:1},{transform:to,opacity:.03}],
            {duration:190,easing:'ease-in',fill:'forwards'});
          function restore(){
            try{a.cancel();}catch(_){ }
            cardbox.style.removeProperty('transform');
            unlock();
            try{cardbox.animate([{transform:to,opacity:.03},{transform:'translateX(0) rotate(0deg)',opacity:1}],
              {duration:230,easing:'cubic-bezier(.22,1,.36,1)'});}catch(_){ }
          }
          a.onfinish=function(){state.advancing=false;then(restore);};
          /* ⛔ oncancel must clear `advancing` too. A cancelled animation with
             the latch still set locks every control on the screen forever —
             the same shape of trap R554 found in the event sheet. */
          a.oncancel=function(){state.advancing=false;cardbox.style.removeProperty('transform');unlock();};
        }
        stage.onpointerdown=function(e){
          if(state.rolling||state.advancing||e.target.closest('button,a,input,select'))return;
          down={x:e.clientX,y:e.clientY,id:e.pointerId};drag=false;
          try{stage.setPointerCapture(e.pointerId);}catch(_){}
        };
        stage.onpointermove=function(e){
          if(!down||e.pointerId!==down.id)return;
          var dx=e.clientX-down.x,dy=e.clientY-down.y;
          if(!drag&&Math.abs(dx)>SWIPE_ARM_PX&&Math.abs(dx)>Math.abs(dy)+8)drag=true;
          if(drag)paintDrag(dx);
        };
        function end(e){
          if(!down||e.pointerId!==down.id)return;
          var dx=e.clientX-down.x,dy=e.clientY-down.y;
          var horizontal=Math.abs(dx)>Math.abs(dy)*1.25;
          down=null;
          /* ⛔ ANY drag must swallow the click that follows it, not just one
             that commits. `cardEl.onclick` flips the card, so a 50px drag that
             springs back was ALSO turning the card over — the gesture would
             cancel and something else would happen instead of nothing. */
          if(drag)state.suppressClickUntil=Date.now()+280;
          if(!drag||!horizontal||state.advancing){release();return;}
          if(dx>=SWIPE_NEXT_PX){fly(1,function(){advance(true);});return;}
          if(dx<=-SWIPE_DUST_PX){
            if(dustArmed()){fly(-1,function(restore){dustCurrent(current(),restore);});return;}
            release();
            state.error='Roll this card first, or swipe right to skip it.';
            paintCard(state.error);
            return;
          }
          release();
        }
        stage.onpointerup=end;
        stage.onpointercancel=function(){down=null;release();};
      }
      function paintDone(){
        box.classList.remove('is-running');
        var rolled=Object.keys(state.rolled).length;
        var paused=state.skipped>0;
        box.innerHTML='<button type="button" class="ffshop-dust-close" aria-label="Close">&times;</button><div class="ffshop-rollq-state ffshop-rollq-complete"><span class="ffshop-rollq-state-icon is-done" aria-hidden="true">&#10022;</span><div class="ffshop-rollq-eyebrow">Review complete</div><h3>'+(paused?'Review paused':'All caught up')+'</h3><p>'+(paused?(state.skipped+' card'+(state.skipped===1?' still needs':'s still need')+' abilities. Reopen Quick review whenever you are ready to finish.'):'Your verified queue is finished. Reopen it any time new cards need abilities.')+'</p><div class="ffshop-rollq-recap"><span><b>'+rolled+'</b><small>rolled</small></span><span><b>'+state.favorites+'</b><small>favorited</small></span><span><b>'+state.dusted+'</b><small>dusted</small></span><span><b>'+state.skipped+'</b><small>skipped</small></span></div>'+
          /* ⛔ R566 · A DESTRUCTIVE SETTING NEEDS AN OFF SWITCH SOMEWHERE.
             "Do not ask again" is a checkbox inside a sheet that, once
             ticked, never opens again — so the control that turns it off was
             inside the thing it turns off. This is where a sweep ends, so it
             is where the setting is reachable, and it only appears when it is
             actually on. */
          (dustQuickSkip()
            ? '<div class="ffshop-rollq-quiet-note">Dusting does not ask you to confirm on this device. '+
              '<button type="button" class="ffshop-rollq-quiet-undo" data-rollq-ask-again>Ask me again</button></div>'
            : '')+
          '<button type="button" class="ffshop-rollq-primary" data-rollq-done>Back to your cards</button></div>';
        var _askAgain=box.querySelector('[data-rollq-ask-again]');
        if(_askAgain)_askAgain.onclick=function(){
          dustQuickSkipSet(false);
          _askAgain.parentNode.textContent='Dusting will ask you to confirm again.';
        };
        box.querySelectorAll('.ffshop-dust-close,[data-rollq-done]').forEach(function(b){b.onclick=modal.__dustClose;});
        queueAnnounce(paused?'Review paused. Some cards still need attention.':'Review complete. All caught up.');
        try{box.querySelector('[data-rollq-done]').focus();}catch(_){ }
      }
      modal.addEventListener('keydown',function(e){
        if(state.rolling||state.advancing||e.altKey||e.ctrlKey||e.metaKey)return;
        /* Never steal activation from the focused pill/button. Enter and
           Space must do what the focused control says; the modal shortcuts
           apply only while focus is on the review surface itself. */
        if(e.target&&e.target.closest&&e.target.closest('button,a,[role="button"],[tabindex],input,select,textarea'))return;
        if(e.key==='ArrowRight'){e.preventDefault();advance(true);}
        /* R566: the keyboard learns the same left/right the thumb does, so a
           desktop sweep is the identical two moves rather than a hunt for the
           right button. ArrowLeft is armed on exactly the same condition as
           the swipe and the Dust button — the card must be rolled. */
        else if(e.key==='ArrowLeft'&&current()&&!(missing(current())||[]).length&&!recovering(current()).length){e.preventDefault();dustCurrent(current());}
        else if((e.key==='f'||e.key==='F')&&current()&&!(missing(current())||[]).length&&!recovering(current()).length){e.preventDefault();toggleFavorite(current());}
        else if((e.key==='d'||e.key==='D')&&current()&&!(missing(current())||[]).length&&!recovering(current()).length){e.preventDefault();dustCurrent(current());}
      });
      paintCard();
    }
    load();
  }

  /* ⛔ ============ R568 · THE EDITOR REACHES THE HALL ============
     Jan: "in the main hall, if you own the card ... also have an Edit Text
     function so they can edit the text on the card ... Only if they own it, it
     should show up, and then when they edit it, it will edit that card's text
     for everyone."

     The editor already existed and already did all of that — it was simply
     unreachable from the Hall, which is where people actually look at their
     hero. Nothing about the SAVE changes: `save_card_text` still writes to
     `characters.card_meta`, which is global, and still refuses anyone whose
     email is not the character's `auth_email` (or Jan's).
     `opts.onSaved` is the whole addition. From the shop, saving reopens the
     shop inspector; from the Hall that would throw a shop overlay on top of
     the Hall page the owner was reading, so the Hall passes its own repaint
     instead. */
  /* ⛔ ============ R609 · THE CARD ITSELF, FRONT AND BACK, AS A PDF ============
     Jan: "there is a card view that I also want the export to work for ... the
     card view right now is this really nice designed card that I made, so I
     want it to be able to, when you export it, export that specific card, and
     it shows the front and the back."

     ⛔ IT RENDERS THE REAL CARD, IT DOES NOT REDRAW IT.
     The Hall's existing Character Card export is ~690 lines of hand-written
     canvas that paints a DIFFERENT layout, and the one thing this repository
     has been bitten by over and over is two copies of one rule with nothing
     comparing them (the payout tables, the play cap, `readPlays`). Painting
     Jan's card a second time in canvas would be exactly that, and it would
     drift the first time anybody touched `cardBlocksHtml` or a rarity colour.
     So this mounts `fullCardHtml` — the same function the Hall, the shop
     inspector and the pack opener use — off screen at its native 380px, and
     photographs it. Change the card, and the export changes with it.

     ⛔ THE ART IS INLINED FIRST, NOT LEFT TO useCORS. html2canvas re-requests
     every image with a crossOrigin attribute and silently drops the ones that
     refuse, which would export a card with a hole where the hero is. Fetching
     it into a data URL here means the capture cannot fail that way, and a
     failure is visible HERE, before a file is produced.

     ⛔ THE CARD'S HEIGHT IS PINNED BEFORE EITHER CAPTURE. The front is the only
     face in flow, so hiding it to photograph the back would collapse the card
     to its 650px floor and the two pages would not be the same size. */
  /* ⛔ ====== R609 · html2canvas 1.4.1 THROWS ON MODERN CSS COLOURS ======
     Measured, not guessed: `harness/browser/cardpdftest.cjs` runs the exact
     capture below against the shipped stylesheet and the first attempt died on
        Attempting to parse an unsupported color function "color"
     and produced NOTHING. This card leans on `color-mix(in srgb, var(--rar) …)`
     in sixteen places, Chrome resolves those to `color(srgb r g b)` at computed
     value time, and html2canvas's own colour parser predates that syntax and
     raises rather than skipping. Two of those sixteen are on the export path:
     the tier glow on `.ffshop-card-*`, and — the one that matters — the rarity
     RIM on `.ffshop-fa::before`, which is the coloured edge of every
     FramedFantasy card.

     So every colour the capture will read is resolved to `rgba()` first, and
     the browser itself does the resolving: a 1x1 canvas is asked to fill with
     the value and the pixel is read back. That is exact for any colour space
     the browser understands and needs no colour maths here that could drift
     from the spec.

     ⛔ IT WALKS THE CLONE, NOT THE LIVE TREE, AND THAT IS THE WHOLE TRICK.
     The first cut read the live card, wrote inline fixes onto the matching
     clone nodes, and emitted a scoped stylesheet for the pseudo-elements a
     pseudo cannot take an inline style for. It STILL threw, and the browser
     said why: by the time `onclone` runs, html2canvas has already flattened
     every pseudo into a REAL element of its own
     (`___html2canvas___pseudoelement_before`) carrying the original computed
     value as an inline style. A stylesheet targeting `::before` can never
     reach an element that is no longer a pseudo, and an inline style outranks
     it anyway. Reading the clone finds those stand-ins like any other node.
     The `::before` / `::after` sweep is kept underneath as belt and braces for
     a build that does not flatten.

     ⛔ NOTHING HERE CHANGES THE LIVE CARD. Every read and every write happens
     inside html2canvas's throwaway clone document. */
  var FFX_MODERN = /(?:^|[\s,(])(?:color|color-mix|lab|lch|oklab|oklch|hwb)\(/;
  var FFX_COLOR_PROPS = ['color','background-color','background-image',
    'border-top-color','border-right-color','border-bottom-color','border-left-color',
    'outline-color','box-shadow','text-shadow','fill','stroke',
    'text-decoration-color','column-rule-color','-webkit-text-fill-color'];
  function ffxCssColorToRgb(v){
    try{
      var cv = ffxCssColorToRgb._c || (ffxCssColorToRgb._c = document.createElement('canvas'));
      cv.width = cv.height = 1;
      var cx = cv.getContext('2d', {willReadFrequently:true});
      cx.clearRect(0,0,1,1);
      cx.fillStyle = 'rgba(0,0,0,0)';
      cx.fillStyle = v;
      cx.fillRect(0,0,1,1);
      var d = cx.getImageData(0,0,1,1).data;
      return 'rgba('+d[0]+','+d[1]+','+d[2]+','+(Math.round(d[3]/255*1000)/1000)+')';
    }catch(_){ return null; }
  }
  function ffxSanitizeColorValue(v){
    var s = String(v||'');
    if(!FFX_MODERN.test(s)) return s;
    var out = '', i = 0, guard = 0;
    while(i < s.length && guard++ < 400){
      var m = FFX_MODERN.exec(s.slice(i));
      if(!m){ out += s.slice(i); return out; }
      /* the match may include one leading separator character; keep it */
      var lead = m[0].charAt(0) === '(' || /[a-z]/i.test(m[0].charAt(0)) ? 0 : 1;
      var start = i + m.index + lead;
      out += s.slice(i, start);
      /* walk to the matching close paren so a nested color-mix comes out whole */
      var j = start, depth = 0, opened = false;
      for(; j < s.length; j++){
        if(s.charAt(j) === '('){ depth++; opened = true; }
        else if(s.charAt(j) === ')'){ depth--; if(opened && depth === 0) break; }
      }
      if(j >= s.length){ out += s.slice(start); return out; }
      out += (ffxCssColorToRgb(s.slice(start, j+1)) || 'rgba(0,0,0,0)');
      i = j + 1;
    }
    return out + s.slice(i);
  }
  /* ⛔ ====== R612 · THE THREE THINGS html2canvas CANNOT PAINT ======
     Jan, of the first export: "the export looks pretty horrendous right now ...
     what they see as the card should be a 1 to 1 export."
     MEASURED against a real mythic FramedFantasy card, which is where all three
     land at once:
       1. `.ffshop-card-mythic .ffshop-fa::before` is a CONIC gradient — the
          rainbow rim. html2canvas 1.4.1 does linear and radial only, so the
          rim vanished entirely.
       2. `.ffshop-artfull` carries a `mask-image` that fades the art into the
          text panel. Unsupported, so the art ended in a hard rectangle with a
          visible seam across the card. This is the one that reads as broken.
       3. `.ffshop-gem-mythic em` is another conic gradient, so the gem
          exported as a black disc.
     A non-FF card has none of them, which is why the classic layout looked
     fine and his did not.

     Both cures are the same shape: paint the thing the browser can paint, with
     the browser, and hand html2canvas a plain image or a plain gradient. No
     colour or geometry is invented here — every value is read back out of
     `getComputedStyle`, so the card stays the single source of truth. */
  function ffxSplitTop(str){
    /* commas inside rgb(...) are not separators */
    var out = [], depth = 0, start = 0, i;
    for(i = 0; i < str.length; i++){
      var ch = str.charAt(i);
      if(ch === '(') depth++;
      else if(ch === ')') depth--;
      else if(ch === ',' && depth === 0){ out.push(str.slice(start, i).trim()); start = i + 1; }
    }
    out.push(str.slice(start).trim());
    return out.filter(function(x){ return x !== ''; });
  }
  /* "rgb(1, 2, 3) 40%" -> {color:'rgb(1, 2, 3)', stops:[0.4]}; a stop may carry
     TWO positions (the gem ring writes `rgba(...) 0 12%`), which is a hard edge
     and needs the colour added at both. */
  function ffxParseStop(part){
    var m = /^(.*?)((?:\s+[-\d.]+(?:deg|%|turn|rad))+)\s*$/.exec(part);
    if(!m) return { color: part, pos: [] };
    var pos = m[2].trim().split(/\s+/).map(function(p){
      var n = parseFloat(p);
      if(/turn$/.test(p)) return n;
      if(/rad$/.test(p)) return n / (Math.PI * 2);
      if(/deg$/.test(p)) return n / 360;
      return n / 100;
    });
    return { color: m[1].trim(), pos: pos };
  }
  function ffxConicToDataUrl(value, w, h){
    if(!w || !h) return '';
    var inner = value.slice(value.indexOf('conic-gradient(') + 15);
    var depth = 1, end = 0;
    for(; end < inner.length; end++){
      if(inner.charAt(end) === '(') depth++;
      else if(inner.charAt(end) === ')'){ depth--; if(!depth) break; }
    }
    inner = inner.slice(0, end);
    var parts = ffxSplitTop(inner);
    var fromDeg = 0, cxPct = 50, cyPct = 50;
    if(parts.length && /^(from|at)\b/.test(parts[0])){
      var head = parts.shift();
      var fm = /from\s+([-\d.]+)deg/.exec(head); if(fm) fromDeg = parseFloat(fm[1]);
      var am = /at\s+([-\d.]+)%\s+([-\d.]+)%/.exec(head);
      if(am){ cxPct = parseFloat(am[1]); cyPct = parseFloat(am[2]); }
    }
    var stops = parts.map(ffxParseStop);
    if(!stops.length) return '';
    var cv = document.createElement('canvas');
    cv.width = Math.max(1, Math.round(w)); cv.height = Math.max(1, Math.round(h));
    var cx = cv.getContext('2d');
    if(!cx || !cx.createConicGradient) return '';
    /* CSS 0deg is twelve o'clock, canvas 0rad is three o'clock */
    var g = cx.createConicGradient((fromDeg - 90) * Math.PI / 180,
      cv.width * cxPct / 100, cv.height * cyPct / 100);
    var last = 0;
    stops.forEach(function(s, idx){
      var at = s.pos.length ? s.pos : [stops.length === 1 ? 0 : idx / (stops.length - 1)];
      at.forEach(function(t){
        t = Math.max(last, Math.min(1, t));
        last = t;
        try{ g.addColorStop(t, s.color); }catch(_){ }
      });
    });
    cx.fillStyle = g;
    cx.fillRect(0, 0, cv.width, cv.height);
    try{ return cv.toDataURL('image/png'); }catch(_){ return ''; }
  }
  /* A gradient MASK fades an element out. html2canvas ignores it, so the export
     paints the backdrop over the faded part instead, which is exactly what the
     mask reveals. The stops are the mask's own, with each alpha inverted and
     each colour swapped for the real backdrop read off the ancestors, so a
     retuned mask needs no change here. */
  function ffxBackdrop(el, view){
    var n = el.parentElement, guard = 0;
    while(n && guard++ < 12){
      var bg = '';
      try{ bg = view.getComputedStyle(n).backgroundColor || ''; }catch(_){ bg = ''; }
      if(bg && bg !== 'transparent' && !/rgba\(\s*0,\s*0,\s*0,\s*0\s*\)/.test(bg)) return bg;
      n = n.parentElement;
    }
    return 'rgb(10,10,15)';
  }
  function ffxInvertMask(mask, backdrop){
    var open = mask.indexOf('linear-gradient(');
    if(open < 0) return '';
    var inner = mask.slice(open + 16), depth = 1, end = 0;
    for(; end < inner.length; end++){
      if(inner.charAt(end) === '(') depth++;
      else if(inner.charAt(end) === ')'){ depth--; if(!depth) break; }
    }
    inner = inner.slice(0, end);
    var parts = ffxSplitTop(inner);
    if(!parts.length) return '';
    var head = /^(to\b|[-\d.]+(deg|turn|rad))/.test(parts[0]) ? parts.shift() : '180deg';
    var bg = /(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)/.exec(backdrop);
    var rgb = bg ? (bg[1] + ',' + bg[2] + ',' + bg[3]) : '10,10,15';
    var out = parts.map(function(part){
      var st = ffxParseStop(part);
      var a = 1;
      var rgba = /rgba?\(([^)]*)\)/.exec(st.color);
      if(/^transparent$/i.test(st.color)) a = 0;
      else if(rgba){ var f = rgba[1].split(','); a = f.length > 3 ? parseFloat(f[3]) : 1; }
      if(!isFinite(a)) a = 1;
      var pos = st.pos.length
        ? st.pos.map(function(t){ return (t * 100) + '%'; }).join(' ')
        : '';
      return 'rgba(' + rgb + ',' + (Math.round((1 - a) * 1000) / 1000) + ')' + (pos ? ' ' + pos : '');
    });
    return 'linear-gradient(' + head + ',' + out.join(',') + ')';
  }
  /* Resolves every modern colour function inside html2canvas's clone, in
     place, and returns a short report the browser proof asserts on. */
  function ffxSanitizeCapture(cloneRoot){
    var doc = (cloneRoot && cloneRoot.ownerDocument) || document;
    var view = doc.defaultView || window;
    var all = [cloneRoot].concat([].slice.call(cloneRoot.querySelectorAll('*')));
    var css = '', tag = 0, fixed = 0, conic = 0, masked = 0, i, p;
    for(i = 0; i < all.length; i++){
      var el = all[i], cs;
      try{ cs = view.getComputedStyle(el); }catch(_){ continue; }
      for(p = 0; p < FFX_COLOR_PROPS.length; p++){
        var raw = ''; try{ raw = cs.getPropertyValue(FFX_COLOR_PROPS[p]) || ''; }catch(_){ raw = ''; }
        if(!raw || !FFX_MODERN.test(raw)) continue;
        try{ el.style.setProperty(FFX_COLOR_PROPS[p], ffxSanitizeColorValue(raw), 'important'); fixed++; }catch(_){ }
      }
      /* R612: a conic gradient becomes a picture of itself, at this element's
         real size, drawn by the browser that owns the spec. */
      try{
        var bgi = cs.getPropertyValue('background-image') || '';
        if(bgi.indexOf('conic-gradient(') !== -1){
          var r = el.getBoundingClientRect();
          /* ⛔ LAYER BY LAYER, NOT THE WHOLE PROPERTY. `background-image` is a
             comma separated STACK, and the mythic gem is a white sheen laid
             OVER its conic. Replacing the property wholesale kept the conic and
             threw the sheen away, so the gem exported flat. Only the layers
             that are conic become pictures; every other layer is left exactly
             as the browser computed it. */
          var layers = ffxSplitTop(bgi).map(function(layer){
            if(layer.indexOf('conic-gradient(') === -1) return layer;
            var u = ffxConicToDataUrl(layer, r.width, r.height);
            if(!u) return layer;
            conic++;
            return 'url("' + u + '")';
          });
          if(conic) el.style.setProperty('background-image', layers.join(','), 'important');
        }
      }catch(_){ }
      /* R612: a gradient mask becomes the backdrop painted over what the mask
         was hiding. Same fade, same edge, in a property html2canvas honours. */
      try{
        var mk = cs.getPropertyValue('mask-image') || cs.getPropertyValue('-webkit-mask-image') || '';
        if(mk && mk !== 'none' && mk.indexOf('linear-gradient(') !== -1){
          var over = ffxInvertMask(mk, ffxBackdrop(el, view));
          if(over){
            var veil = doc.createElement('span');
            veil.setAttribute('data-ffx-veil', '1');
            veil.style.cssText = 'position:absolute;inset:0;pointer-events:none;z-index:9;background:' + over + ';';
            if(cs.getPropertyValue('position') === 'static') el.style.setProperty('position', 'relative', 'important');
            el.appendChild(veil);
            el.style.setProperty('mask-image', 'none', 'important');
            el.style.setProperty('-webkit-mask-image', 'none', 'important');
            masked++;
          }
        }
      }catch(_){ }
      for(var q = 0; q < 2; q++){
        var pe = q ? '::after' : '::before', ps = null;
        try{ ps = view.getComputedStyle(el, pe); }catch(_){ ps = null; }
        if(!ps) continue;
        var content = ''; try{ content = ps.getPropertyValue('content') || ''; }catch(_){ content = ''; }
        if(!content || content === 'none') continue;
        var decls = '';
        for(p = 0; p < FFX_COLOR_PROPS.length; p++){
          var pv = ''; try{ pv = ps.getPropertyValue(FFX_COLOR_PROPS[p]) || ''; }catch(_){ pv = ''; }
          if(!pv || !FFX_MODERN.test(pv)) continue;
          decls += FFX_COLOR_PROPS[p] + ':' + ffxSanitizeColorValue(pv) + ' !important;';
        }
        if(!decls) continue;
        tag++;
        try{ el.setAttribute('data-ffx', String(tag)); }catch(_){ continue; }
        css += '[data-ffx="' + tag + '"]' + pe + '{' + decls + '}';
      }
    }
    /* ⛔ R612 · THE EXPORT IS A STILL, SO IT HAS TO PICK A MOMENT.
       Three things on a mythic card animate forever: the rim hue-rotates, the
       gem hue-rotates, and the holo sweeps its background-position. Capturing
       "whatever it looked like at that millisecond" makes the same hero export
       differently every time and makes this proof impossible to compare against
       anything. Frozen to the authored base state, which is the design as
       written rather than a random frame of it. */
    css += '*,*::before,*::after{animation:none!important;transition:none!important}';
    if(css){
      try{
        var st = doc.createElement('style');
        st.textContent = css;
        (doc.head || doc.documentElement).appendChild(st);
      }catch(_){ }
    }
    return {inline: fixed, pseudo: tag, conic: conic, masked: masked};
  }

  var cardPdfBusy = false;
  function ensureHtml2Canvas(){
    if(window.html2canvas)return Promise.resolve();
    if(ensureHtml2Canvas._inflight)return ensureHtml2Canvas._inflight;
    ensureHtml2Canvas._inflight = new Promise(function(resolve,reject){
      var s=document.createElement('script');
      s.src='https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
      s.async=true;
      s.onload=function(){resolve();};
      s.onerror=function(){ensureHtml2Canvas._inflight=null;reject(new Error('html2canvas failed to load'));};
      document.head.appendChild(s);
    });
    return ensureHtml2Canvas._inflight;
  }
  /* same shape as hoh-section-core's loadImageForPdf, kept local so the card
     renderer and its exporter live in one file */
  function inlineArt(url){
    return new Promise(function(resolve){
      if(!url){resolve(null);return;}
      var img=new Image();
      img.crossOrigin='anonymous';
      img.onload=function(){
        try{
          var w=img.naturalWidth,h=img.naturalHeight,max=1400;
          if(w>max||h>max){var r=Math.min(max/w,max/h);w=Math.round(w*r);h=Math.round(h*r);}
          var cv=document.createElement('canvas');cv.width=w;cv.height=h;
          var cx=cv.getContext('2d');cx.drawImage(img,0,0,w,h);
          resolve(cv.toDataURL('image/png'));
        }catch(_){ resolve(null); }
      };
      img.onerror=function(){resolve(null);};
      img.src=url;
    });
  }
  function cardPdfSlug(s){
    return String(s||'character').trim().toLowerCase()
      .replace(/[‘’']/g,'').replace(/[^a-z0-9]+/g,'-')
      .replace(/^-+|-+$/g,'') || 'character';
  }
  window.hohShopExportCardPdf = function(heroId){
    if(cardPdfBusy){ toast('The card is still being prepared.'); return; }
    var hero = heroById(heroId), snap = collEntry(heroId);
    if(!hero && !snap){ toast('That card could not be found. Reload the Hall and try again.'); return; }
    cardPdfBusy = true;
    toast('Building the card PDF…');
    var host = document.createElement('div');
    host.id = 'hoh-cardpdf-stage';
    host.setAttribute('aria-hidden','true');
    /* on screen for layout, off screen for eyes. `display:none` would measure
       zero and `visibility:hidden` is skipped by html2canvas. */
    host.style.cssText='position:fixed;left:-12000px;top:0;width:380px;z-index:-1;pointer-events:none;opacity:1;';
    host.innerHTML = fullCardHtml(heroId, snap || {}, {hideHearts:true});
    document.body.appendChild(host);
    var cardEl = host.querySelector('.ffshop-card');
    var cleanup = function(){ try{ host.remove(); }catch(_){ } cardPdfBusy = false; };
    if(!cardEl){ cleanup(); toast('That card could not be built. Reload the Hall and try again.'); return; }
    var artSrc = (hero && (hero.image_url || hero.image_thumb_url)) || (snap && snap.img) || '';
    var name = (hero && hero.name) || (snap && snap.name) || 'Hero';

    Promise.all([
      window.hohEnsureJsPDF ? window.hohEnsureJsPDF() : Promise.reject(new Error('jsPDF unavailable')),
      ensureHtml2Canvas(),
      inlineArt(artSrc),
      (document.fonts && document.fonts.ready) ? document.fonts.ready.catch(function(){}) : Promise.resolve()
    ]).then(function(res){
      var dataUrl = res[2];
      if(dataUrl){
        var live = cardEl.querySelector('.ffshop-art img, .ffshop-artfull img');
        if(live) live.src = dataUrl;
      }
      /* the same text fitting the live card gets, or a long name exports at a
         size the screen never showed */
      try{ fitLine(cardEl.querySelector('.ffshop-fit'),11); fitZone(cardEl.querySelector('.ffshop-fitzone')); }catch(_){ }
      cardEl.style.height = Math.max(650, cardEl.offsetHeight) + 'px';
      var front = cardEl.querySelector('.ffshop-face.is-front');
      var back  = cardEl.querySelector('.ffshop-face.is-back');
      var shoot = function(){
        return window.html2canvas(cardEl, {
          scale: 3, backgroundColor: null, useCORS: true, allowTaint: false,
          logging: false, width: 380, height: cardEl.offsetHeight,
          onclone: function(doc){
            var c = doc.getElementById('hoh-cardpdf-stage');
            if(!c) return;
            c.style.left = '0';
            /* R609: resolve every modern colour function before html2canvas
               reaches one and raises. `doc` is the whole clone document and
               `c` is our card inside it; the sweep runs from the card, which
               is the only subtree that will be rendered. */
            try{ ffxSanitizeCapture(c); }catch(_){ }
            var f = c.querySelector('.ffshop-face.is-front');
            var b = c.querySelector('.ffshop-face.is-back');
            /* the flip is a VISIBILITY swap in CSS, and html2canvas honours
               visibility, so each page is composed by taking the other face
               out of the document entirely rather than by adding `.on` */
            /* the back is laid out at all times (R572 keeps both faces in the
               document to cure a WebKit paint fault), so it has to be taken
               out of the clone or html2canvas paints it over the front */
            if(f) f.style.display = '';
            if(b) b.style.display = 'none';
            /* ⛔ NOTHING ELSE IS HIDDEN HERE. The first cut also blanked the
               retired flip-hint overlay, which this file does not emit, and
               harness/r558-ux.cjs asserts its class name appears nowhere in
               this file AT ALL — comments included, which is how it caught
               that line. A defensive hide against markup that cannot exist is
               dead code that reads as a feature, so it is gone rather than
               reworded. If a future face needs an element kept out of the
               export, hide it by a class this file actually writes. */
          }
        });
      };
      /* ⛔ R612 (Jan): "instead just export the front of the card". The back is
         a fixed FramedFantasy logo plate identical on every hero, so a second
         page of it carried no information about the card being exported and
         doubled the file for nothing. */
      return shoot();
    }).then(function(fc){
      var jsPDF = window.jspdf && window.jspdf.jsPDF;
      if(!jsPDF) throw new Error('jsPDF unavailable');
      /* 2.5in wide is a real trading card, and the height follows the card's
         own aspect so a hero with long card text prints taller instead of
         squashed. The same convention character-card-builder.js already uses. */
      var W = 2.5, H = Math.round((W * (fc.height / fc.width)) * 100) / 100;
      var pdf = new jsPDF({orientation:'portrait', unit:'in', format:[W, H]});
      pdf.addImage(fc.toDataURL('image/png'), 'PNG', 0, 0, W, H, undefined, 'FAST');
      pdf.save(cardPdfSlug(name) + '-character-card.pdf');
      toast('Card saved.');
    }).catch(function(err){
      try{ console.error('[cards] R609 CARD PDF FAILED hero=' + heroId + ' reason=' + ((err && err.message) || 'unknown')); }catch(_){ }
      toast(/load/i.test(String((err && err.message) || ''))
        ? 'The PDF tools could not load. Check your connection and try again.'
        : 'That card could not be exported. Try again in a moment.');
    }).then(cleanup, cleanup);
  };

  window.hohShopEditCardText = function(heroId, opts){
    opts = opts || {};
    try{
      var _h = heroById(heroId);
      /* Fail closed and SAY SO. An editor that opens and then cannot save is
         worse than a link that explains itself. */
      var _can = (typeof window.hohYourHallCanEditCharacter === 'function')
        ? window.hohYourHallCanEditCharacter(_h)
        : false;
      if(!_can){ toast('Only the hero’s owner can rewrite this card.'); return; }
      openEdit(heroId, null, opts);
    }catch(_){ toast('The card text editor could not open. Reload and try again.'); }
  };
  function openEdit(heroId, closeInspector, opts){
    opts = opts || {};
    var hero = heroById(heroId);
    var _snapE = collEntry(heroId);
    var t = cardText(hero, _snapE);
    /* R438 (Jan): "non ff cards in the edit text should not have a quote for
       the flavor line". The quote is the italic line under the divider on the
       card face, and only a FramedFantasy card draws that block — cardText's
       campaign fallback at the bottom of cardText is FF-only for the same
       reason. Showing the field to a non-FF owner offered them a place to type
       something that would then be stored and never appear anywhere. */
    var _isFFE = hero ? !!hero.is_ff_portrait : !!(_snapE && _snapE.ff);
    var wrap = document.createElement('div');
    wrap.className = 'ffshop-edit';
    try{ wrap.style.zIndex = '100086'; }catch(_){}
    wrap.innerHTML =
      '<div class="ffshop-edit-box" role="dialog" aria-modal="true" aria-label="Edit card text">' +
        '<h4>Your card, your words</h4>' +
        /* R568: say out loud that this is not a private note. The same words
           print on every collector's copy of this card, and an owner who
           learns that AFTER typing has learned it too late. */
        '<p class="ffshop-edit-lede">Everyone who holds this card sees what you write here.</p>' +
        '<label for="ffshop-ed-legend">Legend (the description)</label>' +
        /* R423: a card still wearing the generic line starts EMPTY with that
           line as the placeholder, so the owner writes rather than deletes,
           and saving an untouched box never stores the placeholder as if it
           were their words. */
        '<textarea rows="4" maxlength="'+CARD_LEGEND_MAX+'" id="ffshop-ed-legend" placeholder="'+esc(GENERIC_LEGEND)+'">'+
          esc((t.pending || t.legend === GENERIC_LEGEND) ? '' : t.legend)+'</textarea>' +
        '<div class="cnt"><span id="ffshop-ed-lc">0</span>/'+CARD_LEGEND_MAX+'</div>' +
        (_isFFE
          ? '<label for="ffshop-ed-quote">Quote (the flavor line)</label>' +
            '<input type="text" maxlength="'+CARD_QUOTE_MAX+'" id="ffshop-ed-quote" value="'+esc(t.quote)+'">' +
            /* R568: the legend had a live counter and the quote did not, so
               the shorter field was the one that ran out without warning. */
            '<div class="cnt"><span id="ffshop-ed-qc">0</span>/'+CARD_QUOTE_MAX+'</div>'
          /* R438/R568 (Jan): "characters that aren't an FF portrait, they
             should not be able to have a quote." Saying so is kinder than an
             absence the owner has to work out. */
          : '<p class="ffshop-edit-note">The flavor quote is part of the FramedFantasy portrait card, so this one carries just the description.</p>') +
        '<div class="ffshop-edit-actions">' +
          '<button type="button" class="ffshop-edit-cancel">Cancel</button>' +
          '<button type="button" class="ffshop-edit-save">Save</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(wrap);
    var ta = wrap.querySelector('#ffshop-ed-legend'), lc = wrap.querySelector('#ffshop-ed-lc');
    var upd = function(){
      lc.textContent = String(ta.value.length);
      lc.parentNode.classList.toggle('is-full', ta.value.length >= CARD_LEGEND_MAX);
    };
    ta.addEventListener('input', upd); upd();
    var qa = wrap.querySelector('#ffshop-ed-quote'), qc = wrap.querySelector('#ffshop-ed-qc');
    if(qa && qc){
      var updQ = function(){
        qc.textContent = String(qa.value.length);
        qc.parentNode.classList.toggle('is-full', qa.value.length >= CARD_QUOTE_MAX);
      };
      qa.addEventListener('input', updQ); updQ();
    }
    wrap.addEventListener('click', function(e){ if(e.target===wrap) close(); });
    /* R568: Escape closes it. Opened from the Hall this sits above a modal
       whose own Escape handler is behind it, so without this the key would
       either do nothing or close the WRONG dialog and strand the editor. */
    function close(){
      try{ document.removeEventListener('keydown', onKey, true); }catch(_){ }
      wrap.remove();
    }
    function onKey(e){
      if(e.key !== 'Escape') return;
      e.preventDefault(); e.stopPropagation();
      if(e.stopImmediatePropagation) e.stopImmediatePropagation();
      close();
    }
    document.addEventListener('keydown', onKey, true);
    /* and it opens with the cursor already in the field, because the only
       reason anyone opened it was to type */
    try{ setTimeout(function(){ try{ ta.focus(); ta.setSelectionRange(ta.value.length, ta.value.length); }catch(_){ ta.focus(); } }, 0); }catch(_){ }
    wrap.querySelector('.ffshop-edit-cancel').addEventListener('click', function(){ close(); });
    wrap.querySelector('.ffshop-edit-save').addEventListener('click', function(){
      var legend = ta.value.trim();
      /* R438: no quote box on a non-FF card. Send the value BACK unchanged
         rather than null, so opening the editor on a card that somehow holds
         a quote can never silently delete it. */
      var _qEl = wrap.querySelector('#ffshop-ed-quote');
      var quote = _qEl ? _qEl.value.trim() : String(t.quote || '').trim();
      rpc('save_card_text', {
        user_email: email(), user_pin: pin(), p_character_id: heroId,
        p_title: null, p_epithet: null, p_legend: legend || null, p_quote: quote || null
      }).then(function(res){
        var d = res && res.data;
        if(d && d.ok){
          var h = heroById(heroId);
          if(h){ h.card_meta = Object.assign({}, h.card_meta||{}, { legend: legend, quote: quote, edited: true }); }
          toast('Card text saved');
          wrap.remove();
          if(closeInspector) closeInspector();
          /* R568: the Hall passes its own repaint. Only the shop reopens the
             shop inspector, because only the shop was showing it. */
          if(typeof opts.onSaved === 'function'){ try{ opts.onSaved(h); }catch(_){ } }
          else window.hohShopInspect(heroId);
        } else {
          /* say what the server said when it says anything — a blanket
             "check your sign-in" sent owners chasing the wrong problem. */
          var _why = (d && (d.error || d.status)) ? String(d.error || d.status) : '';
          toast(_why ? ('Could not save card text: ' + _why) : 'Could not save. Check your sign-in and try again.');
        }
      });
    });
  }

  /* ---------------- pack reveal modal ---------------- */

  /* The dim that cannot be lost: inline styles on the modal and its
     backdrop, applied every time content is (re)built, so no
     stylesheet vintage, cache state, or override can undo it. */
  function hardenModal(m){
    try{
      m.style.position='fixed';
      m.style.top='0'; m.style.left='0'; m.style.right='0'; m.style.bottom='0';
      m.style.zIndex='100070';
      m.style.alignItems='center'; m.style.justifyContent='center';
      /* R242: the dim lives ON THE MODAL ITSELF. The reveal cards render
         centered, which proves this element covers the viewport — so this
         element carries the dark + blur. The child backdrop kept below is
         redundant depth; some live stylesheet was beating it, and nothing
         needs it anymore. */
      /* R244: a single lighter veil so the Hall reads through faintly behind
         the cards, not a full blackout. Light blur keeps it a soft dim. */
      m.style.background='rgba(6,5,10,0.58)';
      try{ m.style.setProperty('-webkit-backdrop-filter','blur(3px)'); }catch(_){}
      try{ m.style.setProperty('backdrop-filter','blur(3px)'); }catch(_){}
      var bd=m.querySelector('.hoh-pack-backdrop');
      if(bd){
        bd.style.position='absolute';
        bd.style.top='0'; bd.style.left='0'; bd.style.right='0'; bd.style.bottom='0';
        bd.style.display='block';
        /* the modal now carries the whole dim, so the child layer stays clear */
        bd.style.background='transparent';
        try{ bd.style.setProperty('-webkit-backdrop-filter','none'); }catch(_){}
        try{ bd.style.setProperty('backdrop-filter','none'); }catch(_){}
      }
      /* one line of truth for any future report: what the browser actually computed */
      try{
        var cs=getComputedStyle(m);
        /* v35: the R323 diagnostic has done its job; it fired on every pack
           open in production. The computed values stay readable in devtools. */
      }catch(_){}
    }catch(_){}
  }
  /* Instant ceremony on press: the screen dims IMMEDIATELY and a pack
     pulses while the server rolls, so the click always answers. */
  var summonAt = 0;
  function showSummoning(){
    if(!modalEl){ modalEl=document.createElement('div'); modalEl.className='hoh-pack-modal ffshop-pack'; document.body.appendChild(modalEl); }
    modalEl.innerHTML =
      '<div class="hoh-pack-backdrop"></div>' +
      '<div class="ffshop-summonstage">' +
        '<div class="ffshop-summoncard"><img src="'+FF_LOGO+'" alt=""><span>Hall of Heroes</span></div>' +
        '<div class="ffshop-summontext">Shuffling the deck…</div>' +
      '</div>';
    hardenModal(modalEl);
    modalEl.style.display='flex';
    document.body.style.overflow='hidden';
    var m=modalEl;
    requestAnimationFrame(function(){ m.classList.add('hoh-pack-open'); });
    summonAt = Date.now();
    return modalEl.querySelector('.ffshop-summoncard');
  }
  /* v38 (Jan, fourth attempt at this bug, and the reason the first three
     missed): every terminal path in the collect flow closes the summon and then
     announces itself somewhere ELSE. A toast, a counter under a card that has
     already been retired, a console line. All of them are missable and the
     first two were literally invisible, so from the sofa the whole feature
     reads as "it says shuffling then goes away and nothing happens" no matter
     which of six different things actually went wrong.
     The fix is not another channel. It is to stop moving the message away from
     the eye. The summon modal is already full screen, already on top, already
     the thing being watched. So the outcome is painted INTO it, in place of
     "Shuffling the deck", and it stays until dismissed.
     It also carries the build stamp and the server's own words, because after
     three rounds of this the single most valuable thing a screenshot can tell
     me is which code is running and what the database actually said. */
  var SHOP_BUILD = 'v39';
  function showPackOutcome(title, body, tone, detail){
    try{
      if(!modalEl){ modalEl=document.createElement('div'); modalEl.className='hoh-pack-modal ffshop-pack'; document.body.appendChild(modalEl); }
      var col = tone === 'good' ? '#8fe0a5' : (tone === 'bad' ? '#e0968f' : '#e8c674');
      modalEl.innerHTML =
        '<div class="hoh-pack-backdrop"></div>' +
        '<div class="ffshop-summonstage" role="dialog" aria-modal="true" aria-live="assertive" style="text-align:center;max-width:min(430px,calc(100vw - 32px))">' +
          '<div style="font-family:\'Cinzel\',serif;font-size:10px;letter-spacing:.22em;text-transform:uppercase;color:'+col+';margin-bottom:10px">'+
            esc(title || 'The packs held')+'</div>' +
          '<div style="font-family:\'Cormorant Garamond\',Georgia,serif;font-size:17px;line-height:1.45;color:#e8e0d0">'+
            esc(body || '')+'</div>' +
          (detail ? '<div style="margin-top:12px;font-family:ui-monospace,Menlo,monospace;font-size:10.5px;line-height:1.5;color:#8a8296;word-break:break-word;background:rgba(0,0,0,.35);border:1px solid #2b2438;border-radius:8px;padding:8px 10px;text-align:left">'+esc(detail)+'</div>' : '') +
          '<button type="button" id="ffshopPackOk" style="margin:16px auto 0;display:block;min-height:44px;padding:11px 26px;'+
            'border-radius:8px;border:1px solid '+col+'66;background:rgba(10,8,15,.6);color:'+col+';cursor:pointer;'+
            'font-family:\'Cinzel\',serif;font-size:11px;letter-spacing:.16em;text-transform:uppercase">Close</button>' +
          '<div style="margin-top:10px;font-size:9px;letter-spacing:.12em;color:#4f4a5c">HALL BUILD '+SHOP_BUILD+'</div>' +
        '</div>';
      hardenModal(modalEl);
      modalEl.style.display='flex';
      document.body.style.overflow='hidden';
      var m=modalEl;
      requestAnimationFrame(function(){ try{ m.classList.add('hoh-pack-open'); }catch(_){} });
      var shut = function(){ try{ closeSummoning(); }catch(_){} };
      var ok = modalEl.querySelector('#ffshopPackOk');
      if(ok) ok.onclick = shut;
      var bd = modalEl.querySelector('.hoh-pack-backdrop');
      if(bd) bd.onclick = shut;
      /* nothing in this flow may wedge the screen, so it also lets go on its
         own. Long enough to read twice, short enough that a player who walked
         away does not come back to a locked page. */
      setTimeout(function(){ try{ if(m===modalEl && m.style.display!=='none') shut(); }catch(_){} }, 14000);
      try{ console.warn('[HOH pack ' + SHOP_BUILD + '] ' + (title||'') + ' :: ' + (body||'') + (detail ? ' :: ' + detail : '')); }catch(_){}
    }catch(_){ try{ closeSummoning(); }catch(__){} }
  }
  function closeSummoning(){
    if(!modalEl) return;
    var closingModal = modalEl, closingGeneration = packOperationGeneration;
    closingModal.classList.remove('hoh-pack-open');
    document.body.style.overflow='';
    setTimeout(function(){
      try{
        if(closingModal===modalEl && closingGeneration===packOperationGeneration){
          closingModal.style.display='none'; closingModal.innerHTML='';
        }
      }catch(_){}
    }, 240);
  }

  /* R242: How packs work — the real numbers, one clean panel. Opened
     from the shelf ("How packs work") and inside the reveal ("Odds and
     rules"), so the rules are one tap away at both moments. */
  function openHow(){
    if(document.getElementById('ffshop-how')) return;
    var dot=function(t){ return '<i class="ffshop-how-dot" style="--dc:'+(TIER_COLOR[t]||'#a09aae')+'"></i>'; };
    var m=document.createElement('div');
    m.id='ffshop-how';
    m.style.cssText='position:fixed;top:0;left:0;right:0;bottom:0;z-index:100078;display:flex;align-items:center;justify-content:center;background:rgba(6,5,10,0.9);padding:18px;opacity:0;transition:opacity .25s ease;';
    try{ m.style.setProperty('-webkit-backdrop-filter','blur(6px)'); m.style.setProperty('backdrop-filter','blur(6px)'); }catch(_){}
    m.innerHTML=
      '<div class="ffshop-how-box" role="dialog" aria-modal="true" aria-label="How packs work">' +
        '<h4>How Packs Work</h4>' +
        '<div class="ffshop-how-sec">The Pack</div>' +
        '<p>Five cards per pack. At least two are always Rare or better. One pack costs '+PACK_COST_HEARTS+' hearts, and one is free every day.</p>' +
        '<div class="ffshop-how-sec">The Odds, Per Card</div>' +
        '<div class="ffshop-how-rows">' +
          '<span>'+dot('common')+'Common<b>58%</b></span>' +
          '<span>'+dot('uncommon')+'Uncommon<b>16%</b></span>' +
          '<span>'+dot('rare')+'Rare<b>20%</b></span>' +
          '<span>'+dot('epic')+'Epic<b>4.5%</b></span>' +
          '<span>'+dot('legendary')+'Legendary<b>1.2%</b></span>' +
          '<span>'+dot('mythic')+'Mythic<b>0.3%</b></span>' +
        '</div>' +
        '<div class="ffshop-how-sec">What You Keep</div>' +
        /* R422 (Jan): every pull is kept now — the pack never dusts for you. */
        '<p>Every card you pull stays in your collection, and every card you collect can fight in the Battlegrounds. You can turn any card into hearts whenever you want, one copy at a time.</p>' +
        '<div class="ffshop-how-sec">The Hearts</div>' +
        '<div class="ffshop-how-ledger">' +
          '<div class="ffshop-how-ledger-row">'+dot('common')+'<span class="ffshop-how-ledger-tier">Common</span><span class="ffshop-how-ledger-val">1<i class="ffshop-how-hearticon">&#9829;&#xFE0E;</i></span></div>' +
          '<div class="ffshop-how-ledger-row">'+dot('uncommon')+'<span class="ffshop-how-ledger-tier">Uncommon</span><span class="ffshop-how-ledger-val">3<i class="ffshop-how-hearticon">&#9829;&#xFE0E;</i></span></div>' +
          '<div class="ffshop-how-ledger-row">'+dot('rare')+'<span class="ffshop-how-ledger-tier">Rare</span><span class="ffshop-how-ledger-val">5<i class="ffshop-how-hearticon">&#9829;&#xFE0E;</i></span></div>' +
          '<div class="ffshop-how-ledger-row">'+dot('epic')+'<span class="ffshop-how-ledger-tier">Epic</span><span class="ffshop-how-ledger-val">10<i class="ffshop-how-hearticon">&#9829;&#xFE0E;</i></span></div>' +
          '<div class="ffshop-how-ledger-row">'+dot('legendary')+'<span class="ffshop-how-ledger-tier">Legendary</span><span class="ffshop-how-ledger-val">20<i class="ffshop-how-hearticon">&#9829;&#xFE0E;</i></span></div>' +
          '<div class="ffshop-how-ledger-row">'+dot('mythic')+'<span class="ffshop-how-ledger-tier">Mythic</span><span class="ffshop-how-ledger-val">30<i class="ffshop-how-hearticon">&#9829;&#xFE0E;</i></span></div>' +
        '</div>' +
        /* R247: this note was crammed against the ledger with no breathing
           room. .ffshop-how-note adds the missing top spacing. */
        '<p class="ffshop-how-note">Spare copies of kept cards can be cashed from the card view. FramedFantasy copies pay <b class="ffshop-how-em">ten times</b> their rarity, your own heroes pay double.</p>' +
        /* R247: cash-timing tip. The Promise section was removed per Jan. */
        '<div class="ffshop-how-tip"><span class="ffshop-how-tip-key">Tip</span>Hold off on dusting your FramedFantasy portraits. If the hero tiers up later, every spare copy is worth more.</div>' +
        '<button type="button" class="ffshop-how-close">Got it</button>' +
      '</div>';
    document.body.appendChild(m);
    requestAnimationFrame(function(){ m.style.opacity='1'; });
    var prevOv=document.body.style.overflow;
    document.body.style.overflow='hidden';
    var done=false;
    function closeHow(){
      if(done) return; done=true;
      m.style.opacity='0';
      /* restore the pack modal's lock only if it is still open — it may
         have collected-and-closed underneath while this panel was up */
      document.body.style.overflow=(modalEl && modalEl.style.display==='flex') ? (prevOv||'') : '';
      document.removeEventListener('keydown', onK);
      setTimeout(function(){ try{ m.remove(); }catch(_){} }, 240);
    }
    function onK(e){ if(e.key==='Escape') closeHow(); }
    document.addEventListener('keydown', onK);
    m.addEventListener('click', function(e){ if(e.target===m) closeHow(); });
    m.querySelector('.ffshop-how-close').addEventListener('click', closeHow);
  }

  function miniBackHtml(){
    return '<div class="ffshop-miniback"><img src="'+FF_LOGO+'" alt="" draggable="false"><span>Hall of Heroes</span></div>';
  }
  function miniFaceHtml(c){
    /* Collection ownership comes from the server. No redundant Yours badge. */
    var badge = '';
    var ff = c.ff ? '<span class="ffshop-mini-ff"><img src="'+FF_LOGO+'" alt="FramedFantasy"></span>' : '';
    /* R268: subtle "New" corner tag on a first-time discovery (see doOpenPacks). */
    var newTag = c.isNew ? '<span class="ffshop-mini-new">New</span>' : '';
    /* R770 (Jan): no account holds two of the same card. A pull of one you
       already have says so on its face and dissolves into Hearts on Collect,
       so the payout is never a card quietly going missing. */
    if(c.kept === false) newTag = '<span class="ffshop-mini-dup">Duplicate</span>';
    return '<div class="ffshop-mini'+(c.kept === false ? ' is-dust' : '')+'">' +
      (c.img?'<img class="ffshop-mini-art" src="'+esc(c.img)+'" alt="" decoding="async" draggable="false" style="'+esc(imgStyle(c.cid))+'">':'') +
      newTag +
      '<div class="ffshop-mini-body">'+ff+badge+
        '<div class="ffshop-mini-name">'+esc(c.name||'Hero')+'</div>' +
        '<div class="ffshop-mini-tier">'+esc(c.tier)+'</div>' +
      '</div></div>';
  }
  /* R240: idle sparks drift off unflipped card BACKS so the good pulls
     announce themselves before the flip. Blue (rare) is subtle, epic is
     livelier, legendary and mythic simmer hardest. */
  var idleTimers = [];
  function clearIdle(){
    idleTimers.forEach(function(t){ try{ clearInterval(t); }catch(_){} });
    idleTimers = [];
  }
  function startIdle(el, tier){
    if(reduced) return;
    var rank = PACK_RANK[tier]||0;
    if(rank < 2) return;
    var layer = document.createElement('div');
    layer.className = 'ffshop-idlefx';
    el.appendChild(layer);
    var cols = PALETTES[tier]||PALETTES.common;
    var period = rank>=5 ? 190 : rank===4 ? 260 : rank===3 ? 420 : 640;
    var t = setInterval(function(){
      if(!layer.isConnected){ try{ clearInterval(t); }catch(_){} return; }
      var s = document.createElement('span');
      s.className = 'ffshop-idle';
      s.style.left = (8+Math.random()*84)+'%';
      s.style.setProperty('--icol', cols[(Math.random()*cols.length)|0]);
      s.style.setProperty('--idur', (1100+Math.random()*700|0)+'ms');
      s.style.setProperty('--idx', ((Math.random()*20-10)|0)+'px');
      if(rank>=5){ s.style.width='6px'; s.style.height='6px'; }
      layer.appendChild(s);
      setTimeout(function(){ try{ s.remove(); }catch(_){} }, 2000);
    }, period);
    idleTimers.push(t);
  }
  function burst(card, tier){
    if(reduced) return;
    var fx = card.querySelector('.hoh-pack-card-fx'); if(!fx) return;
    var rank = PACK_RANK[tier]||0;
    var cols = PALETTES[tier]||PALETTES.common;
    var n = tier==='mythic' ? 46 : 8 + rank*7;
    var html='';
    /* the magic ladder: epic+ gets a shockwave ring and a color flash,
       mythic gets a second delayed ring in its counter-color. */
    if(rank >= 3){
      html += '<span class="ffshop-flash" style="--fcol:'+cols[0]+'"></span>';
      html += '<span class="ffshop-ring" style="--rcol:'+cols[0]+'"></span>';
      if(tier==='mythic') html += '<span class="ffshop-ring" style="--rcol:'+cols[1]+';animation-delay:150ms;"></span>';
    }
    for(var i=0;i<n;i++){
      var a=Math.random()*Math.PI*2, d=36+Math.random()*(rank>=3?130:90);
      html+='<span class="hoh-pack-particle" style="--pdx:'+(Math.cos(a)*d).toFixed(0)+'px;--pdy:'+(Math.sin(a)*d).toFixed(0)+'px;--pcol:'+cols[i%cols.length]+';--pdur:'+((700+Math.random()*(rank>=3?900:700))|0)+'ms;"></span>';
    }
    fx.innerHTML=html;
    /* legendary and mythic rattle the card itself */
    if(rank >= 4){
      var inner = card.querySelector('.hoh-pack-card-inner');
      if(inner){ inner.classList.add('ffshop-shake'); setTimeout(function(){ try{ inner.classList.remove('ffshop-shake'); }catch(_){} }, 560); }
    }
    /* mythic brings down the house */
    if(tier==='mythic'){
      try{ if(typeof window.hohFireConfetti==='function') window.hohFireConfetti(); }catch(_){}
    }
    setTimeout(function(){ try{ fx.innerHTML=''; }catch(_){} }, rank>=3 ? 2300 : 1800);
  }
  function showPack(cards, onDone, accountScope){
    if(!modalEl){ modalEl=document.createElement('div'); modalEl.className='hoh-pack-modal ffshop-pack'; document.body.appendChild(modalEl); }
    var cardsHtml = cards.map(function(c,i){
      /* epic and above glow on the card BACK so the table leans in
         before the flip. */
      var aura = (!reduced && (PACK_RANK[c.tier]||0) >= 3) ? ' ffshop-aura' : '';
      return '<div class="hoh-pack-card hoh-pack-rar-'+c.tier+aura+'" data-idx="'+i+'" style="--pack-i:'+i+';">' +
        '<div class="hoh-pack-card-inner">' +
          '<div class="hoh-pack-card-back">'+miniBackHtml()+'</div>' +
          '<div class="hoh-pack-card-face">'+miniFaceHtml(c)+'</div>' +
        '</div><div class="hoh-pack-card-fx" aria-hidden="true"></div></div>';
    }).join('');
    modalEl.innerHTML =
      '<div class="hoh-pack-backdrop"></div>' +
      '<div class="hoh-pack-stage" role="dialog" aria-modal="true" aria-label="Card pack">' +
        '<div class="hoh-pack-eyebrow">A Pack of Cards</div>' +
        '<div class="hoh-pack-cards">'+cardsHtml+'</div>' +
        '<button type="button" class="hoh-pack-done">Reveal</button>' +
      '</div>';
    hardenModal(modalEl);
    modalEl.style.display='flex';
    document.body.style.overflow='hidden';
    /* R246: re-trigger the open state so the deal animation restarts every
       time, even when the modal was already open from the summon ceremony.
       A forced reflow between the remove and the add makes the browser treat
       the class toggle as a real change and replay the keyframes. The old
       single requestAnimationFrame was routinely skipped on a modal that had
       just switched from display:none, so the cards popped in with no deal. */
    modalEl.classList.remove('hoh-pack-open');
    void modalEl.offsetWidth;
    modalEl.classList.add('hoh-pack-open');
    var cardEls = modalEl.querySelectorAll('.hoh-pack-card');
    var done = modalEl.querySelector('.hoh-pack-done');
    var hint = modalEl.querySelector('.hoh-pack-hint');
    var revealed=0, revealing=false, allDone=false, closing=false;
    /* rare-and-up backs simmer with idle sparks until flipped */
    clearIdle();
    cardEls.forEach(function(el, i){ startIdle(el, cards[i].tier); });
    /* R246: reveal is an opacity crossfade (back -> face) plus a moderate 3D
       peek-tilt and brightness flare, all defined under .ffshop-pack. Both
       faces stay painted the whole time and the tilt never reaches edge-on,
       so the card can never blank mid flip. This JS just toggles the classes;
       the reflow between remove/add replays the flare every time. */
    function flip(el){
      if(el.classList.contains('hoh-pack-flipped')) return;
      el.classList.remove('ffshop-aura');
      var il = el.querySelector('.ffshop-idlefx');
      if(il){ try{ il.remove(); }catch(_){} }
      el.classList.remove('ffshop-flipping'); void el.offsetWidth;
      el.classList.add('ffshop-flipping');
      el.classList.add('hoh-pack-flipped');
      var c = cards[+el.getAttribute('data-idx')];
      if (window.HOHEmber && String(c.cid || c.id || '') === window.HOHEmber.ID) {
        setTimeout(function () {
          if (!closing && el.isConnected && modalEl && modalEl.style.display !== 'none') {
            window.HOHEmber.say({trigger:'reunion',characterId:window.HOHEmber.ID,anchor:el,
              current:function () { return !closing && el.isConnected && modalEl.style.display !== 'none'; }});
          }
        }, 580);
      }
      /* Fire the spark burst partway through the turn so the flash lands
         over the crossfade midpoint and the swap reads clean. */
      setTimeout(function(){ try{ burst(el, c.tier); }catch(_){} }, 160);
      revealed++;
      if(revealed>=cards.length){ allDone=true; modalEl.classList.add('hoh-pack-all-revealed'); done.textContent='Collect'; if(hint) hint.textContent=''; }
      setTimeout(function(){ try{ el.classList.remove('ffshop-flipping'); }catch(_){} }, 560);
    }
    function autoReveal(){
      if(revealing||allDone) return; revealing=true;
      var order=[]; cardEls.forEach(function(el,i){ if(!el.classList.contains('hoh-pack-flipped')) order.push(i); });
      (function run(k){ if(k>=order.length){ revealing=false; return; } flip(cardEls[order[k]]); setTimeout(function(){ run(k+1); },300); })(0);
    }
    function collectAndClose(){
      if(closing) return; closing=true;
      clearIdle();
      var chip = cornerPill();
      var dustTotal = 0;
      cardEls.forEach(function(el, i){
        var c = cards[i];
        if(c.kept){
          /* Kept cards fly DOWN into the collection: a small clone of
             the card sails to the matching grid while the original
             fades, and the new tile pops after the re-render. */
          lastKeptIds.push(String(c.cid));
          /* R430: the same moment marks it NEW in the grid. lastKeptIds is
             consumed by the one-second pop and then emptied, so the badge
             needs its own durable record. */
          try { window.hohShopMarkNewCard(c.cid); } catch(_){}
          var rk = el.getBoundingClientRect();
          if(!reduced){
            var target = collAnchor(hostEl);
            var tr = target ? target.getBoundingClientRect()
                            : { left: innerWidth/2 - 32, top: innerHeight - 90, width: 64, height: 0 };
            var fk = document.createElement('div');
            fk.className = 'ffshop-flykeep';
            fk.innerHTML = c.img ? ('<img src="'+esc(c.img)+'" alt="">') : '';
            fk.style.left = (rk.left + rk.width/2 - 32) + 'px';
            fk.style.top  = (rk.top + rk.height/2 - 45) + 'px';
            document.body.appendChild(fk);
            (function(elm, dx, dy, delay){
              setTimeout(function(){
                elm.style.transform = 'translate('+dx.toFixed(0)+'px,'+dy.toFixed(0)+'px) scale(.5)';
                elm.style.opacity = '0';
              }, 30 + delay);
              setTimeout(function(){ try{ elm.remove(); }catch(_){} }, 950 + delay);
            })(fk, tr.left + tr.width/2 - (rk.left + rk.width/2), tr.top + 46 - (rk.top + rk.height/2), i*70);
          }
          el.style.transition='opacity .4s ease '+(i*40)+'ms';
          el.style.opacity='0';
        } else {
          dustTotal += c.dust;
          el.classList.add('ffshop-dusting');
          var r = el.getBoundingClientRect();
          /* R237: the payout moment — "+X hearts" floats up off the
             dissolving card so the conversion reads at a glance. */
          if(!reduced) (function(val, rect, idx){
            setTimeout(function(){
              var fl=document.createElement('span');
              fl.className='ffshop-float';
              fl.innerHTML='+'+val+' '+heartSvg();
              document.body.appendChild(fl);
              fl.style.left=(rect.left+rect.width/2-36)+'px';
              fl.style.top=(rect.top+rect.height*0.32)+'px';
              setTimeout(function(){ try{ fl.remove(); }catch(_){} }, 1900);
            }, 60+idx*110);
          })(c.dust, r, i);
          /* Jan, 2026-09-27: the card's hearts pop on the card as it
             dissolves (hohHeartBurst in hoh-wallet.js). Four motes used to
             fly to the corner wallet, which the live Hall hides, so they
             landed in the top-left corner. */
          if(!reduced) (function(val, rect, idx){
            setTimeout(function(){
              try{ if(typeof window.hohHeartBurst === 'function') window.hohHeartBurst(rect, { n: val }); }catch(_){ }
            }, 60+idx*110);
          })(c.dust, r, i);
        }
      });
      if(dustTotal>0){
        /* Credit the heart bank when the motes land, so the counter
           ticks up in rhythm with the animation. */
        /* R286: the auto-dust when a pack opens. This is the single biggest
           heart faucet in the Hall (every non-kept card dusts on reveal), and
           it is also where Battlegrounds pack winnings turn into hearts. */
        /* ⛔ R770: A DUPLICATE IS PAID BY THE SERVER, INSIDE THE PULL. The copy
           is spent the moment the pack is dealt, so the Hearts cannot wait on a
           tap that a closed tab may never make. When the reply says it banked
           them, this only SHOWS the payout: it syncs the mirror instead of
           applying a second delta, which would double-credit. The old client
           path stays for any reply that did not bank (nothing does today), so
           a theme pushed before the SQL keeps working. */
        setTimeout(function(){
          if(cards.some(function(c){ return c && c.dustBanked === true; })){
            try { if(window.HOHWallet && window.HOHWallet.sync) window.HOHWallet.sync(true); } catch(_){}
            try { if(typeof window.hohRenderHeaderCounter==='function') window.hohRenderHeaderCounter(); } catch(_){}
            updateHeartChip();
          }
          else if(accountScope) accountShopHeart(accountScope, dustTotal, 'earn_dust_pack');
          else bankAdd(dustTotal, 'earn_dust_pack');
        }, 650);
        if(chip){ chip.classList.remove('ffshop-balance-pop'); void chip.offsetWidth; chip.classList.add('ffshop-balance-pop'); }
      }
      /* R242: linger so the payout reads — floats, dust and heart flights
         finish on stage instead of being cut mid-flight. */
      setTimeout(function(){
        modalEl.classList.remove('hoh-pack-open');
        document.body.style.overflow='';
        setTimeout(function(){ try{ modalEl.style.display='none'; modalEl.innerHTML=''; }catch(_){} if(onDone) onDone(); }, 240);
      }, 1250);
    }
    done.addEventListener('click', function(){ if(allDone) collectAndClose(); else autoReveal(); });
    var howMini = modalEl.querySelector('.ffshop-how-mini');
    if(howMini) howMini.addEventListener('click', openHow);
    cardEls.forEach(function(el){ el.addEventListener('click', function(){ if(!allDone && !revealing) flip(el); }); });
  }
  function runPackQueue(){
    var revealScope = activeRevealScope;
    if(revealScope && !shopAccountIsCurrent(revealScope)){
      packQueue = [];
      activeRevealScope = null;
      closeSummoning();
      return;
    }
    if(!packQueue.length){
      activeRevealScope = null;
      /* ⛔ R600 · FORCE THE WALLET TO RE-READ AFTER A PACK.
         packs_opened had TWO sources: get_shop_state, refreshed here, and the
         wallet's own mirror, which renderShop only nudged with an UNFORCED
         sync. That sync is throttled to 4 seconds, so opening your tenth pack
         repainted a Shop whose crystal module still read nine and still said
         "Locked · 1 more pack". It stayed wrong until a reload, on the single
         most important moment the feature has. Forced, it is right the instant
         the pack finishes. */
      Promise.all([
        fetchState(),
        (window.HOHWallet ? window.HOHWallet.sync(true) : Promise.resolve())
      ]).then(function(){ if(!revealScope || shopAccountIsCurrent(revealScope)){ renderShop(hostEl); popKeptTiles(); } });
      return;
    }
    var cards = packQueue.shift();
    showPack(cards, runPackQueue, revealScope);
  }
  /* After the flight lands and the boards re-render, the new tiles
     flash gold so the eye finds where the card went. */
  function popKeptTiles(){
    if(!hostEl || !lastKeptIds.length) return;
    var ids = lastKeptIds.slice();
    lastKeptIds = [];
    requestAnimationFrame(function(){
      ids.forEach(function(id){
        hostEl.querySelectorAll('.ffshop-tile[data-hero-id="'+id+'"]').forEach(function(t){
          t.classList.add('ffshop-tile-pop');
          setTimeout(function(){ try{ t.classList.remove('ffshop-tile-pop'); }catch(_){} }, 1000);
        });
      });
    });
  }

  /* Open saved packs from the account wallet. Battle wins, purchases and daily
     grants all become server entitlements before this door can see them. The
     server chooses and consumes the oldest credit; `count` controls only how
     many player-approved reveals this call attempts, never what is granted. */
  function openSavedPacks(count, onAllDone){
    count = Math.max(1, count | 0);
    var grantScope = captureShopAccount(), em = grantScope.email, pn = grantScope.pin;
    var grantToken = 0;
    var done = function(ok, why){
      if(grantToken===packOperationGeneration) busy = false;
      if(shopAccountIsCurrent(grantScope) && typeof onAllDone === 'function'){ try{ onAllDone(ok, why); }catch(_){} }
    }; /* R318: passes ok=false on failure so the caller can keep the prize claimable */
    /* v35: SAY FALSE. The caller (hoh-contract-ui, runBatch) treats anything
       that is not strictly false as a batch that opened, and subtracts it from
       what it still owes you — so a token that expired between the win and the
       collect made the prize vanish instead of staying claimable. */
    if(!em || !pn){ done(false); return; }
    if(busy){ /* let any in-flight shop action settle first */ setTimeout(function(){ if(shopAccountIsCurrent(grantScope)) openSavedPacks(count, onAllDone); }, 300); return; }
    grantToken = ++packOperationGeneration;
    busy = true;
    var opened = 0;
    var summonCard = showSummoning();
    /* R323: THE SHUFFLE CANNOT STRAND. Every way this flow can end now runs
       through one terminal: it closes the summon, clears the watchdog, and
       reports once. Two real stranding holes existed — a server reply of
       ok:true with ZERO results reached finishAll without ever closing the
       summon (the screenshotted "Shuffling the deck…" forever), and any
       unexpected throw inside the success handler left no one holding the
       door. A 15 second watchdog backstops anything unforeseen: the packs
       stay claimable, the player is told, the overlay never wedges. */
    var settled = false;
    /* what the server last said, so a failure can quote it rather than
       guessing. Empty on the happy path. */
    var _lastReason = '';
    function term(ok, say, title, detail){
      if(settled) return; settled = true;
      try{ clearTimeout(watchdog); }catch(_){}
      if(grantToken!==packOperationGeneration || !shopAccountIsCurrent(grantScope)){ done(false); return; }
      /* v38: the outcome is painted INTO the summon modal rather than closing
         it and announcing elsewhere. Three rounds of this bug were spent on
         messages the player could not see. */
      if(say) showPackOutcome(title || 'The packs held', say, ok ? 'good' : 'bad', detail || '');
      else closeSummoning();
      /* the reason still travels back with the verdict, so the win panel can
         put it under the pack card where the tap happened */
      done(ok, say);
    }
    var watchdog = setTimeout(function(){
      term(false, 'The shuffle stalled. Your packs stay yours. Tap the pack again in a moment.');
    }, 15000);
    function finishAll(){
      if(grantToken!==packOperationGeneration || !shopAccountIsCurrent(grantScope)){ settled=true; done(false); return; }
      /* fold the new cards into every surface: the shop grid (if open) and the
         Battlegrounds collection cache (so the picker sees them next fight). */
      try { fetchState().then(function(){ try{ if(hostEl) renderShop(hostEl); }catch(_){} popKeptTiles(); }); } catch(_){}
      try { if(window.HOHContractData && window.HOHContractData.loadCollection) window.HOHContractData.loadCollection(); } catch(_){}
      if(settled) return; settled = true;
      try{ clearTimeout(watchdog); }catch(_){}
      done(true);
    }
    function rollOne(){
      /* R323: re-arm per pack — a 3-pack open must be guarded on every rpc,
         not just the first (the watchdog stands down only while a reveal is
         in the player's hands). */
      try{ clearTimeout(watchdog); }catch(_){}
      if(!settled) watchdog = setTimeout(function(){
        term(false, 'The shuffle stalled. Your packs stay yours. Tap the pack again in a moment.');
      }, 15000);
      /* The unified gate can consume only a server-recorded entitlement. Its
         request id makes a lost reply safe to repeat, while the reveal pacing
         remains entirely in the player's hands. */
      var _openPending = packOpenPending(grantScope.account);
      var _openRequestId = (_openPending && _openPending.id) || reqId('savedpack');
      if(!_openPending && !savePackOpenPending(grantScope.account, {id:_openRequestId,at:Date.now()})){
        term(false, 'This device could not save the pack-open retry. Your pack stays in your Wallet.', 'The pack stayed safe', 'storage unavailable before open');
        return;
      }
      var _call = rpc('hoh_open_saved_pack', { p_email: em, p_pin: pn, p_request_id: _openRequestId });
      _call.then(function(res){
       try {
        /* v35: the watchdog may already have reported this batch as failed and
           handed the caller its packs back. Everything below reveals cards and
           queues the next open, so a late reply used to double-open and race
           the summon teardown, blanking the reveal with body scroll still
           locked. One latch, checked before anything paints. */
        if(settled) return;
        if(grantToken!==packOperationGeneration || !shopAccountIsCurrent(grantScope)){ settled=true; done(false); return; }
        var d = res && res.data;
        var _err = res && res.error && String(res.error.message || '');
        if(_err){
          try{ console.warn('[HOH pack] saved-pack rpc errored; credit left untouched: ' + _err); }catch(_){}
          _lastReason = 'claim error: ' + _err;
          term(false, 'The Hall could not open your saved pack just now. It stays yours. Try again in a moment.', 'The pack stayed safe', _lastReason);
          return;
        }
        var _reason = d && (d.reason || d.status);
        if(d && d.ok === false && _reason && _reason !== 'nothing_owed'){
          var _why = String(_reason) + (d.sqlerrm ? ' :: ' + d.sqlerrm : '') +
                     (d.sqlstate ? ' [' + d.sqlstate + ']' : '');
          try{ console.warn('[HOH pack] the wallet refused; credit left untouched (' + _why + ')'); }catch(_){}
          _lastReason = _why;
          term(false, 'The Hall could not open your saved pack just now. It stays yours. Try again in a moment.', 'The pack stayed safe', _lastReason);
          return;
        }
          /* everything collected. Not a failure: the run paid what it owed.
             v35 UX: unless we opened NOTHING. Tapping collect and watching the
             summon flash, close and retire the card in silence reads as the
             game eating a prize, and the two ways to land here are both
             ordinary — you already collected on another device, or you tapped
             twice. Say it plainly and let the card retire, because there
             really is nothing left. */
          if(d && d.ok === false && _reason === 'nothing_owed'){
            clearPackOpenPending(grantScope.account, _openRequestId);
            if(opened > 0){ finishAll(); return; }
            /* v36: two very different states arrive as the same refusal, and
               retiring the prize card in silence for either one reads as the
               game eating a prize. `seen` is how many credit rows this account
               has ever had, so it separates them. Rows on file means you
               already collected, here or on another device. No rows at all
               means the vault has never recorded a pack for you, which is a
               real fault and deserves a straight sentence rather than a
               reassurance nobody can keep. Packs cost nothing either way, so
               say that much, because it is the part that is actually true. */
            if((d.seen | 0) > 0) term(true, 'These are already in your collection. Nothing left to open.', 'Already collected');
            else {
              /* v39: SAY WHY, ON SCREEN. seen 0 means the vault has never held a
                 row for this account, which is never the player's doing. The
                 answer is always in what the judge replied to the settle, and
                 that reply was being discarded. Now it rides along, so the next
                 report carries the fault instead of only the symptom. */
              var _s = null;
              try { _s = window.__hohLastSettle || null; } catch(_){}
              var _det = 'seen 0';
              if(_s && _s.local){
                /* R410: god mode plays the real game now, so the only way to
                   reach a browser judged run is to be signed out. The old copy
                   here told you to flip a localStorage key that no longer does
                   anything, which is worse than saying nothing. */
                term(true, 'This run was judged in your browser because you are signed out, so the vault was never told about it and there is no pack to open. Sign in and the next run pays for real.',
                     'Signed out', 'browser judged | nothing was sent to the worker | seen 0');
                return;
              }
              if(_s){
                _det = 'settle http ' + _s.http +
                       ' | status ' + (_s.status || '?') +
                       ' | packs ' + (_s.packs == null ? '?' : _s.packs) +
                       ' | grant ' + (_s.grant || '-') +
                       (_s.err ? ' | err ' + _s.err : '') +
                       (_s.wv ? ' | worker v' + _s.wv : '') +
                       (_s.cv != null ? ' | client v' + _s.cv : '') +
                       ' | run ' + _s.run + ' | seen 0';
              } else {
                _det = 'no settle reply was recorded on this device | seen 0';
              }
              term(true, 'The vault has no packs recorded against this run. Nothing was charged for them.', 'Nothing to open', _det);
            }
            return;
          }
        if(d && d.ok === true){
          try { if(window.HOHWallet) window.HOHWallet.sync(true); } catch(_) {}
          d = d.pack && d.pack.ok === true ? d.pack : d;
        } else d = null;
        if(!d || d.ok !== true){
          term(false, 'The Hall could not open your saved pack just now. It stays yours. Try again in a moment.', 'The pack stayed safe', _lastReason);
          return;
        }
        var _preFF = {};
        try { (shopState && shopState.collection || []).forEach(function(cc){ if(cc && cc.ff) _preFF[String(cc.character_id)] = 1; }); } catch(_){}
        var _seen = {};
        try { _seen = JSON.parse(localStorage.getItem('hoh-shop-seen-v1')||'{}') || {}; } catch(_){}
        var byPack = {};
        (d.results||[]).forEach(function(c){
          var _k = String(c.cid);
          if(c.ff){ c.isNew = !_preFF[_k]; _preFF[_k] = 1; }
          else { c.isNew = !_seen[_k]; _seen[_k] = 1; }
          c.dustBanked = (d.dust_banked === true);   /* R770: the server already paid these */
          (byPack[c.pack] = byPack[c.pack] || []).push(c);
        });
        try { localStorage.setItem('hoh-shop-seen-v1', JSON.stringify(_seen)); } catch(_){}
        var groups = Object.keys(byPack).sort().map(function(k){ return byPack[k].sort(function(a,b){ return (PACK_RANK[a.tier]||0)-(PACK_RANK[b.tier]||0); }); });
        /* R323: ok with nothing inside = a refusal, not a reveal. */
        if(!groups.length){
          term(false, 'The vault answered with an empty hand. Your packs stay yours. Tap again in a moment.', 'The packs held', _lastReason || 'the open returned ok with zero cards');
          return;
        }
        /* the reveal is in the player's hands now — the watchdog stands down */
        try{ clearTimeout(watchdog); }catch(_){}
        (function revealNext(gi){
          if(gi >= groups.length){
            /* Keep the receipt until the committed cards have actually been
               presented. A reload or rendering interruption before this point
               must replay this receipt, not consume the next saved pack. */
            clearPackOpenPending(grantScope.account, _openRequestId);
            opened++;
            if(opened < count){ rollOne(); } else { finishAll(); }
            return;
          }
          showPack(groups[gi], function(){ revealNext(gi + 1); }, grantScope);
        })(0);
       } catch(e){
        term(false, 'The shuffle slipped. Your packs stay yours. Tap the pack again in a moment.', 'The packs held', String((e && e.message) || e || '') + (_lastReason ? ' | ' + _lastReason : ''));
       }
      }, function(){
        term(false, 'The connection dropped mid-open. Your packs stay yours. Tap again in a moment.', 'The packs held', _lastReason || 'the request never came back');
      });
    }
    var dwell = Math.max(0, 700 - (Date.now() - summonAt));
    setTimeout(rollOne, dwell);
  }
  window.hohShopOpenSavedPacks = openSavedPacks;
  /* Cached battle result screens still use the old public name. Keep it as an
     alias so they reach the same authoritative wallet instead of becoming a
     dead Collect button during rollout. */
  window.hohShopOpenGrantedPacks = openSavedPacks;

  /* ---------------- opening ---------------- */

  /* Fetch-once whether the account still has its free pack, to label the button.
     Does NOT claim (uses a peek: claim_free_pack only grants on first call, so we
     can't peek without consuming). Instead we infer from a local cache set after
     the first real open, plus a lightweight flag the server could expose later.
     For now: assume available until the first open decides it, so the label shows
     for brand-new accounts and clears the moment the free pack is used. */
  function ensureFreePackFlag(cb){
    if(freePackAvail !== null){ if(cb) cb(freePackAvail); return; }
    /* Local hint only — the authority is claim_free_pack at open time. If this
       account has already opened here, the local flag is false. */
    try{
      var used = localStorage.getItem('hoh-free-pack-used-'+ (email()||'').toLowerCase()) === '1';
      freePackAvail = !used;
    }catch(_){ freePackAvail = true; }
    if(cb) cb(freePackAvail);
  }
  /* R271: reconcile the free-pack label with the SERVER without consuming it.
     get_free_pack_state is a read-only peek keyed to the account, so a second
     device or a cleared cache shows the correct label (no false "First pack
     free"). Runs once per session; repaints the shop if the answer differs from
     the local hint. Safe if the RPC is not deployed yet (keeps the local hint). */
  function peekFreePack(cb){
    var scope = captureShopAccount(), em = scope.email, pn = scope.pin;
    if(!em || !pn){ if(cb) cb(freePackAvail); return; }
    rpc('get_free_pack_state', { p_email: em, p_pin: pn }).then(function(res){
      if(!shopAccountIsCurrent(scope)) return;
      var d = res && res.data;
      if(d && d.ok === true && typeof d.available === 'boolean'){
        var was = freePackAvail;
        freePackAvail = d.available;
        try{ localStorage.setItem('hoh-free-pack-used-'+em.toLowerCase(), d.available ? '0' : '1'); }catch(_){}
        if(was !== freePackAvail && hostEl){ try{ renderShop(hostEl); }catch(_){} }
      }
      if(cb) cb(freePackAvail);
    }, function(){ if(cb) cb(freePackAvail); });
  }
  function packSavedFx(fromEl, count){
    purchaseBurst(fromEl);
    var target = cornerPill();
    if(!target || !fromEl || !fromEl.isConnected || motionReduced()){
      try { if(target){ target.classList.remove('ffshop-balance-pop'); void target.offsetWidth; target.classList.add('ffshop-balance-pop'); } } catch(_) {}
      return;
    }
    try {
      var a = fromEl.getBoundingClientRect(), b = target.getBoundingClientRect();
      if(!a.width || !a.height || !b.width || !b.height) return;
      var fly = document.createElement('span');
      fly.className = 'ffshop-pack-save-flight';
      fly.setAttribute('aria-hidden', 'true');
      fly.innerHTML = '<i></i><b>+' + Math.max(1, count | 0) + '</b>';
      fly.style.left = (a.left + a.width / 2 - 22) + 'px';
      fly.style.top = (a.top + a.height / 2 - 31) + 'px';
      document.body.appendChild(fly);
      requestAnimationFrame(function(){
        fly.style.transform = 'translate3d(' +
          (b.left + b.width / 2 - a.left - a.width / 2).toFixed(0) + 'px,' +
          (b.top + b.height / 2 - a.top - a.height / 2).toFixed(0) + 'px,0) scale(.42)';
        fly.style.opacity = '0';
      });
      setTimeout(function(){
        try { fly.remove(); target.classList.remove('ffshop-balance-pop'); void target.offsetWidth; target.classList.add('ffshop-balance-pop'); } catch(_) {}
      }, 760);
    } catch(_) {}
  }
  function bankSavedPack(source, opts){
    opts = opts || {};
    if(source !== 'hearts' && source !== 'free_first' && source !== 'daily' && source !== 'explore'){
      return Promise.resolve({ok:false,status:'source_retired'});
    }
    var scope = opts.accountScope || captureShopAccount(), em = scope.email, pn = scope.pin;
    var visual = !opts.background && shopAccountIsCurrent(scope);
    if(!em || !pn){ if(visual) toast('Sign in to save packs.'); return Promise.resolve({ok:false,status:'signed_out'}); }
    if(visual && busy) return Promise.resolve({ok:false,status:'busy'});
    var pending = source === 'hearts' ? packBankPending(scope.account) : null;
    if(source === 'hearts' && !pending && heartBank() < PACK_COST_HEARTS){
      if(visual) toast('You need ' + PACK_COST_HEARTS + ' hearts for a pack. Explore the Hall to earn more.');
      return Promise.resolve({ok:false,status:'insufficient_hearts'});
    }
    var requestId = opts.requestId || (pending && pending.id) ||
      ('pack-bank-' + source + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2,9));
    if(source === 'hearts' && !pending && !savePackBankPending(scope.account, {source:'hearts',id:requestId,at:Date.now()})){
      if(visual) toast('This device could not save the purchase retry. Nothing was spent.');
      return Promise.resolve({ok:false,status:'storage_unavailable'});
    }
    var opToken = 0, button = opts.button || (visual && hostEl && hostEl.querySelector(
      source === 'daily' ? '[data-daily-pack]' : '.ffshop-open-btn[data-open="hearts"]'));
    if(visual){
      opToken = ++packOperationGeneration; busy = true; setBtns(true);
      if(button){ button.setAttribute('aria-busy','true'); button.classList.remove('ffshop-btn-pressed'); }
    }
    return rpc('hoh_pack_bank', {
      p_email: em, p_pin: pn, p_source: source, p_request_id: requestId
    }).then(function(res){
      var currentOp = !visual || (opToken === packOperationGeneration && shopAccountIsCurrent(scope));
      if(visual && opToken === packOperationGeneration){ busy = false; setBtns(false); if(button) button.removeAttribute('aria-busy'); }
      var d = res && res.data, transportError = res && res.error;
      if(transportError || !d){
        if(currentOp && visual) toast(source === 'hearts'
          ? 'The purchase could not be confirmed. Tap again to check the same purchase.'
          : 'That pack could not be saved. Try again in a moment.');
        if(typeof opts.onError === 'function') try { opts.onError(d || null); } catch(_) {}
        return {ok:false,status:'offline'};
      }
      if(d.ok !== true){
        if(source === 'hearts' && d.status !== 'busy' && d.status !== 'offline') clearPackBankPending(scope.account, requestId);
        if(currentOp && visual){
          toast(d.status === 'insufficient_hearts' ? 'You need ' + PACK_COST_HEARTS + ' hearts for a pack.' :
            d.status === 'unauthenticated' ? 'Sign-in expired. Open Your Hall and sign in again.' :
            d.status === 'source_retired' ? 'Tickets level heroes now. Packs are bought with hearts.' :
            'That pack could not be saved. Nothing was spent.');
        }
        if(typeof opts.onError === 'function') try { opts.onError(d); } catch(_) {}
        return d;
      }
      if(source === 'hearts') clearPackBankPending(scope.account, requestId);
      if(source === 'free_first' && shopAccountIsCurrent(scope)){
        freePackAvail = false;
        try { localStorage.setItem('hoh-free-pack-used-' + scope.account, '1'); } catch(_) {}
      }
      if(source === 'daily') markDailyPackClaimed(scope.account);
      var credited = Math.max(0, d.credited | 0), total = Math.max(0, d.packs_unopened | 0);
      var newlyCredited = credited > 0 && d.idempotent !== true;
      if(currentOp && visual){
        if(newlyCredited) packSavedFx(button, credited);
        else {
          var walletPill = cornerPill();
          try { if(walletPill){ walletPill.classList.remove('ffshop-balance-pop'); void walletPill.offsetWidth; walletPill.classList.add('ffshop-balance-pop'); } } catch(_) {}
        }
        toast(newlyCredited
          ? ((credited === 1 ? '1 pack' : credited + ' packs') + ' saved to your Wallet. ' + total + ' ready to open.')
          : ('That pack is already safe in your Wallet. ' + total + ' ready to open.'));
      }
      try { if(window.HOHWallet) window.HOHWallet.sync(true); } catch(_) {}
      try { fetchState().then(function(){ if(shopAccountIsCurrent(scope) && hostEl) renderShop(hostEl); }); } catch(_) {}
      if(typeof opts.onSuccess === 'function') try { opts.onSuccess(d); } catch(_) {}
      return d;
    }, function(){
      if(visual && opToken === packOperationGeneration){ busy = false; setBtns(false); if(button) button.removeAttribute('aria-busy'); }
      if(shopAccountIsCurrent(scope) && visual) toast(source === 'hearts'
        ? 'The purchase could not be confirmed. Tap again to check the same purchase.'
        : 'That pack could not be saved. Try again in a moment.');
      if(typeof opts.onError === 'function') try { opts.onError(null); } catch(_) {}
      return {ok:false,status:'offline'};
    });
  }
  function openPacks(currency){
    if(busy) return;
    /* R320: hard gate. A pack open with no recognised currency must never reach
       the server — a null slid through every check here AND through the RPC's
       three-valued-logic guard, which is how "enter the Battlegrounds, get a
       free pack" happened. Belt for the selector fix above, so a future caller
       cannot reintroduce it. */
    if(currency !== 'ticket' && currency !== 'hearts') return;
    var scope = captureShopAccount(), em = scope.email, pn = scope.pin;
    if(!em || !pn){ toast('Sign in to save packs.'); return; }
    var god = isGod();
    var godFree = god && debugFree;
    /* God debug free opens without touching the account's real free-pack claim. */
    if(godFree){ doOpenPacks(currency, true, { accountScope:scope }); return; }
    /* Whether the button was PROMISING a free pack at click time. Captured before
       checkFreePack flips the flag, so we can tell a stale "free" promise apart
       from a normal paid open. */
    var expectedFree = (freePackAvail === true);
    if(currency === 'ticket'){
      toast('Tickets level heroes now. Packs are bought with hearts.');
      return;
    }
    bankSavedPack(expectedFree ? 'free_first' : 'hearts', {
      accountScope: scope,
      button: hostEl && hostEl.querySelector('.ffshop-open-btn[data-open="hearts"]')
    });
  }
  function doOpenPacks(currency, free, openOpts){
    openOpts = openOpts || {};
    var scope = openOpts.accountScope || captureShopAccount();
    var em = scope.email, pn = scope.pin;
    var visual = !openOpts.background && shopAccountIsCurrent(scope);
    /* Paid opens never continue after an account boundary. Free entitlements
       that already committed may finish silently for their frozen account. */
    if(!em || !pn || (!visual && !free)) return;
    if(visual && busy) return;
    var god = isGod();
    /* `free` covers both the account's free first pack AND god debug free. */
    if(currency==='ticket' && !free){
      var have = (typeof window.hohGetUnspentTickets==='function') ? window.hohGetUnspentTickets() : 0;
      if(have < 1){ toast('No wallet tickets. Earn more with daily activities.'); return; }
    }
    if(currency==='hearts' && !free){
      if(heartBank() < PACK_COST_HEARTS){ toast('You need '+PACK_COST_HEARTS+' hearts for a pack. Explore the Hall to earn more.'); return; }
    }
    var opToken = 0;
    if(visual){
      opToken = ++packOperationGeneration;
      busy = true;
      setBtns(true);
    }
    /* The purchase moment gets its own spark and press. */
    var pressBtn = visual && hostEl && hostEl.querySelector('.ffshop-open-btn[data-open="'+currency+'"]');
    if(pressBtn){ pressBtn.classList.remove('ffshop-btn-pressed'); void pressBtn.offsetWidth; pressBtn.classList.add('ffshop-btn-pressed'); }
    purchaseBurst(pressBtn);
    /* Spend up front (mirrors the apply flow), refund on failure. A free pack
       skips all spending. */
    var spentTicket = false, spentHearts = 0;
    if(currency==='ticket' && !free && typeof window.hohSpendUnspentTicket==='function'){
      spentTicket = !!window.hohSpendUnspentTicket();
      if(!spentTicket){
        if(visual && opToken===packOperationGeneration){ busy=false; setBtns(false); toast('No wallet tickets available.'); }
        return;
      }
    }
    /* Dim NOW — the click always answers instantly. */
    var summonCard = visual ? showSummoning() : null;
    if(currency==='hearts' && !free){
      spentHearts = PACK_COST_HEARTS;
      /* Hearts stream from the corner counter INTO the summoned pack. */
      if(visual) flyHearts(cornerPill(), summonCard, 10);
      bankAdd(-PACK_COST_HEARTS);
    }
    /* R268: the free pack is enforced purely client-side. Since R235 the server
       does NO hearts/ticket charge (both are client-side wallets — see
       R235-shop-heart-bank.sql), so simply NOT spending above yields a free pack;
       the RPC signature is unchanged and needs no free flag. */
    rpc('open_card_packs', {
      user_email: em, user_pin: pn, pay_currency: currency,
      client_request_id: openOpts.requestId || reqId('open')
    }).then(function(res){
      var currentOp = visual && opToken===packOperationGeneration && shopAccountIsCurrent(scope);
      if(visual && opToken===packOperationGeneration){ busy = false; setBtns(false); }
      var d = res && res.data;
      if(!d || d.ok !== true){
        /* Failed roll: lift the dim, give everything back. */
        if(currentOp) closeSummoning();
        /* v35: THE WALLET RETURN, NOT THE DAILY GRANT. hohAddUnspentTicket is
           the earn path: it consumes one of the account's four daily grants and
           ROLLS ITS OWN BUMP BACK at the cap, so a player at 4/4 whose pack RPC
           failed lost the ticket outright while this line told them nothing was
           spent. hohRefundUnspentTicket is the unconditional return, and it is
           what hoh-contract-data.js already uses for exactly this case. */
        if(spentTicket || spentHearts) queueShopRefund(scope, spentHearts, spentTicket ? 1 : 0);
        var status = (d && d.status) || 'error';
        if(currentOp) toast(status==='rate_capped' ? 'The pack table needs a rest. Come back tomorrow.' :
                      status==='unauthenticated' ? 'Sign-in expired. Open Your Hall and sign in again.' :
                      'Could not open packs. Nothing was spent.');
        if(!visual && typeof openOpts.onBackgroundError === 'function'){
          try { openOpts.onBackgroundError(d); } catch(_){}
        }
        return;
      }
      /* The server grant belongs to the frozen account. Once that account or
         operation generation is stale, do not mark discoveries, replace the
         current reveal queue, or put A's cards in front of B. A refetch shows
         the cards when A returns. */
      if(!currentOp){
        if(!visual && typeof openOpts.onBackgroundSuccess === 'function'){
          try { openOpts.onBackgroundSuccess(d); } catch(_){}
        }
        return;
      }
      /* R268: flag each pulled card as a NEW discovery for the reveal tag.
         FF cards are NEW when they were NOT in the collection BEFORE this pull
         (a duplicate you already own shows nothing). Non-FF cards never save to
         the Hall, so they use a persistent localStorage "seen" set — NEW the
         first time a face appears, quiet ever after. Both maps update as we go
         so the same card pulled twice in one multi-pack open reads new-then-dup. */
      var _preFF = {};
      try { (shopState && shopState.collection || []).forEach(function(cc){ if(cc && cc.ff) _preFF[String(cc.character_id)] = 1; }); } catch(_){}
      var _seen = {};
      try { _seen = JSON.parse(localStorage.getItem('hoh-shop-seen-v1')||'{}') || {}; } catch(_){}
      /* group the flat results by pack for sequential reveals */
      var byPack = {};
      (d.results||[]).forEach(function(c){
        var _k = String(c.cid);
        if(c.ff){ c.isNew = !_preFF[_k]; _preFF[_k] = 1; }
        else { c.isNew = !_seen[_k]; _seen[_k] = 1; }
        c.dustBanked = (d.dust_banked === true);   /* R770: the server already paid these */
        (byPack[c.pack]=byPack[c.pack]||[]).push(c);
      });
      try { localStorage.setItem('hoh-shop-seen-v1', JSON.stringify(_seen)); } catch(_){}
      packQueue = Object.keys(byPack).sort().map(function(k){
        return byPack[k].sort(function(a,b){ return (PACK_RANK[a.tier]||0)-(PACK_RANK[b.tier]||0); });
      });
      activeRevealScope = scope;
      /* Only retire the recovery door once there is a real group of cards to
         put on screen. An `ok` envelope with no results must not make the
         Explore gift disappear forever. */
      if(packQueue.length && typeof openOpts.onRevealReady === 'function'){
        try { openOpts.onRevealReady(d); } catch(_){}
      }
      /* Let the summoning breathe for a beat so fast rolls don't flash. */
      var dwell = Math.max(0, 700 - (Date.now() - summonAt));
      setTimeout(function(){
        if(opToken===packOperationGeneration && shopAccountIsCurrent(scope)) runPackQueue();
      }, dwell);
    });
  }
  function setBtns(disabled){
    if(!hostEl) return;
    hostEl.querySelectorAll('.ffshop-open-btn').forEach(function(b){ b.disabled = disabled || b.hasAttribute('data-hard-off'); });
  }

  /* ---------------- badges (display; counters live server-side) ---------------- */

  /* R236: badges stay INVISIBLE at zero progress — the strip only shows
     what the player has started earning. Curator's ladder is DYNAMIC:
     its final tier is "every FramedFantasy portrait in the Hall", read
     live from the pool so the goal grows as Jan paints more, and that
     top tier renders as the very rare brand badge (Grand Curator). */
  function ffTotalLive(){
    var n=0;
    try{
      (typeof window.hohGetAllCharacters==='function' ? (window.hohGetAllCharacters()||[]) : [])
        .forEach(function(c){ if(c.is_ff_portrait) n++; });
    }catch(_){}
    return n;
  }
  /* R444 (Jan): "add a line under this for x/x Heroes of the Hall collected."
     The FF line counts one named set inside the Hall. This counts the Hall
     itself, the same denominator the card footer already prints under every
     portrait ("320 / 1099 HEROES OF THE HALL"), so the two numbers a player
     sees in two places agree. Live, not cached, because heroes arrive daily. */
  function hallTotalLive(){
    try{
      return (typeof window.hohGetAllCharacters==='function' ? (window.hohGetAllCharacters()||[]) : []).length | 0;
    }catch(_){ return 0; }
  }
  /* R268: the card's collector number — its 1-based rank among ALL FramedFantasy
     portraits ordered by submission date (created_at), out of the live total. So
     the earliest-submitted portrait reads 1 / N. Returns null if the hero isn't a
     loaded FF portrait, in which case the caller hides the line. */
  function ffCollectorNo(heroId){
    try{
      /* COLLECTOR-01 (Jan, 2026-10-23: "Signed and numbered delivery for every new portrait"): the numbers are stored
         and never move. ff-binder.js reads them once per page as window.ffCollectorNumbers (hero id -> number); when
         they are there they win, a portrait without one shows no line, and the total is the latest number. The
         computed order below stays only for a page where they can't be read. */
      var _st = window.ffCollectorNumbers;
      if(_st && typeof _st.get === 'function'){
        var _n = _st.get(String(heroId));
        if(!_n) return null;
        var _top = 0; _st.forEach(function(v){ if(v > _top) _top = v; });
        return { n: _n, total: _top };
      }
      /* ⛔ R577: two latent faults in this sort, closed because Jan asked
         whether the FF cards were broken the same way as the Hall line. They
         were not, and this is the number that is right, but:
         (a) `new Date(x||0)` turns a MISSING or unparseable created_at into
             epoch 0, which would put that portrait at #1 ahead of the real
             first one. NaN is guarded and falls back to 0 explicitly, so the
             behaviour is chosen rather than accidental;
         (b) portraits sharing a created_at (a bulk insert) fell through to a
             stable sort, which preserves the pool's own `id DESC` arrival
             order, so a tie group read BACKWARDS. The id tiebreak mirrors the
             fetch's second key and makes the number stable across reloads. */
      /* ⛔ AND THE OBVIOUS GUARD IS THE WRONG ONE, caught by testing it rather
         than reading it: `new Date(x || 0)` does not produce NaN for a missing
         date, it produces EPOCH, which is a perfectly valid time that sorts
         FIRST. So the dateless portrait still stole #1 from the real one. A
         date we do not have sorts LAST. */
      var _sub = function(c){
        var raw = c && c.created_at;
        if(!raw) return Infinity;
        var t = new Date(raw).getTime();
        return (t === t) ? t : Infinity;       /* NaN never equals itself */
      };
      var list = (typeof window.hohGetAllCharacters==='function' ? (window.hohGetAllCharacters()||[]) : [])
        .filter(function(c){ return c && c.is_ff_portrait; })
        .slice()
        .sort(function(a,b){
          var ta = _sub(a), tb = _sub(b);
          if(ta !== tb) return ta - tb;
          var ia = String((a && a.id) || ''), ib = String((b && b.id) || '');
          return ia < ib ? -1 : (ia > ib ? 1 : 0);
        });
      var total = list.length;
      var idx = -1;
      for(var i=0;i<list.length;i++){ if(String(list[i].id)===String(heroId)){ idx=i; break; } }
      if(idx < 0 || !total) return null;
      return { n: idx+1, total: total };
    }catch(_){ return null; }
  }
  function badgeDefs(){
    var total = Math.max(ffTotalLive(), 41); /* keep the ladder sane before the pool loads */
    return [
      { key:'packbreaker', name:'Packbreaker',   stat:'packs_opened', tiers:[10,50,100,250],
        what:'Open card packs.', unit:'packs opened' },
      { key:'curator',     name:'Curator',       stat:'_unique_ff',   tiers:[5,15,40,total], maxName:'Grand Curator', maxClass:' brand',
        what:'Collect different FramedFantasy portrait cards. Collecting every portrait in the Hall earns the rarest badge in the Shop.', unit:'portraits' },
      { key:'dustdevil',   name:'Dust Devil',    stat:'cards_dusted', tiers:[25,100,400,1000],
        what:'Turn pulled cards into hearts.', unit:'cards dusted' },
      { key:'firsted',     name:'First Edition', stat:'_own_pull',    tiers:[1], maxClass:' t4',
        what:'Pull one of your own submitted heroes from a pack.', unit:'' }
    ];
  }
  /* Tier ladder reads I, II, III, then Mythic at the top. */
  function tierName(def, t){
    if(def.tiers.length === 1) return 'Mythic';
    return ['I','II','III','Mythic'][t-1] || 'Mythic';
  }
  /* Claims: reaching a threshold EARNS a tier; the player taps to CLAIM
     it (the ceremony). Claimed tier is what the chip wears. */
  var BADGE_CLAIMS_KEY = 'ffshop-badge-claims-v1';
  function badgeClaims(){ try{ return JSON.parse(localStorage.getItem(BADGE_CLAIMS_KEY)||'{}')||{}; }catch(_){ return {}; } }
  function setBadgeClaim(key, tier){ try{ var c=badgeClaims(); c[key]=tier; localStorage.setItem(BADGE_CLAIMS_KEY, JSON.stringify(c)); }catch(_){} }
  function badgeStats(){
    var coll=(shopState&&shopState.collection)||[];
    var uniqFF=0, ownPull=0;
    coll.forEach(function(c){ if(c.ff) uniqFF++; if(c.own) ownPull=1; });
    return {
      packs_opened:(shopState&&shopState.packs_opened)||0,
      cards_dusted:(shopState&&shopState.cards_dusted)||0,
      _unique_ff:uniqFF, _own_pull:ownPull
    };
  }
  function badgesHtml(){
    var s=badgeStats(), claims=badgeClaims(), html='';
    badgeDefs().forEach(function(b){
      var v=s[b.stat]||0;
      var earned=0;
      for(var i=0;i<b.tiers.length;i++){ if(v>=b.tiers[i]) earned=i+1; }
      var claimed=Math.min(claims[b.key]||0, earned);
      if(v < 1 && claimed < 1) return; /* invisible until the first point of progress */
      var len=b.tiers.length;
      var maxedClaimed = claimed>=len;
      var claimable = earned>claimed;
      var label = claimed>0
        ? (maxedClaimed && b.maxName ? b.maxName : (len===1 ? b.name : b.name+' '+tierName(b, claimed)))
        : b.name;
      var prog = claimable ? ('Claim'+(len===1?'':' '+tierName(b, claimed+1)))
               : maxedClaimed ? 'Max'
               : (v.toLocaleString()+'/'+b.tiers[claimed].toLocaleString());
      var tip = b.what+' ' + (claimable
        ? (len===1 ? 'Earned. Tap to claim it.' : 'Tier '+tierName(b, claimed+1)+' is earned. Tap to claim it.')
        : maxedClaimed ? 'Fully mastered.'
        : 'Next: '+b.tiers[claimed].toLocaleString()+(b.unit?(' '+b.unit):'')+' for Tier '+tierName(b, claimed+1)+'.');
      var cls = (claimed>0?' is-earned':'') + (maxedClaimed?(b.maxClass||' t4'):'') + (claimable?' is-claimable':'');
      html+='<span class="ffshop-badge'+cls+'" data-badge="'+esc(b.key)+'" role="'+(claimable?'button':'note')+'" tabindex="'+(claimable?'0':'-1')+'">' +
        '<b>'+esc(label)+'</b> '+esc(prog)+'<span class="tip">'+esc(tip)+'</span></span>';
    });
    return html;
  }
  function claimBadge(key, chipEl){
    var defs=badgeDefs(), def=null;
    for(var i=0;i<defs.length;i++){ if(defs[i].key===key) def=defs[i]; }
    if(!def) return;
    var s=badgeStats(), v=s[def.stat]||0, earned=0;
    for(var j=0;j<def.tiers.length;j++){ if(v>=def.tiers[j]) earned=j+1; }
    var claimed=Math.min(badgeClaims()[key]||0, earned);
    if(earned<=claimed) return;
    setBadgeClaim(key, claimed+1);
    /* gold shard burst off the chip */
    try{
      var r=chipEl.getBoundingClientRect();
      for(var k=0;k<10;k++){
        var sp=document.createElement('span');
        sp.className='ffshop-burst';
        var ang=Math.random()*Math.PI*2, d=30+Math.random()*46;
        sp.style.left=(r.left+r.width/2)+'px';
        sp.style.top=(r.top+r.height/2)+'px';
        sp.style.setProperty('--bx',(Math.cos(ang)*d).toFixed(0)+'px');
        sp.style.setProperty('--by',(Math.sin(ang)*d-18).toFixed(0)+'px');
        document.body.appendChild(sp);
        (function(el){ setTimeout(function(){ try{ el.remove(); }catch(_){} }, 950); })(sp);
      }
    }catch(_){}
    var nm = (claimed+1>=def.tiers.length && def.maxName) ? def.maxName
           : (def.tiers.length===1 ? def.name : def.name+' '+tierName(def, claimed+1));
    toast(nm+' claimed');
    setTimeout(function(){ renderShop(hostEl); }, 380);
  }

  /* -------- R241: applied-badge sync (auto-prompt + auto-upgrade) --------
     The Achievements strip stays hidden. The moment an applied badge tier
     is EARNED (server counters), the hero picker auto-opens; with one hero
     it auto-applies. Higher tiers upgrade the SAME wearer silently (Jan's
     rule). Dismissing snoozes the prompt for this page load only, so it
     returns next visit until applied. Wearer state lives in quest_meta on
     the hero — server-side, cross-device — never in localStorage. */
  var badgeSnooze = {};
  function regKeyFor(defKey, tier){ return defKey==='firsted' ? 'first-edition' : defKey+'-'+tier; }
  function syncShopBadges(){
    if(!shopState || authFailed) return;
    if(typeof window.hohBadgeApplyPicker!=='function' || typeof window.hohBadgeWearer!=='function') return;
    if(document.getElementById('hoh-badge-pick')) return;
    if(modalEl && modalEl.style.display==='flex') return; /* never over the pack reveal */
    var s = badgeStats();
    var defs = badgeDefs();
    for(var i=0;i<defs.length;i++){
      var d = defs[i], v = s[d.stat]||0, earned = 0;
      for(var t=0;t<d.tiers.length;t++){ if(v>=d.tiers[t]) earned = t+1; }
      if(earned < 1) continue;
      var fam = (d.key==='firsted') ? 'first-edition' : d.key;
      var key = regKeyFor(d.key, earned);
      var wear = window.hohBadgeWearer(fam);
      if(wear){
        if(wear.key !== key){
          (function(k, wr){
            window.hohApplyAccountBadge(k, wr.hero.id).then(function(r){
              if(r && r.ok){
                var nm = ((window.HOH_QUEST_BADGES||{})[k]||{}).name || 'Badge';
                toast(nm+' — '+(wr.hero.name||'your hero')+' upgraded');
              }
            });
          })(key, wear);
        }
        continue;
      }
      if(badgeSnooze[fam]) continue;
      var openedPick = window.hohBadgeApplyPicker(key, {
        onApplied: function(){ setTimeout(syncShopBadges, 700); },
        onDismiss: function(){ badgeSnooze[fam] = true; }
      });
      if(openedPick) return; /* one prompt at a time */
    }
  }

  /* ---------------- boards ---------------- */

  function tileHtml(c){
    /* R254: rarity from LIVE hearts, not the fetch-time snapshot, so a card that
       tiered up shows (and the mythic rainbow border applies) immediately.
       R252: tier class drives the mythic holographic border. */
    var tier = tierFor(liveHearts(c.character_id, c));
    var rar = TIER_COLOR[tier] || TIER_COLOR.common;
    /* R268 (grid rework): own-non-FF heroes are back in the grid, pinned to the
       end. R429: they no longer render flat or greyed — the .ffshop-tile-ownhero class only
       rarity border + glow and desaturates the art (CSS), so FramedFantasy
       portraits stay visibly the collectibles. An own hero that is ALSO an FF
       portrait (c.ff && c.own) is a normal FF card and gets none of this. */
    var ownHero = !!(c.own && !c.ff);
    /* R380: not pulled yet — grayed, still yours, still openable.
       R388: a SPENT own card wears the same grey and blocks seating too. */
    /* R429: notCollected is no longer produced anywhere — every submitted
       hero and every pull is a collected card since R422 — so the only greyed
       state left is a card the player chose to dust, which only the party
       builder tags (data-spent), and only there does greying mean anything:
       it cannot be seated. The || is kept so an old cached shop payload
       carrying the flag still renders sanely rather than throwing. */
    var unColl = !!c.notCollected || !!c.spentCard;
    /* R430 (Jan): "the packs you open and the new cards you just got should
       show with a new badge."
       TWO REASONS IT MOSTLY DID NOT. First the gate read `c.ff &&`, so only
       FramedFantasy portraits could ever wear it — a leftover from when they
       were the only thing a pack kept. Every pull is collected since R422, so
       a whole pack of non-FF cards landed in the grid announcing nothing.
       Second it leaned entirely on first_pulled_at, a server timestamp that
       is only as fresh as the last state refetch, so a card could sit in the
       grid unmarked until something else happened to reload.
       Both signals now, ORed: the server's timestamp is the durable one that
       survives a reload and expires on its own, and a local record of what
       just arrived covers the seconds before the server view catches up —
       and covers a hero you just SUBMITTED, whose card is granted by trigger
       and has no pull timestamp of its own. */
    var _pulledMs = c.first_pulled_at ? Date.parse(c.first_pulled_at) : NaN;
    var isNew24 = (!isNaN(_pulledMs) && (Date.now() - _pulledMs) < 86400000)
                  || justArrived(c.character_id);
    var progressionKey=(c.ff?'ff:':'own:')+String(c.character_id), upgradeCount=(unColl||c.spentCard)?0:cardUpgradeAttention(progressionKey);
    return '<div class="ffshop-tile ffshop-tile-'+tier+(c.ff?'':' ffshop-tile-nonff')+(ownHero?' ffshop-tile-ownhero':'')+(unColl?' ffshop-tile-uncoll':'')+(upgradeCount?' has-upgrade-ready':'')+'" data-card-key="'+esc(progressionKey)+'" aria-label="'+esc((c.name||'Hero')+(upgradeCount?'. '+upgradeCount+' '+(upgradeCount===1?'upgrade or quest':'upgrades or quests')+' to finish.':''))+'" style="--rar:'+rar+'" role="button" tabindex="0" data-hero-id="'+esc(String(c.character_id))+'"'+(c.spentCard?' data-spent="1"':'')+'>' +
      (c.img?'<img src="'+esc(c.img)+'" alt="" loading="lazy" draggable="false" style="'+esc(imgStyle(c.character_id))+'">':'') +
      /* R268: FF corner logo removed — the collection is FF-portraits only now,
         so the badge on every card is redundant noise. */
      (upgradeCount?'<span class="ffshop-tile-upgrade" aria-hidden="true">!</span>':'') +
      (isNew24?'<span class="ffshop-tile-new">New</span>':'') +
      (c.kept>=2?'<span class="ffshop-tile-dupe">x'+c.kept+'</span>':'') +
      '<span class="ffshop-tile-select" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 4 4 10-10"/></svg>' +
      '</span>' +
      '<span class="ffshop-tile-name'+(String(c.name||'').length>=22?' ffshop-tile-name-long':'')+'">'+esc(c.name||'Hero')+'</span></div>';
  }

  /* The shelf's pack art: the Hall's three shop faces (Ember, Dylora,
     Jaezlan Baenre) crossfading in a fan of card backs with ambient
     sparkles. Falls back to the most-hearted FF portrait. */
  function featuredHero(){
    var h = heroById(EMBER_ID);
    if(h && (h.image_thumb_url || h.image_url)) return h;
    var best = null;
    try{
      (typeof window.hohGetAllCharacters==='function' ? (window.hohGetAllCharacters()||[]) : []).forEach(function(c){
        if(c.is_ff_portrait && (c.image_thumb_url||c.image_url) && (!best || (c.hearts||0) > (best.hearts||0))) best = c;
      });
    }catch(_){}
    return best;
  }
  function rotatorHeroes(){
    var all = [];
    try{ all = (typeof window.hohGetAllCharacters==='function') ? (window.hohGetAllCharacters()||[]) : []; }catch(_){}
    var found = [];
    SHELF_NAMES.forEach(function(n){
      var best = (n==='ember') ? heroById(EMBER_ID) : null;
      if(best && !(best.image_thumb_url||best.image_url)) best = null;
      if(!best){
        for(var i=0;i<all.length;i++){
          var c = all[i];
          if(!c || !c.name || !(c.image_thumb_url||c.image_url)) continue;
          if(String(c.name).toLowerCase().indexOf(n) === -1) continue;
          /* prefer the FF portrait bearing the name; then most hearted */
          if(!best || (c.is_ff_portrait && !best.is_ff_portrait) ||
             (!!c.is_ff_portrait===!!best.is_ff_portrait && (c.hearts||0)>(best.hearts||0))) best = c;
        }
      }
      if(best) found.push(best);
    });
    if(!found.length){ var f = featuredHero(); if(f) found.push(f); }
    return found;
  }
  /* R241: the fan shows ALL THREE shop faces at once — front card
     highlighted with its name, the two behind dimmed and fanned. Every
     4.5s the fan physically rotates: the front recedes to the back while
     the next card slides forward. Fewer than 3 heroes: logo-back fillers
     hold the back slots and only the real cards cycle. */
  function packArtHtml(){
    var hs = rotatorHeroes().slice(0,3);
    var cards = '';
    for(var i=0;i<3;i++){
      var h = hs[i];
      if(h){
        var img = h.image_thumb_url || h.image_url;
        cards += '<span class="ffshop-rotcard ffshop-p'+i+'" data-rot="1"><img src="'+esc(img)+'" alt="" loading="lazy" decoding="async" draggable="false"><b>'+esc(h.name||'')+'</b></span>';
      } else {
        cards += '<span class="ffshop-rotcard ffshop-p'+i+' ffshop-rotfill"><img src="'+FF_LOGO+'" alt=""></span>';
      }
    }
    var sparks = reduced ? '' :
      '<span class="ffshop-spark s1"></span><span class="ffshop-spark s2"></span>' +
      '<span class="ffshop-spark s3"></span><span class="ffshop-spark s4"></span>';
    return '<span class="ffshop-packart ffshop-fan">' + cards + sparks + '</span>';
  }
  function startShelfRotation(host){
    if(shelfTimer){ try{ clearInterval(shelfTimer); }catch(_){} shelfTimer = 0; }
    if(reduced) return;
    var fan = host.querySelector('.ffshop-fan');
    if(!fan) return;
    var cards = [].slice.call(fan.querySelectorAll('.ffshop-rotcard[data-rot]'));
    if(cards.length < 2) return;
    var pos = ['ffshop-p0','ffshop-p1','ffshop-p2'];
    var off = 0;
    shelfTimer = setInterval(function(){
      if(!fan.isConnected){ try{ clearInterval(shelfTimer); }catch(_){} shelfTimer = 0; return; }
      off = (off+1) % cards.length;
      cards.forEach(function(c, i){
        var p = (i - off + cards.length) % cards.length;
        c.classList.remove('ffshop-p0','ffshop-p1','ffshop-p2');
        c.classList.add(pos[Math.min(p, 2)]);
      });
    }, 4500);
  }
  /* ---------------- collection sort ----------------
     R268 (grid rework): drag-to-rearrange was REMOVED. The grid always renders
     in the default order — FramedFantasy portraits first by rarity, then the
     user's own (non-FF) heroes pinned to the end. savedOrder/saveOrder/applyOrder
     /wireDrag are kept as harmless no-op stubs only so any stray external
     reference doesn't throw; nothing in this file calls them anymore. */
  function tierRank(t){ return ({mythic:5,legendary:4,epic:3,rare:2,uncommon:1,common:0})[t] || 0; }
  function defaultCompare(a, b){
    var fa = a.ff ? 0 : 1, fb = b.ff ? 0 : 1;
    if(fa !== fb) return fa - fb;                    /* FF portraits ahead of own-non-FF */
    /* R254: rank by LIVE hearts so a tiered-up card sorts to its current rarity. */
    var ra = tierRank(tierFor(liveHearts(a.character_id, a))), rb = tierRank(tierFor(liveHearts(b.character_id, b)));
    if(ra !== rb) return rb - ra;                    /* rarity, highest first */
    return String(a.name||'').localeCompare(String(b.name||''));
  }
  /* No-op stubs (drag removed). */
  function savedOrder(){ return []; }
  function saveOrder(){}
  function applyOrder(list){ return (list||[]).slice().sort(defaultCompare); }
  function wireDrag(){}
  /* Gold shard burst at the purchase moment. */
  function purchaseBurst(el){
    if(reduced || !el) return;
    try{
      var r = el.getBoundingClientRect();
      for(var k=0;k<12;k++){
        var sp=document.createElement('span');
        sp.className='ffshop-burst';
        var ang=(k/12)*Math.PI*2, d=36+Math.random()*40;
        sp.style.left=(r.left+r.width/2)+'px';
        sp.style.top=(r.top+r.height/2)+'px';
        sp.style.setProperty('--bx',(Math.cos(ang)*d).toFixed(0)+'px');
        sp.style.setProperty('--by',(Math.sin(ang)*d).toFixed(0)+'px');
        document.body.appendChild(sp);
        (function(e2){ setTimeout(function(){ try{ e2.remove(); }catch(_){} }, 950); })(sp);
      }
    }catch(_){}
  }

  /* ---------------- collection block (shared by Shop + Satchel) ----------------
     R268 (grid rework): the grid source is FF portraits PLUS the user's own
     heroes. FF portraits sort first (by rarity, via defaultCompare); own-non-FF
     heroes are pinned to the very END and render greyed with no rarity edge.
     Heroes the user has DUSTED from their collection (dustedOwnIds) are filtered
     out here so they leave the grid on every render but stay in the Hall. */
  /* R384: does this hero have any art at all (collection row or hall row)? */
  function heroImg(id){
    try { var h = heroById(id); return h ? (h.image_thumb_url || h.image_url || '') : ''; } catch(_){ return ''; }
  }
  /* R384 (Jan): the borrowed-party leader. The strongest eligible WIZARD in
     the hall — same legality rules the hireling pool enforces (non FF,
     approved, visible, has art, Epic or under), so the server's
     hoh_is_hireling_key accepts the seat. Falls back to any eligible hero
     when the hall holds no wizard. Deterministic on purpose: the loaner
     reads as "the hall lends you its teacher", not a dice roll. */
  function loanerWizardId(){
    /* R386 (Jan): borrowed members are COMMON AND BELOW. Preference order:
       strongest common wizard, any common, then (only if the hall holds no
       commons at all) the old up-to-Epic fallback so the door never dies. */
    var all = [];
    try { all = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : []; } catch(_){}
    var cWiz = null, cAny = null, fallback = null;
    for(var i = 0; i < all.length; i++){
      var c = all[i];
      if(!c || !c.id || c.is_ff_portrait || c.approved !== true || c.is_hidden) continue;
      if(!(c.image_thumb_url || c.image_url)) continue;
      var hearts = c.hearts || 0;
      var tier = tierFor(hearts);
      if(tier === 'legendary' || tier === 'mythic') continue;
      if(!fallback || hearts > (fallback.hearts || 0)) fallback = c;
      if(tier !== 'common') continue;
      if(!cAny || hearts > (cAny.hearts || 0)) cAny = c;
      var cls = String(c.character_class || '').split(' ')[0].toLowerCase();
      if(cls === 'wizard' && (!cWiz || hearts > (cWiz.hearts || 0))) cWiz = c;
    }
    var pick = cWiz || cAny || fallback;
    return pick ? String(pick.id) : '';
  }
  function collectionSource(){
    var coll = (shopState && shopState.collection) || [];
    /* R422 (Jan): the grid is EVERYTHING COLLECTED. Every pull is kept now,
       so the old FF-or-own gate would hide real cards the player owns. The
       legacy dusted-own localStorage latch stays out of the way too — the
       server's copies/cashed math is the truth about dust. */
    /* R432 (Jan): "when the character is hidden by me it cant appear in the
       hall for anyone... and it shouldnt appear in the battlegrounds either."
       hohGetAllCharacters already hides them everywhere it feeds, but a
       COLLECTED card carries its own name and art snapshot on the collection
       row, so a hero hidden after somebody pulled them kept rendering here off
       that snapshot with no live hero behind it.
       Checked against the FULL list on purpose: heroById reads the filtered
       one, where "hidden" and "not loaded yet" both come back null, and
       dropping on null would blank the grid mid-page-load. Present AND hidden
       is the only condition that removes a tile. */
    /* ⛔ R609 · THIS FILTER HAD NOTHING TO READ AND HAS BEEN REMOVING NOTHING.
       The rule above is right and its safety argument is right. What broke it
       is a query in another file: the Hall's boot fetch was later tightened to
          .eq('approved',true).or('is_hidden.is.null,is_hidden.eq.false')
       so `hohGetAllCharactersFull()` cannot contain a hidden row for anybody.
       "Present AND hidden" is therefore unsatisfiable and `_hiddenIds` builds
       empty on every paint — which is why a hero Jan hid kept rendering as a
       card off its collection snapshot exactly as R432 described.
       The lazily loaded hidden set is added as a second source so a god-mode
       session that has already fetched it gets the intended behaviour with no
       extra request. It is NOT loaded on demand here: it is a full-column read
       of every hidden hero in the Hall, which is not a cost a phone should pay
       to paint a collection grid.
       ⛔ THE COMPLETE CURE IS SERVER SIDE. While the collection rows still
       exist, a client can only decline to draw them. Quick Review takes the
       stricter line (abilityQueueHeroState) because an unresolvable hero there
       is not cosmetic: it produces a roll built on a guessed class that the
       server then refuses. */
    /* ⛔ R663 (Jan): "if anyone still owns it it just disappears from their
       owned cards." The filter below is unchanged in shape and is finally
       given something to read: hohEnsureHiddenIds fetches the hidden heroes'
       IDS ONLY, which is the cheap version of the full-column read the note
       above rightly refused. It is asked for once and repaints every surface
       when it lands, so the first paint of a session can still show a card
       that the second does not.
       A null answer means the read has not completed, and a null answer
       removes NOTHING. Failing toward showing one stale card is the right way
       round: the other direction eats a collection somebody paid for. */
    var _hiddenLive = null;
    try {
      if(typeof window.hohHiddenIdSet === 'function') _hiddenLive = window.hohHiddenIdSet();
      if(_hiddenLive == null && typeof window.hohEnsureHiddenIds === 'function'){
        window.hohEnsureHiddenIds(function(m){
          if(m) { try { refreshCollectionEverywhere(); } catch(_){} }
        });
      }
    } catch(_){}
    var _hiddenIds = null;
    function _isHiddenHero(id){
      try {
        if(_hiddenIds === null){
          _hiddenIds = {};
          var full = (typeof window.hohGetAllCharactersFull === 'function') ? (window.hohGetAllCharactersFull() || []) : [];
          for(var i = 0; i < full.length; i++){
            if(full[i] && full[i].is_hidden === true && full[i].id != null) _hiddenIds[String(full[i].id)] = 1;
          }
          var lazy = window.__hohHidden;
          if(Array.isArray(lazy)){
            for(var h = 0; h < lazy.length; h++){
              if(lazy[h] && lazy[h].id != null) _hiddenIds[String(lazy[h].id)] = 1;
            }
          }
          if(_hiddenLive){ for(var k in _hiddenLive){ if(_hiddenLive[k]) _hiddenIds[k] = 1; } }
        }
        return !!_hiddenIds[String(id)];
      } catch(_){ return false; }
    }
    var gridCards = coll.filter(function(c){
      if(!c || c.character_id == null) return false;
      if(_isHiddenHero(c.character_id)) return false;   /* pulled from the Hall */
      /* R384 (Jan): a hero with no portrait renders as a broken empty frame —
         keep it out of the grid entirely. Its hearts still count elsewhere. */
      if(!c.img && !heroImg(c.character_id)) return false;
      var remaining = Math.max(0, (c.copies | 0) - ((c.cashed_copies != null ? c.cashed_copies : c.cashed) | 0));
      /* R558: FF and non-FF stacks obey the same ownership law. A fully
         dusted row can remain visible as an unavailable explanation inside
         the party builder, but it is not still a card in Profile inventory. */
      if(window.__hohCollMode !== 'builder' && remaining <= 0) return false;
      return true;
    });
    /* R388 (Jan): an unfieldable card STAYS VISIBLE in the builder but greyed
       and unseatable, exactly like an uncollected one. R422: the only
       unfieldable state left is FULLY DUSTED (copies all cashed) — the
       one-battle spend is dead, so spent_copies no longer counts against a
       card. Tagged on a copy so shared state stays clean. */
    if(window.__hohCollMode === 'builder'){
      gridCards = gridCards.map(function(c){
        var _pl = (c.copies || 0) - ((c.cashed_copies != null ? c.cashed_copies : c.cashed) || 0);
        if(_pl <= 0) return Object.assign({}, c, { spentCard: 1 });
        return c;
      });
    }
    var ffCards = gridCards.filter(function(c){
      return c.ff && Math.max(0, (c.copies | 0) - ((c.cashed_copies != null ? c.cashed_copies : c.cashed) | 0)) > 0;
    });
    /* The server collection is also Battlegrounds' authority. Never invent
       copies from a submitted-hero/name lookup to conceal a missing grant. */
    /* defaultCompare already ranks FF (0) ahead of non-FF (1), then by rarity,
       then name — so own-non-FF land after every FF card. No saved drag order. */
    var allCards = gridCards.slice().sort(defaultCompare);
    var totalFF = ffTotalLive();
    /* R260: tally spare copies (everything above the one kept per hero) and what
       dusting them all would pay, for the Dust extras button. Own heroes' extras
       count too (they pay double, no x10). */
    var dustCopies = 0, dustHearts = 0;
    gridCards.forEach(function(c){
      var extra = Math.max(0, dustRemaining(c) - 1);
      if(extra > 0){
        dustCopies += extra;
        dustHearts += extra * (TIER_VALUE[tierFor(liveHearts(c.character_id, c))] * (c.ff?10:1) * (c.own?2:1));
      }
    });
    return { ffCards:ffCards, allCards:allCards, totalFF:totalFF, totalHall:hallTotalLive(), dustCopies:dustCopies, dustHearts:dustHearts };
  }
  /* The controls row (count + Dust extras) and the grid, as one HTML string.
     Shared verbatim between the Shop tab and the Satchel's top-of-page mount.
     `forSatchel` swaps the empty-state to an "Open a Pack" CTA. */
  /* R335: THE COLLECTION ANCHOR.
     Every fly-to animation used to resolve #ffshop-grid-all and then test it
     for TRUTHINESS. In card view that node still exists, it is merely
     display:none -- and a hidden element returns an all-zero DOMRect. So the
     sensible fallback rect was skipped and every kept card, every dust mote
     and the grand-total float sailed to the top-left corner of the screen.
     This returns whichever collection surface is actually VISIBLE, or null so
     the callers' existing fallbacks engage. offsetWidth is the cheap
     "is it laid out" test -- it is 0 for display:none and for a collapsed
     Satchel pocket alike. */
  function collAnchor(root){
    root = root || hostEl;
    var live = document.querySelector('#ffshop-rail-wrap');
    if(live && !live.hidden && live.offsetWidth) return document.querySelector('#ffshop-rail') || live;
    var g = document.querySelector('#ffshop-grid-all');
    if(g && g.offsetWidth) return g;
    if(root){
      var rw = root.querySelector('#ffshop-rail-wrap');
      if(rw && !rw.hidden && rw.offsetWidth) return root.querySelector('#ffshop-rail') || rw;
      var rg = root.querySelector('#ffshop-grid-all');
      if(rg && rg.offsetWidth) return rg;
    }
    return null;
  }
  /* R437 · the Profile collection section. Deliberately mirrors the main
     Hall's filter bar class for class (.hoh-filters / .hoh-filter-combo /
     .hoh-combo-seg / .hoh-count-row / .hoh-sort) so the two surfaces are the
     same object in two contexts rather than two designs that rhyme. What
     changes is what it searches and what it sorts by, because these are
     cards, not heroes. */
  var COLL_SORTS = [
    ['rarity',  'Rarity'],
    ['level',   'Card level'],
    ['ability', 'Ability rarity'],
    ['hearts',  'Most hearts'],
    ['new',     'Newest'],
    ['name',    'Name']
  ];
  /* R437 · filter + sort for the Profile collection.
     The card rows carry almost nothing sortable — name, img, ff, own, copies,
     cashed, kept, hearts — so race, class, card level and ability rarity all
     have to be JOINED. That join is done ONCE per paint into a map keyed by
     hero id, not inside the comparator, because a comparator runs O(n log n)
     times and each of these lookups walks the whole Hall pool. */
  /* ============ R472 · THE CLASS FILTER LISTS CLASSES ============
     Jan: "it's showing every single class right now ... even the custom ones
     that someone plugged in, which is wrong ... right now we have, like, moon
     druid, wild shifting, often as raccoon. That's not a specific class. So
     make it so that there's only the standard classes in there instead of
     every single one that anyone's ever put in."

     character_class is a FREE TEXT field a player fills in on their own
     character, so the dropdown was a list of every sentence anybody had ever
     typed. It grew forever and half its rows matched exactly one card.

     Exact match against the standard list, case and spacing forgiven, and
     everything else lands in Other. A compound answer like "Rogue/Fighter" is
     deliberately Other rather than being filed under whichever name happens
     to appear first: guessing a primary class out of a sentence the player
     wrote is how "Moon Druid, Wild Heart Barbarian" ends up filed as Druid
     with the Barbarian half silently thrown away.

     THE RAW TEXT IS NOT LOST. It stays in the search haystack below, so
     typing raccoon still finds the raccoon druid. The filter is the thing
     that needed a fixed vocabulary, not the search. */
  var COLL_CLASS_STD = ['Artificer','Barbarian','Bard','Blood Hunter','Cleric','Druid',
                        'Fighter','Monk','Paladin','Ranger','Rogue','Sorcerer','Warlock','Wizard'];
  var COLL_CLASS_LOOK = (function(){
    var m = {};
    for(var i = 0; i < COLL_CLASS_STD.length; i++) m[COLL_CLASS_STD[i].toLowerCase()] = COLL_CLASS_STD[i];
    return m;
  })();
  function stdClass(raw){
    var s = String(raw || '').replace(/\s+/g, ' ').trim();
    if(!s) return '';
    return COLL_CLASS_LOOK[s.toLowerCase()] || 'Other';
  }
  /* ⛔ ============ R567 · THE RACE FILTER LISTS RACES ============
     Jan: "there are also way too many races in the menu, cut it so its just
     the standard and everything else goes to other like the main hall has it."
     His screenshot is the collection grid's Races menu, opening on "A Vigil, a
     guardian angel type aasimar", "Aasimar tiefling hybrid", "Alien/Elf",
     "Bee Hive", "Celestial (Fallen)", "Death Knight (Was Elf)" — every phrase
     any player has ever typed into a free-text field, sorted alphabetically,
     most of them matching exactly one card.

     ⛔ THIS IS R472's BUG IN THE OTHER COLUMN. That wave fixed the CLASS menu
     here and R484 fixed the RACE menu in the party builder, and both left this
     one alone, so the collection grid has been listing raw races ever since.
     Same cure, same shape, deliberately sharing `RACES` — the twenty the Hall
     itself uses (hoh-data.js) — so "like the main hall has it" is literally
     true rather than a second list that drifts.

     THE FIRST WORD, then an exact match. Unlike a class, a race really is
     usually the first word of whatever the player wrote ("Aasimar tiefling
     hybrid" is an Aasimar), and that is the rule the party builder already
     uses, so the two menus agree. Anything else is Other.
     ⛔ THE RAW TEXT IS NOT LOST: `hay` below still carries it, so typing
     "guardian angel" still finds that card. The filter is what needed a fixed
     vocabulary, not the search. */
  var COLL_RACE_LOOK = null;
  function stdRace(raw){
    var s = String(raw || '').replace(/\s+/g, ' ').trim();
    if(!s) return '';
    var list = (window.RACES && window.RACES.length) ? window.RACES : null;
    /* ⛔ THE ABSENT LIST IS NEVER CACHED. `RACES` is a top-level var in
       hoh-data.js, so whether it exists yet depends on script order, and the
       first collection paint can beat it. Caching a miss would lock the filter
       into raw races for the rest of the session — the exact bug this function
       was written to fix, back again and only for some visitors. The cache is
       keyed on the list's own length, so it rebuilds the moment one arrives. */
    if(!list) return s;   /* fail OPEN to the raw race: a shortened menu is the
                             goal, an EMPTY one is not */
    if(!COLL_RACE_LOOK || COLL_RACE_LOOK.__n !== list.length){
      COLL_RACE_LOOK = { __n: list.length };
      for(var i = 0; i < list.length; i++) COLL_RACE_LOOK[String(list[i]).toLowerCase()] = list[i];
    }
    var first = s.split(' ')[0].replace(/[^A-Za-z-]/g, '');
    return COLL_RACE_LOOK[first.toLowerCase()] || COLL_RACE_LOOK[s.toLowerCase()] || 'Other';
  }
  function collMetaFor(cs){
    var meta = {};
    var races = {}, classes = {};
    var D = window.HOHContractData;
    for(var i = 0; i < cs.allCards.length; i++){
      var c = cs.allCards[i];
      var id = String(c.character_id);
      var hero = heroById(id) || {};
      var key = (c.ff ? 'ff:' : 'own:') + id;
      var lvl = 1, abil = -1, rolled = false, pending = false, malformed = false;
      try { if(D && D.cardXpFor) lvl = (D.cardXpFor(key) || {}).level || 1; } catch(_){}
      try {
        function rarityOf(row){
          if(!row||!row.id||row.id==='PENDING')return -1;
          var E=window.HOHContract||{},actual=NaN,def=E.abDef?E.abDef(String(row.id)):null;
          if(def)actual=Number(def.r);
          if((!isFinite(actual)||actual<0||actual>5||Math.floor(actual)!==actual)&&E.content&&E.content.SKILLS&&E.content.SKILLS[row.id])actual=Number(E.content.SKILLS[row.id].r);
          if(isFinite(actual)&&actual>=0&&actual<=5&&Math.floor(actual)===actual)return actual;
          var saved=Number(row.r);
          return isFinite(saved)&&saved>=0&&saved<=5&&Math.floor(saved)===saved?saved:-1;
        }
        var g={},gm={},gearKeys=[key,id,'own:'+id,'ff:'+id,'hall:'+id],seenGear={};
        for(var gi=0;gi<gearKeys.length;gi++){
          var gearKey=gearKeys[gi];
          if(!gearKey||seenGear[gearKey])continue;seenGear[gearKey]=1;
          var nextG=(D&&D.gearFor)?(D.gearFor(gearKey)||{}):{};
          var nextM=(D&&D.gearMetaFor)?(D.gearMetaFor(gearKey)||{}):{};
          ['active','passive'].forEach(function(slot){
            if(!g[slot]&&nextG[slot])g[slot]=nextG[slot];
            if(!gm[slot]&&nextM[slot])gm[slot]=nextM[slot];
          });
          if(g.active||g.passive||gm.active||gm.passive)break;
        }
        pending = !!((gm.active && gm.active.id === 'PENDING') || (gm.passive && gm.passive.id === 'PENDING'));
        ['active','passive'].forEach(function(slot){
          var row=gm[slot];
          if(row&&row.id!=='PENDING'&&(!String(row.id||'').trim()||row.r==null||!isFinite(row.r)||row.r<0||row.r>5))malformed=true;
        });
        var hasActive = !!(g.active && g.active.id && g.active.id !== 'PENDING');
        var hasPassive = !!(g.passive && g.passive.id && g.passive.id !== 'PENDING');
        rolled = !pending && (hasActive || hasPassive);
        if(rolled) abil = Math.max(hasActive ? rarityOf(g.active) : -1, hasPassive ? rarityOf(g.passive) : -1);
      } catch(_){}
      /* R440: liveHearts(id, c) would call heroById AGAIN for a row we are
         already holding. With ~1000 heroes in the pool that is a second
         ~500-iteration walk per card, doubling this function's cost for
         nothing. Same formula, inlined against the hero we already have. */
      var hearts = Math.max((hero && hero.hearts) || 0, (c && c.hearts) || 0);
      var raceRaw = String(hero.race || '').trim();
      var race = stdRace(raceRaw);       /* R567: the filter's vocabulary */
      var clsRaw = String(hero.character_class || '').trim();
      var cls = stdClass(clsRaw);        /* R472: the filter's vocabulary */
      if(race) races[race] = 1;
      if(cls) classes[cls] = 1;
      var pulled = 0;
      try { pulled = c.first_pulled_at ? new Date(c.first_pulled_at).getTime() : 0; } catch(_){}
      meta[id] = {
        name: String(c.name || '').toLowerCase(),
        race: race, cls: cls, classLabel: clsRaw,
        /* R472: the RAW class stays searchable. Only the filter is bucketed. */
        hay: (String(c.name || '') + ' ' + raceRaw + ' ' + race + ' ' + clsRaw).toLowerCase(),
        tier: TIER_VALUE[tierFor(hearts)] || 0,
        cardRarity: tierRank(tierFor(hearts)),
        level: lvl, ability: abil, rolled:rolled, pending:pending, malformed:malformed, hearts: hearts,
        pulled: isNaN(pulled) ? 0 : pulled
      };
    }
    /* R472: Other is a bucket, not a class, so it sits at the BOTTOM of the
       list instead of sorting itself in between Monk and Paladin where it
       reads as one more class the player has never heard of. */
    var clsList = Object.keys(classes).sort().filter(function(k){ return k !== 'Other'; });
    if(classes.Other) clsList.push('Other');
    /* R567: Other is a bucket, not a race, so it sits at the bottom for the
       same reason it does for classes — sorted in alphabetically it reads as
       one more race the player has never heard of. */
    var raceList = Object.keys(races).sort().filter(function(k){ return k !== 'Other'; });
    if(races.Other) raceList.push('Other');
    return { meta: meta, races: raceList, classes: clsList };
  }
  function wireCollProfile(host, cs){
    var grid = host.querySelector('#ffshop-grid-all');
    var q    = host.querySelector('#ffcollSearch');
    var rSel = host.querySelector('#ffcollRace');
    var cSel = host.querySelector('#ffcollClass');
    var sSel = host.querySelector('#ffcollSort');
    var cnt  = host.querySelector('#ffcollCount');
    var none = host.querySelector('#ffcollNone');
    var clr  = host.querySelector('#ffcollClear');
    if(!grid || !q || !sSel) return;
    var built = collMetaFor(cs);
    var meta = built.meta;
    function fill(sel, list){
      if(!sel) return;
      for(var i = 0; i < list.length; i++){
        var o = document.createElement('option');
        o.value = list[i]; o.textContent = list[i];
        sel.appendChild(o);
      }
    }
    /* Only what they actually own. A Races list full of races with no card
       behind it is a list of dead ends. */
    fill(rSel, built.races);
    fill(cSel, built.classes);
    /* State survives a repaint (dust, a pull, a level) because it lives on
       window, not in the DOM the repaint throws away. */
    var st = window.__ffCollState || (window.__ffCollState = { q:'', race:'', cls:'', sort:'rarity' });
    q.value = st.q; sSel.value = st.sort;
    if(rSel && built.races.indexOf(st.race) >= 0) rSel.value = st.race; else st.race = '';
    if(cSel && built.classes.indexOf(st.cls) >= 0) cSel.value = st.cls; else st.cls = '';
    var tiles = Array.prototype.slice.call(grid.querySelectorAll('.ffshop-tile'));
    tiles.forEach(function(t, i){
      t.__ffOrder = i;
      t.__ffLabel = t.getAttribute('aria-label') || '';
      var details = document.createElement('span');
      details.className = 'ffshop-tile-details';
      details.setAttribute('aria-hidden', 'true');
      details.innerHTML = '<span class="ffshop-tile-class"></span><span class="ffshop-tile-level"></span>';
      t.appendChild(details);
    });
    function apply(){
      st.q = String(q.value || '').toLowerCase().trim();
      st.race = rSel ? rSel.value : '';
      st.cls = cSel ? cSel.value : '';
      st.sort = sSel.value;
      var shown = 0;
      tiles.forEach(function(t){
        var m = meta[String(t.getAttribute('data-hero-id'))];
        if(m){
          var classLabel = m.classLabel || '';
          var levelLabel = 'Lv. ' + m.level;
          var details = t.querySelector('.ffshop-tile-details');
          if(details.firstChild.textContent !== classLabel) details.firstChild.textContent = classLabel;
          if(details.lastChild.textContent !== levelLabel) details.lastChild.textContent = levelLabel;
          t.setAttribute('aria-label', t.__ffLabel + '. ' + (classLabel ? classLabel + ', ' : '') + 'Level ' + m.level);
        }
        var ok = !!m;
        if(ok && st.q && m.hay.indexOf(st.q) < 0) ok = false;
        if(ok && st.race && m.race !== st.race) ok = false;
        if(ok && st.cls && m.cls !== st.cls) ok = false;
        t.style.display = ok ? '' : 'none';
        if(ok) shown++;
      });
      var k = st.sort;
      var order = tiles.slice().sort(function(a, b){
        var ma = meta[String(a.getAttribute('data-hero-id'))] || {};
        var mb = meta[String(b.getAttribute('data-hero-id'))] || {};
        var d = 0;
        if(k === 'rarity')       d = (mb.tier || 0)    - (ma.tier || 0);
        else if(k === 'level')   d = (mb.level || 0)   - (ma.level || 0);
        else if(k === 'ability') d = (mb.ability || 0) - (ma.ability || 0);
        else if(k === 'hearts')  d = (mb.hearts || 0)  - (ma.hearts || 0);
        else if(k === 'new')     d = (mb.pulled || 0)  - (ma.pulled || 0);
        else if(k === 'name')    d = String(ma.name || '').localeCompare(String(mb.name || ''));
        /* every sort falls back to the paint order, so equal values never
           shuffle between renders and the grid does not twitch. */
        return d || ((a.__ffOrder || 0) - (b.__ffOrder || 0));
      });
      var frag = document.createDocumentFragment();
      order.forEach(function(t){ frag.appendChild(t); });
      grid.appendChild(frag);
      if(cnt) cnt.textContent = shown + (shown === 1 ? ' card' : ' cards');
      /* An empty grid with a filter on top is the state nobody builds and
         everybody hits. It says what happened and offers the way out. */
      if(none) none.hidden = !(shown === 0 && tiles.length > 0);
      syncDustSelectionUI(host);
    }
    window.__ffCollApply = apply;
    /* R444 (Jan): "when i tried sorting by ability rarity, it should put even a
       common character on top if they have a legendary ability but it didnt."
       ROOT CAUSE. collMetaFor reads ability rarity out of D.gearFor(key) and
       card level out of D.cardXpFor(key). Both of those are pure cache reads.
       gearFor walks gearCache, cardXpFor indexes xpCache, and NEITHER cache is
       populated until something calls loadGear / loadCardXp. Every caller of
       those two loaders lives in hoh-contract-ui.js, on the Battlegrounds
       panel. This file never called either one. So on a cold Profile tab
       gearCache was null, gearFor returned {active:null,passive:null} for
       every card, ability came out 0 across the board, and the comparator's
       (b.ability - a.ability) was 0 for every pair. It then fell through to
       the paint-order tiebreak, which is why the grid looked like the sort
       had done nothing at all rather than sorted it wrong. Card level had the
       identical fault and would have read level 1 for everything.
       Warm both caches once, rebuild the meta map, repaint. It runs after the
       first synchronous apply() so the grid never waits on the network, and
       it is latched per mount so a repaint cannot stack fetches. loadGear and
       loadCardXp both no-op to a resolved promise when they already hold a
       cache or nobody is signed in, so the common case costs one microtask. */
    try {
      var _D = window.HOHContractData;
      if(_D && (_D.loadGear || _D.loadCardXp) && !host.__ffCollWarm){
        host.__ffCollWarm = 1;
        Promise.all([
          _D.loadGear    ? _D.loadGear()    : null,
          _D.loadCardXp  ? _D.loadCardXp()  : null
        ]).then(function(res){
          /* Nothing came back, so the caches are still cold and this mount
             must be allowed to try again on its next paint. loadGear caches
             on success, so a retry after a good load is one resolved promise
             and no request. */
          if(!res || !res[0] || !res[0].length) host.__ffCollWarm = 0;
          /* the mount can be gone by now, and a later paint may have replaced
             this closure's grid with a fresh one. Either way this repaint is
             stale and must not touch the DOM. */
          if(!grid || !document.body.contains(grid)) return;
          try {
            meta = collMetaFor(cs).meta;
            apply();
          } catch(_){}
        }, function(){ host.__ffCollWarm = 0; });
      }
    } catch(_){}
    q.addEventListener('input', apply);
    if(rSel) rSel.addEventListener('change', apply);
    if(cSel) cSel.addEventListener('change', apply);
    sSel.addEventListener('change', apply);
    if(clr) clr.addEventListener('click', function(){
      q.value = ''; if(rSel) rSel.value = ''; if(cSel) cSel.value = ''; apply();
    });
    apply();
  }
  function collProfileHtml(cs, emptyHtml, view){
    var n = cs.allCards.length;
    var dust = n
      ? '<button type="button" class="ffshop-dust-btn" id="ffshop-dust-btn" title="Choose cards to turn into hearts">' +
          '<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><path d="M12 3l1.5 4.2L18 8.7l-4.5 1.5L12 14.5l-1.5-4.3L6 8.7l4.5-1.5z" fill="currentColor"/><circle cx="18.4" cy="16.6" r="1.3" fill="currentColor"/><circle cx="6.2" cy="16.4" r="1" fill="currentColor"/></svg>' +
          'Dust cards <small>Manage</small></button>'
      : '';
    var roll = n
      ? '<button type="button" class="ffshop-rollq-btn" id="ffshop-rollq-btn" title="Roll unrolled abilities one card at a time">' +
          '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M5 4h14v16H5z"/><path d="m9 9 3-2 3 2v4l-3 2-3-2z"/><path d="M8 18h8"/></svg>' +
          'Roll abilities <small>Quick review</small></button>'
      : '';
    var sortOpts = COLL_SORTS.map(function(o){
      return '<option value="' + o[0] + '">' + o[1] + '</option>';
    }).join('');
    return '<div class="ffshop-collsec">' +
      '<div class="ffshop-collsec-head">' +
        '<div class="ffshop-collsec-title">Your cards</div>' +
        /* R444: two tallies now, so they need a column of their own. The head
           is a space-between flex row, and a third loose child would have
           spread the two numbers to opposite ends of it instead of stacking
           them under the title. */
        ((cs.totalFF || cs.totalHall)
          ? '<div class="ffshop-collsec-stats">' +
              (cs.totalFF
                ? '<div class="ffshop-collection-stat"><b>' + cs.ffCards.length + '</b> of <b>' + cs.totalFF + '</b> FramedFantasy portraits collected</div>'
                : '') +
              (cs.totalHall
                ? '<div class="ffshop-collection-stat"><b>' + cs.allCards.length + '</b> of <b>' + cs.totalHall + '</b> Heroes of the Hall collected</div>'
                : '') +
            '</div>'
          : '') +
      '</div>' +
      '<div class="hoh-filters ffshop-collbar">' +
        '<div class="hoh-filter-row">' +
          '<div class="hoh-filter-combo">' +
            '<div class="hoh-combo-seg hoh-combo-search">' +
              '<span class="hoh-search-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg></span>' +
              '<input type="search" id="ffcollSearch" class="hoh-search" placeholder="Search by name, race, class..." aria-label="Search your cards">' +
            '</div>' +
            '<div class="hoh-combo-divider"></div>' +
            '<div class="hoh-combo-seg hoh-combo-select"><select id="ffcollRace" class="hoh-select" aria-label="Filter by race"><option value="">Races</option></select></div>' +
            '<div class="hoh-combo-divider"></div>' +
            '<div class="hoh-combo-seg hoh-combo-select"><select id="ffcollClass" class="hoh-select" aria-label="Filter by class"><option value="">Classes</option></select></div>' +
          '</div>' +
          ((roll||dust) ? '<div class="hoh-filter-right"><div class="ffshop-coll-actions">' + roll + dust + '</div></div>' : '') +
        '</div>' +
        '<div class="hoh-count-row">' +
          '<span class="hoh-count" id="ffcollCount">' + n + (n === 1 ? ' card' : ' cards') + '</span>' +
          '<select id="ffcollSort" class="hoh-sort" aria-label="Sort your cards">' + sortOpts + '</select>' +
        '</div>' +
      '</div>' +
      '<div class="ffshop-grid" id="ffshop-grid-all"' + (view === 'card' ? ' hidden style="display:none"' : '') + '>' +
        (n ? cs.allCards.map(tileHtml).join('') : emptyHtml) + '</div>' +
      '<div class="ffshop-collsec-none" id="ffcollNone" hidden>No cards match that. <button type="button" class="ffshop-collsec-clear" id="ffcollClear">Clear filters</button></div>' +
      '<div class="ffshop-selectbar" id="ffshopSelectBar" hidden role="region" aria-label="Selected cards">' +
        '<div class="ffshop-selectbar-copy"><b id="ffshopSelectCount">0 selected</b><span>Tap cards to add or remove them.</span></div>' +
        '<div class="ffshop-selectbar-actions">' +
          '<button type="button" class="ffshop-select-cancel" id="ffshopSelectCancel">Cancel</button>' +
          '<button type="button" class="ffshop-select-review" id="ffshopSelectReview" disabled>Review selected</button>' +
        '</div>' +
      '</div>' +
    '</div>';
  }
  function collectionInnerHtml(cs, forSatchel){
    var emptyHtml = forSatchel
      ? '<div class="ffshop-empty ffshop-empty-cta">' +
          '<div class="ffshop-empty-line">No cards yet. Open a pack to start your collection.</div>' +
          '<button type="button" class="ffshop-open-pack-cta" id="ffshop-open-pack-cta">Open a Pack</button>' +
        '</div>'
      : '<div class="ffshop-empty">None yet. FramedFantasy portraits and your own heroes land here as cards you keep.</div>';
    /* R335: THE VIEW TOGGLE.
       Grid view answers "what do I have?" — it is a dense inventory, and it
       is the right default. Card view answers "what did I get?" — one real
       trading card at a time, the same face the inspector shows, at the size
       the art was made for. Both are legitimate ways to look at a collection
       and neither replaces the other, so this is a toggle rather than a
       redesign. The choice persists, because a player who prefers cards
       prefers cards every visit.
       It sits ABOVE the "N of 95 collected" line: the toggle governs
       everything below it, so it reads top-down as control then content. */
    var view = collView();
    /* R380: no view toggle in Profile or in the builder. The mount IS the view. */
    var _noTog = (window.__hohCollMode === 'profile' || window.__hohCollMode === 'builder');
    var toggle = _noTog ? '' :
      '<div class="ffshop-viewtog" role="tablist" aria-label="Collection view">' +
        /* v20 (Jan): the builder leads — parties are why the collection exists */
        '<button type="button" class="ffshop-viewtog-btn' + (view === 'party' ? ' on' : '') + '" data-view="party" role="tab" aria-selected="' + (view === 'party') + '">' +
          '<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><circle cx="8" cy="8" r="3.4"/><circle cx="16.5" cy="9.5" r="2.6" opacity=".6"/><path d="M2.5 20c.6-3.6 2.9-5.6 5.5-5.6s4.9 2 5.5 5.6z"/><path d="M13.6 19.6c.5-2.6 1.7-4.1 2.9-4.1 1.6 0 3.1 1.5 3.6 4.1z" opacity=".6"/></svg>' +
          'Party builder</button>' +
        '<button type="button" class="ffshop-viewtog-btn' + (view === 'grid' ? ' on' : '') + '" data-view="grid" role="tab" aria-selected="' + (view === 'grid') + '">' +
          '<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><rect x="3" y="3" width="7.5" height="7.5" rx="1.5"/><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5"/><rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5"/><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5"/></svg>' +
          'Grid view</button>' +
        '<button type="button" class="ffshop-viewtog-btn' + (view === 'card' ? ' on' : '') + '" data-view="card" role="tab" aria-selected="' + (view === 'card') + '">' +
          '<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><rect x="8" y="2.5" width="8" height="19" rx="2"/><rect x="2.5" y="5.5" width="4" height="13" rx="1.5" opacity=".55"/><rect x="17.5" y="5.5" width="4" height="13" rx="1.5" opacity=".55"/></svg>' +
          'Card view</button>' +
      '</div>';
    /* R437 (Jan) · ONE SECTION, NOT FOUR STRANGERS
       "right now its like a line of text, then a green box, then 88/104
       collected more text, then dust extras as a separate button ... the
       88/104 and the dust extras and the search bar and the grid should be a
       section itself."
       They were four sibling blocks with nothing tying them together, and the
       search input was not even part of this markup — it was injected into the
       DOM after paint, so it had no place in the layout and got wiped on every
       repaint. Profile now emits ONE bordered section: the tally as its
       header, the Hall's own filter bar under it, a count row, then the grid.
       Everything else (satchel, party builder) keeps the old flat controls,
       because those mounts are inside their own chrome already. */
    var _isProfile = (window.__hohCollMode === 'profile');
    if(_isProfile) return toggle + collProfileHtml(cs, emptyHtml, view);
    return toggle +
      '<div class="ffshop-coll-controls">' +
        (cs.totalFF?'<span class="ffshop-collection-stat"><b>'+cs.ffCards.length+'</b> of <b>'+cs.totalFF+'</b> FramedFantasy portraits collected</span>':'') +
        '<span class="ffshop-coll-btns">' +
          /* R260: Dust extras — cash every spare copy (each hero keeps one) for
             hearts, with the running total shown right on the button. */
          /* R400 (Jan): dusting is shop business — the battle builder shows
             nothing but party building on the way into a fight. */
          (cs.dustCopies>0 && window.__hohCollMode!=='builder'?'<button type="button" class="ffshop-dust-btn" id="ffshop-dust-btn" title="Dust every spare copy for hearts">'+
            '<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><path d="M12 3l1.5 4.2L18 8.7l-4.5 1.5L12 14.5l-1.5-4.3L6 8.7l4.5-1.5z" fill="currentColor"/><circle cx="18.4" cy="16.6" r="1.3" fill="currentColor"/><circle cx="6.2" cy="16.4" r="1" fill="currentColor"/></svg>'+
            'Dust extras <small>~'+cs.dustHearts+'&#9829;</small></button>':'') +
        '</span>' +
      '</div>' +
      /* the grid stays exactly as it was; the rail is a sibling that only one
         of the two ever occupies, so neither view can disturb the other */
      /* R339c: hidden AND display, for the same reason applyCollView sets both.
         Without the inline style the grid would paint for one frame in card
         view before the reconcile hid it. */
      '<div class="ffshop-grid" id="ffshop-grid-all"' + (view === 'card' ? ' hidden style="display:none"' : '') + '>' +
        (cs.allCards.length?cs.allCards.map(tileHtml).join(''):emptyHtml) + '</div>' +
      '<div class="ffshop-rail-wrap" id="ffshop-rail-wrap"' + (view === 'card' ? '' : ' hidden style="display:none"') + '>' +
        '<div class="ffshop-deck">' +
          '<button type="button" class="ffshop-deck-arrow ffshop-deck-arrow--prev" data-deck="prev" aria-label="Previous card">&lsaquo;</button>' +
          /* the stage keeps the id the rest of the file already looks for */
          '<div class="ffshop-deck-stage" id="ffshop-rail"></div>' +
          '<button type="button" class="ffshop-deck-arrow ffshop-deck-arrow--next" data-deck="next" aria-label="Next card">&rsaquo;</button>' +
        '</div>' +
        /* R340: the name is ON the card, in the typeface it was designed in.
           Repeating it underneath in a different font said the same thing
           twice and pushed the button further from the card it acts on. */
        (cs.allCards.length
          ? '<div class="ffshop-deck-foot">' +
              '<button type="button" class="ffshop-rail-open" data-act="inspect">Open card tools</button>' +
              '<div class="ffshop-rail-hint">swipe or use the arrows &middot; tap the front card to turn it over</div>' +
            '</div>'
          : '') +
      '</div>';
  }
  /* R335: which view the player last chose. localStorage rather than memory
     because refreshCollectionEverywhere() repaints both mounts from scratch
     after a dust or a pack open, and a memory-only flag would silently snap
     them back to grid mid-session. */
  var COLL_VIEW_KEY = 'ffshop-coll-view-v1';
  /* v20 (Jan): three views now — PARTY BUILDER is the default. */
  /* R380 (2026-08-06): the collection now mounts in exactly one of two
     places, and the place decides the view. In the Profile tab it is a plain
     grid, full stop. In the Battlegrounds party builder it is the builder.
     The old three way toggle is gone with the Shop collection. __hohCollMode
     is set by whichever renderer owns the current mount. */
  window.__hohCollMode = window.__hohCollMode || '';
  function collView(){
    if(window.__hohCollMode === 'profile') return 'grid';
    if(window.__hohCollMode === 'builder') return 'party';
    try {
      var v = localStorage.getItem(COLL_VIEW_KEY);
      return (v === 'card' || v === 'grid' || v === 'party') ? v : 'party';
    } catch(_){ return 'party'; }
  }
  /* v33 (Jan): the Battlegrounds "Build another party" button needs a door into
     this tab and there was no global to call, so it fell through to a toast on
     every tap. setCollView already does exactly the right thing; it just was
     not reachable from outside this file. Exposed by name rather than
     re-implementing the tab switch anywhere else. */
  window.hohShopSetCollView = function(v){
    /* R380: 'party' now means THE BUILDER, and the builder lives inside the
       Battlegrounds flow on the Events tab. Old callers (the battle's Build
       another party button, the Company picker) land in the right place
       without knowing the map changed. */
    if(v === 'party'){
      if(typeof window.hohShopOpenBuilder === 'function'){ window.hohShopOpenBuilder(); return; }
    }
    try { setCollView(v); } catch(_) {}
    try {
      var host = document.querySelector('#ffshop-grid-all');
      if (host) { var pane = host.closest('.ffshop-coll') || host.parentNode;
                  if (pane && pane.scrollIntoView) pane.scrollIntoView({ behavior:'smooth', block:'start' }); }
    } catch(_) {}
  };
  /* ============================================================
     R380: THE PARTY BUILDER SCREEN (Battlegrounds only)
     The grid of playable cards with the seat tray above it. It renders
     into the Events tab's battle stage (#hoh-yh-bg-stage). The tray
     starts collapsed; the first seated hero opens it, so the player
     SEES the seat fill. Back returns to Explore and the door.
     ============================================================ */
  window.hohShopOpenBuilder = function(){
    /* R381b (Jan): pressing Enter must NOT re-render the Events tab. The old
       order called switchView('events') unconditionally, which rebuilt the
       whole tab (parked and re-moved Explore, repainted everything) — that
       rebuild was the "reset and scroll up" feel. Now: only render Events if
       its stage is missing (coming from another tab); when it already exists
       the scene simply steps aside and the builder paints in place. */
    var mount = document.getElementById('hoh-yh-bg-stage');
    if(!mount){
      try { if(typeof window.hohYourHallSwitchView === 'function') window.hohYourHallSwitchView('events'); } catch(_){}
      mount = document.getElementById('hoh-yh-bg-stage');
    }
    if(!mount && typeof window.hohOpenYourHall === 'function'){
      try {
        document.body.classList.add('hoh-page-mode-yours');
        window.hohOpenYourHall();
        if(typeof window.hohYourHallSwitchView === 'function') window.hohYourHallSwitchView('events');
        mount = document.getElementById('hoh-yh-bg-stage');
      } catch(_){}
    }
    if(!mount) return false;
    window.__hohCollMode = 'builder';
    document.body.classList.add('hoh-bg-building');
    /* R381: the Battlegrounds sub tab holds the builder; the scene card
       steps aside while the builder owns the pane. */
    try { if(typeof window.hohYourHallSetEventsTab === 'function') window.hohYourHallSetEventsTab('bg'); } catch(_){}
    try { if(typeof window.hohPaintBgDoor === 'function') window.hohPaintBgDoor(); } catch(_){}
    var getCs = function(){ return collectionSource(); };
    function paintBuilder(){
      if(!document.body.contains(mount)){ return; }
      var cs = getCs();
      /* R381 (Jan): a real header. Themed ghost back (the old button leaked
         the browser default look), centered title, ticket count on the right. */
      var _tkHeld = 0;
      try { _tkHeld = (typeof window.hohGetBgTickets === 'function') ? (window.hohGetBgTickets() | 0) : 0; } catch(_){}
      mount.innerHTML =
        '<div class="ffbg-builder">' +
          '<div class="ffbg-builder-top">' +
            '<button type="button" class="ffbg-back" id="ffbgBuilderBack"><span aria-hidden="true">&larr;</span>Back</button>' +
            '<div class="ffbg-builder-title">Pick your party</div>' +
            '<div class="ffbg-builder-tickets">' + _tkHeld + (_tkHeld === 1 ? ' entry' : ' entries') + '</div>' +
          '</div>' +
          collectionInnerHtml(cs, false) +
        '</div>';
      try { wireCollection(mount, getCs); } catch(_){}
      try { applyCollView(mount, getCs); } catch(_){}
      /* R388: STALE SEATS PRUNE. A card seated in an earlier session can be
         spent or gone by now (Chad, seated then bounced at launch — Jan's
         screenshot). Drop tray keys that no longer map to a playable card
         BEFORE the tray paints, so seats can never contradict the grid. */
      try {
        if(window.__ffPB && window.__ffPB.length){
          var _playableIds = {};
          cs.allCards.forEach(function(pc){
            if(!pc.notCollected && !pc.spentCard) _playableIds[String(pc.character_id)] = 1;
          });
          var _kept = window.__ffPB.filter(function(k){
            return _playableIds[String(k).replace(/^(ff|own):/, '')] === 1;
          });
          if(_kept.length !== window.__ffPB.length){
            window.__ffPB = _kept;
            toast('A seated card is no longer playable, so its seat was cleared.');
          }
        }
      } catch(_){}
      /* R380 AUDIT FIX: no playable cards = say so, and hand them the way
         forward instead of an empty grid under a party tray.
         R388: PLAYABLE is the test now — spent cards still render greyed
         below, so when unplayable tiles exist the CTA sits ABOVE the grid
         instead of replacing it. */
      try {
        var _playableN = 0;
        cs.allCards.forEach(function(pc){ if(!pc.notCollected && !pc.spentCard) _playableN++; });
        if(!_playableN){
          var eg = mount.querySelector('#ffshop-grid-all');
          var _ctaHtml =
            '<div class="ffshop-empty ffshop-empty-cta">' +
              '<div class="ffshop-empty-head">No cards can fight yet</div>' +
              /* R422 (Jan): the rule is one line now — collect it, field it. */
              '<div class="ffshop-empty-line">Every card you collect can fight in the Battlegrounds. Open a pack and every card in it is yours to play.</div>' +
              '<div class="ffshop-empty-btns">' +
                '<button type="button" class="ffshop-open-pack-cta" id="ffbgEmptyShop">Open a pack</button>' +
                '<button type="button" class="ffshop-loaner-cta" id="ffbgLoaner">Borrow a starter party</button>' +
              '</div>' +
              '<div class="ffshop-empty-sub">Borrowed heroes fight at level 1. Winning is hard without your own.</div>' +
            '</div>';
          if(eg && cs.allCards.length){
            var _ctaWrap = document.createElement('div');
            _ctaWrap.innerHTML = _ctaHtml;
            eg.parentNode.insertBefore(_ctaWrap.firstChild, eg);
          } else if(eg) eg.innerHTML = _ctaHtml;
          var eb = mount.querySelector('#ffbgEmptyShop');
          if(eb) eb.addEventListener('click', function(){
            window.hohShopCloseBuilder();
            try { if(typeof window.hohYourHallSwitchView === 'function') window.hohYourHallSwitchView('shop'); } catch(_){}
          });
          var lb = mount.querySelector('#ffbgLoaner');
          if(lb) lb.addEventListener('click', function(){
            var wiz = loanerWizardId();
            if(!wiz){ toast('The hall has no one to lend right now. Open a pack instead.'); return; }
            try { localStorage.setItem('hoh-party-launch', JSON.stringify({ keys: ['hall:' + wiz], name: 'Borrowed Party', loaner: 1 })); } catch(_){}
            if(typeof window.hohEnterBattlegrounds === 'function') window.hohEnterBattlegrounds();
          });
        }
      } catch(_){}
      var pane = mount.querySelector('#ffshopPartyPane');
      if(pane){
        /* collapsed until something sits down, unless a party is mid build */
        if(!(window.__ffPB && window.__ffPB.length)) pane.classList.add('ffpb-collapsed');
        if(!pane.__collapseWired){
          pane.__collapseWired = 1;
          pane.addEventListener('click', function(e){
            if(pane.classList.contains('ffpb-collapsed')){ pane.classList.remove('ffpb-collapsed'); }
          });
        }
      }
      var back = mount.querySelector('#ffbgBuilderBack');
      if(back) back.addEventListener('click', window.hohShopCloseBuilder);
    }
    window.__hohBuilderRepaint = paintBuilder;
    /* R381b: glide DOWN to the builder after it actually paints, so the
       page moves toward the thing that appeared, never away from it. */
    window.hohShopEnsureState().then(function(){
      paintBuilder();
      try { mount.scrollIntoView({ behavior:'smooth', block:'start' }); } catch(_){}
    });
    return true;
  };
  window.hohShopCloseBuilder = function(){
    window.__hohCollMode = '';
    window.__hohBuilderRepaint = null;
    document.body.classList.remove('hoh-bg-building');
    var mount = document.getElementById('hoh-yh-bg-stage');
    if(mount) mount.innerHTML = '';
    /* R381: closing the builder lands back on the Battlegrounds sub tab, not
       Explore — the player was mid Battlegrounds. R381b: and with no full tab
       rebuild — if the panes are mounted the scene just stands back up in
       place; the full render only runs when Events isn't built at all. */
    window.__hohEvTab = 'bg';
    if(document.getElementById('hoh-yh-evpane-bg')){
      try { if(typeof window.hohYourHallSetEventsTab === 'function') window.hohYourHallSetEventsTab('bg'); } catch(_){}
      try { if(typeof window.hohPaintBgDoor === 'function') window.hohPaintBgDoor(); } catch(_){}
    } else {
      try { if(typeof window.hohYourHallSwitchView === 'function') window.hohYourHallSwitchView('events'); } catch(_){}
    }
  };
  function setCollView(v){
    try { localStorage.setItem(COLL_VIEW_KEY, (v === 'card' || v === 'party') ? v : 'grid'); } catch(_){}
  }
  /* v20b (Jan): the build in progress — hero KEYS in seat order, max five,
     plus the party's NAME, typed right here and carried into battle. */
  window.__ffPB = window.__ffPB || [];
  window.__ffPBName = window.__ffPBName || '';
  function pbPaint(host){
    var pane = host.querySelector('#ffshopPartyPane'); if(!pane) return;
    var cs = null; try{ cs = (typeof window.hohShopOwnedCards==='function') ? window.hohShopOwnedCards() : []; }catch(_){ cs = []; }
    function escA(t){ return String(t==null?'':t).replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;'); }
    function cardOf(key){
      var id = String(key).replace(/^(ff|own):/,'');
      return (cs||[]).filter(function(c){ return String(c.id||c.card_id||c.character_id)===id; })[0];
    }
    /* THE SEATS — real portrait tiles, not thumbnails. Tap a seated hero to
       unseat them (the corner ✕ says so without an OS tooltip). */
    var seats = '';
    var RCn = ((window.HOHContractContent||{}).RARITY_COLORS)||[];
    for(var i=0;i<5;i++){
      var k = window.__ffPB[i];
      var c = k ? cardOf(k) : null;
      if(c){
        /* v20b (Jan): SQUARE seats wearing their RARITY — the frame is the
           gem colour, same language as the grid. Order badge = turn order. */
        var _t = window.HOHContract ? HOHContract.tierOf(c.hearts||0) : 0;
        seats += '<span class="ffpb-seat" data-seat="'+i+'" role="button" aria-label="Drag to reorder, tap to unseat '+escA(c.name||'hero')+'" style="--rar:'+(RCn[_t]||'#c9a84c')+'">'+
            '<span class="ffpb-ord" aria-hidden="true">'+(i+1)+'</span>'+
            '<img src="'+escA(c.image_thumb_url||c.image_url||c.img||'')+'" alt="" draggable="false">'+
            '<b>'+escA(String(c.name||'?').split(' ')[0])+'</b><i class="ffpb-x" aria-hidden="true">✕</i></span>';
      } else {
        /* v33 (Jan): THE FIRST SEAT IS NOT OPTIONAL. Every empty seat read
           "by fate", including seat one, so the screen told a new player the
           Hall would fill all five and Battle stayed disabled with nothing on
           screen explaining why. Seat one asks for a hero; two through five
           are the ones fate actually fills. */
        /* v34 (Jan), two things wrong with this seat.
           IT WAS DEAD. It is drawn as a call to action, in gold, against four
           grey ones, and tapping it did nothing at all. The cards it is asking
           for sit below the fold on a phone and below the saved-party shelf on
           a desktop, so the one instruction on screen pointed at something the
           player could not see. It scrolls to them now and marks them.
           IT WAS NOT CENTRED. It carried `need` but not `fate`, and every box
           rule for an empty seat — the square, box-sizing, justify-content —
           lives on `.fate`. So this seat had no height of its own, stretched to
           the row, and sat its plus and its label against the top edge while
           the four beside it were centred. Carrying both classes fixes the box
           and keeps it out of the drag wiring, which bails on `.fate`. */
        seats += (i === 0)
          ? '<span class="ffpb-seat fate need" role="button" tabindex="0" data-pbfind ' +
              'aria-label="Pick a hero. Jumps to your cards below."><i aria-hidden="true">+</i><b>pick a hero</b></span>'
          : '<span class="ffpb-seat fate"><i aria-hidden="true">+</i><b>by fate</b></span>';
      }
    }
    var parties = (window.HOHContractData && HOHContractData.partiesGet) ? HOHContractData.partiesGet() : [];
    var liveCount = parties.filter(function(p){ return p && p.keys && p.keys.length; }).length;
    var full = liveCount >= 4;
    /* THE SHELF — the four saved parties live here, each under its name. */
    var shelf = '';
    parties.forEach(function(p, si){
      if(!p || !p.keys || !p.keys.length) return;
      var thumbs = p.keys.slice(0,5).map(function(k){
        var c = cardOf(k);
        return c ? '<img src="'+escA(c.image_thumb_url||c.image_url||c.img||'')+'" alt="">' : '';
      }).join('');
      shelf += '<div class="ffpb-party"><span class="ffpb-party-thumbs">'+thumbs+'</span>'+
        '<b class="ffpb-party-name">'+escA(p.name||('Party '+(si+1)))+'</b>'+
        '<span class="ffpb-party-acts">'+
          '<button type="button" class="ffpb-btn go sm" data-pspfight="'+si+'">⚔ Select team</button>'+
          '<button type="button" class="ffpb-btn sm" data-pspload="'+si+'">Edit</button>'+
          '<button type="button" class="ffpb-btn sm" data-pspdel="'+si+'" aria-label="Delete party">✕</button>'+
        '</span></div>';
    });
    pane.innerHTML =
      '<div class="ffpb-head">Your party · empty seats are filled by fate</div>'+
      '<div class="ffpb-seats">'+seats+'</div>'+
      '<div class="ffpb-actrow">'+
        '<input type="text" class="ffpb-name" id="ffpbName" maxlength="24" placeholder="Name this party…" value="'+escA(window.__ffPBName)+'" aria-label="Party name">'+
        '<button type="button" class="ffpb-btn go" data-pbfight>⚔ Battle with this party</button>'+
        '<button type="button" class="ffpb-btn" data-pbsave'+(full?' disabled':'')+'>'+(full?'All four slots full':'Save party')+'</button>'+
        '<button type="button" class="ffpb-btn" data-pbclear>Clear</button>'+
      '</div>'+
      (shelf ? '<div class="ffpb-shelf-head">Your saved parties</div><div class="ffpb-shelf">'+shelf+'</div>' : '')+
      (function(){
        /* v20c: the bonds the seated five ALREADY form — the synergy compass */
        var live = window.__ffPB.map(cardOf).filter(Boolean).map(function(c){
          return { cls: c.character_class || c.cls || '', race: c.race || '', hearts: c.hearts || 0, lvl: 1 };
        });
        var chips = [];
        try { if(live.length && window.HOHContract) chips = (HOHContract.synergy(live).chips || []); } catch(_){ }
        return chips.length
          ? '<div class="ffpb-bonds">'+chips.map(function(cn){ return '<span class="ffpb-bond">'+escA(cn)+'</span>'; }).join('')+'</div>'
          : (live.length >= 2 ? '<div class="ffpb-bonds"><span class="ffpb-bond dim">no bonds yet — match classes, callings or races</span></div>' : '');
      })()+
      (function(){
        /* v21 (Jan): the hall's own bar — search plus Races and Classes
           dropdowns in one bordered rail, not pills. Options are drawn
           from the collection itself. */
        /* R473, FOUND IN REVIEW: THE OTHER CLASS DROPDOWN. R472 taught
           collMetaFor a fixed vocabulary and left this one splitting the free
           text field on its first space, so the party builder's Classes menu
           still offered "Moon" for "Moon Druid, Wild Shifting, Often as a
           Raccoon". Two class filters in the same takeover, one fixed. Both
           now go through stdClass. */
        /* ============ R484 · THE RACE LIST IS THE STANDARD ONE ============
           Jan: "just have the races be the standard races, dont show every
           single race submitted its way too long right now."

           This built its menu from whatever players had typed into the race
           field, first word only, so the list opened with A, Aarakocra,
           Aasimar, Alien/Elf, Astral, Bee, Black, Blood, Celestari, Changeling,
           Cursed, Death, Doll, Dusk, Ex-Human and kept going. Unusable as a
           filter and, because a select sizes itself to its widest option, it
           was also what pushed the Classes control onto a second row.

           THE HALL ALREADY SOLVED THIS. hoh-data.js publishes RACES, the
           twenty standard ones, and hoh-section-core.js has filtered on them
           plus an "Other" bucket since R319. This is that same rule, applied
           to the one filter that never got it. Only races actually present in
           the collection are offered, so the menu never lists a race the
           player cannot pick, and everything else falls into Other rather than
           becoming its own line.

           NO SECOND COPY OF THE LIST. If RACES has not loaded, the old
           behaviour stands rather than a stale duplicate drifting out of step
           with the Hall's. */
        var races={}, classes={}, other=false;
        var STD = (window.RACES && window.RACES.length) ? window.RACES : null;
        var stdSet = {}; if (STD) STD.forEach(function(r){ stdSet[r]=1; });
        (cs||[]).forEach(function(c){
          var r=String(c.race||'').split(' ')[0];
          if(r){ if(!STD || stdSet[r]) races[r]=1; else other=true; }
          var k=stdClass(c.character_class||c.cls||''); if(k) classes[k]=1;
        });
        function opts(map, sel, tail){
          var out = Object.keys(map).sort().map(function(v){
            return '<option value="'+escA(v)+'"'+(sel===v?' selected':'')+'>'+escA(v)+'</option>';
          }).join('');
          if(tail) out += '<option value="Other"'+(sel==='Other'?' selected':'')+'>Other</option>';
          return out;
        }
        return '<div class="ffpb-bar">'+
          '<span class="ffpb-bar-ico" aria-hidden="true"><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg></span>'+
          /* v29 (Jan): THE SAME BAR AS THE HALL. The Hall's bar carries a
             search, a sort and two filters and this one carried a search and
             two filters, so the two read as different tools for the same job.
             The sort is added here to match. The Hall's fourth control is
             "My submitted only", which has nothing to filter in a builder
             that already shows only cards you own, so its seat goes to the
             sort — which is the control a player actually wants when they are
             picking five out of a hundred. */
          '<input type="text" id="ffpbSearch" placeholder="Search by name, race, class, story..." value="'+escA(window.__ffPBQ||'')+'" aria-label="Search cards">'+
          '<select id="ffpbSort" class="ffpb-sel" aria-label="Sort cards">'+
            /* R484 (Jan): "you are supposed to be able to favorite/sort by
               ability rarity". The ability a card rolled is the thing a player
               is actually shopping for once they have a collection, and it was
               sortable in the card picker and nowhere a builder can reach. */
            ['hearts|Most hearts','ability|Best ability','level|Highest level','new|Newest added','name|A to Z'].map(function(o){
              var kv=o.split('|');
              return '<option value="'+kv[0]+'"'+((window.__ffPBSort||'hearts')===kv[0]?' selected':'')+'>'+kv[1]+'</option>';
            }).join('')+
          '</select>'+
          '<select id="ffpbRace" class="ffpb-sel" aria-label="Filter by race"><option value="">Races</option>'+opts(races, window.__ffPBRace, other)+'</select>'+
          '<select id="ffpbClass" class="ffpb-sel" aria-label="Filter by class"><option value="">Classes</option>'+opts(classes, window.__ffPBClass)+'</select>'+
          /* R484: favourites is a one tap state, not a two option menu, so it
             is a chip rather than a fifth select. It also keeps the row from
             growing a control that would put Classes back on its own line. */
          '<button type="button" id="ffpbFav" class="ffpb-fav'+(window.__ffPBFav?' on':'')+'"'+
            ' aria-pressed="'+(window.__ffPBFav?'true':'false')+'" title="Show only your favourites">'+
            '<span class="ffpb-fav-star" aria-hidden="true">&#9733;</span>Favourites</button>'+
        '</div>';
      })()+
      '<div class="ffpb-sub">Tap a seated hero to unseat them · drag seats to set the turn order.</div>';
    /* wiring */
    var nameEl = pane.querySelector('#ffpbName');
    if(nameEl) nameEl.oninput = function(){ window.__ffPBName = nameEl.value; };
    /* v20b: ONE pointer wire for the seats — a still tap UNSEATS, a 7px
       drag REORDERS (the same grammar as the picker tray). Pointer events
       cover mouse and touch alike; seats are touch-action:pan-y so the
       page still scrolls vertically on phones. */
    /* v34: the empty first seat is a door to the cards. Scroll the collection
       bar into view, then pulse it once so the eye lands on the search and the
       first row of cards rather than wherever the scroll happened to stop. */
    (function wirePickHero(){
      var row = pane.querySelector('.ffpb-seats'); if(!row) return;
      function jump(){
        var target = host.querySelector('#ffshopPartyBar') || host.querySelector('#ffshop-grid-all');
        if(!target){
          /* the collection block has not painted yet. Say so rather than doing
             nothing, which is the failure this seat already had. */
          var s0 = pane.querySelector('.ffpb-seat.need b');
          if(s0){ var old = s0.textContent; s0.textContent = 'cards loading…';
                  setTimeout(function(){ try{ s0.textContent = old; }catch(_){ } }, 1400); }
          return;
        }
        var reduce = false;
        try{ reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; }catch(_){ }
        try{ target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }); }
        catch(_){ try{ target.scrollIntoView(); }catch(__){ } }
        target.classList.remove('ffpb-callout');
        void target.offsetWidth;                 /* restart the animation */
        target.classList.add('ffpb-callout');
        setTimeout(function(){ try{ target.classList.remove('ffpb-callout'); }catch(_){ } }, 2000);
        /* focus the search so a keyboard player lands inside the thing they
           were sent to, without stealing the viewport back on a phone */
        try{
          var q0 = host.querySelector('#ffpbSearch');
          if(q0 && !reduce) setTimeout(function(){ try{ q0.focus({ preventScroll: true }); }catch(_){ } }, 620);
        }catch(_){ }
      }
      row.addEventListener('click', function(e){
        if(e.target.closest('[data-pbfind]')) jump();
      });
      row.addEventListener('keydown', function(e){
        if(e.key!=='Enter' && e.key!==' ' && e.key!=='Spacebar') return;
        if(!e.target.closest('[data-pbfind]')) return;
        e.preventDefault(); jump();
      });
    })();
    (function wireSeats(){
      var row = pane.querySelector('.ffpb-seats'); if(!row) return;
      var act=null, sx=0, sy=0, dragging=false;
      row.addEventListener('pointerdown', function(e){
        var t = e.target.closest('.ffpb-seat'); if(!t || t.classList.contains('fate')) return;
        act = t; sx = e.clientX; sy = e.clientY; dragging = false;
        try{ row.setPointerCapture(e.pointerId); }catch(_){ }
      });
      row.addEventListener('pointermove', function(e){
        if(!act) return;
        if(!dragging){
          if(Math.abs(e.clientX-sx) < 7 && Math.abs(e.clientY-sy) < 7) return;
          dragging = true; act.classList.add('drag'); row.classList.add('dragging');
          /* v22 (Jan): the drag was highlighting random page text on
             desktop — kill selection globally for the drag's lifetime */
          document.body.classList.add('ffpb-dragging');
          try{ window.getSelection().removeAllRanges(); }catch(_){ }
        }
        if(dragging) e.preventDefault();
        /* PERF-12 (2026-08-06): elementFromPoint already forces a layout
           flush per move; the class sweep across every seat on every move
           doubled the style work. Classes now change only when the hovered
           seat actually changes. */
        var el = document.elementFromPoint(e.clientX, e.clientY);
        var tgt = el && el.closest ? el.closest('.ffpb-seat') : null;
        var want = (tgt && tgt !== act && !tgt.classList.contains('fate')) ? tgt : null;
        var cur = row.querySelector('.ffpb-seat.over');
        if(cur !== want){
          if(cur) cur.classList.remove('over');
          if(want) want.classList.add('over');
        }
      });
      row.addEventListener('pointerup', function(e){
        if(!act) return;
        var was = act; act = null;
        row.classList.remove('dragging'); was.classList.remove('drag');
        document.body.classList.remove('ffpb-dragging');
        var from = +was.getAttribute('data-seat');
        if(dragging){
          var el = document.elementFromPoint(e.clientX, e.clientY);
          var tgt = el && el.closest ? el.closest('.ffpb-seat') : null;
          row.querySelectorAll('.ffpb-seat').forEach(function(s2){ s2.classList.remove('over'); });
          if(tgt && !tgt.classList.contains('fate') && tgt !== was){
            var to = +tgt.getAttribute('data-seat');
            var mv = window.__ffPB.splice(from,1)[0];
            window.__ffPB.splice(to,0,mv);
            pbPaint(host);
          }
          return;
        }
        window.__ffPB.splice(from,1);   /* the still tap: unseat */
        pbPaint(host);
      });
      row.addEventListener('pointercancel', function(){
        if(act){ act.classList.remove('drag'); act=null; }
        row.classList.remove('dragging');
        document.body.classList.remove('ffpb-dragging');
        row.querySelectorAll('.ffpb-seat').forEach(function(s2){ s2.classList.remove('over'); });
      });
    })();
    var fb = pane.querySelector('[data-pbfight]');
    if(fb) fb.onclick = function(){
      try{ localStorage.setItem('hoh-party-launch', JSON.stringify({ keys: window.__ffPB.slice(0,5), name: (window.__ffPBName||'').trim() || null })); }catch(_){}
      if(typeof window.hohEnterBattlegrounds==='function') window.hohEnterBattlegrounds();
    };
    var sb2 = pane.querySelector('[data-pbsave]');
    if(sb2) sb2.onclick = function(){
      if(!window.__ffPB.length){ sb2.textContent = 'Seat a hero first'; setTimeout(function(){ pbPaint(host); }, 1300); return; }
      var all = (window.HOHContractData && HOHContractData.partiesGet) ? HOHContractData.partiesGet() : [];
      var slot = -1;
      for(var q=0;q<4;q++){ if(!all[q] || !all[q].keys || !all[q].keys.length){ slot = q; break; } }
      if(slot < 0){ pbPaint(host); return; }
      var nm = (window.__ffPBName||'').trim() || ('Party '+(slot+1));
      if(window.HOHContractData && HOHContractData.partySave) HOHContractData.partySave(slot, nm, window.__ffPB.slice(0,5));
      window.__ffPB = []; window.__ffPBName = '';
      pbPaint(host);
    };
    var cb = pane.querySelector('[data-pbclear]');
    if(cb) cb.onclick = function(){ window.__ffPB=[]; window.__ffPBName=''; pbPaint(host); };
    pane.querySelectorAll('[data-pspfight]').forEach(function(b){
      b.onclick = function(){
        var p = HOHContractData.partiesGet()[+b.getAttribute('data-pspfight')]; if(!p) return;
        try{ localStorage.setItem('hoh-party-launch', JSON.stringify({ keys: p.keys.slice(0,5), name: p.name || null })); }catch(_){}
        if(typeof window.hohEnterBattlegrounds==='function') window.hohEnterBattlegrounds();
      };
    });
    pane.querySelectorAll('[data-pspload]').forEach(function(b){
      b.onclick = function(){
        var p = HOHContractData.partiesGet()[+b.getAttribute('data-pspload')]; if(!p) return;
        window.__ffPB = p.keys.slice(0,5); window.__ffPBName = p.name || '';
        pbPaint(host);
      };
    });
    pane.querySelectorAll('[data-pspdel]').forEach(function(b){
      b.onclick = function(){ if(HOHContractData.partyDelete) HOHContractData.partyDelete(+b.getAttribute('data-pspdel')); pbPaint(host); };
    });
    /* v23 (Jan): THE BAR BELONGS TO THE GRID IT FILTERS. It moves out of
       the party box into its own rail directly above the cards, so the
       hierarchy reads: your party | find your heroes | the cards. */
    (function(){
      var bar = pane.querySelector('.ffpb-bar');
      var gridEl = host.querySelector('#ffshop-grid-all');
      if(!bar || !gridEl || !gridEl.parentNode) return;
      var box = host.querySelector('#ffshopPartyBar');
      if(!box){
        box = document.createElement('div');
        box.id = 'ffshopPartyBar'; box.className = 'ffpb-barbox';
        gridEl.parentNode.insertBefore(box, gridEl);
      }
      box.innerHTML = '<div class="ffpb-barbox-t">Find your heroes</div>';   /* v29: kept, the Hall has no eyebrow but this bar sits inside a panel */
      box.appendChild(bar);
      var hint = document.createElement('div');
      hint.className = 'ffpb-sub';
      hint.innerHTML = 'Tap a card to seat it · the eye opens it without seating.';
      box.appendChild(hint);
      box.hidden = false; box.style.display = '';
    })();
    var q = host.querySelector('#ffpbSearch');
    if(q){ q.oninput = function(){ window.__ffPBQ = q.value; pbFilterGrid(host); }; }
    var rs = host.querySelector('#ffpbRace');
    if(rs){ rs.onchange = function(){ window.__ffPBRace = rs.value; pbFilterGrid(host); }; }
    var ks = host.querySelector('#ffpbClass');
    if(ks){ ks.onchange = function(){ window.__ffPBClass = ks.value; pbFilterGrid(host); }; }
    /* v29: the new sort, wired to the sorter the builder already had */
    var ss = host.querySelector('#ffpbSort');
    if(ss){ ss.onchange = function(){ window.__ffPBSort = ss.value; pbSortGrid(host); pbFilterGrid(host); }; }
    /* R484: the favourites chip. Sorting and filtering both re-run, because a
       favourites-only view of an ability sort still has to be sorted. */
    var fv = pane.querySelector('#ffpbFav');
    if(fv){ fv.onclick = function(){
      window.__ffPBFav = !window.__ffPBFav;
      fv.classList.toggle('on', !!window.__ffPBFav);
      fv.setAttribute('aria-pressed', window.__ffPBFav ? 'true' : 'false');
      pbFilterGrid(host);
    }; }
    pbSortGrid(host);
    pbFilterGrid(host);
    injectPartyEyes(host);
  }
  /* v21: hide what the bar rules out; everything returns on leaving the
     builder (applyCollView's non-party branch clears the filter). */
  function pbFilterGrid(host){
    var grid = host.querySelector('#ffshop-grid-all'); if(!grid) return;
    var cs = null; try{ cs = (typeof window.hohShopOwnedCards==='function') ? window.hohShopOwnedCards() : []; }catch(_){ cs=[]; }
    var byId = {}; (cs||[]).forEach(function(c){ byId[String(c.id||c.card_id||c.character_id)] = c; });
    var qq = String(window.__ffPBQ||'').toLowerCase().trim();
    var fr = window.__ffPBRace||'', fk = window.__ffPBClass||'';   /* R473: fk is a stdClass bucket */
    var fav = !!window.__ffPBFav;
    var STD2 = (window.RACES && window.RACES.length) ? window.RACES : null;
    var shown = 0;
    grid.querySelectorAll('.ffshop-tile').forEach(function(t){
      var hid = String(t.getAttribute('data-hero-id'));
      var c = byId[hid] || {};
      var nm = String(c.name||'').toLowerCase(), rc = String(c.race||''), kl = String(c.character_class||c.cls||'');
      var ok = true;
      if(qq && (nm+' '+rc+' '+kl).toLowerCase().indexOf(qq) < 0) ok = false;
      /* R484: Other is every race that is not one of the standard twenty,
         which is the same bucket the Hall's own filter has used since R319. */
      if(fr){
        var r1 = rc.split(' ')[0];
        if(fr === 'Other'){ if(!STD2 || STD2.indexOf(r1) !== -1) ok = false; }
        else if(r1 !== fr) ok = false;
      }
      if(fk && stdClass(kl) !== fk) ok = false;   /* R473: match the bucket the menu offers */
      if(fav && !pbIsFav(hid)) ok = false;
      t.style.display = ok ? '' : 'none';
      if(ok) shown++;
    });
    pbEmptyNote(host, shown, fav);
  }
  /* ============ R484 · FAVOURITES AND THE ABILITY A CARD ROLLED ============
     Both already existed and neither could be reached from the screen a player
     builds a party on. The favourite is the Hall's own star (hohHallIsSaved),
     not a second list, so starring a hero anywhere stars them everywhere. The
     ability rarity is read through HOHContractData.gearFor, the same reader
     the card picker and the Stats panel use, so three screens can never
     disagree about what a card rolled.

     A card key is a PREFIX plus the hero id ('ff:', 'own:' or 'hall:'), and a
     hero can legitimately be held under more than one, so the best of them is
     the honest answer to "what did this card roll". */
  function pbIsFav(heroId){
    try { return (typeof window.hohHallIsSaved === 'function') && !!window.hohHallIsSaved(heroId); }
    catch(_){ return false; }
  }
  function pbAbilR(heroId){
    try {
      var D0 = window.HOHContractData; if(!D0 || !D0.gearFor) return -1;
      var best = -1, pre = ['ff:', 'own:', 'hall:'];
      for(var i=0;i<pre.length;i++){
        var g = D0.gearFor(pre[i] + heroId) || {};
        best = Math.max(best, (g.active && g.active.r)|0, (g.passive && g.passive.r)|0);
      }
      return best;
    } catch(_){ return -1; }
  }
  /* R484: an empty result used to be a silent wall of nothing. The favourites
     filter makes that reachable in one tap on a fresh account, so the state
     that was never built is built now and it says how to leave it. */
  function pbEmptyNote(host, shown, fav){
    try {
      var grid = host.querySelector('#ffshop-grid-all'); if(!grid) return;
      var note = grid.parentNode.querySelector('.ffpb-none');
      if(shown > 0){ if(note) note.remove(); return; }
      if(!note){
        note = document.createElement('div');
        note.className = 'ffpb-none';
        grid.parentNode.insertBefore(note, grid);
      }
      note.textContent = fav
        ? 'No favourites yet. Tap the star on a hero and they show up here.'
        : 'No cards match that. Clear the filters to see everything you own.';
    } catch(_){}
  }
  /* v20b: the EYE on every card in builder mode — inspect without seating.
     Injected idempotently (the grid repaints on collection changes) and
     shown only while the builder view is active. */
  /* v20c (Jan): SORT THE FORGE. Building a party that synergizes means
     grouping what bonds — rarity for power, class for the ensembles and
     the Balanced Line, race for CLAN. The pills re-order the LIVE grid
     tiles in place; leaving builder mode restores the original order
     (each tile remembers its birth index). */
  window.__ffPBSort = window.__ffPBSort || 'rarity';
  function pbSortGrid(host){
    var grid = host.querySelector('#ffshop-grid-all'); if(!grid) return;
    var tiles = [].slice.call(grid.querySelectorAll('.ffshop-tile'));
    if(!tiles.length) return;
    tiles.forEach(function(t,i){ if(t.__ffIdx == null) t.__ffIdx = i; });
    var cs = null; try{ cs = (typeof window.hohShopOwnedCards==='function') ? window.hohShopOwnedCards() : []; }catch(_){ cs=[]; }
    var byId = {}; (cs||[]).forEach(function(c){ byId[String(c.id||c.card_id||c.character_id)] = c; });
    var mode = window.__ffPBSort;
    function keyOf(t){
      var c = byId[String(t.getAttribute('data-hero-id'))] || {};
      var tier = window.HOHContract ? HOHContract.tierOf(c.hearts||0) : 0;
      var nm = String(c.name||'~').toLowerCase();
      if(mode === 'class') return [String(c.character_class||c.cls||'~').toLowerCase(), -tier, nm];
      if(mode === 'race')  return [String(c.race||'~').split(' ')[0].toLowerCase(), -tier, nm];
      if(mode === 'name')  return [nm];
      /* v29: the three keys the new Hall-style sort offers. hearts is the
         default and is what the old fall-through already did by rarity, so a
         player who never touches the control sees exactly what they saw. */
      /* R484: rarest ability first, then hearts, so two cards with the same
         roll still fall in a stable, meaningful order. */
      if(mode === 'ability') return [-pbAbilR(String(t.getAttribute('data-hero-id'))), -(c.hearts|0), nm];
      if(mode === 'hearts') return [-(c.hearts|0), nm];
      if(mode === 'level')  return [-(c.level|c.lvl|0), -(c.hearts|0), nm];
      if(mode === 'new')    return [-(Date.parse(c.created_at||c.added_at||0)||0), nm];
      return [-tier, nm];   /* rarity, rarest first */
    }
    if(mode === 'default'){
      tiles.sort(function(a,b){ return a.__ffIdx - b.__ffIdx; });
    } else {
      tiles.sort(function(a,b){
        var ka=keyOf(a), kb=keyOf(b);
        for(var i=0;i<Math.max(ka.length,kb.length);i++){
          if(ka[i]===kb[i]) continue;
          return ka[i]<kb[i] ? -1 : 1;
        }
        return 0;
      });
    }
    tiles.forEach(function(t){ grid.appendChild(t); });
  }
  function pbRestoreGrid(host){
    var grid = host.querySelector('#ffshop-grid-all'); if(!grid) return;
    var tiles = [].slice.call(grid.querySelectorAll('.ffshop-tile'));
    if(!tiles.length || tiles[0].__ffIdx == null) return;
    tiles.sort(function(a,b){ return a.__ffIdx - b.__ffIdx; });
    tiles.forEach(function(t){ grid.appendChild(t); });
  }
  function injectPartyEyes(host){
    var grid = host.querySelector('#ffshop-grid-all'); if(!grid) return;
    /* v30 (Jan): THE EYE IS GONE FROM THE COLLECTION. On an owned card it sat
       in the same top-right corner as the x2 duplicate badge and the two
       overlapped, and it was redundant there anyway: tapping the card already
       opens it. It stays in PARTY BUILDER mode and only there, because that is
       the one view where a tap means SEAT and inspecting needs its own door.
       Any eye already injected into a non-builder grid is removed, so a view
       switch cannot leave one stranded. */
    var _builder = false;
    try { _builder = (collView() === 'party'); } catch(_) {}
    if(!_builder){
      grid.querySelectorAll('.ffshop-tile-eye').forEach(function(el){ try{ el.remove(); }catch(_){ } });
      return;
    }
    grid.querySelectorAll('.ffshop-tile').forEach(function(t){
      if(t.querySelector('.ffshop-tile-eye')) return;
      t.insertAdjacentHTML('beforeend',
        '<button type="button" class="ffshop-tile-eye" aria-label="Open this card">'+
        '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg></button>');
    });
  }
  /* Wire the collection block within `host`: grid clicks branch (own-non-FF ->
     the upgrade lightbox; everything else -> the FF inspector), and the Dust
     extras button. Idempotent per host via a guard flag on the grid element.
     `getCs` may be a cs object or a function returning a fresh one (the Satchel
     recomputes after a dust so the count + button update). */
  function wireCollection(host, getCs){
    var grid = host.querySelector('#ffshop-grid-all');
    /* BUG-UX1 (2026-08-06): seating a hero from the grid gave ZERO feedback,
       and a full party or an already-seated hero did nothing at all on tap,
       which reads as a frozen page (the seat row can be scrolled off screen
       above the grid). Every outcome now answers where the finger is. The
       full seat-tray redesign is a separate confirmed pass; this is the
       minimum honest feedback. */
    function seatFromTile(tile, key){
      var say = (typeof window.hohShowHeartToast==='function') ? window.hohShowHeartToast : null;
      var seated = window.__ffPB || (window.__ffPB=[]);
      if(seated.indexOf(key)>=0){
        if(say) say('Already seated','They hold a seat in the party row above. Tap them there to unseat them.');
        return;
      }
      if(seated.length>=5){
        if(say) say('The party is full','Five seats. Tap a seated hero in the row above to make room.');
        return;
      }
      seated.push(key); pbPaint(host);
      /* R380: the tray starts collapsed in the builder; the first seat opens
         it so the player watches the seat fill. */
      try { var _pn = host.querySelector('#ffshopPartyPane'); if(_pn) _pn.classList.remove('ffpb-collapsed'); } catch(_){}
      var nmEl = tile.querySelector('.ffshop-tile-name');
      var nm = (nmEl && nmEl.textContent) || '';
      if(say) say('Seated'+(nm?(' · '+nm):''),'Seat '+seated.length+' of 5'+(seated.length<5?' · fate fills the rest':' · your party is full'));
    }
    if(grid && !grid.__ffCollWired){
      grid.__ffCollWired = true;
      grid.addEventListener('click', function(e){
        var t=e.target.closest('.ffshop-tile'); if(!t) return;
        /* R558 manual batch selection owns the grid while active. Opening a
           card here would make selection feel random and can hide the sticky
           review bar below a modal. One tap means exactly one toggle. */
        if(host.querySelector('.ffshop-collsec') && dustSelection().active){
          e.preventDefault(); e.stopPropagation();
          var sid=String(t.getAttribute('data-hero-id')||''), sst=dustSelection();
          if(!sid) return;
          if(sst.ids[sid]) delete sst.ids[sid]; else sst.ids[sid]=1;
          syncDustSelectionUI(host);
          return;
        }
        /* v20b: the corner eye always INSPECTS, even in builder mode */
        if(e.target.closest('.ffshop-tile-eye')){
          var ide=t.getAttribute('data-hero-id');
          /* R380: own heroes open the SAME card viewer as every card. The
             old upgrade lightbox is retired; the card itself tells the single
             use truth. */
          window.hohShopInspect(ide);
          return;
        }
        /* R380: an uncollected own hero answers the tap in words. The eye
           above already inspected and returned, so this only guards the
           plain tap and the seat path. */
        if(t.classList.contains('ffshop-tile-uncoll')){
          if(collView()==='party'){
            var sayU = (typeof window.hohShowHeartToast==='function') ? window.hohShowHeartToast : null;
            var nmU = t.querySelector('.ffshop-tile-name');
            var _nmT = (nmU && nmU.textContent) || 'this card';
            /* R422 (Jan): repair is dead — the only greyed tiles left are a
               fully DUSTED card (a choice made on the card itself) or an
               uncollected one, and the road back for both is a pack. The
               dusted tap still opens the card so the player sees what they
               hold; the words stop promising a repair that no longer exists. */
            if(t.getAttribute('data-spent') === '1'){
              if(sayU) sayU('Turned to hearts', _nmT + ' was dusted. Pull a copy from a pack to play them again.');
              window.hohShopInspect(t.getAttribute('data-hero-id'));
              return;
            }
            if(sayU) sayU('Not collected yet', 'Pull ' + _nmT + ' from a pack to seat them.');
            return;
          }
          /* Profile: open the card. Its locked battle side says everything. */
          window.hohShopInspect(t.getAttribute('data-hero-id'));
          return;
        }
        /* v20: in Party Builder mode a tap SEATS the hero instead of
           inspecting — the pane says so, and Grid/Card views still inspect. */
        if(collView()==='party'){
          var idp=t.getAttribute('data-hero-id');
          /* R422 (Jan): the key names WHAT the card is, not whose it was.
             The server files every non-FF card under own: whoever authored
             it — the old ownhero-class test sent a stranger's card to the
             muster as ff:, which the launch then refused. */
          var keyp=(t.classList.contains('ffshop-tile-nonff')?'own:':'ff:')+idp;
          seatFromTile(t, keyp);
          return;
        }
        var id=t.getAttribute('data-hero-id');
        /* Own-non-FF tiles open the upgrade lightbox, NOT the FF inspector. */
        /* R380: one viewer for every card, own heroes included. */
        window.hohShopInspect(id);
      });
      grid.addEventListener('keydown', function(e){
        if(e.key!=='Enter'&&e.key!==' ') return;
        var t=e.target.closest('.ffshop-tile'); if(!t) return;
        e.preventDefault();
        var id=t.getAttribute('data-hero-id');
        if(host.querySelector('.ffshop-collsec') && dustSelection().active){
          e.stopPropagation();
          var sst=dustSelection(); if(sst.ids[id]) delete sst.ids[id]; else sst.ids[id]=1;
          syncDustSelectionUI(host); return;
        }
        /* R494: the eye is its own action for keyboard players too. The old
           handler treated Enter on the focused eye as Enter on the tile, so a
           full party answered "party full" instead of opening the card. */
        if(e.target.closest('.ffshop-tile-eye')){
          e.stopPropagation();
          window.hohShopInspect(id);
          return;
        }
        /* BUG-UX2 (2026-08-06): the keyboard path ignored builder mode, so
           Enter and Space could never seat a hero. Same branch as the click. */
        if(collView()==='party'){
          /* R422: same key law as the click path — non-FF is own:, always. */
          var keyk=(t.classList.contains('ffshop-tile-nonff')?'own:':'ff:')+id;
          seatFromTile(t, keyk);
          return;
        }
        /* R380: one viewer for every card, own heroes included. */
        window.hohShopInspect(id);
      });
      wireHoverFx(grid);
    }
    var dustBtn = host.querySelector('#ffshop-dust-btn');
    if(dustBtn) dustBtn.addEventListener('click', function(){
      /* Every visible dust entry uses the same exact reviewed transaction.
         Legacy non-Profile collection mounts may not expose the sticky
         multi-select bar, but spare/rarity review remains fully available;
         Profile is the intentional home for grid multi-select. */
      openDustManager(host);
    });
    var rollQueueBtn = host.querySelector('#ffshop-rollq-btn');
    if(rollQueueBtn) rollQueueBtn.addEventListener('click',function(){ openAbilityRollQueue(host); });
    var selectCancel=host.querySelector('#ffshopSelectCancel');
    if(selectCancel) selectCancel.addEventListener('click',function(){
      dustClearSelection(); syncDustSelectionUI(host);
      var back=host.querySelector('#ffshop-dust-btn'); if(back) try{ back.focus({preventScroll:true}); }catch(_){ try{ back.focus(); }catch(__){} }
    });
    var selectReview=host.querySelector('#ffshopSelectReview');
    if(selectReview) selectReview.addEventListener('click',function(){
      var ids=dustSelectionIds(); if(!ids.length) return;
      openDustReview(host,'manual',null,ids);
    });
    syncDustSelectionUI(host);
    var openCta = host.querySelector('#ffshop-open-pack-cta');
    if(openCta) openCta.addEventListener('click', function(){
      try{ if(typeof window.hohYourHallSwitchView==='function'){ window.hohYourHallSwitchView('shop'); return; } }catch(_){}
      try{ if(typeof window.hohOpenYourHall==='function') window.hohOpenYourHall('shop'); }catch(_){}
    });
    wireCollView(host, getCs);
  }

  /* ==================== R335: CARD VIEW ====================
     A horizontal rail of the REAL trading cards, reusing the exact face the
     inspector builds (fullCardHtml) so a card can never look different in
     two places. Three deliberate decisions:

     1. LAZY. The rail builds only when card view is actually shown, and only
        renders a WINDOW of cards around the current index, not all 95. A
        full card is a heavy subtree (art, gem, two text fitters, a back
        face); mounting ninety of them would jank the tab open on a phone
        for a view most players will glance at. The window slides as you
        scroll, so it always feels complete.
     2. SCROLL-SNAP, NOT A JS SLIDER. The browser's own snapping gives real
        momentum, real accessibility and zero animation code — and it cannot
        fight the card's tap-to-flip the way a custom drag handler would.
     3. THE RARITY GLOW COMES FREE. fullCardHtml already stamps
        .ffshop-card-<tier> and --rar, which the existing opened-card glow
        rules key off. Nothing new to keep in sync. */
  /* ==================== R339: THE DECK ====================
     Ported from the Quest 1 archetype carousel (character-quest.js ~3694),
     transform ladder and all, because Jan asked for that exact feel and a
     lookalike would drift from it. What that carousel does, and why each part
     earns its place:

       * ONE card is upright and readable at the front; the neighbours sit
         behind it, smaller and rotated away. That is what makes it read as a
         DECK you are holding rather than a filmstrip you are scrolling. A
         flat scroller gives every card equal weight, so nothing is "current"
         and there is nothing to act on.
       * The ladder is fixed at three depths (0, +/-1, +/-2) and anything
         further is not rendered at all. That caps the live DOM at five cards
         no matter how big the collection gets -- the virtualisation falls out
         of the design instead of being bolted on.
       * A positioner owns the TRANSLATION and a child owns the SCALE and
         ROTATION. Two elements because one cannot animate translate(-50%,-50%)
         and a scale independently without them fighting.

     One thing deliberately NOT ported: Quest 1 shows flat images. These are
     the real trading cards, 380x650 of live DOM each. So --deck-k drives
     everything -- the footprint is calc(380px * k) by calc(650px * k) and the
     card inside is scale(k). One number per breakpoint, and CSS does the
     arithmetic, so the frame can never disagree with what is inside it. */
  var DECK_K = 380;            /* the card's true width; --deck-k scales it */
  function deckCardHtml(c){
    var id = c.character_id;
    /* the inspector's private path, NOT window.hohShopCardHtml: that public
       wrapper passes snap=null and so resolves only from the hall pool. Here
       the collection snapshot is the better source. */
    return '<div class="ffshop-deck-card" data-hero-id="' + esc(String(id)) + '">' +
        '<div class="ffshop-cardbox">' +
          '<div class="ffshop-tiltwrap">' + fullCardHtml(id, collEntry(id)) + '</div>' +
        '</div>' +
      '</div>';
  }
  function buildRail(host, cards){
    var stage = host.querySelector('#ffshop-rail');
    if(!stage) return;
    if(!cards.length){
      stage.innerHTML = '<div class="ffshop-empty">No cards yet. Open a pack to start your collection.</div>';
      return;
    }
    stage.__ffCards = cards;
    stage.__ffNodes = {};        /* card index -> positioner element */
    stage.__ffIdx = 0;
    stage.innerHTML = '';
    layoutDeck(stage, host);
    /* the fitters run on the next frame and the webfonts land later still,
       and both change how tall the text is -- so measure again after each. */
    try { requestAnimationFrame(function(){ fitDeckHeight(stage); }); } catch(_){}
    try {
      if(document.fonts && document.fonts.ready && document.fonts.ready.then){
        document.fonts.ready.then(function(){
          if(document.body.contains(stage)) fitDeckHeight(stage);
        });
      }
    } catch(_){}
    if(!stage.__ffDeckWired){
      stage.__ffDeckWired = true;
      wireDeck(stage, host);
    }
  }
  /* place every card within two of the front one, mount what is missing,
     drop what has drifted out of reach. Idempotent: safe to call on every
     step, every resize and every repaint. */
  function layoutDeck(stage, host){
    var cards = stage.__ffCards || [];
    var n = cards.length;
    if(!n) return;
    var idx = stage.__ffIdx | 0;
    var live = {};
    /* R339b: PRIORITY ORDER, NOT A SIZE GUARD.
       The first version skipped a depth whenever n <= 2*|d|, which was far too
       blunt: a 2-card collection drew ONE card and a 4-card collection drew
       three. `live[i]` is the only dedupe needed -- a small deck simply wraps
       onto itself -- but the ORDER then decides who wins a contested index. It
       must be the front card, or a 2-card deck resolves d=-2 to index 0 first
       and the front slot is skipped as a duplicate, leaving no front card at
       all. Hence 0 first, then the near pair, then the far pair. Right before
       left so the second card of a two-card deck reads as "next". */
    var ORDER = [0, 1, -1, 2, -2];
    for(var oi = 0; oi < ORDER.length; oi++){
      var d = ORDER[oi];
      var i = ((idx + d) % n + n) % n;
      if(live[i]) continue;
      live[i] = 1;
      var pos = stage.__ffNodes[i];
      if(!pos){
        pos = document.createElement('div');
        pos.className = 'ffshop-deck-pos';
        pos.setAttribute('data-i', String(i));
        try { pos.innerHTML = deckCardHtml(cards[i]); }
        catch(err){
          try { if(window.console && console.error) console.error('[ffshop] deck card failed', cards[i] && cards[i].character_id, err); }catch(_){}
          pos.innerHTML = '<div class="ffshop-deck-card"><div class="ffshop-rail-broken">' +
            '<span>' + esc((cards[i] && cards[i].name) || 'This card') + '</span>' +
            '<em>could not be drawn. It is still yours. Open it from Grid view.</em></div></div>';
        }
        stage.appendChild(pos);
        try {
          var tw = pos.querySelector('.ffshop-tiltwrap');
          if(tw && typeof window.hohShopWireCard === 'function') window.hohShopWireCard(tw);
        } catch(_){}
        stage.__ffNodes[i] = pos;
      }
      var a = Math.abs(d);
      var depth = a === 0 ? 'c' : (a === 1 ? 'a' : 'b');
      var side  = d === 0 ? 'x' : (d > 0 ? 'r' : 'l');
      /* the ladder lives in CSS as three depth classes, so the numbers are in
         one place and a designer can retune it without touching JS */
      pos.className = 'ffshop-deck-pos ffshop-deck-d' + depth + ' ffshop-deck-s' + side;
      pos.setAttribute('aria-hidden', d === 0 ? 'false' : 'true');
    }
    /* release anything no longer in the window */
    for(var k in stage.__ffNodes){
      if(live[k]) continue;
      try { stage.__ffNodes[k].remove(); } catch(_){}
      delete stage.__ffNodes[k];
    }
    fitDeckHeight(stage);
    paintDeckFoot(stage, host);
  }
  /* R340: THE CARD IS NOT ALWAYS 650px TALL.
     .ffshop-card is `min-height:650px`, so a long legend or a long epithet
     makes it grow -- and the deck was assuming a fixed 650. The card is
     anchored to the TOP of its positioner and scaled from there, so the extra
     height all spills downward, straight past the stage's overflow:hidden.
     That is the clipping.
     So measure instead of assume. offsetHeight is the LAYOUT height and is not
     affected by the ancestor scale, so one read per mounted card gives the true
     size; the positioner is then sized to its own card and the stage to the
     tallest of them. Five reads, batched after mounting, so it is a single
     layout pass. */
  function fitDeckHeight(stage){
    try {
      var k = parseFloat(getComputedStyle(stage.parentNode || stage).getPropertyValue('--deck-k')) || 0.5;
      var maxH = 650;
      var nodes = stage.querySelectorAll('.ffshop-deck-pos');
      var i, hs = [];
      for(i = 0; i < nodes.length; i++){
        var card = nodes[i].querySelector('.ffshop-card') || nodes[i].querySelector('.ffshop-rail-broken');
        var h = (card && card.offsetHeight) || 650;
        if(h < 200) h = 650;                       /* pre-layout read: trust the floor */
        hs.push(h);
        if(h > maxH) maxH = h;
      }
      for(i = 0; i < nodes.length; i++){
        nodes[i].style.height = Math.round(hs[i] * k) + 'px';
      }
      /* +30 so the neighbours' rotation has somewhere to go */
      stage.style.height = Math.round(maxH * k + 30) + 'px';
    } catch(_){}
  }
  function paintDeckFoot(stage, host){
    var cards = stage.__ffCards || [];
    var cur = cards[stage.__ffIdx | 0];
    var open = host && host.querySelector('.ffshop-rail-open');
    if(open && cur) open.setAttribute('data-hero-id', String(cur.character_id));
  }
  function deckGo(stage, host, delta, absolute){
    var cards = stage.__ffCards || [];
    var n = cards.length; if(!n) return;
    stage.__ffIdx = absolute != null
      ? ((absolute % n) + n) % n
      : ((((stage.__ffIdx | 0) + delta) % n) + n) % n;
    layoutDeck(stage, host);
  }
  function wireDeck(stage, host){
    var wrap = host.querySelector('#ffshop-rail-wrap');
    if(!wrap) return;
    wrap.addEventListener('click', function(e){
      var arrow = e.target.closest ? e.target.closest('[data-deck]') : null;
      if(arrow){
        e.preventDefault(); e.stopPropagation();
        deckGo(stage, host, arrow.getAttribute('data-deck') === 'next' ? 1 : -1);
        return;
      }
      var open = e.target.closest ? e.target.closest('[data-act="inspect"]') : null;
      if(open){
        e.stopPropagation();
        var id = open.getAttribute('data-hero-id');
        if(id && typeof window.hohShopInspect === 'function') window.hohShopInspect(id);
      }
    });
    /* CAPTURE, so a tap on a card BEHIND the front one is turned into a
       navigation before it can reach that card's own flip handler. The front
       card is left alone and flips, which is what the hint promises. */
    stage.addEventListener('click', function(e){
      var pos = e.target.closest ? e.target.closest('.ffshop-deck-pos') : null;
      if(!pos || pos.classList.contains('ffshop-deck-dc')) return;
      e.preventDefault(); e.stopPropagation();
      deckGo(stage, host, 0, parseInt(pos.getAttribute('data-i'), 10) || 0);
    }, true);
    /* swipe: the same 40px threshold Quest 1 uses */
    var sx = 0, sy = 0, tracking = false;
    stage.addEventListener('touchstart', function(e){
      if(!e.touches || !e.touches[0]) return;
      sx = e.touches[0].clientX; sy = e.touches[0].clientY; tracking = true;
    }, { passive: true });
    stage.addEventListener('touchend', function(e){
      if(!tracking) return; tracking = false;
      var t = (e.changedTouches && e.changedTouches[0]); if(!t) return;
      var dx = t.clientX - sx, dy = t.clientY - sy;
      /* only a mostly-horizontal swipe steers the deck, so a vertical scroll
         through the page is never stolen */
      if(Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      deckGo(stage, host, dx < 0 ? 1 : -1);
    }, { passive: true });
    stage.setAttribute('tabindex', '0');
    stage.addEventListener('keydown', function(e){
      if(e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      deckGo(stage, host, e.key === 'ArrowRight' ? 1 : -1);
    });
  }
  function wireCollView(host, getCs){
    var tog = host.querySelector('.ffshop-viewtog');
    if(!tog) return;
    if(!tog.__ffWired){
      tog.__ffWired = true;
      tog.addEventListener('click', function(e){
        var b = e.target.closest ? e.target.closest('.ffshop-viewtog-btn') : null;
        if(!b) return;
        var v = b.getAttribute('data-view');
        if(v === collView()) return;
        setCollView(v);
        applyCollView(host, getCs);
      });
    }
    /* R338b: RECONCILE ON EVERY PAINT, don't just conditionally build.
       collectionInnerHtml stamps the hidden flags from collView() at string
       time, and this used to only call ensureRail -- so if those two ever
       disagreed (a repaint racing a toggle, a cached mount, a second host)
       nothing put them back in step. applyCollView sets the button states,
       the hidden flags AND builds, and it is idempotent, so running it on
       every paint is both cheaper to reason about and impossible to desync. */
    applyCollView(host, getCs);
  }
  /* R338b: BUILD ALWAYS. THE WIDTH GATE WAS THE BUG.
     R335b refused to build while rail.offsetWidth was 0, reasoning that the
     text fitters measure zero inside display:none and would clip names and
     legends permanently. The reasoning about the FITTERS is correct. The
     conclusion was not: writing the HTML needs no layout at all, and gating
     the whole build on width meant any first paint that measured zero -- for
     any reason, in either mount -- produced a permanently empty rail with the
     hint underneath it, which is exactly what Jan is looking at.

     So: build unconditionally, and treat the fit as the thing that needs
     width. If a card mounts with no width, it is flagged and re-fitted the
     moment width appears. Worst case a card is briefly unfitted; before, the
     worst case was no cards at all. */
  function ensureRail(host, getCs){
    var rail = host.querySelector('#ffshop-rail');
    if(!rail || rail.__ffCards) return;
    var cs = (typeof getCs === 'function') ? getCs() : getCs;
    buildRail(host, (cs && cs.allCards) || []);
    if(!rail.offsetWidth) watchForWidth(rail);
  }
  /* the fitters are the only width-sensitive part, so this is the only thing
     that ever has to wait. Observer for speed, poll for certainty, both stop
     on the first success or when the node leaves the page. */
  function watchForWidth(rail){
    if(rail.__ffWidthWatch) return;
    rail.__ffWidthWatch = 1;
    var io = null, tick = null;
    function stop(){
      try{ if(io) io.disconnect(); }catch(_){}
      try{ if(tick) clearInterval(tick); }catch(_){}
      io = null; tick = null; rail.__ffWidthWatch = 0;
    }
    function go(){
      if(!document.body.contains(rail)){ stop(); return; }
      if(!rail.offsetWidth) return;
      stop();
      refitRail(rail);
    }
    try{
      if(typeof IntersectionObserver === 'function'){
        io = new IntersectionObserver(function(){ go(); });
        io.observe(rail);
      }
    }catch(_){}
    /* PERF-10 (2026-08-06): while the rail sat in a hidden panel this poll
       read offsetWidth (a forced layout) three times a second, forever, in
       background tabs included. The IntersectionObserver above is the fast
       path and fires the moment the rail gains layout; the interval is only
       a fallback, so it can be slow and it can sleep in hidden tabs. */
    tick = setInterval(function(){ if(document.hidden) return; go(); }, 1200);
  }
  /* re-run the shared card wiring on everything already mounted. hohShopWireCard
     is idempotent enough for this: it re-binds a flip handler and re-runs the
     fitters, and the fitters are what we are here for. */
  function refitRail(rail){
    try{
      var wraps = rail.querySelectorAll('.ffshop-tiltwrap');
      for(var i = 0; i < wraps.length; i++){
        if(typeof window.hohShopWireCard === 'function') window.hohShopWireCard(wraps[i]);
      }
    }catch(_){}
  }
  function applyCollView(host, getCs){
    var v = collView();
    var grid = host.querySelector('#ffshop-grid-all');
    var wrap = host.querySelector('#ffshop-rail-wrap');
    var btns = host.querySelectorAll('.ffshop-viewtog-btn');
    for(var i = 0; i < btns.length; i++){
      var on = btns[i].getAttribute('data-view') === v;
      btns[i].classList.toggle('on', on);
      btns[i].setAttribute('aria-selected', on ? 'true' : 'false');
    }
    /* R339c: BELT AND BRACES. The `hidden` attribute alone is not enough --
       an author `display` rule beats the UA sheet's [hidden] rule, and that
       has now silently broken this toggle twice. Setting style.display makes
       the switch work whatever the stylesheet says. */
    function show(el, on){
      if(!el) return;
      if(on){ el.hidden = false; el.style.display = ''; }
      else { el.hidden = true; el.style.display = 'none'; }
    }
    /* v20: PARTY BUILDER — the grid stays visible (the cards ARE the picker);
       a pane above it holds the five seats, the saves and the launch. */
    var pane = host.querySelector('#ffshopPartyPane');
    if(v === 'party'){
      if(!pane && grid && grid.parentNode){
        pane = document.createElement('div');
        pane.id = 'ffshopPartyPane'; pane.className = 'ffshop-pb';
        grid.parentNode.insertBefore(pane, grid);
      }
      if(pane){ pane.hidden = false; pane.style.display = ''; pbPaint(host); }
      if(grid) grid.classList.add('ffshop-pb-mode');
      show(wrap, false);
      show(grid, true);
    } else {
      if(pane){ pane.hidden = true; pane.style.display = 'none'; }
      if(grid){
        grid.classList.remove('ffshop-pb-mode');
        pbRestoreGrid(host);
        grid.querySelectorAll('.ffshop-tile').forEach(function(t){ t.style.display = ''; });
        /* R437: that blanket reset also un-hides everything the Profile
           filter had hidden. Re-apply it so leaving the builder does not
           silently drop the player's search back to "everything". */
        try { if(window.__ffCollApply) window.__ffCollApply(); } catch(_){}
      }
      var barBox = host.querySelector('#ffshopPartyBar');
      if(barBox){ barBox.hidden = true; barBox.style.display = 'none'; }
    }
    if(v === 'card'){
      /* R338 ROOT CAUSE OF "the cards are not loading".
         ensureRail ran FIRST, while #ffshop-rail-wrap still carried `hidden`.
         hidden is display:none, so rail.offsetWidth was 0, so R335b's
         zero-width guard refused to build and parked an IntersectionObserver
         instead -- and an observer on a node that is already inside the
         viewport when it un-hides does not reliably re-fire. Net effect: the
         toggle switched to an empty rail and stayed empty.
         Reveal, THEN build. The node has real width by the time ensureRail
         measures it, and the deferral path is left for the case it was
         actually written for: a collapsed Satchel pocket. */
      show(grid, false);
      show(wrap, true);
      ensureRail(host, getCs);
    } else {
      show(wrap, false);
      show(grid, true);
    }
  }
  /* R268 (grid rework): the own-hero upgrade lightbox. Clicking one of YOUR OWN
     heroes (a non-FF card) opens this instead of the FF card inspector. Two
     choices: commission it into a real Hall card (the desired action, opens the
     FramedFantasy waitlist), or dust it for hearts (leaves the grid, stays in the
     Hall). Copy is about THEM and THEIR character. */
  /* v29: the one line under the pitch. Deliberately not a second button. */
  var COMMISSION_URL = 'https://framedfantasy.com/products/fantasy-character-commission';
  function openOwnHeroLightbox(heroId){
    var snap = collEntry(heroId);
    var hero = heroById(heroId);
    var name = (hero && hero.name) || (snap && snap.name) || 'your hero';
    var img = (hero && (hero.image_thumb_url || hero.image_url)) || (snap && snap.img) || '';
    /* Dust value = this card's worth, same math as cashIn / the dust tally:
       rarity value x2 for an own hero, no x10 because it is not an FF portrait. */
    var dust = TIER_VALUE[tierFor(liveHearts(heroId, snap))] * ((snap && snap.own) ? 2 : 1);
    var ov = document.createElement('div');
    ov.className = 'ffshop-own-lb';
    ov.innerHTML =
      '<div class="ffshop-own-box" role="dialog" aria-modal="true" aria-label="Your hero">' +
        (img?'<div class="ffshop-own-art"><img src="'+esc(img)+'" alt="" decoding="async"></div>':'') +
        '<div class="ffshop-own-name">'+esc(name)+'</div>' +
        /* v29 (Jan): SHORTER, AND SAY THE REAL THING. The old block was four
           lines about collectibility and never once said the fact that
           actually moves someone: this hero cannot fight yet. Only FramedFantasy
           portraits are playable, on purpose, because every card in the
           Battlegrounds is a piece Jan painted for a real client and that is
           what keeps the deck worth collecting. The Hall stays free. The
           battlefield is for commissioned heroes. */
        '<div class="ffshop-own-sub"><b>'+esc(name)+' cannot fight yet.</b> Only FramedFantasy portraits are playable, so every playable card is one I painted for a real client. Commission '+esc(name)+' and they become a playable card straight away.</div>' +
        '<div class="ffshop-own-note">The Hall is free. The battlefield is for commissioned heroes.</div>' +
        '<div class="ffshop-own-actions">' +
          '<a class="ffshop-own-commission" href="'+COMMISSION_URL+'" target="_blank" rel="noopener">Upgrade to a playable card</a>' +
          '<button type="button" class="ffshop-own-dust">Dust '+esc(name)+' for '+dust+'&#9829;</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(ov);
    requestAnimationFrame(function(){ ov.classList.add('on'); });
    var prevOv = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function close(){ try{ ov.remove(); }catch(_){} document.body.style.overflow = prevOv||''; document.removeEventListener('keydown', onKey); }
    function onKey(e){ if(e.key==='Escape') close(); }
    document.addEventListener('keydown', onKey);
    ov.addEventListener('click', function(e){ if(e.target===ov) close(); });
    /* Commission link closes the lightbox as it opens the tab. */
    var comm = ov.querySelector('.ffshop-own-commission');
    if(comm) comm.addEventListener('click', function(){ setTimeout(close, 60); });
    var dustBtn = ov.querySelector('.ffshop-own-dust');
    if(dustBtn) dustBtn.addEventListener('click', function(){
      dustBtn.disabled = true;
      /* Add the hearts, mark this hero dusted (persisted), and drop it from the
         grid. The hero itself is NOT deleted — it stays in the Hall. */
      bankAdd(dust, 'earn_dust_own');
      addDustedOwn(heroId);
      toast('Dusted '+name+' for '+dust+' hearts. It still lives in your Hall.');
      close();
      refreshCollectionEverywhere();
    });
  }
  /* Re-render whichever collection mount is currently live (the Shop tab and/or
     the Satchel top), after a state change like an own-hero dust. */
  function refreshCollectionEverywhere(){
    try{ if(hostEl && document.body.contains(hostEl) && hostEl.querySelector('#ffshop-grid-all') && !hostEl.querySelector('.hoh-satchel-shell')) renderShop(hostEl); }catch(_){}
    try{ if(typeof window.hohShopRefreshSatchelCollection==='function') window.hohShopRefreshSatchelCollection(); }catch(_){}
    try{ if(typeof window.__hohBuilderRepaint==='function') window.__hohBuilderRepaint(); }catch(_){}
  }
  /* R268 (Satchel): mount the SAME collection block at the top of the Satchel.
     Ensures shop state is loaded first (the Satchel can open before the Shop tab
     was ever visited), then renders the controls + grid and wires them. */
  /* R260: how many character cards this account holds (FF pulls + own
     heroes — exactly what the collection shows). -1 = not loaded yet.
     The satchel's party gate reads this. */
  /* R444 (Jan): "on mobile normal heroes' cards do not load properly ...
     right now it just says Hero and it doesn't even show an image."
     ROOT CAUSE. fullCardHtml resolves the name, the art, and the race/class
     line from heroById(), which reads window.hohGetAllCharacters. That getter
     is not even DEFINED until loadCharacters() finishes, and loadCharacters is
     a SEQUENTIAL paginated fetch of ~57 columns per row over the whole Hall.
     On a phone that is seconds. Open a card inside that window and heroById
     returns null, so the name falls back to the literal 'Hero', the art url is
     '' and the race/class line is empty. Every symptom in the screenshot comes
     off that one null. Desktop rarely shows it because the fetch has usually
     landed before you can tap.
     The real defect is not the race, it is that NOTHING RECOVERED. Every other
     module in this theme subscribes to hoh:characters-loaded and repaints:
     hoh-hall-cast, hoh-hall-saved, hoh-hall-party, hoh-your-hall and
     hoh-fresh-faces all do. hoh-shop was the only one that never did, so a
     card opened early stayed broken until a full page reload.
     One subscription fixes the inspector, the collection grid and the card
     rail together. It is idempotent and it only acts on what is actually
     mounted, so it costs nothing when the pool lands before anyone opens
     anything. */
  try {
    document.addEventListener('hoh:characters-loaded', function(){
      try {
        var ov = document.querySelector('.ffshop-insp');
        var hid = ov && ov.getAttribute('data-insp-hero');
        if(ov && hid){
          /* Only redraw a card that actually resolved badly. A healthy card
             must not blink: re-opening throws away the scroll position and any
             open sub panel, and this event also fires on pages where the card
             was already correct.
             The test is the symptom, not the cause. fullCardHtml falls back to
             the literal 'Hero' when heroById returns null, so a name band
             reading exactly that (or reading nothing) is the broken card and
             nothing else is. */
          var nameEl = ov.querySelector('.ffshop-cname');
          var shown = nameEl ? String(nameEl.textContent || '').trim() : '';
          var h = heroById(hid);
          if((!shown || shown === 'Hero') && h && (h.name || h.image_url || h.image_thumb_url)){
            /* Use the overlay's teardown so the X, Escape listener and
               lifecycle event all close with the placeholder card. */
            try{
              if(typeof ov.__ffshopClose === 'function') ov.__ffshopClose();
              else ov.remove();
            }catch(_){}
            try{ window.hohShopInspect(hid); }catch(_){}
          }
        }
      } catch(_){}
      /* the collection grid reads the same pool for race, class and art */
      try { if(typeof window.hohShopRefreshSatchelCollection === 'function') window.hohShopRefreshSatchelCollection(); } catch(_){}
      try { if(typeof window.__ffCollApply === 'function') window.__ffCollApply(); } catch(_){}
    }, false);
  } catch(_){}

  document.addEventListener('hoh:card-level-changed',updateGridUpgradeAttention);
  document.addEventListener('hoh:card-progression-changed',updateGridUpgradeAttention);

  window.hohShopCollectionCount = function(){
    if(!shopState) return -1;
    return ((shopState.collection || []).length) | 0;
  };
  window.hohShopRenderCollection = function(host){
    if(!host) return;
    var em = email();
    if(!em || !pin()){
      host.innerHTML = '<div class="ffshop-coll-signin">Sign in on the Profile tab to build your card collection.</div>';
      return;
    }
    function paint(){
      /* R380 AUDIT FIX: painting with NO state used to show a veteran's
         hundred cards as "None yet" and every own hero as uncollected — a
         quiet lie after any failed fetch. Say what happened and offer the
         one honest action instead. */
      if(!shopState){
        host.innerHTML =
          '<div class="ffshop-empty">Could not reach your cards. Nothing is lost.' +
          '<div style="margin-top:10px"><button type="button" class="ffpb-btn sm" id="ffshopCollRetry">Try again</button></div></div>';
        var rb = host.querySelector('#ffshopCollRetry');
        if(rb) rb.addEventListener('click', function(){
          host.innerHTML = '<div class="ffshop-empty">Loading your collection…</div>';
          try { fetchState().then(function(){ if(document.body.contains(host)) paint(); }); } catch(_){ paint(); }
        });
        return;
      }
      var cs = collectionSource();
      /* R258: the satchel's collapsible header IS the label now — no inner
         duplicate title. The "N of M collected" counter stays. */
      host.innerHTML = collectionInnerHtml(cs, true);
      wireCollection(host, collectionSource);
      /* R437: the old post-hoc search injection lived here. It matched names
         only, read them out of the DOM rather than the data, had no owner for
         its state, and was destroyed on every repaint. Replaced by a real
         filter bar that is part of the markup. */
      try { if(window.__hohCollMode === 'profile') wireCollProfile(host, cs); } catch(_){}
      /* A committed server receipt can survive a tab close between the RPC
         and the device-local Heart update. Opening Profile retries that exact
         request/receipt; it never invents a second request. */
      try { if(window.__hohCollMode === 'profile') setTimeout(recoverPendingDust,0); } catch(_){}
      /* R260: the party pocket gates on the collection count — sync it
         every time the cards paint so the 4th pull unlocks it live. */
      try{ if(typeof window.hohSatchelSyncPartyGate === 'function') window.hohSatchelSyncPartyGate(); }catch(_){}
    }
    /* Expose a refresher so a dust from anywhere can repaint this mount. */
    window.hohShopRefreshSatchelCollection = function(){ if(document.body.contains(host)) paint(); };
    if(shopState){ paint(); }
    else {
      host.innerHTML = '<div class="ffshop-empty">Loading your collection…</div>';
      try { fetchState().then(function(){ if(document.body.contains(host)) paint(); }); }
      catch(_){ paint(); }
    }
  };

  /* ==================== R296: card text recovery tools ====================
     The "A hero of the Hall." symptom = a cache row missing BOTH story and
     card_meta. Two answers: (1) whenever the inspector opens such a hero,
     fetch the full row once, patch the cache, and re-open so real text
     renders; (2) a god-only regenerate that rebuilds card_meta from
     whatever the hero still carries (labeled quest story, plain story,
     campaign) and saves it. */
  window.hohShopRegenCardText = function(heroId){
    try{
      if(!(typeof window.hohIsGodMode === 'function' && window.hohIsGodMode())) return;
      var c = sb(); if(!c){ toast('Database client unavailable.'); return; }
      function go(h){
        if(!h){ toast('No hero row found to write from.'); return; }
        if(h.card_meta && h.card_meta.edited && !window.confirm('This card text was hand-edited by the owner. Overwrite it?')) return;
        /* R312 ROOT CAUSE of "regenerate does nothing": it only ever wrote from
           `story`, so every hero without one (Olivia and friends) bailed on the
           toast and stayed stuck reading "A hero of the Hall." Now it composes
           from EVERYTHING the row carries — the NPC details players fill in,
           the campaign, race and class — and only refuses when the row is
           genuinely empty. */
        var story = (h.story || '').trim();
        var camp  = (h.campaign || '').trim();
        var who   = [ (h.race || '').trim(), (h.character_class || '').trim() ].filter(Boolean).join(' ');
        var goal  = (h.npc_current_goal || '').trim();
        var secret= (h.npc_secret || '').trim();
        var habit = (h.npc_habit || '').trim();
        var enemy = (h.npc_enemy || '').trim();
        var weak  = (h.npc_hidden_weakness || '').trim();
        var line  = (h.npc_catchphrase || '').trim();
        /* Players write these fields however they like — some as fragments
           ("the guard captain who framed her"), some as commands ("Find her
           brother's killer"). So each one stands as its own sentence rather
           than being glued behind a lead-in that only fits one of those. */
        function sentence(s){ s = String(s || '').trim(); if(!s) return ''; return /[.!?]$/.test(s) ? s : s + '.'; }
        function cap(s){ s = String(s || '').trim(); return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; }
        function lower(s){ s = String(s || '').trim(); return s ? s.charAt(0).toLowerCase() + s.slice(1) : ''; }
        /* R423 (Jan): THIS TOOL NO LONGER DUMPS THE RAW STORY. It used to
           write excerpt(story, 240) straight into card_meta.legend, which is
           how Olivia's card ended up printing 239 characters of her
           submitter's backstory cut mid-word. cardText now refuses a legend
           that merely echoes the story, so that branch could only ever write
           text that would not display. It composes from the STRUCTURED
           fields instead — race, class, goal, enemy, habit, secret — which
           is card writing rather than a form dump, and the story stays where
           it belongs, on the hero's profile. */
        var legend = '';
        {
          var parts = [];
          if(who)    parts.push(sentence((/^[aeiou]/i.test(who) ? 'An ' : 'A ') + who.toLowerCase() + ' of the Hall'));
          if(goal)   parts.push(sentence(cap(goal)));
          if(enemy)  parts.push(sentence('Never forgave ' + lower(enemy)));
          if(habit)  parts.push(sentence(cap(habit)));
          if(secret) parts.push(sentence(cap(secret)));
          if(!parts.length && weak) parts.push(sentence(cap(weak)));
          legend = excerpt(parts.join(' '), 240);
        }
        if(!legend && !line && !camp){
          toast('This hero has no campaign or NPC details yet — nothing to write a card from.');
          return;
        }
        var meta = {
          title: null, epithet: null,
          legend: legend || null,
          quote: line || camp || null,
          edited: false, source: 'god-regen'
        };
        c.from('characters').update({ card_meta: meta }).eq('id', heroId).select('id').then(function(r){
          if(r.error){ toast('Could not save: ' + (r.error.message || 'error')); return; }
          h.card_meta = meta;
          toast('Card text rebuilt from the hero\'s data.');
          closeShopInspectors();
          window.hohShopInspect(heroId);
        });
      }
      /* R312: always pull the live row — the cached hall copy can predate the
         NPC details this now writes from, and a stale cache was part of why
         regenerate looked broken. */
      var hero = heroById(heroId);
      c.from('characters').select(HOH_CHAR_COLS).eq('id', heroId).limit(1).then(function(r){
        var row = r && r.data && r.data[0];
        if(row){
          try{
            var all = window.hohGetAllCharacters ? window.hohGetAllCharacters() : null;
            if(all){ var f = null; for(var i2=0;i2<all.length;i2++){ if(String(all[i2].id)===String(heroId)){ f=all[i2]; break; } }
              if(f) Object.assign(f, row); else all.push(row); }
          }catch(_){}
        }
        go(row || hero);
      });
    }catch(_){}
  };
  /* (1) the silent self-heal, for everyone: */
  (function(){
    var _origInspect = window.hohShopInspect;
    if(typeof _origInspect !== 'function') return;
    window.hohShopInspect = function(heroId){
      /* This wrapper is the final public boundary after the inspector above
         is replaced. Normalize here too, or its self-heal lookup can still
         query `own:<id>` even though the actual overlay opened the bare id. */
      heroId = String(heroId == null ? '' : heroId).replace(/^(ff|own|hall):/, '');
      var inspectArgs = Array.prototype.slice.call(arguments);
      inspectArgs[0] = heroId;
      _origInspect.apply(this, inspectArgs);
      try{
        var h = heroById(heroId);
        if(h && (h.story || (h.card_meta && h.card_meta.legend))) return; /* healthy */
        var c = sb(); if(!c) return;
        c.from('characters').select(HOH_CHAR_COLS).eq('id', heroId).limit(1).then(function(r){
          var row = r && r.data && r.data[0];
          if(!row || !(row.story || (row.card_meta && row.card_meta.legend))) return;
          try{
            var all = window.hohGetAllCharacters ? window.hohGetAllCharacters() : null;
            if(all){ var f = null; for(var i3=0;i3<all.length;i3++){ if(String(all[i3].id)===String(heroId)){ f=all[i3]; break; } }
              if(f) Object.assign(f, row); else all.push(row); }
          }catch(_){}
          var ov = document.querySelector('.ffshop-insp');
          if(!ov) return;
          closeShopInspectors();
          _origInspect(heroId);
        });
      }catch(_){}
    };
  })();

  /* -------- R256: sweep responsiveness on the collection board --------
     Gliding the cursor across the cards wakes each one: the tile lifts
     (CSS, no shake) and sheds a few motes in ITS OWN rarity color.
     Fine pointers only (touch owns drag-reorder), per-tile cooldown +
     a global live-node cap so sweeping 50 cards stays calm, and
     reduced-motion skips the particles entirely. */
  var _hovLive = 0, _hovCool = {};
  function wireHoverFx(grid){
    var fine = false;
    try{ fine = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches); }catch(_){}
    if(!fine || reduced) return;
    grid.addEventListener('pointerover', function(e){
      var t = e.target && e.target.closest ? e.target.closest('.ffshop-tile') : null;
      if(!t || !grid.contains(t)) return;
      var id = t.getAttribute('data-hero-id') || '';
      var now = Date.now();
      if(_hovCool[id] && now - _hovCool[id] < 650) return;
      _hovCool[id] = now;
      if(_hovLive > 22) return;
      var col = '';
      try{ col = (t.style.getPropertyValue('--rar') || '').trim(); }catch(_){}
      if(!col) col = 'rgba(200,164,94,.8)';
      var r = t.getBoundingClientRect();
      var n = 3;
      for(var k=0; k<n; k++){
        var s = document.createElement('span');
        s.className = 'ffshop-hovmote';
        s.style.left = (r.left + 8 + Math.random()*(r.width-16)) + 'px';
        s.style.top  = (r.top + r.height*0.22 + Math.random()*r.height*0.3) + 'px';
        s.style.background = col;
        s.style.boxShadow = '0 0 7px ' + col;
        s.style.setProperty('--hx', ((Math.random()*22-11)|0) + 'px');
        s.style.setProperty('--hd', ((650+Math.random()*350)|0) + 'ms');
        document.body.appendChild(s);
        _hovLive++;
        (function(el){ setTimeout(function(){ try{ el.remove(); }catch(_){} _hovLive--; }, 1050); })(s);
      }
    });
  }

  /* ---------------- main renderer ---------------- */

  function shopHostIsActive(host){
    if(!host || !document.body.contains(host)) return false;
    /* The Hall reuses this node for Events and Profile. A late wallet or
       pack response must not replace its new contents with the Shop. */
    return host.id !== 'hoh-yh-tab-content' || !!document.querySelector(
      '.hoh-yh-subtab-active[data-yh-subtab="shop"]');
  }
  function renderShop(host){
    if(!shopHostIsActive(host)) return;
    useShopAccount();
    hostEl = host;
    var em = email();
    if(!em || !pin() || authFailed){
      host.innerHTML = '<div class="hoh-yh-empty">Sign in on the Profile tab to open packs and build your collection.</div>';
      return;
    }
    /* The hub prepends a tab intro AFTER the renderer runs, but this
       renderer replaces innerHTML asynchronously, so it injects its own
       (prependTabIntro skips when one already exists). */
    /* R422 (Jan): every pull is a kept card now. */
    var intro = '<div class="hoh-yh-tab-intro">Buy or earn packs, keep them safely in your Wallet, and open them whenever you choose. Every card you pull joins your collection and can fight in the Battlegrounds.</div>';
    var st = shopState || {};
    var cs = collectionSource();
    var ffCards = cs.ffCards, allCards = cs.allCards, totalFF = cs.totalFF;
    var dustCopies = cs.dustCopies, dustHearts = cs.dustHearts;
    var god = isGod();
    /* R268: free-first-pack label hint. Cached local flag (authority is the RPC
       at open time). Shows "First pack free" on the primary button while the
       account still has its free claim. */
    ensureFreePackFlag();
    /* R271: reconcile the label with the server once per session (read-only, does
       not consume the free pack), so a second device never shows a false "free". */
    if(!freePackPeeked && email() && pin()){ freePackPeeked = true; try{ peekFreePack(); }catch(_){} }
    var freeAvail = (freePackAvail === true);
    /* R421 (Jan): a first-timer must not be able to MISS the free pack — it
       is the whole road to their first FramedFantasy fighter. The label
       leads with the word that matters and the button breathes (same pulse
       the Battlegrounds' Battle button wears). */
    var primarySmall = freeAvail ? 'free \u00b7 saved to Wallet' : (PACK_COST_HEARTS+' hearts \u00b7 saved to Wallet');
    /* R391: the daily free pack paints from the local day mirror; the click
       re-checks against the server gate. */
    var dailyPackOpen = !dailyPackClaimedLocal();
    var savedPacks = 0;
    try { savedPacks = window.HOHWallet && typeof window.HOHWallet.packsUnopened === 'function' ? window.HOHWallet.packsUnopened() | 0 : 0; } catch(_) {}
    savedPacks = Math.max(0, savedPacks);
    var packPurchasePending = !!packBankPending(shopAccount());

    host.innerHTML =
      intro +
      '<div class="ffshop-head">' +
        '<div class="ffshop-shelf">' +
          '<div class="ffshop-item" id="ffshop-pack-mod">' +
            '<div class="ffshop-item-art">'+packArtHtml()+'</div>' +
            '<div class="ffshop-item-name">Card Packs</div>' +
            '<div class="ffshop-item-sub">Five cards a pack. Saved first, opened when you are ready.</div>' +
            '<button type="button" class="ffshop-how-link">How packs work</button>' +
            '<div class="ffshop-btns">' +
              /* R737 (Jan): the "Open a Saved Pack" button is gone from the
                 shelf. Saved packs open from the Wallet panel ("Unopened
                 packs, Open one"); the shelf sells and claims, and the stat
                 line below still shows how many are saved. Every column now
                 has the same two controls: a gold Buy and a quiet daily claim. */
              '<button type="button" class="ffshop-open-btn ffshop-open-btn--primary'+(freeAvail?' ffshop-pulse':'')+'" data-open="hearts">'+
                (packPurchasePending ? 'Check Pack Purchase' : (freeAvail ? 'Claim Your First Pack' : 'Buy a Pack'))+'<small>'+
                (packPurchasePending ? 'confirm the saved request' : primarySmall)+'</small></button>' +
              /* R391 (Jan): the raffle-ticket pack door is retired — raffle
                 tickets belong to heroes, never to pack currency. Its slot is
                 the DAILY FREE PACK now: one a day, server gated, cooldown
                 chip after the claim. */
              (dailyPackOpen
                ? '<button type="button" class="ffshop-open-btn ffshop-open-btn--daily" data-daily-pack="1">Claim Daily Pack<small>one a day \u00b7 saved to Wallet</small></button>'
                : '<button type="button" class="ffshop-open-btn ffshop-open-btn--daily is-claimed" data-daily-done="1" disabled>Pack claimed<small>today &middot; '+dailyResetText()+'</small></button>') +
            '</div>' +
            '<div class="ffshop-item-stat"><b>'+savedPacks+'</b> saved &middot; <b>'+(st.packs_opened||0)+'</b> opened</div>' +
          '</div>' +
          /* R376 (2026-08-06, Jan): the Shop sells exactly two things now.
             Packs, and the way into the Battlegrounds. The old launcher tile
             is gone; entry always costs a ticket, and this is where you buy
             one. The collection lives on the Profile tab. */
          /* ⛔ R599 (Jan): "mythic crystals should also be in the center
             battlegrounds last." Crystals sit beside Packs because both are
             things you BUY WITH HEARTS, and Battlegrounds goes last because it
             is where you GO rather than something you buy. The shelf now reads
             as two purchases and a door.
             On a phone the grid is one column, so this same order simply
             becomes the top-to-bottom order, which is the same argument. */
          crystalModuleHtml() +
          ((window.HOH_CONTRACT_ENABLED || (typeof window.hohIsGodMode==='function' && window.hohIsGodMode()))
            ? '<div class="ffshop-item ffshop-item--battle" id="ffshop-bg-ticket">' +
                '<div class="ffshop-item-art"><span class="ffshop-bg-emblem" aria-hidden="true">' +
                  '<svg viewBox="0 0 24 24" width="42" height="42" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
                  '<path d="M14.5 3.5 20 3.5 20 9"/><path d="M20 3.5 9.5 14"/><path d="M4 20l3.5-3.5"/><path d="M6.5 17.5 4.5 19.5"/>' +
                  '<path d="M9.5 3.5 4 3.5 4 9"/><path d="M4 3.5 14.5 14"/><path d="M20 20l-3.5-3.5"/></svg></span></div>' +
                '<div class="ffshop-item-name">Battlegrounds Entry</div>' +
                '<div class="ffshop-item-sub">One entry starts one run.</div>' +
                '<div class="ffshop-btns">' +
                  '<button type="button" class="ffshop-open-btn ffshop-open-btn--primary" data-bg-buy="1">Buy an Entry<small>300 hearts</small></button>' +
                  '<button type="button" class="ffshop-open-btn ffshop-open-btn--daily" data-bg-daily="1">Claim Daily Entry<small>one a day &middot; saved to Wallet</small></button>' +
                '</div>' +
                '<div class="ffshop-item-stat"><b id="ffshop-bg-held">0</b> owned</div>' +
              '</div>'
            : '<div class="ffshop-item ffshop-item--soon">' +
                '<div class="ffshop-item-art"><span class="ffshop-packart"><span class="ffshop-packart-back b1"></span><span class="ffshop-packart-back b2"></span><span class="ffshop-packart-hero ffshop-packart-empty"><img src="'+FF_LOGO+'" alt=""></span></span></div>' +
                '<div class="ffshop-item-name">New Wares Soon</div>' +
                '<div class="ffshop-item-sub">The shopkeeper is restocking. Card frames, favors, and stranger things.</div>' +
              '</div>') +
          /* ⛔ R597 (Jan): the third module, "designed the same way as the card
             packs where it shows a mythic glowing crystal icon with Mythic
             Crystals Label under it with a subtext and a how it works tab just
             like the card packs module". Same parts, same order, so the shelf
             reads as one shop rather than two designs side by side.
             The shelf grid is repeat(auto-fit,minmax(240px,300px)), so a third
             card flows on its own and still collapses to one column on a
             phone. No layout work was needed for it. */
        '</div>' +
      '</div>';

    /* ---------- R597 · the crystal module's controls ---------- */
    (function(){
      var W = window.HOHWallet; if(!W) return;
      var buyBtn = host.querySelector('[data-xtal-buy]');
      var dailyBtn = host.querySelector('[data-xtal-daily]');
      var howBtn = host.querySelector('[data-xtal-how]');
      function repaintShelf(){ try{ if(hostEl) renderShop(hostEl); }catch(_){ } }

      if(howBtn) howBtn.onclick = function(){ crystalHowPanel(); };

      if(dailyBtn) dailyBtn.onclick = function(){
        dailyBtn.disabled = true;
        W.claimDailyCrystal().then(function(r){
          if(r && r.ok && r.granted){ try{ flyCrystals(dailyBtn, 1); }catch(_){ } toast('A Mythic Crystal is yours.'); }
          else if(r && r.status === 'locked') toast('Open ' + W.unlockPacks() + ' packs to unlock crystals.');
          else if(r && r.ok) toast('Today\'s crystal is already claimed. Another lands tomorrow.');
          else toast('That could not be claimed. Try again in a moment.');
          repaintShelf();
        });
      };

      /* ⛔ R597: the normal button keeps Jan's single-buy / double-press sheet
         gesture. R605 recovery is intentionally one press; see the helper. */
      bindCrystalBuyButton(buyBtn);
    })();

    /* ⛔ R598: the shelf is painted from the wallet mirror, which may not have
       heard from the server yet on a first load. Nudge a sync (throttled to
       once every 4s inside the wallet, so opening the Shop repeatedly is free)
       and repaint when it answers. Without this the crystal module can only
       ever show its pre-sync state. */
    try { if (window.HOHWallet) window.HOHWallet.sync(); } catch (_) {}
    if (!window.__hohXtalShelfBound) {
      window.__hohXtalShelfBound = true;
      try {
        document.addEventListener('hoh:wallet-synced', function () {
          try { if (hostEl && hostEl.querySelector('#ffshop-xtal-mod')) renderShop(hostEl); } catch (_) {}
        });
      } catch (_) {}
    }

    startShelfRotation(host);
    /* Purchases add entries; claiming and entering are separate actions. */
    var bgBuy = host.querySelector('[data-bg-buy]');
    function bgHeldNow(){
      try { return Math.max(0, typeof window.hohGetBgTickets === 'function' ? window.hohGetBgTickets() | 0 : 0); }
      catch(_){ return 0; }
    }
    function bgMirror(kind){
      try {
        var getter = kind === 'unlimited' ? window.hohCurrentBgUnlimited : window.hohCurrentBgClimb;
        if(typeof getter === 'function') return getter();
        var row = kind === 'unlimited' ? window.__hohBgUnlimited : window.__hohBgClimb;
        if(!row) return null;
        var local = typeof window.hohContractIsLocal === 'function' && window.hohContractIsLocal();
        return row.account == null ? (local ? row : null) :
          (String(row.account).trim().toLowerCase() === String(email() || '').trim().toLowerCase() ? row : null);
      } catch(_) { return null; }
    }
    function bgAuthState(){
      try { if(typeof window.hohContractIsLocal === 'function' && window.hohContractIsLocal()) return 'ready'; } catch(_){}
      if(!email() || !pin()) return 'sign_in_required';
      return window.hohSupabaseClient ? 'ready' : 'still_loading';
    }
    function bgAskSignIn(){
      toast('Sign in with your email and PIN to keep entries with your account.');
      try { if(typeof window.hohSignInToYourHall === 'function') window.hohSignInToYourHall(); } catch(_){}
    }
    function paintBgHeld(){
      var held = bgHeldNow(), label = host.querySelector('#ffshop-bg-held');
      if(label) label.textContent = String(held);
      var buy = host.querySelector('[data-bg-buy]'), daily = host.querySelector('[data-bg-daily]'), enter = host.querySelector('[data-bg-enter]');
      var auth = bgAuthState(), pending = false, claimed = false;
      try { pending = typeof window.hohBgPurchasePending === 'function' && window.hohBgPurchasePending(); } catch(_){}
      try { claimed = typeof window.hohBgDailyClaimedToday === 'function' && window.hohBgDailyClaimedToday(); } catch(_){}
      if(buy && buy.getAttribute('data-bg-busy') !== '1'){
        buy.disabled = auth === 'still_loading';
        buy.innerHTML = pending ? 'Confirm Entry Purchase<small>Check your saved purchase</small>' : 'Buy an Entry<small>300 hearts</small>';
      }
      if(daily && daily.getAttribute('data-bg-busy') !== '1'){
        daily.disabled = claimed || auth === 'still_loading';
        /* R737: same claimed paint as the pack and crystal dailies. */
        daily.classList.toggle('is-claimed', !!claimed);
        daily.innerHTML = claimed ? 'Entry claimed<small>today &middot; '+dailyResetText()+'</small>' : 'Claim Daily Entry<small>one a day &middot; saved to Wallet</small>';
      }
      if(enter && enter.getAttribute('data-bg-busy') !== '1'){
        var u = bgMirror('unlimited'), climb = u && u.mode_open === false ? bgMirror('climb') : null;
        var active = u && u.mode_open ? u : climb;
        var resume = !!(active && active.live && active.run_id != null &&
          (!active.run_status || active.run_status === 'open' || active.run_status === 'settled_win' || active.at_stop));
        enter.disabled = auth === 'still_loading';
        enter.innerHTML = resume ? 'Resume Battlegrounds<small>' + (active === u ? 'Round ' + Math.max(1, u.round | 0) : 'Continue your saved run') + '</small>' :
          'Enter Battlegrounds<small>' + (held ? held + (held === 1 ? ' entry ready' : ' entries ready') : 'Choose your party') + '</small>';
      }
    }
    function bgBuyEntry(){
      if(typeof window.hohBgBuy !== 'function'){ toast('The entry desk is still loading. Try again in a moment.'); return; }
      var btn = host.querySelector('[data-bg-buy]'), scope = captureShopAccount();
      if(!btn || btn.getAttribute('data-bg-busy') === '1') return;
      btn.disabled = true; btn.setAttribute('data-bg-busy','1'); btn.setAttribute('aria-busy','true');
      btn.innerHTML = 'Buying Entry<small>Confirming your purchase</small>';
      Promise.resolve().then(function(){ return window.hohBgBuy(); }).then(function(res){
        if(!shopAccountIsCurrent(scope)) return;
        if(res && res.ok) toast('1 entry added.');
        else if(res && res.status === 'confirming_purchase') toast('Your purchase is saved. Confirm it to check the same request.');
        else if(res && res.status === 'confirming_payment') toast('Your saved Heart balance changed. Reopen the Hall before trying again.');
        else if(res && res.status === 'database_update_required') toast('Entry purchases need the receipt update. Your reserved Hearts were returned.');
        else if(res && (res.status === 'entry_held' || res.status === 'entry_already_held')) toast('Extra entries are temporarily unavailable. Your reserved Hearts were returned.');
        else if(res && res.status === 'not_enough_hearts') toast('You need 300 hearts for an entry.');
        else if(res && res.status === 'refund_waiting') toast('Return to this account to restore its reserved Hearts.');
        else toast('The entry could not be bought. Any reserved Hearts were returned.');
      }, function(){
        if(shopAccountIsCurrent(scope)) toast('Your purchase is saved. Confirm it to check the same request.');
      }).then(function(){
        if(!shopAccountIsCurrent(scope)) return;
        btn.removeAttribute('data-bg-busy'); btn.removeAttribute('aria-busy'); paintBgHeld();
      });
    }
    function bgRefresh(){
      var D = window.HOHContractData;
      try { if(typeof window.hohSyncBgTickets === 'function') window.hohSyncBgTickets().then(paintBgHeld,paintBgHeld); } catch(_){}
      try { if(D && D.unlimitedState) D.unlimitedState(true).then(paintBgHeld,paintBgHeld); } catch(_){}
      try { if(D && D.ladderState) D.ladderState(true).then(paintBgHeld,paintBgHeld); } catch(_){}
    }
    if(bgBuy){
      paintBgHeld(); bgRefresh();
      if(!host.__bgHeldWired){
        host.__bgHeldWired = 1;
        ['hoh:bg-tickets-changed','hoh:bg-climb-changed','hoh:bg-unlimited-changed'].forEach(function(name){
          document.addEventListener(name,function(){ if(document.body.contains(host)) paintBgHeld(); });
        });
      }
      bgBuy.addEventListener('click',function(){
        if(bgBuy.disabled) return;
        if(bgAuthState() === 'sign_in_required'){ bgAskSignIn(); return; }
        bgBuyEntry();
      });
      var daily = host.querySelector('[data-bg-daily]');
      if(daily) daily.addEventListener('click',function(){
        if(daily.disabled || daily.getAttribute('data-bg-busy') === '1') return;
        if(bgAuthState() === 'sign_in_required'){ bgAskSignIn(); return; }
        if(typeof window.hohClaimDailyBgEntry !== 'function'){ toast('The free entry desk is still loading.'); return; }
        var scope = captureShopAccount();
        daily.disabled = true; daily.setAttribute('data-bg-busy','1'); daily.setAttribute('aria-busy','true'); daily.textContent = 'Claiming Entry…';
        Promise.resolve().then(function(){ return window.hohClaimDailyBgEntry(); }).then(function(res){
          if(!shopAccountIsCurrent(scope)) return;
          toast(res && res.ok ? (res.granted ? '1 free entry added.' : 'Your free entry has already been claimed.') : 'The free entry could not be checked. Try again.');
        },function(){ if(shopAccountIsCurrent(scope)) toast('The free entry could not be checked. Try again.'); }).then(function(){
          if(!shopAccountIsCurrent(scope)) return;
          daily.removeAttribute('data-bg-busy'); daily.removeAttribute('aria-busy'); paintBgHeld();
        });
      });
      var enter = host.querySelector('[data-bg-enter]');
      if(enter) enter.addEventListener('click',function(){
        if(enter.disabled || enter.getAttribute('data-bg-busy') === '1') return;
        if(bgAuthState() === 'sign_in_required'){ bgAskSignIn(); return; }
        if(typeof window.hohActivateBattlegroundsEntry !== 'function'){ toast('Battlegrounds is still loading.'); return; }
        var scope = captureShopAccount();
        enter.disabled = true; enter.setAttribute('data-bg-busy','1'); enter.setAttribute('aria-busy','true'); enter.textContent = 'Checking Your Run…';
        Promise.resolve().then(function(){ return window.hohActivateBattlegroundsEntry(); }).then(function(res){
          if(!shopAccountIsCurrent(scope)) return;
          if(!res || !res.ok) toast(res && res.status === 'no_entry' ? 'Buy an entry or claim a free one above.' : 'Your run could not be checked. Try again.');
        },function(){ if(shopAccountIsCurrent(scope)) toast('Your run could not be checked. Try again.'); }).then(function(){
          if(!shopAccountIsCurrent(scope)) return;
          enter.removeAttribute('data-bg-busy'); enter.removeAttribute('aria-busy'); paintBgHeld();
        });
      });
    }
    var howLink = host.querySelector('.ffshop-how-link');
    if(howLink) howLink.addEventListener('click', openHow);
    /* R320: [data-open] scopes the wiring to the real pack buttons only. */
    host.querySelectorAll('.ffshop-open-btn[data-open]').forEach(function(b){
      b.addEventListener('click', function(){ openPacks(b.getAttribute('data-open')); });
    });
    /* R391: the daily free pack door */
    var dpBtn = host.querySelector('[data-daily-pack]');
    if(dpBtn) dpBtn.addEventListener('click', function(){ claimDailyPack(dpBtn); });
    /* applied badges: prompt or upgrade once the board settles */
    setTimeout(syncShopBadges, 550);
    var strip = host.querySelector('.ffshop-badges');
    if(strip){
      strip.addEventListener('click', function(e){
        var chip = e.target.closest('.ffshop-badge.is-claimable');
        if(chip) claimBadge(chip.getAttribute('data-badge'), chip);
      });
      strip.addEventListener('keydown', function(e){
        if(e.key!=='Enter'&&e.key!==' ') return;
        var chip = e.target.closest('.ffshop-badge.is-claimable');
        if(chip){ e.preventDefault(); claimBadge(chip.getAttribute('data-badge'), chip); }
      });
    }
    /* R247: the god debug actions moved to the gear panel's Shop tab
       (hoh-fresh-faces.js), wired to window.hohShopDebug — nothing to bind
       inside the shop render anymore. */
  }

  /* R247: reopen the badge apply menu on demand for testing — clears the
     session snooze and shows the highest tier actually earned (falls back to
     Packbreaker I). Picking a hero applies for real, same as the live flow. */
  function debugBadgePicker(){
    badgeSnooze = {};
    var sT=badgeStats(), keyT='packbreaker-1';
    var defsT=badgeDefs();
    for(var iT=0;iT<defsT.length;iT++){
      var dT=defsT[iT], vT=sT[dT.stat]||0, eT=0;
      for(var tT=0;tT<dT.tiers.length;tT++){ if(vT>=dT.tiers[tT]) eT=tT+1; }
      if(eT>0){ keyT=regKeyFor(dT.key, eT); break; }
    }
    if(typeof window.hohBadgeApplyPicker==='function') window.hohBadgeApplyPicker(keyT, {});
  }
  /* R247: expose the shop's god-only debug actions so the gear panel's Shop
     tab can drive them from outside this module. */
  window.hohShopDebug = {
    isFree: function(){ return !!debugFree; },
    toggleFree: function(){
      debugFree = !debugFree;
      if(hostEl && document.body.contains(hostEl)) renderShop(hostEl);
      return debugFree;
    },
    generate: function(btn){ try { generateMissingCardText(btn); } catch(_){} },
    badgePicker: function(){ try { debugBadgePicker(); } catch(_){} }
  };

  /* ⛔ ============ R568 · THE RETRO-FILL COVERS THE WHOLE HALL ============
     Jan: "at all of the cards that say a brave hero of the hall, I want it to
     retroactively populate them in the same way that all of the cards that
     were retroactively already populated ... Do not overwrite any changes of
     people who already changed this."

     THIS IS THE SAME TOOL AND THE SAME PROMPT. `/card-text` on the gift-cards
     worker is the endpoint that wrote every card text in the Hall today; this
     button is what walked it across the collection last time. Two things
     change and nothing else does.

     1. IT IS NO LONGER FF-ONLY. R423 restricted it because Jan's ruling then
        was that non-FF cards read one generic line their owner could edit.
        That ruling is what this ask reverses, in his own words — and the
        cards in his screenshots wearing "A brave hero of the Hall." are HALL
        heroes, not FramedFantasy portraits, so an FF-only sweep would have
        walked straight past every card he was pointing at.
     2. IT SELECTS ON `needsCardText`, which checks `edited:true` FIRST and
        alone. A hero whose owner typed their own words is skipped before any
        other condition is even considered.

     ⛔ WHAT THIS TOOL CANNOT DO IS ENFORCE THE LENGTH. The worker writes
     `card_meta` itself and its source is not in this repo, so a long
     generation lands long in the database. `cardText` clamps at the glass so
     nothing can ever PRINT past the ceiling, and `sql/R568A-...` trims the
     stored rows so the database agrees with the screen. Run the SQL after the
     sweep; the order matters only for tidiness, never for what a visitor
     sees. */
  /* ⛔ R572 UX: a sweep over a thousand cards is a QUARTER OF AN HOUR of work,
     and the only way to end it used to be closing the tab, which is what Jan
     did. A long job a person cannot stop is a trap. The button becomes the
     stop control while a run is going, and stopping is safe because every card
     is written to the database the moment it is generated: pressing the button
     again picks up exactly what is still missing. */
  /* Jan, 2026-10-28: "we have a ton of character cards that stil say "brave hero of the hall" so i want to be able to
     click that and have it generate each cahracters subtext and text ... but obviously only the ones that say brave hero
     of the hall". The card writer passes no minimum now: Epic or higher reached 2 of the 88 public cards still on the
     neutral line (most are Uncommon, like Theoden). needsCardText alone decides, so an owner's own words are never
     touched. The encounter prep in hoh-tools.js still passes one; with the rarity picker gone from the debug panel it
     is Epic or higher, as before. */
  var missingWriting = window.hohMissingWriting = {
    minimum: function(){ return document.getElementById('hoh-god-writing-rarity')?.value === 'rare' ? 'rare' : 'epic'; },
    label: function(minimum){ return minimum === 'rare' ? 'Rare or higher' : 'Epic or higher'; },
    eligible: function(hero, minimum){
      return !!hero && hero.approved === true && !hero.is_hidden &&
        (!minimum || (minimum === 'rare' ? ['rare','epic','legendary','mythic'] : ['epic','legendary','mythic']).includes(tierFor(hero.hearts)));
    },
    load: async function(){
      var client=window.hohSupabaseClient, rows=[], seen=new Set();
      if(!client?.from)throw Error('The public Hall could not be refreshed. Try again.');
      for(var offset=0;offset<60000;offset+=1000){
        var timer, result;
        try{
          result=await Promise.race([
            client.from('characters').select('id,name,hearts,approved,is_hidden,is_ff_portrait,story,card_meta')
              .eq('approved',true).or('is_hidden.is.null,is_hidden.eq.false').order('id',{ascending:true}).range(offset,offset+999),
            new Promise(function(_,reject){timer=setTimeout(function(){reject(Error('The public Hall took too long to refresh. Try again.'));},15000);})
          ]);
        }finally{clearTimeout(timer);}
        if(result?.error || !Array.isArray(result?.data))throw Error('The public Hall could not be refreshed. Try again.');
        result.data.forEach(function(hero){if(hero.approved===true&&!hero.is_hidden&&!seen.has(hero.id)){seen.add(hero.id);rows.push(hero);}});
        if(result.data.length<1000)return rows;
      }
      throw Error('The public Hall could not be fully checked. Try again.');
    }
  };
  var _cardTextRun = null;
  async function generateMissingCardText(btn){
    if(_cardTextRun){ _cardTextRun.cancel = true; return; }
    var run = _cardTextRun = { cancel:false };
    btn.disabled=false;btn.textContent='Refreshing public Hall · tap to stop';
    var all;
    try{all=await missingWriting.load();}catch(error){release();toast(error.message);return;}
    if(run.cancel){release();toast('Stopped before writing any card texts.');return;}
    var todo=all.filter(needsCardText).filter(function(hero){ return missingWriting.eligible(hero); });
    if(!todo.length){release();toast('Every card already has its own text.');return;}
    var i = 0, okN = 0, failN = 0, failWhy = {};
    btn.disabled = true;
    btn.textContent = 'Checking the worker…';
    /* OPTIONS checks reachability without a deliberate 400 or a model call.
       Bound both requests and response reads so an interrupted connection
       cannot leave this button stuck. A timed-out write is checked on rerun
       through the Worker's saved-text guard, never immediately duplicated. */
    function requestCard(options){
      var controller=new AbortController(), timer;
      options.signal=controller.signal;
      return Promise.race([
        fetch(CARD_TEXT_ENDPOINT,options).then(function(r){
          if(options.method==='OPTIONS')return r;
          return r.json().catch(function(){return {};}).then(function(data){return {status:r.status,json:function(){return Promise.resolve(data);}};});
        }),
        new Promise(function(_,reject){timer=setTimeout(function(){controller.abort();reject({give:'request timed out; check saved text on the next run'});},65000);})
      ]).finally(function(){clearTimeout(timer);});
    }
    requestCard({method:'OPTIONS'}).then(function(h){
      if(h.status===404) throw { code:'route' };
      if(h.status===403) throw { code:'origin' };
      if(h.status!==204&&h.status!==200)throw {code:'service'};
      next();
    }).catch(function(err){
      /* ⛔ R572: release() and not just a label reset. This path used to leave
         `_cardTextRun` set, so the button had quietly become a Stop control for
         a run that had already died, and the next press would do nothing at all
         while looking like it worked. */
      release();
      toast(err && err.code==='route'
        ? 'The worker is running OLD code without /card-text. Paste the current Worker file into quest-ai, then try again.'
        : err && err.code==='origin'
        ? 'The worker refused this page. Run this from framedfantasy.com, not a preview URL.'
        : 'Could not reach the worker at all. Check your connection.');
    });
    /* ⛔ ===== R572 · ONE BAD CARD MUST NOT END THE RUN =====
       Jan: "every time i press the generate button it always somehow has more
       even if previously it already did like 353/353", and "how come some
       characters still say a brave hero of the hall".
       THOSE ARE ONE FAULT AND IT IS RIGHT HERE. The old catch below stopped
       the WHOLE run on the FIRST failure of ANY card. Over a thousand
       sequential model calls that is not an edge case, it is a certainty: one
       `anthropic_429` rate limit, one `529 overloaded`, one reply the JSON
       parser cannot read, and every remaining card was abandoned. The button
       then reset to its normal label, so the run looked finished. It was not.
       That is why the number shrank a little on every press and never reached
       zero, and why cards like Knight-Solarus Samael Pendry still carry the
       neutral line: nobody ever got to them.
       ⛔ AND A SECOND, QUIETER LEAK: a 200 carrying neither `meta` nor
       `error` fell through to `setTimeout(next)` and was skipped in total
       silence, not counted as written and not counted as failed. The progress
       counter still marched to N/N. That is the "353/353 and yet there are
       more" reading exactly.
       THE RULE NOW: only a RUN-WIDE fault stops the run (no API key, the
       route missing, the origin refused). Anything a single card can cause is
       retried with backoff, and if it still will not write, that ONE card is
       recorded as failed and the run carries on. Nothing is ever skipped
       silently, and the finish line tells the truth. */
    var RETRIES = 3;
    /* ⛔ `at` is passed in, never read off `i`. `i` has already advanced by the
       time a retry paints, so reading it there numbered the card being retried
       as the NEXT one and the counter appeared to jump forward and back. */
    function paint(at, retrying){
      btn.disabled = false;                       /* stoppable, see _cardTextRun */
      btn.textContent = 'Writing ' + at + '/' + todo.length +
                        (retrying ? ' · retrying' : '') +
                        (failN ? ' · ' + failN + ' failed' : '') + ' · tap to stop';
    }
    function release(){ _cardTextRun = null; btn.disabled = false; btn.textContent = CARD_TEXT_BTN; }
    function stop(fatal){
      release();
      /* ⛔ a fatal stop still has to report what it DID write, or an hour of
         successful work reads as nothing having happened. */
      var did = okN ? ' ' + okN + ' cards were written before it stopped.' : '';
      toast((fatal === 'key'
        ? 'The worker is live but its Anthropic key is missing. Add ANTHROPIC_API_KEY to quest-ai, then press the button again.'
        : fatal === 'origin'
        ? 'The worker refused this page. Run this from framedfantasy.com, not a preview link.'
        : 'The worker is running OLD code without /card-text. Paste the current Worker file into quest-ai, then try again.') + did);
    }
    function finish(){
      var stopped = run.cancel;
      release();
      /* ⛔ Never announce a total run when part of it failed. The number that
         matters to Jan is how many cards STILL say the neutral line. */
      /* ⛔ R575: name the commonest reason. A bare count of failures is what
         left Jan with nothing to tell me except that it had stopped. */
      var top = '', topN = 0, w;
      for(w in failWhy){ if(failWhy[w] > topN){ topN = failWhy[w]; top = w; } }
      var why = top ? ' Most were ' + top + '.' : '';
      toast(stopped
        ? 'Stopped. ' + okN + ' written, ' + (todo.length - i) + ' not started. Press the button again to carry on.'
        : failN
        ? 'Wrote ' + okN + '. ' + failN + ' could not be written and still need words.' + why + ' Press the button again to retry just those.'
        : 'Wrote ' + okN + '. Every card that said “A brave hero of the Hall.” has its own text now.');
    }
    function step(){ setTimeout(next, 250); }
    function next(){
      if(run.cancel || i >= todo.length) return finish();
      paint(i + 1, false);
      attempt(todo[i++], 0, i);
    }
    function attempt(hero, tries, at){
      if(run.cancel)return finish();
      requestCard( { method:'POST', headers:{'content-type':'application/json'}, body: JSON.stringify({ character_id: hero.id, public_only:true }) })
        .then(function(r){
          /* ⛔ ===== R575 · A 404 IS NOT ALWAYS A MISSING ROUTE =====
             Jan, with the R572 fix already live: "again when i click it its
             still 217 left, is it randomly stopping? i never clicked stop yet
             when i check it its stopped."
             THIS LINE IS WHY. The worker answers 404 in TWO different
             situations: the route does not exist, and `{"error":"not_found"}`
             when it cannot read one particular character row. R572 treated
             every 404 as the first, so a SINGLE unreadable card ended the whole
             sweep and the toast blamed the worker's version, which was a lie
             and sent the hunt in the wrong direction.
             The route question is already settled before the loop ever runs:
             OPTIONS checks reachability; the response body distinguishes
             a missing character from a missing route on the actual write.
             ⛔ AND IT IS NOT RETRIED. A row the worker cannot read will not
             become readable 0.8 seconds later, so retrying it three times only
             makes the sweep slower without ever changing the answer. */
          if(r.status===403) throw { fatal:'origin' };
          if(r.status===404)return r.json().then(function(d){if(d&&d.error==='not_found')throw {give:'not found on the server'};throw {fatal:'route'};});
          return r.json().catch(function(){ throw { retry:'unreadable reply' }; });
        })
        .then(function(d){
          if(d && d.error === 'no_api_key') throw { fatal:'key' };
          /* ⛔ ===== R575 · A CARD WITH NO WORDS IS NOT A WRITTEN CARD =====
             MEASURED ON LIVE DATA, and this is the real reason the sweep never
             finished. 215 cards sat stamped source:"ai" with legend:"" and every
             other field empty, because the worker answered 200 with a meta
             object carrying no words. This line counted that as written, so the
             button reported progress it had not made, while needsCardText kept
             picking the same cards up on the very next run. Forever.
             The words are the test now, not the envelope. */
          if(d && d.meta && String(d.meta.legend || '').trim()){
            hero.card_meta = d.meta;
            if(needsCardText(hero))throw {give:'saved text is still a placeholder or copied story'};
            var displayed=(window.hohGetAllCharacters?.()||[]).find(function(row){return row.id===hero.id;});
            if(displayed && !displayed.card_meta?.edited)displayed.card_meta=d.meta;
            okN++; return step();
          }
          if(d && d.meta){
            /* keep the row in step with the server, then count it honestly */
            hero.card_meta = d.meta;
            throw { retry:'written back empty' };
          }
          /* a row the server cannot read will not appear 0.8 seconds later */
          if(d && d.error === 'not_found') throw { give:'not found on the server' };
          /* ⛔ EMPTINESS IS RETRIED, because the timestamps say it is TRANSIENT.
             The 215 blanks and 848 successes are interleaved across the very
             same half hour, so the same card that came back empty can come back
             written moments later. Giving up on the first blank would leave a
             third of the Hall permanently unwritten for no reason. */
          if(d && (d.error === 'empty_legend' || d.error === 'empty_from_model' || d.error === 'no_json_from_model')){
            throw { retry:'the writer returned no words' };
          }
          if(d && d.error) throw { retry:String(d.error) };
          /* ⛔ the silent-skip hole, closed. A reply with no meta and no error
             is a FAILURE, not a card to walk past without a word. */
          throw { retry:'empty reply' };
        })
        .catch(function(err){
          if(err && err.fatal) return stop(err.fatal);
          if(run.cancel) return finish();
          /* `give` means this card is hopeless but the run is not */
          if(!(err && err.give) && tries < RETRIES){
            /* 429 and 529 are the common ones and both want room, so back off
               rather than hammering: 0.8s, 1.6s, 3.2s. */
            paint(at, true);
            return setTimeout(function(){ attempt(hero, tries + 1, at); }, 800 * Math.pow(2, tries));
          }
          failN++;
          /* ⛔ SAY WHY, ON EVERY FAILURE, FLAT. R572 counted failures but never
             named one, so a run that ended early left nothing to diagnose with
             and Jan could only report that it had stopped. The R560 rule: a
             diagnostic that prints [object Object] tells nobody anything. */
          var why = (err && (err.give || err.retry)) || 'unknown';
          failWhy[why] = (failWhy[why] || 0) + 1;
          try{ console.warn('R575 CARD TEXT SKIPPED · ' + (hero && hero.name) + ' · id=' + (hero && hero.id) + ' · ' + why); }catch(_){ }
          step();
        });
    }
  }

  /* ---------------- hub entry ---------------- */

  /* Advance the response generation at the real account boundary, including a
     rapid A -> B -> A round trip where the email ultimately looks unchanged.
     Cross-tab token updates get the same treatment. */
  function syncShopAccountBoundary(){
    /* HALLHERO: the hero page (hoh-hero-page.js) loads this file only to draw
       one card in its lightbox, with window.__hohShopCardOnly set. The shop's
       account switch and refund flush belong to the Hall, where the wallet
       funnel that tells the server lives, so they never run there. */
    if(window.__hohShopCardOnly) return;
    try{ useShopAccount(); }catch(_){}
    try{ flushShopRefunds(); }catch(_){}
  }
  try{
    document.addEventListener('hoh:signed-in', syncShopAccountBoundary);
    document.addEventListener('hoh:signed-out', syncShopAccountBoundary);
  }catch(_){}
  try{
    window.addEventListener('storage', function(ev){
      if(ev && ev.key === 'hoh-your-hall-token') syncShopAccountBoundary();
    });
  }catch(_){}
  syncShopAccountBoundary();

  var bgEntryShopFocus = false;
  var walletShopFocus = '';
  function openHallShop(){
    /* Opening Profile first is intentional: it is the established public door
       that turns on Your Hall page mode. The following synchronous tab switch
       then lands on Shop whether the dashboard already existed or not. */
    if(typeof window.hohOpenYourHallProfile === 'function') window.hohOpenYourHallProfile();
    if(typeof window.hohYourHallSwitchView === 'function') window.hohYourHallSwitchView('shop');
    else return false;
    return true;
  }
  function focusWalletShop(){
    if(!walletShopFocus) return;
    var selector = walletShopFocus === 'crystals' ? '#ffshop-xtal-mod' : '#ffshop-pack-mod';
    var card = document.querySelector(selector);
    if(!card) return;
    var want = walletShopFocus;
    walletShopFocus = '';
    card.scrollIntoView({block:'center',behavior:'auto'});
    var button = want === 'crystals'
      ? card.querySelector('[data-xtal-buy]:not([disabled]),[data-xtal-daily]:not([disabled])')
      : card.querySelector('[data-open="hearts"]:not([disabled]),[data-daily-pack]:not([disabled])');
    if(button) try { button.focus({preventScroll:true}); } catch(_) { button.focus(); }
  }
  window.hohOpenPackShop = function(){
    walletShopFocus = 'packs';
    if(!openHallShop()){ walletShopFocus = ''; return false; }
    requestAnimationFrame(focusWalletShop);
    return true;
  };
  window.hohOpenCrystalShop = function(){
    walletShopFocus = 'crystals';
    if(!openHallShop()){ walletShopFocus = ''; return false; }
    requestAnimationFrame(focusWalletShop);
    return true;
  };
  function focusBgEntryShop(){
    if(!bgEntryShopFocus) return;
    var card = document.getElementById('ffshop-bg-ticket');
    if(!card) return;
    bgEntryShopFocus = false;
    card.scrollIntoView({block:'center',behavior:'auto'});
    var button = card.querySelector('[data-bg-buy]');
    if(button) try { button.focus({preventScroll:true}); } catch(_) { button.focus(); }
  }
  window.hohOpenBgEntryShop = function(){
    bgEntryShopFocus = true;
    if(typeof window.hohYourHallSwitchView === 'function') window.hohYourHallSwitchView('shop');
    else if(typeof window.hohOpenYourHall === 'function') window.hohOpenYourHall('shop');
    else { bgEntryShopFocus = false; return false; }
    requestAnimationFrame(focusBgEntryShop);
    return true;
  };
  window.hohHallRenderShop = function(host){
    hostEl = host;
    host.innerHTML = '<div class="hoh-yh-empty">Opening the shop…</div>';
    fetchState().then(function(){ renderShop(host); focusBgEntryShop(); focusWalletShop(); });
  };
})();
